import type {
  ActivitySubjectType,
  AdditionalCostType,
  AncillaryCostsType,
  ApiCallCredentialClass,
  ApiCallRejectionReason,
  ApiKeyRequestOutcomeDto,
  DashboardDataGap,
  DashboardFlaggedDeal,
  EmailLogKind,
  EmailLogStatus,
  ArchiveReason,
  CommercialAmenityCode,
  AreaCode,
  Condition,
  ConstructionPhase,
  ContactSource,
  ContactType,
  DataFieldGroup,
  DataFieldName,
  DocumentCategoryGroup,
  DocumentSection,
  DocumentTemplateCategory,
  SigningProofEmail,
  DOCUMENT_SOURCE_INPUTS,
  DealPhase,
  InquiryOutcome,
  ProspectLossReason,
  ProspectPhase,
  ProspectSource,
  ProspectSourceGroup,
  ProspectStatus,
  DealStatus,
  DealSource,
  DealProbability,
  DealCompletionHorizon,
  DealLossReason,
  DealType,
  MandateType,
  LeadSource,
  PropertyContactRole,
  EnergyCertificateBasis,
  EnergyCertificateYear,
  EnergyClass,
  FiringType,
  Furnishing,
  HeatingType,
  InteriorQuality,
  LeaseTerm,
  MandatePropertyState,
  MarketingType,
  PropertyStatus,
  RentType,
  SiteConstructibleType,
  SiteDevelopmentType,
  Subletting,
  ResidentialAmenityCode,
  ValuationRequestStatus,
  ValuationObjectType,
  ValuationUsage,
  ValuationReason,
  ValuationTimeHorizon,
  ValuationPriceExpectationKind,
  ValuationPriceExpectationType,
  ValuationHeatingType,
  ValuationEnergyEfficiencyClass,
  ValuationHouseType,
  ValuationBasement,
  ValuationDevelopmentPlan,
  ValuationServicingStatus,
  ValuationContaminationSuspicion,
  ValuationGraduatedIndexRent,
  ValuationUnitBreakdown,
  ValuationQuality,
  ValuationCondition,
  ValuationBuildingElement,
  ValuationParkingSpaceType,
  ValuationUnitLettingStatus,
  ValuationFieldKey,
  CompletenessProduct,
  ValuationAgentRequestKind,
  ValuationSource,
  ValuationViewingKind,
  CompletenessMarker,
  VatTreatment,
} from '@immo/api-contract';
import type { Messages } from '../index';
import type { EnumTranslation } from '../types';
import { DOCUMENT_CATEGORY_LABELS } from './document-category-labels';

// Domain-enum label tables, keyed by the en-US tokens from
// `@immo/api-contract`. Declared with `satisfies EnumTranslation<E>` so
// adding a value to the contract enum without a matching translation
// breaks `tsc` (ADR 0009, 2026-05-21 amendment).

const VALUATION_PRODUCT_LABELS = {
  ACQUISITION_BRIEFING: 'Acquisition briefing',
  VALUATION_1A: 'Valuation 1a',
  VALUATION_1B: 'Valuation 1b',
} as const satisfies EnumTranslation<ValuationAgentRequestKind>;

const COMPLETENESS_PRODUCT_LABELS = {
  ...VALUATION_PRODUCT_LABELS,
  EXPOSE: 'Exposé',
} as const satisfies EnumTranslation<CompletenessProduct>;

const VALUATION_SOURCE_KIND_LABELS = {
  OPERATOR: 'Operator valuation',
  VALUATION_1A: 'Valuation 1a',
  VALUATION_1B: 'Valuation 1b · {viewingKind}',
} as const satisfies EnumTranslation<ValuationSource['kind']>;

const VALUATION_VIEWING_KIND_LABELS = {
  INTERIOR_AND_EXTERIOR: 'Interior and exterior viewing',
  EXTERIOR_ONLY: 'Exterior viewing only',
  PARTIAL: 'Partial viewing',
  VIDEO: 'Video viewing',
  NONE: 'No viewing',
} as const satisfies EnumTranslation<ValuationViewingKind>;

const COMPLETENESS_MARKER_LABELS = {
  CONTRADICTED: 'contradicted',
  ASSUMED: 'assumed',
  UNKNOWN: 'unknown',
} as const satisfies EnumTranslation<CompletenessMarker>;

const MARKETING_TYPE_LABELS = {
  SALE: 'For sale',
  RENTAL: 'For rent',
} satisfies EnumTranslation<MarketingType>;

const PROPERTY_STATUS_LABELS = {
  DRAFT: 'Draft',
  IN_ACQUISITION: 'In Acquisition',
  ACTIVE: 'On the market',
  INACTIVE: 'Paused',
  ARCHIVED: 'Archived',
} satisfies EnumTranslation<PropertyStatus>;

const ARCHIVE_REASON_LABELS = {
  SOLD: 'Sold',
  RENTED: 'Let',
  TRANSACTED_EXTERNALLY: 'Sold/let elsewhere',
  WITHDRAWN: 'Withdrawn',
  MANDATE_EXPIRED: 'Mandate expired',
  MANDATE_TERMINATED: 'Mandate terminated',
  DUPLICATE: 'Duplicate / mis-entry',
  OTHER: 'Other',
} satisfies EnumTranslation<ArchiveReason>;

const CONDITION_LABELS = {
  FIRST_OCCUPATION: 'First occupation',
  LIKE_NEW: 'Like new',
  WELL_MAINTAINED: 'Well maintained',
  MODERNISED: 'Modernised',
  PARTIALLY_RENOVATED: 'Partially renovated',
  FULLY_RENOVATED: 'Fully renovated',
  PARTIALLY_FULLY_RENOVATED: 'Partially / fully renovated',
  NEEDS_PARTIAL_FULL_RENOVATION: 'Needs partial / full renovation',
  NEEDS_RENOVATION: 'Needs renovation',
  DILAPIDATED: 'Dilapidated',
  BY_ARRANGEMENT: 'By arrangement',
} satisfies EnumTranslation<Condition>;

const INTERIOR_QUALITY_LABELS = {
  STANDARD: 'Standard',
  UPSCALE: 'Upscale',
  LUXURY: 'Luxury',
} satisfies EnumTranslation<InteriorQuality>;

const CONSTRUCTION_PHASE_LABELS = {
  PROJECTED: 'Projected',
  UNDER_CONSTRUCTION: 'Under construction',
  COMPLETED: 'Completed',
} satisfies EnumTranslation<ConstructionPhase>;

const ENERGY_CERTIFICATE_BASIS_LABELS = {
  DEMAND: 'Demand certificate',
  CONSUMPTION: 'Consumption certificate',
} satisfies EnumTranslation<EnergyCertificateBasis>;

const ENERGY_CERTIFICATE_YEAR_LABELS = {
  EN_2008: 'EnEV 2008',
  EN_2014: 'EnEV 2014',
  GMODG_2027: 'GModG (from 2027)',
  WITHOUT: 'No certificate',
  NOT_REQUIRED: 'Not required',
  AT_VIEWING: 'At viewing',
} satisfies EnumTranslation<EnergyCertificateYear>;

const ENERGY_CLASS_LABELS = {
  A_PLUS: 'A+',
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',
  F: 'F',
  G: 'G',
  H: 'H',
} satisfies EnumTranslation<EnergyClass>;

/** The 20 catalog group names, shared by the fact review and the capture. */
const FACT_KEY_GROUP_LABELS = {
  AUF: 'Assignment, reference dates and inspection',
  KAT: 'Cadastre and plot',
  LAG: 'Location and surroundings',
  PLR: 'Planning law and public-law status',
  ERS: 'Servicing and contributions',
  GB: 'Land register, rights and encumbrances',
  GEB: 'Buildings in general',
  FLA: 'Areas',
  BAU: 'Structural elements and fittings',
  ENE: 'Energy and building services',
  MOD: 'Modernisation',
  ZUS: 'Condition and damage',
  NEB: 'Outbuildings, parking and outdoor areas',
  NUT: 'Use and letting',
  WEG: 'Condominium ownership',
  ZVG: 'Compulsory auction',
  UNT: 'Documents and information',
  SON: 'Other',
  VMK: 'Marketing',
  GEW: 'Commercial',
} as const;

/**
 * The enum↔label binding the operator Dossier renders through (IMM-686), keyed by
 * the fact key the taxonomy seeds — NOT by the contract type name, because a
 * `DossierEntry` only ever carries its `factKey`.
 *
 * Composed from the five maps above rather than re-typed, so the Dossier and the
 * customer-app surfaces that already read them can never disagree: this adds ZERO
 * new copy. `dossier-value-labels.test.ts` asserts totality against
 * `DOSSIER_FACT_ENUM_VALUES` — every enum fact key present, every declared value
 * labelled — which is BL-069's remedy in the executable form a runtime field set
 * allows.
 */
const DOSSIER_FACT_ENUM_LABELS = {
  'ZUS.OBJEKTZUSTAND': CONDITION_LABELS,
  'BAU.AUSSTATTUNGSQUALITAET': INTERIOR_QUALITY_LABELS,
  'GEB.BAUPHASE': CONSTRUCTION_PHASE_LABELS,
  'ENE.ENERGIEAUSWEIS.ausweisart': ENERGY_CERTIFICATE_BASIS_LABELS,
  'ENE.ENERGIEAUSWEIS.effizienzklasse': ENERGY_CLASS_LABELS,
} as const;

const HEATING_TYPE_LABELS = {
  STOVE: 'Stove',
  FLOOR_LEVEL: 'Floor-level heating',
  CENTRAL: 'Central heating',
  DISTRICT: 'District heating',
  UNDERFLOOR: 'Underfloor heating',
} satisfies EnumTranslation<HeatingType>;

const ADDITIONAL_COST_TYPE_LABELS = {
  SERVICE_CHARGE: 'Service charge',
  HEATING_COSTS: 'Heating costs',
  MAINTENANCE_RESERVE: 'Maintenance reserve',
  RENT_SUBSIDY: 'Rent subsidy',
  PARKING_SPACE_PRICE: 'Parking space price',
  FURNITURE_SURCHARGE: 'Furniture surcharge',
} satisfies EnumTranslation<AdditionalCostType>;

// IMM-788.1 — one dictionary block for the VAT treatment, imported by every
// price section/dialog that renders it (BL-082, BL-024's totality guard).
const VAT_TREATMENT_LABELS = {
  NET_PLUS_VAT: 'net plus VAT',
  VAT_INCLUSIVE: 'VAT inclusive',
} satisfies EnumTranslation<VatTreatment>;

const ANCILLARY_COSTS_TYPE_LABELS = {
  ADVANCE: 'Advance payment',
  FLAT_RATE: 'Flat rate',
} satisfies EnumTranslation<AncillaryCostsType>;

const FURNISHING_LABELS = {
  NONE: 'Unfurnished',
  PARTIALLY_FURNISHED: 'Partly furnished',
  FURNISHED: 'Furnished',
} satisfies EnumTranslation<Furnishing>;

const LEASE_TERM_LABELS = {
  INDEFINITE: 'Indefinite',
  FIXED_TERM: 'Fixed term',
} satisfies EnumTranslation<LeaseTerm>;

const RENT_TYPE_LABELS = {
  FIXED: 'Fixed rent',
  GRADUATED: 'Graduated rent',
  INDEXED: 'Index-linked rent',
} satisfies EnumTranslation<RentType>;

const SUBLETTING_LABELS = {
  ALLOWED: 'Allowed',
  NOT_ALLOWED: 'Not allowed',
  ON_REQUEST: 'On request',
} satisfies EnumTranslation<Subletting>;

export const FIRING_TYPE_LABELS = {
  OIL: 'Oil',
  GAS: 'Gas',
  LIQUID_GAS: 'Liquid gas',
  NATURAL_GAS_LIGHT: 'Natural gas (light)',
  NATURAL_GAS_HEAVY: 'Natural gas (heavy)',
  ELECTRIC: 'Electric',
  ALTERNATIVE: 'Alternative',
  SOLAR: 'Solar',
  GEOTHERMAL: 'Geothermal',
  AIR_HEAT_PUMP: 'Air heat pump',
  DISTRICT: 'District',
  DISTRICT_STEAM: 'District (steam)',
  LOCAL_HEAT: 'Local heat',
  HEAT_DELIVERY: 'Heat delivery',
  BLOCK_CHP: 'Block CHP',
  WATER_ELECTRIC: 'Water-electric',
  PELLET: 'Pellet',
  WOOD: 'Wood',
  COAL: 'Coal',
  BIOENERGY: 'Bioenergy',
  WIND_ENERGY: 'Wind',
  WATER_ENERGY: 'Water',
  ENVIRONMENTAL_HEAT: 'Environmental heat',
} satisfies EnumTranslation<FiringType>;

const SITE_DEVELOPMENT_TYPE_LABELS = {
  UNDEVELOPED: 'Undeveloped',
  PARTIAL: 'Partially developed',
  FULL: 'Fully developed',
  LOCAL_STANDARD: 'Local standard',
} satisfies EnumTranslation<SiteDevelopmentType>;

const SITE_CONSTRUCTIBLE_TYPE_LABELS = {
  PARAGRAPH_34_NEIGHBOURHOOD: 'Inner area (§ 34 BauGB)',
  PARAGRAPH_35_OUTSIDE_AREA: 'Outer area (§ 35 BauGB)',
  B_PLAN: 'Development plan (B-Plan)',
  NO_BUILDING_LAND: 'No building land',
} satisfies EnumTranslation<SiteConstructibleType>;

const RESIDENTIAL_AMENITY_LABELS = {
  BALCONY: 'Balcony',
  GARDEN: 'Garden',
  LIFT: 'Lift',
  BARRIER_FREE: 'Barrier-free',
  GUEST_TOILET: 'Guest toilet',
  BUILT_IN_KITCHEN: 'Built-in kitchen',
  LISTED_MONUMENT: 'Listed monument',
  LODGER_FLAT: 'Lodger flat',
  FLAT_SHARE_SUITABLE: 'Suitable for flat-share',
  HOLIDAY_HOME: 'Holiday home',
  REQUIRES_CERTIFICATE_OF_ELIGIBILITY: 'Certificate of eligibility required',
  LAN_CABLING: 'LAN cabling',
  AIR_CONDITIONING: 'Air conditioning',
  PARKING_SPACES: 'Parking spaces',
  NUMBER_OF_BALCONIES: 'Number of balconies',
  NUMBER_OF_TERRACES: 'Number of terraces',
  PARKING_SPACE_PRICE: 'Parking space price',
  CELLAR_STATE: 'Cellar',
  PETS_POLICY: 'Pets policy',
  PARKING_SPACE_TYPE: 'Parking space type',
  FLOORING_TYPE: 'Flooring type',
} satisfies EnumTranslation<ResidentialAmenityCode>;

const COMMERCIAL_AMENITY_LABELS = {
  GOODS_LIFT: 'Goods lift',
  LOADING_RAMP: 'Loading ramp',
  HALL_HEIGHT: 'Hall height',
  GOODS_LIFT_LOAD: 'Goods lift load',
  FLOOR_LOAD: 'Floor load',
  CONNECTED_LOAD: 'Connected load',
  CRANE_RUNWAY_LOAD: 'Crane runway load',
  SHOP_WINDOW_LENGTH: 'Shop window length',
  NUMBER_OF_SEATS: 'Number of seats',
  NUMBER_OF_BEDS: 'Number of beds',
} satisfies EnumTranslation<CommercialAmenityCode>;

const CONTACT_TYPE_LABELS = {
  OWNER: 'Owner',
  BUYER: 'Buyer',
  TENANT: 'Tenant',
  LANDLORD: 'Landlord',
  PARTNER: 'Partner',
} satisfies EnumTranslation<ContactType>;

const PROPERTY_CONTACT_ROLE_LABELS = {
  OWNER: 'Owner',
  PURCHASE_PROSPECT: 'Purchase prospect',
  RENTAL_PROSPECT: 'Rental prospect',
  TENANT: 'Tenant',
  PROPERTY_MANAGER: 'Property manager',
  EXTERNAL_BROKER: 'External broker',
  NOTARY: 'Notary',
  APPRAISER: 'Appraiser',
  CRAFTSMAN: 'Craftsman',
  FINANCIER: 'Lender',
  OTHER: 'Other',
} satisfies EnumTranslation<PropertyContactRole>;

const DOCUMENT_CATEGORY_GROUP_LABELS = {
  MARKETING: 'Marketing',
  ENERGY_TECHNICAL: 'Energy & technical',
  OWNERSHIP_LAND_REGISTRY: 'Ownership & land registry',
  BUILDING_PERMITS: 'Building & permits',
  ADMINISTRATION: 'Administration',
  CONTRACTS_TRANSACTION: 'Contracts & transaction',
  FINANCE_TAX: 'Finance & tax',
  PERSON_LEGITIMATION: 'Person & legitimation',
  INSURANCE: 'Insurance',
  OTHER: 'Other',
} satisfies EnumTranslation<DocumentCategoryGroup>;

const DOCUMENT_SECTION_LABELS = {
  PROPERTY: 'Property documents',
  INTERNAL: 'Internal documents',
} satisfies EnumTranslation<DocumentSection>;

const DOCUMENT_CLASSIFICATION_MARKERS = {
  sorting: 'Sorting …',
  'auto-sorted': 'Automatically sorted',
} as const;

const CONTACT_SOURCE_LABELS = {
  MANUAL: 'Manual',
  PARTNER: 'Partner',
  PORTAL: 'Portal',
  WEBSITE: 'Website',
  REFERRAL: 'Referral',
  COLD_OUTREACH: 'Cold outreach',
  OTHER: 'Other',
} satisfies EnumTranslation<ContactSource>;

const AREA_CODE_LABELS = {
  LIVING_AREA: 'Living area',
  PLOT_AREA: 'Plot area',
  USABLE_AREA: 'Usable area',
  TOTAL_AREA: 'Total area',
  NET_FLOOR_SPACE: 'Net floor space',
  OFFICE_AREA: 'Office area',
  RETAIL_AREA: 'Retail area',
  SHOP_AREA: 'Shop area',
  STORAGE_AREA: 'Storage area',
  HALL_AREA: 'Hall area',
  GASTRONOMY_AREA: 'Catering area',
  LETTABLE_AREA: 'Lettable area',
  PARKING_SPACES: 'Parking spaces',
  ADDITIONAL_AREA: 'Additional area',
  BALCONY_AREA: 'Balcony area',
  CUBATURE: 'Cubature',
  MIN_DIVISIBLE_AREA: 'Min. divisible',
  ROOMS: 'Rooms',
  BEDROOMS: 'Bedrooms',
  BATHROOMS: 'Bathrooms',
  FLOORS_TOTAL: 'Floors',
  FLOOR: 'Floor',
} satisfies EnumTranslation<AreaCode>;

const PROSPECT_PHASE_LABELS = {
  INTEREST: 'Interest',
  WITHDRAWAL_NOTICE_CONFIRMED: 'Withdrawal notice confirmed',
  CONTACTED: 'Contacted',
  VIEWING_PROPOSED: 'Viewing proposed',
  VIEWING_SCHEDULED: 'Invited to viewing',
  VIEWING_COMPLETED: 'Viewing completed',
  OFFER_RECEIVED: 'Offer received',
  FINANCING_CONFIRMED: 'Financing confirmed',
  RESERVED: 'Reserved',
  NOTARY_APPOINTMENT: 'Notary appointment',
  PURCHASED: 'Purchased',
} as const satisfies EnumTranslation<ProspectPhase>;

const PROSPECT_STATUS_LABELS = {
  ACTIVE: 'Active',
  WON: 'Won',
  LOST: 'Lost',
} as const satisfies EnumTranslation<ProspectStatus>;

const PROSPECT_LOSS_REASON_LABELS = {
  NO_LONGER_INTERESTED: 'No longer interested',
  WITHDRAWAL_EXERCISED: 'Withdrawal exercised',
  BOUGHT_ELSEWHERE: 'Bought another property',
  FINANCING_FAILED: 'Financing not possible',
  PRICE_TOO_HIGH: 'Price too high',
  PROPERTY_NOT_SUITABLE: 'Property not suitable',
  SOLD_TO_OTHER: 'Property sold to someone else',
  UNREACHABLE: 'Unreachable',
  OTHER: 'Other',
} as const satisfies EnumTranslation<ProspectLossReason>;

const PROSPECT_SOURCE_LABELS = {
  IMMOSCOUT24: 'ImmoScout24',
  IMMOWELT: 'Immowelt',
  KLEINANZEIGEN: 'Kleinanzeigen',
  OTHER_PORTAL: 'Other portal',
  WEBSITE: 'Website',
  WEB_EXPOSE: 'Web exposé',
  SOCIAL_MEDIA: 'Social media',
  PROPERTY_SIGN: 'Sign at the property',
  SHOP_WINDOW_OR_PRINT: 'Shop window / print',
  BUYER_DATABASE_OUTREACH: 'Outreach from own buyer list',
  REFERRAL: 'Referral',
  TIPSTER_OR_PARTNER: 'Tipster / partner',
  PHONE: 'Phone',
  EMAIL: 'Email',
  OPEN_HOUSE: 'Open viewing',
  OTHER: 'Other',
} as const satisfies EnumTranslation<ProspectSource>;

const INQUIRY_OUTCOME_LABELS = {
  PROSPECT_CREATED: 'Prospect created',
  ATTACHED_TO_ACTIVE_PROSPECT: 'Added to existing prospect',
  NO_PROSPECT_PROPERTY_ARCHIVED: 'No prospect — property archived',
  NO_PROSPECT_RENTAL: 'No prospect — rental',
} as const satisfies EnumTranslation<InquiryOutcome>;

const PROSPECT_SOURCE_GROUP_LABELS = {
  PORTALS: 'Portals',
  OWN_CHANNELS: 'Own channels',
  AGENT_OUTREACH: 'Agent outreach',
  NETWORK: 'Network',
  DIRECT_CONTACT: 'Direct contact',
} as const satisfies EnumTranslation<ProspectSourceGroup>;

const DEAL_PHASE_LABELS = {
  IN_EXCHANGE: 'In exchange',
  APPOINTMENT_SCHEDULED: 'Appointment scheduled',
  APPOINTMENT_COMPLETED: 'Appointment completed',
  VALUATION_COMPLETED: 'Valuation completed',
  MANDATE_RECEIVED: 'Mandate received',
  CONTRACT_SENT: 'Contract sent',
  CONTRACT_RECEIVED: 'Contract received',
  EXPOSE_REVIEW: 'Exposé review',
  ONLINE: 'Online',
} satisfies EnumTranslation<DealPhase>;

const DEAL_STATUS_LABELS = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  DEFERRED: 'Deferred',
  WON: 'Won',
  LOST: 'Lost',
} satisfies EnumTranslation<DealStatus>;

const DEAL_SOURCE_LABELS = {
  MANUAL: 'Manual',
  LEAD: 'Lead',
} satisfies EnumTranslation<DealSource>;

// One table, two mount points (IMM-456.6): the leads list and the deal rail's
// origin card name the same acquisition channel, so a second copy is exactly how
// the two surfaces would start wording it differently.
const LEAD_SOURCE_LABELS = {
  MANUAL: 'Manual',
  PARTNER: 'Partner',
  PORTAL: 'Portal',
  WEBSITE: 'Website',
  REFERRAL: 'Referral',
  COLD_OUTREACH: 'Cold outreach',
  OTHER: 'Other',
} satisfies EnumTranslation<LeadSource>;

const DEAL_PROBABILITY_LABELS = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
} satisfies EnumTranslation<DealProbability>;

const DEAL_COMPLETION_HORIZON_LABELS = {
  UNDER_ONE_MONTH: 'Under 1 month',
  ONE_TO_THREE_MONTHS: '1–3 months',
  THREE_TO_SIX_MONTHS: '3–6 months',
  OVER_SIX_MONTHS: 'Over 6 months',
} satisfies EnumTranslation<DealCompletionHorizon>;

const DEAL_TYPE_LABELS = {
  SALE: 'For sale',
  RENTAL: 'For rent',
} satisfies EnumTranslation<DealType>;

const DEAL_MANDATE_TYPE_LABELS = {
  SIMPLE_BROKERAGE: 'Simple brokerage mandate',
  SIMPLE_EXCLUSIVE: 'Simple exclusive mandate',
  QUALIFIED_EXCLUSIVE: 'Qualified exclusive mandate',
} satisfies EnumTranslation<MandateType>;

const DEAL_LOSS_REASON_LABELS = {
  OTHER_BROKER: 'Another broker',
  OWNER_NOT_SELLING: 'Owner no longer selling',
  OWNER_UNREACHABLE: 'Owner unreachable',
  UNREALISTIC_PRICE: 'Unrealistic price expectation',
  OWNER_SELLS_PRIVATE: 'Owner selling privately',
  WRONG_CONTACT_TYPE: 'Wrong point of contact',
  OWNER_UNDECIDED: 'Owner undecided',
  PROPERTY_NOT_SELLABLE: 'Property not marketable',
  SALE_UNECONOMICAL: 'Sale uneconomical',
  OWNER_NO_RESPONSE: 'No response',
  OWNER_ALREADY_KNOWN: 'Owner already known elsewhere',
  NO_AGREEMENT: 'No agreement reached',
  OTHER: 'Other',
} satisfies EnumTranslation<DealLossReason>;

// Bewertungsanfrage enum labels (IMM-407.2.1). The app is German-facing; these
// en-US strings mirror the DE catalogue key-for-key (ADR 0009).
const VALUATION_STATUS_LABELS = {
  DRAFT: 'Draft',
  REQUESTED: 'Requested',
  IN_PROGRESS: 'In progress',
  AWAITING_RELEASE: 'Awaiting approval',
  RELEASED: 'Released',
  WITHDRAWN: 'Withdrawn',
} satisfies EnumTranslation<ValuationRequestStatus>;

const VALUATION_OBJEKTART_LABELS = {
  CONDOMINIUM: 'Condominium',
  SINGLE_FAMILY_TERRACED_HOUSE: 'Single-family / terraced house',
  MULTI_FAMILY_HOUSE: 'Multi-family house',
  PLOT: 'Plot',
  COMMERCIAL_PROPERTY: 'Commercial property',
  GARAGE_PARKING_SPACE: 'Garage / parking space',
} satisfies EnumTranslation<ValuationObjectType>;

const VALUATION_NUTZUNG_LABELS = {
  OWNER_OCCUPIED: 'Owner-occupied',
  RENTED: 'Rented',
  PARTIALLY_RENTED: 'Partially rented',
  VACANT: 'Vacant',
} satisfies EnumTranslation<ValuationUsage>;

const VALUATION_ANLASS_LABELS = {
  SALE_PREPARATION: 'Sale preparation',
  ACQUISITION: 'Acquisition',
  INHERITANCE_DIVISION: 'Inheritance / division',
  DIVORCE: 'Divorce',
  RELOCATION: 'Relocation',
  FINANCING: 'Financing',
  INVESTMENT_DECISION: 'Investment decision',
  OTHER: 'Other',
} satisfies EnumTranslation<ValuationReason>;

const VALUATION_ZEITHORIZONT_LABELS = {
  QUICK_SALE: 'Quick sale wanted',
  WITHIN_3_MONTHS: 'Within 3 months',
  WITHIN_12_MONTHS: 'Within 12 months',
  NO_TIME_PRESSURE: 'No time pressure',
  MARKET_EXPLORATION_ONLY: 'Market exploration only',
} satisfies EnumTranslation<ValuationTimeHorizon>;

const VALUATION_PREISVORSTELLUNG_ART_LABELS = {
  NONE: 'None',
  FIXED_PRICE: 'Fixed price',
  PRICE_RANGE: 'Price range',
} satisfies EnumTranslation<ValuationPriceExpectationType>;

// Whose price expectation it is — the form, the preview and the
// operator detail all label the three captures from this one table.
const VALUATION_PRICE_EXPECTATION_KIND_LABELS = {
  OWNER: "Owner's price expectation",
  BROKER: "Broker's price expectation",
  TARGET: 'Realistic target price',
} satisfies EnumTranslation<ValuationPriceExpectationKind>;

// Type-specific enum labels (IMM-407.2.2). The app is German-facing; these en-US
// strings mirror the DE catalogue key-for-key.
const VALUATION_HEIZUNGSART_LABELS = {
  GAS: 'Gas',
  OIL: 'Oil',
  DISTRICT_HEATING: 'District heating',
  HEAT_PUMP: 'Heat pump',
  PELLET_WOOD: 'Pellet / wood',
  NIGHT_STORAGE: 'Night storage',
  OTHER: 'Other',
} satisfies EnumTranslation<ValuationHeatingType>;

const VALUATION_ENERGIEEFFIZIENZKLASSE_LABELS = {
  A_PLUS: 'A+',
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',
  F: 'F',
  G: 'G',
  H: 'H',
  UNKNOWN: 'Unknown',
} satisfies EnumTranslation<ValuationEnergyEfficiencyClass>;

const VALUATION_HAUSTYP_LABELS = {
  SINGLE_FAMILY_HOUSE: 'Single-family house',
  SEMI_DETACHED_HOUSE: 'Semi-detached house',
  MID_TERRACE_HOUSE: 'Mid-terrace house',
  END_TERRACE_HOUSE: 'End-terrace house',
  VILLA: 'Villa',
} satisfies EnumTranslation<ValuationHouseType>;

const VALUATION_UNTERKELLERT_LABELS = {
  NO_BASEMENT: 'No basement',
  PARTIAL_BASEMENT: 'Partial basement',
  FULL_BASEMENT: 'Full basement',
} satisfies EnumTranslation<ValuationBasement>;

const VALUATION_BEBAUUNGSPLAN_LABELS = {
  DEVELOPMENT_PLAN_IN_PLACE: 'Development plan in place',
  PARAGRAPH_34_BAUGB: '§ 34 BauGB (inner area)',
  PARAGRAPH_35_BAUGB: '§ 35 BauGB (outer area)',
  UNKNOWN: 'Unknown',
} satisfies EnumTranslation<ValuationDevelopmentPlan>;

const VALUATION_ERSCHLIESSUNGSZUSTAND_LABELS = {
  FULLY_SERVICED: 'Fully serviced',
  PARTIALLY_SERVICED: 'Partially serviced',
  NOT_SERVICED: 'Not serviced',
  UNKNOWN: 'Unknown',
} satisfies EnumTranslation<ValuationServicingStatus>;

const VALUATION_ALTLASTENVERDACHT_LABELS = {
  YES: 'Yes',
  NO: 'No',
  UNKNOWN: 'Unknown',
} satisfies EnumTranslation<ValuationContaminationSuspicion>;

const VALUATION_STAFFEL_INDEX_MIETE_LABELS = {
  NONE: 'None',
  GRADUATED_RENT: 'Graduated rent',
  INDEX_RENT: 'Index-linked rent',
} satisfies EnumTranslation<ValuationGraduatedIndexRent>;

const VALUATION_ANGABE_DER_EINHEITEN_LABELS = {
  AGGREGATE: 'Aggregate',
  INDIVIDUAL: 'Unit by unit',
} satisfies EnumTranslation<ValuationUnitBreakdown>;

// Gesamtzustand + collection enums (IMM-407.2.3), keyed by the German lexeme tokens.
const VALUATION_QUALITAET_LABELS = {
  BASIC: 'Basic',
  STANDARD: 'Standard',
  UPSCALE: 'Upscale',
  HIGH_END: 'High-end',
} satisfies EnumTranslation<ValuationQuality>;

const VALUATION_ZUSTAND_LABELS = {
  NEEDS_MODERNISATION: 'In need of modernisation',
  WELL_KEPT: 'Well-kept',
  AS_NEW: 'As new',
} satisfies EnumTranslation<ValuationCondition>;

const VALUATION_GEBAEUDEELEMENT_LABELS = {
  KITCHEN: 'Kitchen',
  BATHROOM: 'Bathroom',
  FLOORING: 'Flooring',
  WINDOWS: 'Windows',
} satisfies EnumTranslation<ValuationBuildingElement>;

const VALUATION_STELLPLATZART_LABELS = {
  GARAGE: 'Garage',
  UNDERGROUND_PARKING_SPACE: 'Underground parking space',
  CARPORT: 'Carport',
  OUTDOOR_PARKING_SPACE: 'Outdoor parking space',
  DUPLEX_STACKER: 'Duplex/stacker',
} satisfies EnumTranslation<ValuationParkingSpaceType>;

const VALUATION_EINHEIT_VERMIETUNGSSTATUS_LABELS = {
  LET: 'Let',
  VACANT: 'Vacant',
} satisfies EnumTranslation<ValuationUnitLettingStatus>;

const VALUATION_FIELD_NAME_LABELS = {
  yearBuilt: 'Year built',
  plotArea: 'Plot area',
  rooms: 'Rooms',
  heatingType: 'Heating type',
  energyEfficiencyClass: 'Energy efficiency class',
  lastModernisation: 'Last modernisation',
  modernisationsDone: 'Modernisations carried out',
  livingArea: 'Living area',
  floor: 'Floor',
  hasLift: 'Lift available',
  houseType: 'House type',
  basement: 'Basement',
  residentialUnits: 'Residential units',
  commercialUnits: 'Commercial units',
  totalLivingArea: 'Total living area',
  commercialArea: 'Commercial area',
  deferredMaintenance: 'Deferred maintenance',
  unitBreakdown: 'Units specification',
  developmentPlan: 'Development plan',
  groundAreaRatio: 'GRZ (plot ratio)',
  floorAreaRatio: 'GFZ (floor area ratio)',
  permittedDevelopment: 'Permitted development',
  servicingStatus: 'Servicing status',
  positivePreliminaryEnquiry: 'Positive preliminary building enquiry',
  hasBuildingPermit: 'Building permit available',
  hasExistingBuilding: 'Existing building present',
  estimatedDemolitionCost: 'Estimated demolition cost',
  contaminationSuspicion: 'Suspected contamination',
  actualRentMonthly: 'Actual rent (net, monthly)',
  serviceCharges: 'Service charges',
  occupancyRate: 'Occupancy rate (let share)',
  tenancyStart: 'Tenancy start',
  graduatedIndexRent: 'Graduated / index rent',
  monthlyServiceCharge: 'Monthly service charge',
  nonRecoverableShare: 'of which non-recoverable',
  maintenanceReserve: 'Maintenance reserve',
  coOwnershipShare: 'Co-ownership share',
  resolvedSpecialLevies: 'Resolved special levies',
  // Gesamtzustand (IMM-407.2.3) — per element × axis.
  kitchenQuality: 'Kitchen – quality',
  kitchenCondition: 'Kitchen – condition',
  bathroomQuality: 'Bathroom – quality',
  bathroomCondition: 'Bathroom – condition',
  flooringQuality: 'Flooring – quality',
  flooringCondition: 'Flooring – condition',
  windowsQuality: 'Windows – quality',
  windowsCondition: 'Windows – condition',
} satisfies EnumTranslation<ValuationFieldKey>;

export const pages: Messages['pages'] = {
  shell: {
    nav: {
      dashboard: 'Dashboard',
      workspace: 'Workspace',
      organisations: 'Locations',
      members: 'Members',
      activities: 'Activities',
      chat: 'Ask AI',
      'chat-experimental': 'Experimental',
      emails: 'Email',
      'emails-inbox': 'Inbox',
      'emails-outbox': 'Outbox',
      'emails-expand': 'Expand Email',
      'emails-collapse': 'Collapse Email',
      calendar: 'Calendar',
      assets: 'Assets',
      companies: 'Companies',
      property: 'Real estate',
      properties: 'Properties',
      relationship: 'CRM',
      contacts: 'Contacts',
      leads: 'Leads',
      owners: 'Owners',
      acquisition: 'Acquisition',
      acquisitions: 'Acquisitions',
      deals: 'Deals',
      settings: 'Settings',
      start: 'Start',
      akquise: 'Acquisition',
      'akquise-flow': 'Lead → Mandate',
      vermarktung: 'Marketing',
      'vermarktung-flow': 'Mandate → Closing',
      stammdaten: 'Master data',
      'owner-leads': 'Owner leads',
      'acquisition-pipeline': 'Acquisition pipeline',
      'deal-overview': 'Deal overview',
      valuations: 'Valuations',
      mandates: 'Mandates',
      prospects: 'Prospects',
      'sales-pipeline': 'Sales pipeline',
      persons: 'Contacts',
      'real-estate': 'Properties',
      partner: 'Partner',
    },
    search: {
      placeholder: 'Search … contacts · properties · deals · leads · documents',
      'topbar-hint': 'Click or press ⌘K to open search',
      'quick-nav-title': 'Jump to',
      'results-label': 'Search results',
      'no-hits': 'No matches for “{query}”.',
      'group-empty': 'No matches',
      truncated: '{shown} of {total}',
      'all-results': 'All results in {area}',
      failed: 'The search failed. Please try again.',
      footer: '↑ ↓ navigate · ↵ open · esc close',
      groups: {
        kontakt: 'Contacts',
        objekt: 'Properties',
        deal: 'Deals',
        lead: 'Leads',
        dokument: 'Documents',
      },
      // Mirrors the German dictionary's flat token namespace: every enum token a
      // result row can carry on `secondaryToken` — Objekt status, Deal phase,
      // Lead status and document category.
      status: {
        objekt: { ...PROPERTY_STATUS_LABELS },
        deal: { ...DEAL_PHASE_LABELS },
        dokument: { ...DOCUMENT_CATEGORY_LABELS },
        lead: {
          NEW: 'New',
          CONTACTED: 'Contacted',
          CONVERTED: 'Converted',
          LOST: 'Lost',
          ARCHIVED: 'Archived',
        },
      },
    },
    'user-menu': {
      settings: 'Settings',
      'sign-out': 'Sign out',
    },
    'dashboard-link': {
      back: 'Back to overview',
      chip: 'Filtered from the overview',
      clear: 'Remove filter',
      'chip-status': 'Status: {statuses}',
      'chip-waiting': 'no response for over {hours} h',
      'chip-received': 'Received in the chosen period',
    },
    'location-selector': {
      'workspace-label': 'Workspace',
      'standort-label': 'Location',
      'all-standorte': 'All locations',
      'all-my-standorte': 'All my locations',
      'all-workspace-standorte': 'All {workspace} locations',
      'modal-title': 'Switch location',
      standort: 'Location: {name}',
      'not-onboarded': 'This organisation is not fully set up yet',
    },
    'test-banner': {
      message: 'Testing environment for Workspace <name>{workspaceName}</name>',
    },
    // Hosted sandbox (a copy of production data); built with VITE_SANDBOX=true.
    'sandbox-banner': {
      message:
        '<name>Sandbox</name> — a copy of production data. Every e-mail and message goes to the engineering team, not to customers.',
    },
    'impersonation-banner': {
      message: 'You are impersonating <name>{memberName}</name>',
    },
  },
  chat: {
    'coming-soon': 'Coming soon',
    headline: 'Ask me anything',
    subline: 'Your next deal might be one message away.',
    'disabled-note': 'The AI chat is still in development and will be available soon.',
    'input-label': 'Message',
    'input-placeholder': 'How can I help you today?',
    'model-label': 'Choose model',
    'new-chat': 'New chat',
    'conversations-label': 'Select conversation',
    untitled: 'New conversation',
    'page-read': 'Page read: {title}',
    attach: 'Add attachment',
    voice: 'Voice input',
    'voice-mode': 'Voice mode',
    working: 'Thinking...',
    // Live loader label, updated per run step (IMM-348): the engine emits the
    // current activity (`model` → thinking, `tool:<name>` → that tool) and the
    // client narrates it here. Unknown/other steps fall back to `thinking`.
    activity: {
      thinking: 'Thinking…',
      composing: 'Writing the answer…',
      deal_list: 'Searching your deals…',
      deal_detail: 'Loading deal details…',
      listing_list: 'Searching your listings…',
      listing_detail: 'Loading listing details…',
    },
    // Live token counter (IMM-348), shown only when the token-count debug flag is
    // on (?feature_flag=token-count:enable). Formats a running total.
    tokens: '{count} tokens',
    send: 'Send',
    'empty-title': 'Ask me anything',
    'empty-subline': 'Ask a question about your listings, deals, or contacts.',
    'error-generic': 'Something went wrong. Please try again.',
    // Display labels for the assistant models, keyed by the backend's wire id
    // (IMM-348). The backend defines the model identity; these translate it.
    models: {
      'nvidia.nemotron-nano-3-30b': 'Nemotron Nano — fast & economical',
      'nvidia.nemotron-super-3-120b': 'Nemotron Super — more capable',
      'openai.gpt-oss-120b': 'GPT-OSS 120B — powerful & open',
      'amazon.nova-pro-v1:0': 'Amazon Nova Pro — versatile',
      'anthropic.claude-sonnet-5': 'Claude Sonnet — balanced',
      'anthropic.claude-opus-4-8': 'Claude Opus — most thorough',
      'anthropic.claude-haiku-4-5': 'Claude Haiku — fastest',
    },
  },
  placeholder: {
    description: "This section isn't available yet.",
  },
  home: {
    title: 'Dashboard',
    'signed-in-as': 'Signed in as {name}',
    'choose-standort': 'Choose a location to see its overview.',
    'all-standorte': 'All locations',
    'as-of': 'As of {date}, {time}',
    funnel: {
      title: 'Lead → Mandate → Online',
      'excluded-valuation':
        '{count, plural, one {# deal without a valuation not included} other {# deals without a valuation not included}}',
      leads: {
        title: 'Leads',
        open: 'open',
        received: '{count} received',
        converted: '{count} converted to a deal',
        new: '{count} new',
        contacted: '{count} contacted',
        rate: '{rate} % → deal',
        'rate-none': 'No new leads in this period',
      },
      acquisition: { title: 'Deals in acquisition', courtage: 'Pipeline commission', intake: '+{count} created' },
      mandates: { title: 'Mandates', courtage: 'Commission', intake: '+{count} mandate received' },
      online: {
        title: 'Online',
        unit: '{count, plural, one {property} other {properties}}',
        courtage: 'Commission',
        intake: '+{count} went online',
        empty: 'No property is online right now.',
        since: 'since {date}',
        prospects: '{count, plural, one {# prospect} other {# prospects}}',
        more: '+{count} more',
      },
      'deals-unit': '{count, plural, one {deal} other {deals}}',
      'phase-count': '{phase} {count}',
    },
    period: {
      label: 'Period',
      LAST_7_DAYS: 'Last 7 days',
      LAST_30_DAYS: 'Last 30 days',
      LAST_90_DAYS: 'Last 90 days',
      ALL_TIME: 'All time',
    },
    'new-leads': { title: 'New leads', empty: 'No new leads.' },
    'new-inquiries': { title: 'New buyer inquiries', empty: 'No new buyer inquiries.' },
    attention: {
      title: 'Needs attention',
      view: 'View →',
      'calm-line': '✓ {text}',
      'view-named': 'View → {title}',
      preview: {
        more: '+ {count} more',
        'more-named': '+ {count} more · {title}',
        'overdue-days': '{days, plural, =0 {under 1 day} one {# day} other {# days}} overdue',
        'waiting-days': 'waiting {days, plural, =0 {under 1 day} one {# day} other {# days}}',
        flag: {
          OVERDUE: 'Overdue',
          NO_NEXT_STEP: 'No next step',
          TOO_LONG_IN_PHASE: 'Too long in phase',
        } satisfies Record<DashboardFlaggedDeal['flag'], string>,
        gap: {
          WITHOUT_VALUATION: 'No valuation',
          UNASSIGNED: 'Unassigned',
        } satisfies Record<DashboardDataGap, string>,
        'property-unavailable': 'Property unavailable',
      },
      'overdue-tasks': {
        title: 'Overdue tasks',
        context: 'Oldest overdue for {days, plural, =0 {less than a day} one {# day} other {# days}}',
        calm: 'Nothing overdue',
      },
      'leads-without-response': {
        title: 'Leads without a response',
        context: 'Longest has been waiting for {days, plural, =0 {less than a day} one {# day} other {# days}}',
        calm: 'All leads contacted',
      },
      'deals-needing-action': {
        title: 'Deals needing action',
        part: {
          OVERDUE: '{count} overdue',
          NO_NEXT_STEP: '{count} without a next step',
          TOO_LONG_IN_PHASE: '{count} too long in phase',
        },
        calm: 'All deals on track',
      },
      'data-gaps': {
        title: 'Data gaps',
        part: {
          'without-valuation': '{count} without a valuation',
          unassigned: '{count} without a responsible member',
        },
        calm: 'No gaps',
      },
    },
    upcoming: { title: 'Upcoming tasks', all: 'Show all', empty: 'No upcoming tasks.' },
    tables: {
      'show-all': 'Show all',
      'show-all-count': 'Show all ({count})',
      waiting: 'for {count, plural, one {# day} other {# days}}',
      'current-state': '{kind} · {date} · {text}',
      'no-contact': 'No contact yet',
      column: {
        owner: 'Owner',
        prospect: 'Prospect',
        property: 'Property',
        source: 'Source',
        received: 'Received',
        status: 'Status',
        'current-state': 'Current state',
      },
    },
    setup: {
      title: 'What is still to do',
      progress: '{done} of {total} done',
      more: '+{count} more',
      chip: {
        MEMBER_ONBOARDING: 'Complete profile',
        MAILBOX: { OPEN: 'Connect mailbox', RECONNECT: 'Reconnect mailbox' },
      },
      'chip-named': '{name}: {action}',
    },
    team: {
      title: 'Team',
      column: {
        member: 'Name',
        'active-deals': 'Active deals',
        'pipeline-courtage': 'Pipeline commission',
        'mandate-online-courtage': 'Commission from mandates & online',
        'overdue-tasks': 'Overdue tasks',
        'leads-without-response': 'Leads without response',
      },
      unassigned: 'Unassigned',
      'figure-label': '{member}, {column}',
    },
    'block-failed': 'This section could not be loaded.',
    // Names the block that failed: several can fail at once and the message alone does not say which.
    'block-name': {
      funnel: 'Lead → Mandate → Online',
      'new-records': 'New leads and buyer inquiries',
      attention: 'Needs attention',
      setup: 'What is still to do',
      team: 'Team',
    },
    retry: 'Reload',
    'count-link': '{label}: {count}',
  },
  error: {
    title: 'This page could not be loaded',
    body: 'An unexpected error occurred.',
    'try-again': 'Try again',
    home: 'Back to home',
  },
  loading: 'Loading…',
  'not-found': {
    title: 'Not found',
    body: "The page you're looking for doesn't exist.",
    'back-to-home': 'Back to home',
  },
  // Sandbox sign-in is engineer-facing tooling: English in both locales (PRD Copy, slice D5). Do not translate.
  sandbox: {
    'sign-in': {
      title: 'Sandbox sign-in',
      google: 'Sign in with Google',
      'signed-in-as': 'Signed in with Google as {email}',
      'signed-in-as-agent': 'Signed in with the agent token as {email}',
      login: 'Log in',
      'search-label': 'Search members',
      'search-placeholder': 'Search by name, e-mail or workspace…',
      'loading-members': 'Loading members…',
      empty: 'No members found',
      loading: 'Signing in…',
    },
    callback: {
      loading: 'Signing in…',
      'try-another': 'Try another account',
    },
  },
  auth: {
    'sign-in': {
      title: 'Sign in to immoOS',
      'email-password': 'Sign in with email and password',
      google: 'Sign in with Google',
      microsoft: 'Sign in with Microsoft',
      loading: 'Signing in…',
      error: 'Sign-in failed — please try again.',
    },
    callback: {
      loading: 'Finishing sign-in…',
      'missing-code': 'The sign-in flow is incomplete. Please start again.',
      failed: 'Sign-in failed — please try again.',
      'back-to-sign-in': 'Back to sign-in',
    },
    impersonate: {
      loading: 'Starting impersonation session…',
      'missing-code': 'This impersonation link is incomplete. Please start again from the admin console.',
      failed: 'This impersonation link is no longer valid. Please start a new session from the admin console.',
      'back-to-sign-in': 'Back to sign-in',
    },
    'sign-out': {
      loading: 'Signing out…',
      failed: 'Sign-out failed — please try again.',
      'back-to-sign-in': 'Go to sign-in',
    },
    /*
     * The OAuth flow's consent screen (IMM-601.8.2): an MCP client sends the
     * member here, they sign in the way they always do and approve the access —
     * no secret pasted anywhere.
     *
     * The screen deliberately names BOTH reasons a requested permission is not
     * granted, because they lead to different next steps: "you do not hold it"
     * is an Owner's to fix, "it does not exist" is the client author's. A client
     * that silently gets less than it asked for learns nowhere else what is
     * missing.
     */
    'oauth-consent': {
      title: 'Connect a client',
      'subtitle-named': '"{clientName}" wants to access immoOS on your behalf.',
      subtitle: 'A client wants to access immoOS on your behalf.',
      'workspace-label': 'Workspace',
      'workspace-choose-label': 'Choose a workspace',
      'workspace-choose-hint': 'The client will be connected for this workspace.',
      'account-label': 'Signed in as',
      'granted-empty': 'No permission is left that you could grant.',
      'withheld-heading': 'What you are not allowing',
      'withheld-hint':
        'You do not hold these permissions yourself. immoOS never passes on more than you have — an Owner of your workspace can grant them to you.',
      'unsupported-hint': 'These permissions do not exist in immoOS. Let the client’s developers know.',
      'revoke-hint': 'You can change or withdraw this access at any time under Settings → MCP.',
      'not-allowed': 'You may not connect clients in this workspace. Ask an Owner to do it for you.',
      'approve-cta': 'Connect',
      'approve-loading': 'Connecting…',
      'cancel-cta': 'Cancel',
      'cancel-loading': 'Declining…',
      'generic-error': 'The connection could not be completed — please try again.',
      'back-cta': 'Back to settings',
    },
  },
  settings: {
    title: 'Settings',
    'title-scoped': '{workspace} Settings',
    back: 'Back',
    version: 'Version {version}',
    nav: {
      'load-error': 'Additional applications could not be loaded.',
      retry: 'Try again',
      skills: 'Skills',
      'api-keys': 'API keys',
      'company-mailboxes': 'Company mailboxes',
      mcp: 'MCP',
      'lead-partners': 'Lead partners',
      'print-material': 'Print material',
      locations: 'Locations',
      'my-profile': 'My profile',
      mailbox: 'Mailbox',
      company: 'Company',
      branding: 'Branding & CI',
      'document-templates': 'Templates',
      portals: 'Portals',
      members: 'Members',
      admins: 'Admins',
      personal: 'Personal',
      workspace: 'Workspace',
    },
    admins: {
      title: 'Workspace Admins',
      subtitle: 'Manage who administers this workspace — owners, admins, and billing.',
      'empty-title': 'No admins yet',
      'empty-description': 'No members hold a workspace role yet.',
      'load-error': 'Could not load members.',
      'add-cta': 'Add Admin',
      'add-title': 'Add Admin',
      'add-submit': 'Assign role',
      'add-loading': 'Assigning…',
      'cancel-cta': 'Cancel',
      'remove-cta': 'Remove',
      you: 'You',
      'save-success': 'Workspace role updated.',
      'save-error': 'Could not update the workspace role — please try again.',
      'remove-success': 'Workspace role removed.',
      columns: {
        member: 'Member',
        email: 'Email',
        role: 'Role',
      },
      roles: {
        'workspace-owner': 'Owner',
        'workspace-admin': 'Admin',
        billing: 'Billing',
      },
    },
    skills: {
      title: 'Skills',
      subtitle:
        'Skills do recurring work with your access. You decide which ones to activate — and you can revoke them at any time.',
      'list-load-error': 'The skills could not be loaded. Please try again.',
      'mine-heading': 'Your skills',
      'mine-empty': 'You have not activated any skill yet.',
      'available-heading': 'Available skills',
      'available-empty': 'No further skills are available at the moment.',
      'activate-cta': 'Activate',
      'revoke-cta': 'Revoke',
      'next-run': 'Next run: {date}',
      'no-next-run': 'No run scheduled',
      status: {
        ACTIVE: 'Active',
        INACTIVE: 'Not activated',
        PAUSED_OPERATOR: 'Paused by the operator',
        PAUSED_FAILURES: 'Paused after failures',
        REVOKED: 'Revoked',
      },
      'reactivate-cta': 'Reactivate',
      approvals: {
        heading: 'Waiting for your approval',
        waiting: '“{skill}” is waiting for your approval: {summary}',
        expires: 'Without an answer this approval lapses on {date}.',
        'review-cta': 'Review',
        'detail-title': 'Approval required',
        'detail-body': '“{skill}” would like to carry out the following action:',
        'expires-note': 'Without an answer this approval lapses on {date}.',
        'approve-cta': 'Approve',
        'reject-cta': 'Decline',
        close: 'Close',
        'generic-error': 'That did not work. Please try again.',
        terminal: {
          APPROVED: 'Approved — the action was carried out.',
          REJECTED: 'Declined — the action was not carried out.',
          EXPIRED: 'Lapsed — the action was not carried out.',
          VOID: 'No longer valid — access was revoked, and the action was not carried out.',
          PENDING: '',
        },
      },
      runs: {
        heading: 'Runs',
        empty: 'No runs yet.',
        'run-cta': 'Run now',
        'run-error': 'The run could not be started. Please try again.',
        'already-running': 'Already running.',
        'load-error': 'The runs could not be loaded.',
        columns: {
          trigger: 'Trigger',
          status: 'Status',
          started: 'Started',
          tokens: 'Tokens',
          cost: 'Cost',
        },
        status: {
          QUEUED: 'Queued',
          RUNNING: 'Running',
          COMPLETED: 'Completed',
          FAILED: 'Failed',
          STOPPED: 'Stopped',
        },
        'status-revoked': 'Stopped — access revoked',
        trigger: {
          MANUAL: 'Started manually',
          SCHEDULE: 'Scheduled',
          EVENT: 'Event',
        },
        'tool-calls': 'Tool calls',
        // Exactly what the skill called and what came back — on a failure, the
        // only place the reason exists.
        'call-parameters': 'Call',
        'call-result': 'Result',
        outcome: {
          OK: 'OK',
          ERROR: 'Error',
          REFUSED: 'Refused',
          PROPOSED: 'Awaiting approval',
        },
        fingerprint: 'Signature',
        'not-started': '—',
      },
      state: {
        heading: 'State',
        empty: 'This skill has not remembered anything yet.',
        'reset-cta': 'Reset state',
        'reset-error': 'The state could not be reset. Please try again.',
        'load-error': 'The state could not be loaded.',
        columns: {
          key: 'Key',
          value: 'Value',
          updated: 'Updated',
        },
      },
      // Trigger configuration (IMM-436.6/.7) — the same fields in the consent
      // dialog and the configure dialog. Event names themselves stay
      // untranslated: the catalogue is derived from the event registry, so a
      // hand-written label set would go stale the moment a module publishes a
      // new event, and nobody adding one would be looking at this file.
      trigger: {
        kind: 'When should this skill run?',
        kinds: {
          MANUAL: 'Only when I ask',
          SCHEDULE: 'On a schedule',
          EVENT: 'When something happens',
        },
        'events-heading': 'Events',
        'events-hint': 'The skill runs as soon as one of these happens in your workspace.',
        'events-filter': 'Filter events…',
        'events-empty': 'No event matches your search.',
        'events-selected': '{count} selected',
        'coalescing-label': 'Group events together (minutes)',
        'coalescing-hint': 'Several events inside this window trigger only one run.',
        'weekdays-label': 'Weekdays',
        weekdays: {
          '1': 'Mon',
          '2': 'Tue',
          '3': 'Wed',
          '4': 'Thu',
          '5': 'Fri',
          '6': 'Sat',
          '7': 'Sun',
        },
        'times-label': 'Times',
        'time-at': 'Time {index}',
        'time-add': 'Add a time',
        'time-remove': 'Remove',
        'timezone-label': 'Time zone',
        'missed-label': 'Missed runs',
        missed: {
          SKIP: 'Skip them',
          CATCH_UP_ONCE: 'Catch up once',
        },
        problems: {
          'events-empty': 'Pick at least one event.',
          'schedule-empty': 'Pick at least one weekday and one time.',
          'schedule-too-tight': 'Runs must be at least {minutes} minutes apart.',
        },
      },
      configure: {
        cta: 'Configure',
        title: 'Configure {name}',
        'tools-heading': 'Tools',
        'tools-hint': 'You can only narrow what this skill may use — never widen it.',
        save: 'Save',
        cancel: 'Cancel',
        'load-error': 'The configuration could not be loaded.',
        'generic-error': 'The configuration could not be saved. Please try again.',
      },
      consent: {
        title: 'Activate {name}',
        'acting-identity': 'This skill acts with your access — it sees and does only what you may.',
        'tools-heading': 'Tools it may use',
        'tools-empty': 'No tools allowed yet.',
        // What a tool may do unattended — the catalogue's classification, never
        // the instance's configuration. UNKNOWN: the tool is no longer
        // catalogued, so any claim about it would be a guess.
        'tool-effect': {
          QUERY: 'reads only',
          AUTO_WITH_AUDIT: 'without asking',
          APPROVAL_REQUIRED: 'only with your approval',
          UNKNOWN: 'unknown',
        },
        'approval-window': 'A proposed action waits {days} days for your approval, then it expires.',
        revocable: 'You can revoke this access at any time.',
        confirm: 'Grant access and activate',
        cancel: 'Cancel',
        'generic-error': 'The skill could not be activated. Please try again.',
      },
      revoke: {
        title: 'Revoke access?',
        body: '{name} will not start any further runs from now on.',
        'history-note': 'A run already in flight finishes. The history stays readable — revoking deletes nothing.',
        confirm: 'Revoke',
        cancel: 'Cancel',
        'generic-error': 'The access could not be revoked. Please try again.',
      },
    },
    'company-mailboxes': {
      title: 'Company mailboxes',
      subtitle: 'Company addresses such as bewerbung@ or info@ — only owners and admins can see them.',
      'add-cta': 'Add company mailbox',
      empty: {
        title: 'No company mailbox connected yet',
        body: 'Connect a company mailbox such as bewerbung@ or info@ to read its e-mails in immoOS. Only owners and admins can see it.',
      },
      status: {
        ACTIVE: 'Active',
        SYNCING: 'Syncing …',
        EXPIRED: 'Expired',
      },
      'connected-by': 'Connected by {name} on {date}',
      'imap-connected-by': 'IMAP · {provider} · Connected by {name} on {date}',
      forbidden: 'You are not allowed to perform this action.',
      'load-error': 'The company mailboxes could not be loaded.',
      'load-retry': 'Try again',
      'connect-dialog': {
        title: 'Add company mailbox',
        intro: 'Choose the provider of the company mailbox.',
        hint: 'The address must be its own Google Workspace account. Google Groups and distribution lists cannot be connected. In the next step, sign in with the company mailbox — not with your own account.',
        'hint-with-microsoft':
          'The address must be its own mailbox in Google Workspace or Microsoft 365. Groups, distribution lists and shared mailboxes cannot be connected. In the next step, sign in with the company mailbox — not with your own account.',
        'hint-with-imap':
          'The address must be a mailbox of its own — at Google Workspace, Microsoft 365 or a provider with IMAP. Groups, distribution lists and shared mailboxes cannot be connected. In the next step, sign in with the company mailbox — not with your own account.',
        'continue-imap': 'Continue',
        'cancel-cta': 'Cancel',
        'confirm-cta': 'Continue to sign-in',
      },
      'reconnect-cta': 'Reconnect',
      'disconnect-cta': 'Disconnect',
      'disconnect-dialog': {
        title: 'Disconnect company mailbox?',
        body: 'The connection to {email} will be revoked at the provider and removed from immoOS. You can reconnect the company mailbox at any time.',
        'body-imap':
          'The access data for {email} is deleted from immoOS. The mailbox at the provider stays unchanged. You can reconnect the company mailbox at any time.',
        'cancel-cta': 'Cancel',
        'confirm-cta': 'Disconnect',
      },
    },
    'email-templates': {
      columns: {
        name: 'Name',
        area: 'Area',
        subject: 'Subject',
        'updated-at': 'Updated on',
        'updated-by': 'Updated by',
        actions: 'Actions',
      },
      immoos: 'immoOS',
      empty: 'No e-mail templates yet.',
      'new-cta': 'New e-mail template',
      'load-error': 'The e-mail templates could not be loaded.',
      'delete-dialog': {
        body: 'Delete e-mail template ‘{name}’? E-mails that have already been sent stay unchanged.',
        success: 'E-mail template deleted',
      },
      view: {
        breadcrumb: 'Settings / <link>Templates</link> / {name}',
        'subject-label': 'Subject',
        'load-error': 'The e-mail template could not be loaded.',
        gone: 'This e-mail template no longer exists.',
        back: 'Back to templates',
      },
      editor: {
        name: 'Name',
        area: 'Area',
        subject: 'Subject',
        text: 'Text',
        'insert-placeholder': 'Insert placeholder',
        'insert-fill-field': 'Insert fill-in field',
        'fill-dialog': {
          title: 'Insert fill-in field',
          label: 'Label',
          placeholder: 'e.g. Appointment',
          insert: 'Insert',
        },
        saved: 'E-mail template saved',
        'name-required': 'Please enter a name.',
        'name-too-long': 'The name may be at most {max} characters long.',
      },
    },
    'document-templates': {
      title: 'Templates',
      tabs: {
        documents: 'Documents',
        email: 'E-mail templates',
      },
      subtitle:
        'Maintain your agency’s templates here. Data fields such as owner, property and commission are filled in automatically when a document is created.',
      columns: {
        name: 'Name',
        category: 'Category',
        'updated-at': 'Updated on',
        'created-by': 'Created by',
        actions: 'Actions',
      },
      'sort-label': 'Sort by {column}',
      'updated-at-value': '{date} {time}',
      'no-creator': '—',
      empty: 'No template yet.',
      'read-only-banner': 'Only owners and admins can edit templates.',
      actions: {
        edit: 'Edit',
        view: 'View',
        delete: 'Delete',
      },
      'load-error': 'The templates could not be loaded.',
      'load-retry': 'Try again',
      'delete-dialog': {
        title: 'Delete template',
        body: 'Delete template ‘{name}’? Documents that have already been generated are kept.',
        confirm: 'Delete',
        cancel: 'Cancel',
        error: 'The template could not be deleted. Please try again.',
      },
      'new-cta': 'New template',
      'new-dialog': {
        title: 'New template',
        category: 'Category',
        name: 'Name',
        start: 'How would you like to start?',
        'start-blank': 'Start blank',
        'start-import': 'Import from a Word file',
        'import-hint':
          'Upload a Word file (.docx). To use a Google Doc, choose File → Download → Microsoft Word (.docx) first.',
        drop: 'Drag a file here or choose one',
        'drop-sub': '.docx · up to 20 MB',
        'other-file': 'Another file',
        reading: 'Reading the file …',
        suggesting: 'Looking for data fields …',
        next: 'Continue',
        cancel: 'Cancel',
        errors: {
          'name-invalid': 'Enter a name of at most 80 characters.',
          'not-docx':
            'Only Word files (.docx) can be imported. To use a Google Doc, choose File → Download → Microsoft Word (.docx) first.',
          'too-large': 'The file is larger than 20 MB.',
          unreadable: 'The file could not be read. Check whether it is damaged or password-protected.',
        },
      },
      editor: {
        'toolbar-label': 'Formatting',
        paragraph: 'Paragraph',
        'paragraph-short': '¶',
        'heading-1': 'Heading 1',
        'heading-1-short': 'H1',
        'heading-2': 'Heading 2',
        'heading-2-short': 'H2',
        'heading-3': 'Heading 3',
        'heading-3-short': 'H3',
        bold: 'Bold',
        'bold-short': 'B',
        italic: 'Italic',
        'italic-short': 'I',
        underline: 'Underline',
        'underline-short': 'U',
        'bullet-list': 'Bulleted list',
        'bullet-list-short': '•',
        'ordered-list': 'Numbered list',
        'ordered-list-short': '1.',
        'insert-table': 'Insert table',
        'add-row': 'Add row',
        'add-column': 'Add column',
        'delete-row': 'Delete row',
        'delete-column': 'Delete column',
        'page-break': 'Page break',
        'table-rows': 'Rows',
        'table-columns': 'Columns',
        'table-insert': 'Insert',
        'footer-label': 'Footer',
        'footer-hint': 'Appears on every page, next to ‘Page x / y’.',
        'footer-counter': '{count} / 200',
        'footer-too-long': 'The footer can be at most 200 characters long.',
        'palette-title': 'Insert data fields',
        'chip-required': 'Required',
        'chip-optional': 'May stay empty',
        'chip-optional-hint': 'If the value is missing, a line to fill in is printed.',
        save: 'Save',
        cancel: 'Cancel',
        saved: 'Template saved',
        'save-error': 'The template could not be saved. Please try again.',
        'category-label': 'Category',
        'footer-truncated': 'The footer was shortened to 200 characters.',
        'not-carried-over':
          'Some content was not carried over: {list}. Your logo, colours and fonts come from your branding.',
        dropped: {
          IMAGES: 'images',
          HEADER: 'header',
          FOOTNOTES: 'footnotes',
          TEXT_COLOURS: 'text colours',
          FONTS: 'fonts',
          MERGED_CELLS: 'merged cells',
        },
        suggestions: {
          count: '{count, plural, one {# data field suggestion} other {# data field suggestions}}',
          accept: 'Accept',
          reject: 'Dismiss',
          'accept-all': 'Accept all',
          'reject-all': 'Dismiss all',
          proposal: '→ {chip}',
          none: 'No suggestions found — place the data fields yourself.',
          'save-blocked': 'Review all data field suggestions first.',
        },
        discard: {
          title: 'Discard unsaved changes?',
          confirm: 'Discard',
          keep: 'Keep editing',
        },
      },
      gone: 'This template no longer exists.',
      back: 'Back to templates',
      'buyer-contract': {
        adapt: 'Adapt',
        edit: 'Edit',
        'adapt-banner': 'You are adapting the standard. Once saved, your template is active.',
        'standard-read-only':
          'You cannot edit the standard. Use “Adapt” in the template list to create your own template from it.',
        'default-name': 'Buyer brokerage contract with notice of withdrawal – {companyName}',
        'blocks-required': 'Fill in the brokerage contract, the notice of withdrawal and the model withdrawal form.',
        'name-label': 'Name',
        activate: 'Activate',
        activated: '{name} is now active.',
        'activate-error': 'The template could not be activated. Please try again.',
        delete: {
          title: 'Delete template',
          body: 'Delete template ‘{name}’? The standard will be active afterwards. Contracts already concluded keep their wording.',
          confirm: 'Delete',
          cancel: 'Cancel',
        },
      },
      view: {
        breadcrumb: 'Settings / <link>Templates</link> / {name}',
        'footer-label': 'Footer',
        'no-footer': '—',
        'page-break': 'Page break',
        'load-error': 'The template could not be loaded.',
      },
      categories: {
        BROKER_CONTRACT: DOCUMENT_CATEGORY_LABELS.BROKER_CONTRACT,
        POWER_OF_ATTORNEY: DOCUMENT_CATEGORY_LABELS.POWER_OF_ATTORNEY,
      } satisfies Record<DocumentTemplateCategory, string>,
      'data-fields': {
        groups: {
          OWNERS: 'Owners (all)',
          OWNER_1: 'Owner 1',
          OWNER_2: 'Owner 2',
          OWNER_3: 'Owner 3',
          OWNER_4: 'Owner 4',
          PROPERTY: 'Property',
          DEAL: 'Deal',
          MANDATE: 'Mandate',
          LOCATION: 'Location',
          COMPANY: 'Company',
          DATE: 'Date',
        } satisfies Record<DataFieldGroup, string>,
        'chip-prefix': {
          OWNERS: 'Owners',
          OWNER_1: 'Owner 1',
          OWNER_2: 'Owner 2',
          OWNER_3: 'Owner 3',
          OWNER_4: 'Owner 4',
          PROPERTY: 'Property',
          DEAL: 'Deal',
          MANDATE: 'Mandate',
          LOCATION: 'Location',
          COMPANY: 'Company',
          DATE: 'Date',
        } satisfies Record<DataFieldGroup, string>,
        'field-labels': {
          NAMES: 'Names',
          ADDRESS_BLOCK: 'Address block',
          NAME: 'Name',
          BIRTH_DATE: 'Date of birth',
          NATIONALITY: 'Nationality',
          HOME_ADDRESS: 'Home address',
          EMAIL: 'Email',
          PHONE: 'Phone',
          BIRTH_NAME: 'Birth name',
          BIRTH_PLACE: 'Place of birth',
          ADDRESS: 'Address',
          PROPERTY_TYPE: 'Property type',
          EXPECTED_SALE_PRICE: 'Expected sale price',
          MANDATE_TYPE: 'Mandate type',
          START_OF_WORK: 'Start of work',
          DURATION: 'Duration',
          ENDS_ON: 'Ends on',
          SELLER_COMMISSION: 'Seller commission',
          BUYER_COMMISSION: 'Buyer commission',
          BROKERS_NAMES: 'Brokers (names)',
          BROKERS_EMAILS: 'Brokers (email)',
          BROKERS_PHONES: 'Brokers (phone)',
          BROKERS_REPRESENTATION: 'Brokers (representation)',
          COMPANY_NAME: 'Company name',
          LEGAL_FORM: 'Legal form',
          MANAGING_DIRECTORS: 'Managing directors',
          COMMERCIAL_REGISTER_COURT: 'Commercial register',
          COMMERCIAL_REGISTER_NUMBER: 'Register number',
          VAT_ID: 'VAT ID',
          SUPERVISORY_AUTHORITY: 'Supervisory authority §34c',
          PROFESSIONAL_INDEMNITY_INSURANCE: 'Professional indemnity insurance',
          TODAY: 'Today',
        } satisfies Record<DataFieldName, string>,
        chip: '{prefix}: {label}',
      },
    },
    /*
     * IMM-895 — Settings → MCP carries every MCP credential: the clients
     * connected by sign-in and the MCP keys. API keys keeps HTTP-API keys only
     * (design run imm-895-mcp-credentials-split).
     */
    mcp: {
      title: 'MCP connections',
      description: 'Clients like Claude that work with immoOS on your behalf. Each can only do what you can.',
      'connected-clients-description':
        'Connected through your sign-in — no key involved. Revoking disconnects the client immediately.',
      keys: {
        heading: 'MCP keys',
        description: 'For MCP clients that cannot connect through sign-in. An MCP key always expires.',
        'issue-cta': 'Create MCP key',
        empty: 'You have not created an MCP key yet.',
        'list-load-error': 'Could not load MCP keys.',
        issue: {
          title: 'Create MCP key',
          'secret-heading': 'Your new MCP key',
        },
      },
    },
    'api-keys': {
      title: 'API keys',
      description:
        'For integrations that call the immoOS API. Connect MCP clients under <mcpLink>Settings → MCP</mcpLink>.',
      'issue-cta': 'Issue key',
      'list-load-error': 'Could not load API keys.',
      'list-load-retry': 'Try again',
      'empty-title': 'No API keys yet',
      'empty-description': 'Issue your first key to access the API programmatically.',
      'never-used': 'Never',
      'revoke-action': 'Revoke',
      // Non-MCP keys never expire, so their "Valid until" cell shows a dash.
      'no-expiry': '—',
      'expired-hint': 'This key has expired. Issue a new one to reconnect your client.',
      // IMM-829.1 — a SECOND mark beside "Active", never a fourth Status.
      'partner-inactive-badge': 'Partner deactivated',
      'partner-inactive-hint':
        'The Lead Partner is deactivated. Submitted leads are rejected until it is reactivated — <leadPartnersLink>Settings → Lead Partners</leadPartnersLink>.',
      columns: {
        name: 'Name',
        status: 'Status',
        permissions: 'Permissions',
        'rate-limit': 'Rate limit',
        created: 'Created',
        'valid-until': 'Valid until',
        'last-used': 'Last used',
        actions: 'Actions',
      },
      status: {
        active: 'Active',
        revoked: 'Revoked',
        expired: 'Expired',
      },
      'rate-limit-value': '{count}/s',
      revoke: {
        title: 'Revoke key?',
        'confirm-body':
          'The key "{name}" will be permanently disabled immediately. Requests using it will be rejected from then on. This cannot be undone.',
        'cancel-cta': 'Cancel',
        'confirm-cta': 'Revoke',
        'confirm-loading': 'Revoking…',
        'success-toast': 'Key "{name}" has been revoked.',
        'generic-error': 'Could not revoke the key — please try again.',
      },
      'edit-action': 'Edit',
      edit: {
        title: 'Edit key "{name}"',
        intro:
          'Changes apply from the next request. The key itself stays the same — connected integrations and MCP clients do not need to be set up again.',
        'expiry-fixed': 'Valid until {date}. The validity of an MCP key cannot be changed.',
        'rate-limit-label': 'Rate limit (requests per second)',
        'permissions-hint':
          'You can only grant new permissions that you hold yourself and that the member this key is bound to also holds.',
        'retained-group': 'Other permissions',
        'retained-hint':
          'These permissions were granted by the immoOS team. If you remove one, you cannot add it back yourself.',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'success-toast': 'Key "{name}" was updated.',
        'generic-error': 'Could not save the key — please try again.',
        errors: {
          'rate-limit': { invalid: 'Enter a whole number between 1 and 60.' },
          'permission-not-held':
            'You do not hold at least one of the newly added permissions, or the member this key is bound to does not. Remove it from the selection.',
          revoked: 'This key has since been revoked and can no longer be edited.',
          'not-found': 'This key no longer exists. The list has been refreshed.',
        },
      },
      issue: {
        title: 'Issue API key',
        'name-label': 'Name',
        'name-placeholder': 'Production integration',
        'description-label': 'Description',
        'description-placeholder': 'What this key is used for (optional)',
        'permissions-label': 'Permissions',
        'permissions-hint':
          'Choose the permissions for this key. A key can only do what you can — lose a permission and the key loses it too.',
        groups: {
          lead: 'Leads',
          deal: 'Deals',
          property: 'Properties',
          activity: 'Activities',
          relationship: 'Contacts & companies',
          valuation: 'Valuations',
          identity: 'Team & locations',
        },
        'permissions-not-held-hint':
          'You do not hold the greyed-out permissions yourself, so you cannot give them to a key. If you need them, ask an Owner to create the key.',
        'mcp-hint':
          'With this key, an MCP client like Claude can access your workspace — with exactly the permissions you choose below.',
        'expiry-label': 'Valid until',
        'expiry-hint': 'MCP keys always expire. Choose how long this one should stay valid.',
        'expiry-option': '{days} days',
        // `{limit, number}` renders with the active locale's grouping (en-US: 2,000).
        'volume-cap-hint': 'This key may make at most {limit, number} requests per day.',
        'submit-cta': 'Issue key',
        'submit-loading': 'Issuing…',
        'cancel-cta': 'Cancel',
        'done-cta': 'Done',
        'generic-error': 'Could not issue the key — please try again.',
        errors: {
          name: {
            required: 'Name is required.',
            'too-long': 'Name is too long.',
            invalid: 'Name is invalid.',
          },
          description: {
            'too-long': 'Description is too long.',
          },
          permissions: {
            required: 'Select at least one permission.',
          },
          expiry: {
            required: 'Choose how long this key should stay valid.',
          },
          // Keyed on the refusal's error code, not on a field. Carries NO
          // placeholder: the response deliberately withholds which grant was
          // refused (IMM-569.3), so the copy states the rule and sends the Owner
          // back to their own selection.
          'permission-not-held':
            'You can only grant permissions you hold yourself. Check your selection, or ask an Owner to create the key.',
        },
        'secret-heading': 'Your new API key',
        'secret-warning': 'Copy this key now — it is shown only once and cannot be retrieved again.',
        'copy-cta': 'Copy',
        'copied-toast': 'API key copied to clipboard.',
        'config-heading': 'How to connect your client',
        'config-hint': 'Copy this configuration into your MCP client. It contains your key — treat it like a password.',
        'config-copy-cta': 'Copy configuration',
        'config-copied-toast': 'Configuration copied to clipboard.',
        'config-endpoint-label': 'Endpoint URL',
        'config-key-label': 'Key',
      },
      /*
       * IMM-601.8.2 — the OAuth grants held by MCP clients. Same page as the
       * keys, its own list: a grant has no secret, no expiry and no rate limit,
       * so it has no columns for them either.
       */
      'connected-clients': {
        heading: 'Connected clients',
        empty: 'You have not connected a client yet.',
        'list-load-error': 'Could not load connected clients.',
        // Same wording as the key list: a grant that has never been used reads
        // "Never" rather than an empty cell or a placeholder date.
        'never-used': 'Never',
        'all-tools': 'All tools',
        'revoke-action': 'Revoke',
        'edit-action': 'Edit permissions',
        columns: {
          client: 'Client',
          permissions: 'Permissions',
          'connected-at': 'Connected on',
          'last-used': 'Last used',
          actions: 'Actions',
        },
        revoke: {
          title: 'Revoke access?',
          'confirm-body':
            'Access for "{name}" will be withdrawn immediately. Requests from this client will be rejected from then on.',
          'cancel-cta': 'Cancel',
          'confirm-cta': 'Revoke',
          'confirm-loading': 'Revoking…',
          'success-toast': 'Access for "{name}" has been revoked.',
          'generic-error': 'Could not revoke access — please try again.',
        },
        edit: {
          title: 'Edit permissions',
          subtitle: 'Changes apply from the next call by “{clientName}”. You do not need to reconnect the client.',
          'save-cta': 'Save',
          'save-loading': 'Saving…',
          'cancel-cta': 'Cancel',
          'success-toast': 'Permissions saved.',
          'generic-error': 'The permissions could not be saved — please try again.',
        },
      },
    },
    portals: {
      title: 'Portals',
      subtitle: "Connect your workspace's account with a property portal to publish properties there directly.",
      'card-title': 'ImmoScout24',
      status: {
        'not-connected': 'Not connected',
        connected: 'Connected',
        expired: 'Connection expired',
      },
      state: {
        'not-connected':
          "Not connected yet. Connect your workspace's ImmoScout24 account to publish properties directly.",
        connected: 'Connected as {accountName} · connected by {displayName} on {date, date, medium}',
        'connected-no-member': 'Connected as {accountName} · connected on {date, date, medium}',
        expired:
          'The connection to ImmoScout24 has expired or was revoked. Publishing is paused until the connection is renewed.',
        unavailable: 'The ImmoScout24 integration is not set up in this environment.',
        'read-only': 'Only owners and admins can manage the ImmoScout24 connection.',
      },
      'connect-cta': 'Connect account',
      'disconnect-cta': 'Disconnect',
      'renew-cta': 'Renew connection',
      'connecting-loading': 'Redirecting …',
      'load-error': 'The portal access could not be loaded.',
      'load-retry': 'Try again',
      disconnect: {
        title: 'Disconnect ImmoScout24?',
        body: 'Existing listings stay online but can no longer be controlled from immoOS.',
        'confirm-cta': 'Disconnect',
        'cancel-cta': 'Back',
        'confirm-loading': 'Disconnecting …',
      },
      'disconnect-success': 'The ImmoScout24 connection was disconnected.',
      openimmo: {
        status: { expired: 'Access invalid' },
        state: {
          'not-connected': 'Not connected yet. Enter the upload access that {portal} sent your agency.',
          connected: 'Connected · entered by {displayName} on {date, date, medium}',
          'connected-no-member': 'Connected · entered on {date, date, medium}',
          expired: 'The access to {portal} is invalid. Please check the access data.',
          unavailable: 'The {portal} integration is not set up in this environment.',
          'read-only': 'Only owners and admins can manage portal access.',
        },
        'enter-cta': 'Enter access',
        'change-cta': 'Change access',
        'disconnect-cta': 'Disconnect',
        dialog: {
          title: 'Enter access to {portal}',
          hint: {
            kleinanzeigen:
              'You receive the upload access (FTP) from Kleinanzeigen once your agency has booked a real estate partnership. Ideally type the data in by hand – copying easily adds spaces.',
            immowelt:
              'You receive the upload access (FTP) from Immowelt customer service for your partnership. It is not your login for immowelt.de.',
          },
          fields: {
            host: 'Server',
            username: 'Username',
            password: 'Password',
            'provider-id': 'Provider ID',
            'listing-email': 'Email for listings',
          },
          'listing-email-hint':
            'This address is the contact in every listing on {portal}. On Kleinanzeigen you see your listings under the account with exactly this address.',
          required: 'Please enter {field}.',
          'invalid-email': 'Please enter a valid email address.',
          'submit-cta': 'Check and save access',
          'submit-loading': 'Checking access …',
          'cancel-cta': 'Cancel',
        },
        disconnect: {
          title: 'Disconnect from {portal}?',
          body: 'The stored access will be deleted. Listings already on {portal} stay there but can no longer be controlled from immoOS.',
          'confirm-cta': 'Disconnect',
          'cancel-cta': 'Back',
          'confirm-loading': 'Disconnecting …',
        },
        'disconnect-success': 'The connection to {portal} was disconnected.',
      },
      callback: {
        loading: 'Connecting to ImmoScout24 …',
        'back-cta': 'Back to Portals',
      },
      banner: {
        title: 'The ImmoScout24 connection has expired. Listings cannot be published, updated or withdrawn.',
        'renew-cta': 'Renew connection',
        'openimmo-title':
          'The access to {portal} is invalid. Listings cannot be published, updated or withdrawn there.',
        'openimmo-cta': 'Change access',
      },
      extension: {
        title: 'Browser extension for ImmoScout24',
        body: 'With the immoOS extension for Chrome you create properties in your own ImmoScout24 account and keep them up to date. Your ImmoScout24 login stays in your browser — immoOS never sees it.',
        'connect-cta': 'Connect extension',
        'connect-loading': 'Connecting …',
        connected: 'The extension is connected to immoOS. Open ImmoScout24 and click the immoOS icon in the toolbar.',
        'not-installed':
          'The immoOS extension is not installed in this browser. Install it, then click “Connect extension” again.',
        'install-cta': 'Install extension',
        failed: 'The extension could not be connected. Please try again.',
        'read-only': 'You may not connect an extension. Ask someone with admin rights.',
        'key-name': 'Browser extension',
        'import-cta': 'Import ImmoScout24 listings',
      },
    },
    mailbox: {
      title: 'Mailbox',
      subtitle: 'Connect your email mailbox so immoOS can read and send on your behalf.',
      'load-error': 'Could not load your mailbox connection status.',
      'load-retry': 'Try again',
      status: {
        'connected-label': 'Connected',
        'not-connected-label': 'Not connected',
        'expired-label': 'Connection expired',
      },
      provider: {
        GOOGLE: 'Google',
        MICROSOFT: 'Microsoft 365',
        IMAP: 'IMAP',
      },
      // Short brand names shown on the account row's provider badge (design 08b).
      'provider-brand': {
        GOOGLE: 'Gmail',
        MICROSOFT: 'Outlook',
        IMAP: 'IMAP',
      },
      'section-title': 'My mailboxes',
      'secure-note':
        "Secure sign-in via the provider — immoOS stores no passwords. Connecting and disconnecting run through the provider's secured sign-in.",
      'imap-provider-line': 'IMAP · {provider}',
      'providers-note-with-imap':
        'With Google and Microsoft you sign in directly with the provider — immoOS stores no password there. With IMAP, immoOS stores your password encrypted.',
      imap: {
        title: 'Connect a mailbox via IMAP',
        'title-company': 'Connect company mailbox via IMAP',
        email: 'Email address',
        'email-placeholder': 'name@provider.com',
        password: 'Password',
        'password-hint': 'Some providers require a separate password for email programs, or an app password.',
        recognised: 'Provider recognised: {provider}',
        'edit-servers': 'Enter server details myself',
        'unknown-intro': 'We do not know the server details for {domain}. You can find them in your provider’s help.',
        'imap-host': 'Incoming mail server (IMAP)',
        'smtp-host': 'Outgoing mail server (SMTP)',
        port: 'Port',
        username: 'Username',
        'imap-host-placeholder': 'imap.example.com',
        'smtp-host-placeholder': 'smtp.example.com',
        'security-note':
          'immoOS stores your password encrypted and uses it only to retrieve your mailbox and to send on your behalf.',
        'security-note-company': 'immoOS stores the password encrypted and uses it only to fetch the company mailbox.',
        back: 'Back',
        connect: 'Connect',
        checking: 'Checking the connection …',
        hint: {
          'gmx-web-de':
            'At {provider} you have to switch IMAP on first: email settings → POP3/IMAP → ‘Allow POP3 and IMAP access’. With two-factor sign-in you need an app password.',
          't-online':
            'At T-Online you need the password for email programs, not your Telekom login password. You create it in the Email Center under Settings → Passwords.',
          freenet: 'At freenet you have to switch access on first in your mailbox: ‘Enable POP3, IMAP and SMTP’.',
        },
        'required-email': 'Please enter your email address.',
        'invalid-email': 'Please enter a valid email address.',
        'required-password': 'Please enter your password.',
        'required-imap-host': 'Please enter the incoming mail server.',
        'required-smtp-host': 'Please enter the outgoing mail server.',
        'invalid-port': 'Please enter a port between 1 and 65535.',
        providers: {
          GMX: 'GMX',
          WEB_DE: 'WEB.DE',
          T_ONLINE: 'T-Online',
          IONOS: 'IONOS',
          STRATO: 'STRATO',
          MAILBOX_ORG: 'mailbox.org',
          POSTEO: 'Posteo',
          FREENET: 'freenet',
        },
      },
      'connected-as': 'Connected as {email}',
      'provider-label': 'Provider: {provider}',
      'not-connected-hint': 'Choose your provider to connect your mailbox.',
      'unsupported-providers':
        'Only a Gmail mailbox can be connected at the moment. Microsoft 365, Exchange and IMAP are not supported yet.',
      'unsupported-providers-with-microsoft':
        'Gmail and Microsoft mailboxes can be connected. Exchange on your own servers and IMAP are not supported yet.',
      'connect-cta': 'Connect mailbox',
      'connect-with': 'Connect with {provider}',
      'connecting-loading': 'Connecting…',
      'disconnect-cta': 'Disconnect',
      'reconnect-cta': 'Reconnect mailbox',
      'connect-error': 'Could not start the connection — please try again.',
      'disconnect-success': 'Mailbox disconnected.',
      'disconnect-error': 'Could not disconnect the mailbox — please try again.',
      'connect-success': 'Mailbox connected: {email}.',
      expired: {
        title: 'Your mailbox connection has expired.',
        body: 'immoOS can no longer access email on your behalf. This usually happens after you change your provider password. Reconnect your mailbox to resume syncing.',
      },
      switchover: {
        title: 'We have improved the mailbox connection.',
        body: 'immoOS now connects directly to Google. Please reconnect your mailbox once — everything keeps working as usual until you do.',
      },
      disconnect: {
        title: 'Disconnect mailbox?',
        'confirm-body':
          'The connection to {email} will be revoked at the provider and removed from immoOS. You can reconnect your mailbox again at any time.',
        'confirm-body-imap':
          'The access data for {email} will be deleted from immoOS. Your mailbox at the provider stays unchanged. You can reconnect it at any time.',
        'cancel-cta': 'Cancel',
        'confirm-cta': 'Disconnect',
        'confirm-loading': 'Disconnecting…',
      },
      callback: {
        loading: 'Completing mailbox connection…',
        'missing-params': 'The provider returned an incomplete connection. Please try again.',
        error: 'The mailbox connection could not be completed. Please try again.',
        'back-cta': 'Back to mailbox settings',
        release: {
          title: 'Your IT needs to approve immoOS first',
          body: 'Your Microsoft account belongs to an organisation where only administrators can allow new apps. Ask your IT to approve immoOS once — you can forward them the guide directly. Then connect your mailbox here again.',
          'guide-cta': 'Open the guide for your IT',
        },
      },
    },
    'print-material': {
      title: 'Print material',
      subtitle:
        'Order business cards and presentation folders for your location — with the details immoOS already holds.',
      action: 'Go to ordering',
      pending: 'Being set up — available shortly.',
      'not-available': 'No application is enabled for your workspace.',
    },
    'lead-partners': {
      title: 'Lead Partners',
      subtitle: 'The partners your workspace receives Owner-Leads from — reusable across leads and reportable later.',
      'add-cta': 'Add Lead Partner',
      'list-load-error': 'Could not load Lead Partners.',
      'empty-title': 'No Lead Partners yet',
      'empty-description': 'Add your first Lead Partner so it can be picked when capturing a lead from a partner.',
      'success-toast': 'Lead Partner “{name}” added.',
      banner: {
        title: 'Lead Partners are workspace-wide.',
        body: 'Deactivated partners stay attached to existing leads but can’t be picked on new ones. Hard-delete isn’t supported — deactivation is the only retirement path.',
      },
      columns: {
        name: 'Partner',
        slug: 'Slug',
        status: 'Status',
        'api-key': 'API key',
        actions: 'Actions',
      },
      status: {
        active: 'Active',
        inactive: 'Deactivated',
      },
      'key-status': {
        issued: 'Linked',
        none: '—',
      },
      'deactivate-action': 'Deactivate',
      'reactivate-action': 'Reactivate',
      'issue-key-action': 'Issue key',
      add: {
        title: 'Add Lead Partner',
        'name-label': 'Name',
        'name-placeholder': 'e.g. Aroundhome',
        'submit-cta': 'Add partner',
        'submit-loading': 'Adding…',
        'cancel-cta': 'Cancel',
        errors: {
          'name-required': 'Name is required.',
          'name-taken': 'A Lead Partner with this name already exists.',
        },
        'generic-error': 'Could not add the Lead Partner — please try again.',
        'forbidden-error': 'You are not allowed to manage Lead Partners.',
      },
      'issue-key': {
        title: 'Issue push API key',
        'confirm-body':
          'Issue a lead-push API key for “{name}”. Leads pushed with this key are automatically attributed to this partner. The secret is shown exactly once.',
        'cancel-cta': 'Cancel',
        'confirm-cta': 'Issue key',
        'confirm-loading': 'Issuing…',
        'secret-heading': 'Your API key',
        'secret-warning': 'Copy the key now — it won’t be shown again.',
        'workspace-id-label': 'Workspace ID',
        'secret-label': 'API key',
        'copy-cta': 'Copy',
        'copied-toast': 'Copied to clipboard.',
        'done-cta': 'Done',
        'success-toast': 'API key issued for “{name}”.',
        'generic-error': 'Could not issue the key — please try again.',
      },
    },
    'my-profile': {
      title: 'My profile',
      subtitle: 'These details appear in the Exposé and on your agent profile.',
      'empty-hint': 'Complete your profile so it appears in the Exposé and on your agent profile.',
      'load-error': 'Your profile could not be loaded.',
      retry: 'Try again',
      'edit-cta': 'Edit profile',
      'cancel-cta': 'Cancel',
      'save-cta': 'Save',
      'save-loading': 'Saving…',
      'save-success': 'Profile saved',
      'generic-error': 'Could not save the profile — please try again.',
      'forbidden-error': 'You are not allowed to edit this profile.',
      'location-source': 'from Locations',
      'picklist-no-results': 'No matches',
      'remove-entry': 'Remove {label}',
      'add-certification': 'Add certification',
      'add-social-link': 'Add social link',
      badges: {
        'source-account': 'from your account',
        'source-readonly': 'read-only',
      },
      'password-stub': {
        title: 'Change password',
        badge: 'Coming soon',
        hint: 'This feature will be available soon.',
        'change-cta': 'Change',
      },
      whatsapp: {
        heading: 'WhatsApp',
        intro: 'Connect your WhatsApp number to ask immoOS questions on the go.',
        'connect-cta': 'Connect WhatsApp',
        'step-1': 'Open WhatsApp using the button below.',
        'step-2': 'The message with your code is already filled in.',
        'step-3': "Send it — that's it.",
        'open-cta': 'Open in WhatsApp',
        fallback: 'Button not working? Send this code by WhatsApp to {immoosNumber}: {code}',
        waiting: 'Waiting for your message …',
        connected: 'Connected to {phone}. Message {immoosNumber} to ask immoOS over WhatsApp.',
        'connected-badge': 'Connected',
        'disconnect-cta': 'Disconnect WhatsApp',
        prefilled: "Hello immoOS, I'd like to connect my WhatsApp number. My code: {code}",
        error: "That didn't work. Please try again.",
      },
      telegram: {
        heading: 'Telegram',
        intro:
          'Ask immoOS about your leads, contacts and appointments on Telegram. Connect your Telegram account once to get started.',
        'connect-cta': 'Connect Telegram',
        'step-1': 'Tap "Open in Telegram". Telegram opens the immoOS bot.',
        'step-2': 'Tap "Start" in the bot. You don\'t need to type anything.',
        'step-3': "Done — we'll message you on Telegram as soon as your account is connected.",
        'open-cta': 'Open in Telegram',
        fallback: 'Button not working? Send this code on Telegram to {botName}: {code}',
        waiting: 'Waiting for your message …',
        connected: 'Connected to {account}. Message {botName} to ask immoOS on Telegram.',
        'connected-badge': 'Connected',
        'disconnect-cta': 'Disconnect Telegram',
        error: "That didn't work. Please try again.",
        'load-error': "Telegram couldn't be loaded. Please try again.",
        'retry-cta': 'Try again',
        'disconnect-title': 'Disconnect Telegram account {account}?',
        'disconnect-body':
          'The assistant will no longer answer you in {botName}. You can connect your account again at any time.',
        'cancel-cta': 'Cancel',
        'disconnect-confirm-cta': 'Disconnect',
        'disconnect-loading': 'Disconnecting…',
      },
      photo: {
        label: 'Profile photo',
        change: 'Replace photo',
        remove: 'Remove photo',
        uploading: 'Uploading…',
        alt: 'Profile photo',
        formats: 'JPG, PNG or WebP · max. 5 MB',
        invalid: 'Please upload a JPG, PNG or WebP image up to 5 MB.',
        'upload-error': 'The photo could not be uploaded — please try again.',
        saving: 'Saving…',
        'save-error': 'The photo was uploaded but could not be saved — please try again.',
      },
      sections: {
        identity: 'Account details',
        'agent-profile': 'Agent profile',
        profile: 'Profile',
        'contact-qualification': 'Contact & qualification',
      },
      'location-empty': 'Not set',
      fields: {
        'display-name': 'Display name',
        'first-name': 'First name',
        'last-name': 'Last name',
        email: 'Email',
        location: 'Location',
        salutation: 'Salutation',
        'academic-title': 'Title',
        position: 'Position',
        phone: 'Phone',
        mobile: 'Mobile',
        bio: 'Short bio',
        specialisations: 'Specialisations',
        languages: 'Languages',
        certifications: 'Certifications',
        'social-links': 'Social links',
      },
      placeholders: {
        'first-name': 'e.g. Anna',
        'last-name': 'e.g. Becker',
        salutation: 'Please choose',
        'academic-title': 'e.g. Dr.',
        position: 'e.g. Property consultant',
        phone: '+49 40 123456',
        mobile: '+49 151 23456789',
        bio: 'A short introduction for your agent profile',
        certifications: 'One entry per line',
        'social-links': 'One link per line',
        specialisations: 'Choose a specialisation…',
        languages: 'Choose a language…',
        'certification-entry': 'e.g. IHK-certified estate agent',
        'social-link-entry': 'https://…',
      },
      helpers: {
        location: 'Your primary location — not editable here.',
        'location-derived': 'Derived from your primary location — not editable here.',
        picklist: 'Multiple selection from a fixed list · no free text',
        bio: 'At most 600 characters.',
        certifications: 'One entry per line.',
        'social-links': 'One link per line, starting with https://.',
      },
      errors: {
        'name-required': 'Please enter your first and last name.',
        bio: 'The short bio must be at most 600 characters.',
        phone: 'Please enter a valid phone number.',
        'social-links': 'Please enter a valid URL (starting with https://).',
        list: 'Please choose a value from the list.',
        'too-long': 'The value is too long.',
      },
      salutations: {
        MR: 'Mr',
        MRS: 'Mrs',
        DIVERSE: 'Diverse',
      },
      specialisations: {
        RESIDENTIAL: 'Residential',
        COMMERCIAL: 'Commercial',
        INVESTMENT: 'Investment',
        NEW_BUILD: 'New build',
        LAND: 'Land',
        RENTAL: 'Rental',
      },
      languages: {
        DE: 'German',
        EN: 'English',
        FR: 'French',
        ES: 'Spanish',
        IT: 'Italian',
        TR: 'Turkish',
        RU: 'Russian',
        PL: 'Polish',
      },
    },
    company: {
      title: 'Company data',
      subtitle:
        "Add your firm's legal master data. It feeds the legal notice and the mandatory details in your Exposés and documents.",
      'empty-hint':
        "Add your firm's details — they appear in the legal notice and the Exposé footer of outward-facing documents.",
      'load-error': 'The company data could not be loaded.',
      'load-retry': 'Try again',
      'readonly-hint': 'Only owners and admins can edit the company data.',
      'save-cta': 'Save',
      'save-loading': 'Saving…',
      'save-success': 'Company data saved',
      'generic-error': 'Company data could not be saved — please try again.',
      'forbidden-error': 'You are not authorised for this action.',
      'web-address': {
        label: 'Web address',
        help: 'Your web exposés appear at this address: immoos.ai/{webAddress}/expose/…',
        'change-confirm': {
          title: 'Change web address?',
          body: 'Existing web exposé links will stop working. You will need to resend links you have already sent.',
          confirm: 'Change',
          cancel: 'Cancel',
        },
      },
      'address-search-label': 'Search address',
      'social-links-add': 'Add link',
      'social-links-remove': 'Remove link',
      countries: {
        DE: 'Germany',
        AT: 'Austria',
        CH: 'Switzerland',
      },
      sections: {
        company: 'Company details',
        address: 'Address',
        contact: 'Contact',
        legal: 'Legal notice',
      },
      fields: {
        'company-name': 'Company name',
        'legal-form': 'Legal form',
        street: 'Street',
        'house-number': 'House number',
        'postal-code': 'Postcode',
        city: 'City',
        country: 'Country',
        phone: 'Phone',
        fax: 'Fax',
        email: 'Email',
        website: 'Website',
        'social-links': 'Social links',
        'managing-directors': 'Managing director(s)',
        'commercial-register-court': 'Register court',
        'commercial-register-number': 'Register number',
        'vat-id': 'VAT ID',
        'supervisory-authority': 'Supervisory authority (§34c GewO)',
        'professional-indemnity-insurance': 'Professional indemnity insurance',
        'privacy-policy-url': 'Privacy policy (URL)',
      },
      placeholders: {
        'company-name': 'Mustermakler GmbH',
        'legal-form': 'Please select',
        'address-search': 'Enter an address…',
        street: 'Musterstraße',
        'house-number': '1',
        'postal-code': '20251',
        city: 'Hamburg',
        country: 'Deutschland',
        phone: '+49 40 123456',
        fax: '+49 40 123457',
        email: 'info@mustermakler.de',
        website: 'https://www.mustermakler.de',
        'social-links': 'https://www.linkedin.com/company/…',
        'managing-directors': 'Max Mustermann, Erika Mustermann',
        'commercial-register-court': 'Amtsgericht Hamburg',
        'commercial-register-number': 'HRB 123456',
        'vat-id': 'DE123456789',
        'supervisory-authority': 'Behörde für Wirtschaft Hamburg, Erlaubnis §34c GewO',
        'professional-indemnity-insurance': 'Allianz Versicherungs-AG, Police 4711',
        'privacy-policy-url': 'https://www.your-company.com/privacy',
      },
      helpers: {
        'social-links': 'One full URL each, starting with https://.',
        'managing-directors': 'Separate multiple names with a comma.',
      },
      errors: {
        'privacy-policy-url': 'Please enter a valid address starting with https://.',
        'company-name': 'The company name must be at most 120 characters.',
        email: 'Please enter a valid email address.',
        website: 'Please enter a valid URL (starting with https://).',
        'social-links': 'Please enter a valid URL (starting with https://).',
        'social-links-duplicate': 'This link is already in the list — please remove the duplicate.',
        'legal-form': 'Please choose a legal form from the list.',
        phone: 'Please enter a valid phone number.',
        'vat-id': 'Please enter a valid VAT ID (e.g. DE123456789).',
        'too-long': 'The value is too long.',
        address: {
          street: 'Please enter a street.',
          'house-number': 'Please enter a house number.',
          'postal-code': 'The postcode must be 5 digits.',
          city: 'Please enter a city.',
          country: 'Please enter a country.',
        },
      },
      'legal-forms': {
        GMBH: 'GmbH',
        UG: 'UG (haftungsbeschränkt)',
        GMBH_CO_KG: 'GmbH & Co. KG',
        EINZELUNTERNEHMEN: 'Einzelunternehmen',
        EK: 'e.K.',
        GBR: 'GbR',
        PARTG_MBB: 'PartG mbB',
        OHG: 'OHG',
        KG: 'KG',
        UG_CO_KG: 'UG (haftungsbeschränkt) & Co. KG',
        AG: 'AG',
      },
    },
    branding: {
      title: 'Branding & CI',
      subtitle: "Set the colours and typography that define your firm's identity — they flow into every Exposé.",
      'empty-hint': "Store your firm's visual identity — logo, colours and typography appear on every Exposé.",
      'load-error': 'The branding could not be loaded.',
      'readonly-hint': 'Only owners and admins can edit the company branding.',
      'save-cta': 'Save',
      'save-loading': 'Saving…',
      'save-success': 'Branding saved',
      'glyph-warning':
        '{fonts}: this typeface is missing some characters (ä, ö, ü, ß, €, ²). Individual characters may be absent in the Exposé — you can still use it.',
      'generic-error': 'The branding could not be saved — please try again.',
      'forbidden-error': 'You are not authorised to perform this action.',
      sections: {
        logo: 'Logo',
        colours: 'Colours',
        typography: 'Typography',
      },
      groups: {
        'house-colours': 'House colours',
        'document-colours': 'Document colours',
      },
      fields: {
        logoPrimary: 'Logo (primary)',
        logoLight: 'Light logo',
        logoDark: 'Dark logo',
        favicon: 'Favicon',
        brandColour: 'Brand colour (primary)',
        secondaryColor: 'Brand colour (secondary)',
        accentColor: 'Accent colour',
        paperColor: 'Paper colour',
        inkColor: 'Ink colour',
        mutedColor: 'Muted colour',
        headingFont: 'Heading typeface',
        bodyFont: 'Body typeface',
        labelFont: 'Label typeface',
        labelCasing: 'Label casing',
        labelTracking: 'Label tracking',
      },
      placeholders: {
        font: 'Please select',
        'label-casing': 'Please select',
      },
      'font-picker': {
        title: 'Choose a typeface',
        choose: 'Choose a typeface',
        'preview-label': 'Preview',
        sample: 'Häuser & Höfe · Größe 85 m² · 1.250 € · Zürich',
        recommended: 'Recommended typefaces',
        'default-option': 'Default (template)',
        'browse-google': 'Browse all Google Fonts',
        'search-placeholder': 'Search Google Fonts …',
        'no-results': 'No typefaces found.',
        'source-google': 'Google Fonts',
        'source-curated': 'Recommended',
      },
      'label-casings': {
        NORMAL: 'Normal',
        UPPERCASE: 'UPPERCASE',
        SMALL_CAPS: 'Small caps',
      },
      errors: {
        colour: 'Please enter a valid colour as a hex value (e.g. #0F5132).',
        font: 'Please choose a typeface from the list.',
        tracking: 'Please enter a valid value in em or px (e.g. 0.08em).',
      },
      logo: {
        change: 'Change',
        remove: 'Remove',
        uploading: 'Uploading…',
        invalid: 'Please upload a PNG or JPG image up to 5 MB.',
        'invalid-favicon': 'Please upload a PNG or ICO image up to 5 MB.',
        'upload-error': 'The image could not be uploaded — please try again.',
      },
    },
    signature: {
      title: 'E-mail signature',
      subtitle:
        'One company signature template for the whole workspace. Profile variables resolve automatically from each sender’s profile.',
      'load-error': 'The signature could not be loaded.',
      'readonly-hint': 'Only owners and admins can edit the company signature.',
      'editor-label': 'Signature editor',
      'footer-hint': 'This signature is added by default beneath outgoing e-mails.',
      counter: '{count} / {max}',
      preview: {
        title: 'Preview',
        'profile-label': 'Profile',
        'empty-hint': 'No signature is set for this profile yet.',
        'load-error': 'The preview could not be loaded.',
        'degradation-note': 'Missing values (e.g. mobile or profile photo) are omitted automatically per profile.',
      },
      'save-cta': 'Save',
      'save-loading': 'Saving…',
      'save-success': 'Signature saved',
      'generic-error': 'The signature could not be saved — please try again.',
      'forbidden-error': 'You are not authorised to perform this action.',
      toolbar: {
        label: 'Formatting',
        'font-family': 'Typeface',
        'font-size': 'Font size',
        bold: 'Bold',
        italic: 'Italic',
        underline: 'Underline',
        colour: 'Text colour',
        'insert-logo': 'Insert logo',
      },
      fonts: {
        standard: 'Default',
        'empty-hint': 'No CI typeface configured yet — only “Default” is available.',
      },
      palette: {
        title: 'Profile variables',
        subtitle: 'Values are pulled automatically from each user’s profile.',
        groups: {
          person: 'Person',
          contact: 'Contact',
          company: 'Company',
        },
      },
      logo: {
        'block-title': 'Logo in the signature',
        'size-label': 'Logo size',
        'placeholder-alt': 'Company logo',
        sizes: {
          SMALL: 'Small · 120 px',
          MEDIUM: 'Medium · 180 px',
          LARGE: 'Large · 240 px',
        },
      },
      errors: {
        'too-long': 'The signature may be at most 5,000 characters long.',
        'unsupported-variable': 'This variable is not supported.',
      },
    },
  },
  workspace: {
    'buyer-commission': {
      rate: '{rate, number, ::.00} % incl. VAT',
      amount: '{amount, number, ::currency/EUR} incl. VAT',
      'buyer-free': 'commission-free for the buyer',
      missing: 'Buyer commission missing',
      label: 'Buyer commission',
      edit: 'Edit',
    },
    'objekt-commission': {
      'load-error': 'The property could not be loaded. Please try again.',
    },
    completeness: {
      products: COMPLETENESS_PRODUCT_LABELS,
      markers: { ...COMPLETENESS_MARKER_LABELS, separator: ' · ' },
      title: 'Data status',
      complete: 'Complete',
      'missing-required': '{count, plural, one {# required field missing} other {# required fields missing}}',
      'missing-recommended': '{count, plural, one {# recommended field missing} other {# recommended fields missing}}',
      'load-error': 'The data status could not be loaded.',
      'load-retry': 'Try again',
      'no-needs': 'No needs are defined for this property type yet.',
      'agent-outcome': {
        'no-comparison-data': {
          'acquisition-briefing':
            'Comparison data is missing for this region. An acquisition briefing is not yet possible here.',
          'valuation-1a': 'Comparison data is missing for this region. A valuation 1a is not yet possible here.',
          'valuation-1b': 'Comparison data is missing for this region. A valuation 1b is not yet possible here.',
        },
        'address-not-found': "The address could not be found. Please check the property's address.",
        'object-type-not-supported': 'The valuation agent does not support this property type yet.',
        error: '{product} could not be created: {hint}',
        'timed-out': {
          'acquisition-briefing': 'The acquisition briefing could not be created.',
          'valuation-1a': 'Valuation 1a could not be created.',
          'valuation-1b': 'Valuation 1b could not be created.',
        },
      },
      'desk-valuation': {
        start: 'Create valuation 1a',
        'missing-hint':
          '{count, plural, one {# required field is missing.} other {# required fields are missing.}} The valuation agent will then work with assumptions and state them in the valuation.',
        preparing: 'Valuation 1a is being created … (started {time})',
        delivered: 'Valuation 1a of {date}: {from}–{to} €',
        view: 'View',
        renew: 'Recreate',
        retry: 'Try again',
        'object-type-unsupported': 'There is no valuation 1a for this property type yet.',
        'address-incomplete': 'A valuation needs the property to have a complete address.',
        'renew-dialog': {
          title: 'Create a new valuation 1a?',
          description: 'The previous one will be replaced.',
          confirm: 'Recreate',
          cancel: 'Cancel',
        },
      },
    },
    'valuation-source': {
      kind: VALUATION_SOURCE_KIND_LABELS,
      'viewing-kind': VALUATION_VIEWING_KIND_LABELS,
      'with-assumptions': 'with assumptions',
    },
    'search-filter': {
      chip: 'Search: {query}',
      remove: 'Remove search "{query}"',
    },
    'linked-record': {
      'read-only-title': 'Read-only access',
      'read-only-hint':
        'You are viewing this record through a link. Only members with access to the {organization} location can edit it.',
      'read-only-hint-unknown': 'You are viewing this record through a link. It cannot be edited here.',
      'read-only-reason': 'Opened through a link — read-only.',
    },
    'compose-entry': {
      label: 'Write e-mail',
      'no-permission': "You don't have permission to send e-mails.",
      'no-mailbox': 'Your mailbox is not connected. Connect it to write e-mails.',
      'no-address': 'No e-mail address is on file for this contact.',
    },
    'connection-gap': {
      'mailbox-and-calendar':
        'Mailbox and calendar are not connected. Connect a mailbox with a calendar to write e-mails and invite to viewings – an IMAP mailbox has no calendar.',
      'connect-calendar': 'Connect calendar',
      'connect-mailbox': 'Connect mailbox',
    },
    'reassign-standort': {
      title: 'Change Standort',
      label: 'Standort',
      cancel: 'Cancel',
      submit: 'Change Standort',
      submitting: 'Changing…',
      success: 'Standort changed',
      'forbidden-error': 'You are not authorised to move this record to the selected Standort.',
      'not-found-error': 'Record not found. Please reload the page.',
      'generic-error': 'The Standort could not be changed. Please try again.',
    },
    activityTimeline: {
      'prospect-phase-reason-code': { VIEWING_CANCELLED: 'Viewing cancelled' },
      'email-delete-title': 'Remove this e-mail from the activity?',
      'email-delete-hint': 'The e-mail stays in your mailbox. Only the timeline entry is removed.',
      'direction-inbound': 'Inbound',
      'direction-outbound': 'Outbound',
      'email-delete-confirm': 'Remove',
      'email-delete-cancel': 'Cancel',
      'email-delete-error': 'The e-mail could not be removed — please try again.',
      'group-today': 'Today',
      'group-yesterday': 'Yesterday',
      'relative-just-now': 'Just now',
      'relative-minutes': '{count} minutes ago',
      'relative-hours': '{count} hours ago',
      'relative-yesterday': 'Yesterday',
      'entry-note': 'Note',
      'entry-appointment': 'Appointment',
      'entry-appointment-valuation': 'Valuation appointment',
      'entry-appointment-on-site': 'On-site appointment',
      'entry-appointment-property-survey': 'Property survey',
      'entry-appointment-contract-signing': 'Contract signing',
      'entry-appointment-other': 'Other appointment',
      'entry-appointment-viewing-on-site': 'On-site viewing',
      'entry-appointment-viewing-virtual': 'Virtual viewing',
      'entry-appointment-consultation': 'Consultation',
      'entry-appointment-financing-consultation': 'Financing consultation',
      'entry-appointment-negotiation': 'Negotiation',
      'entry-appointment-notary': 'Notary appointment',
      'entry-appointment-handover': 'Handover',
      'entry-viewing': 'Viewing',
      'entry-viewing-assessed': 'Viewing of {date} assessed: {outcome}',
      'entry-viewing-response-ACCEPTED': '{name} accepted the viewing',
      'entry-viewing-response-TENTATIVE': '{name} tentatively accepted the viewing',
      'entry-viewing-response-DECLINED': '{name} declined the viewing',
      'entry-viewing-response-ACCEPTED-unnamed': 'The prospect accepted the viewing',
      'entry-viewing-response-TENTATIVE-unnamed': 'The prospect tentatively accepted the viewing',
      'entry-viewing-response-DECLINED-unnamed': 'The prospect declined the viewing',
      'filter-appointment': 'Appointment',
      'entry-call-inbound': 'Inbound call',
      'entry-call-outbound': 'Outbound call',
      'entry-sms-inbound': 'Inbound SMS',
      'entry-sms-outbound': 'Outbound SMS',
      'entry-whatsapp-inbound': 'Inbound WhatsApp',
      'entry-whatsapp-outbound': 'Outbound WhatsApp',
      'entry-fax-inbound': 'Inbound fax',
      'entry-fax-outbound': 'Outbound fax',
      'entry-letter-inbound': 'Inbound letter',
      'entry-letter-outbound': 'Outbound letter',
      'entry-email-inbound': 'Inbound email',
      'entry-email-outbound': 'Outbound email',
      'entry-email-empty-subject': '(No subject)',
      'entry-email-open-thread': 'Open conversation',
      'entry-company-badge': 'Company',
      'entry-email-reply': 'Reply',
      'entry-email-recipient': 'To: {recipient}',
      'entry-email-unavailable': 'Conversation no longer available from the provider',
      'entry-email-auto-placed': 'Assigned automatically',
      'entry-email-mailbox-of': "From {name}'s mailbox",
      'entry-email-attachment': 'Attachment',
      'entry-calendar-cancelled': 'Cancelled: {title}',
      'entry-calendar-untitled': '(Untitled)',
      'email-activity-footer':
        'Email is the same activity as everything else — call, note, task. Use the "{filter}" filter to see conversations only.',
      'entry-prospect-created': 'Prospect created',
      'entry-prospect-created-via-inquiry': 'Created via {source} inquiry',
      'entry-inquiry-received': 'Inquiry via {source}',
      'prospect-source': 'Source {source}',
      'prospect-offer': 'Offer {price}',
      'prospect-purchase-price': 'Purchase price {price}',
      'prospect-notary-date': 'Notary appointment {date}',
      'prospect-won': 'Purchase won',
      'entry-prospect-offer-changed': 'Offer changed',
      'entry-prospect-notary-moved': 'Notary appointment moved',
      'entry-prospect-notary-captured': 'Notary appointment recorded',
      'entry-prospect-offer-captured': 'Offer recorded',
      'entry-prospect-counter-offer-captured': 'Counter-offer recorded',
      'entry-prospect-offer-accepted': 'Offer accepted',
      'entry-prospect-counter-offer-accepted': 'Counter-offer accepted',
      'entry-prospect-offer-rejected': 'Offer rejected',
      'entry-prospect-counter-offer-rejected': 'Counter-offer rejected',
      'entry-prospect-offer-withdrawn': 'Offer withdrawn',
      'entry-prospect-counter-offer-withdrawn': 'Counter-offer withdrawn',
      'entry-prospect-offer-reopened': 'Offer reopened',
      'entry-prospect-counter-offer-reopened': 'Counter-offer reopened',
      'entry-prospect-lost': 'Marked as lost',
      'entry-prospect-reopened': 'Reopened',
      'entry-brokerage-notice-sent': 'Cancellation notice sent to {recipient}',
      'entry-brokerage-reminder-sent': 'Reminder sent to {recipient}',
      'entry-brokerage-concluded-early-start': 'Concluded online · early start requested',
      'entry-brokerage-concluded-no-early-start': 'Concluded online · early start not requested',
      'entry-brokerage-acceptance-reply-received': 'E-mail acceptance noted',
      'entry-brokerage-withdrawal-receipt-sent': 'Withdrawal received · acknowledgement sent',
      'entry-brokerage-withdrawal-receipt-failed': 'Withdrawal received · acknowledgement failed',
      'actor-prospect-online': 'Prospect (online)',
      'entry-deal-created': 'Deal created',
      'entry-deal-advanced': 'Phase advanced',
      'entry-deal-reverted': 'Phase reverted',
      'entry-deal-contact-added': 'Owner added',
      'entry-deal-contact-removed': 'Owner removed',
      'entry-deal-resumed': 'Resumed',
      'entry-deal-restored': 'Deal restored',
      'entry-deal-deferred': 'On hold',
      'entry-deal-deferred-until': 'until {date}',
      'entry-deal-lost': 'Lost',
      'entry-deal-reopened': 'Reopened',
      'entry-deal-document-generated': 'Document created',
      'entry-deal-document-open': 'Open PDF',
      'entry-deal-document-line': '{documentType} · {templateName}',
      'entry-deal-document-manually-adjusted': 'Edited by hand',
      'entry-deal-signing-sent': 'Sent for signature',
      'entry-deal-signing-sent-line': '{documentType} to {names}',
      'entry-deal-signing-signed': 'Signed by {name}',
      'entry-deal-signing-signed-line': '{documentType}',
      'entry-deal-signing-completed': 'Fully signed',
      'entry-deal-signing-withdrawn': 'Signature request withdrawn',
      'entry-deal-signing-acceptance-marked': 'Acceptance by e-mail from {name} recorded',
      'entry-deal-signing-revoked': 'Withdrawal by {name}',
      'entry-deal-signing-completed-open': 'Open PDF',
      'origin-online-signature': 'Signature (online)',
      'entry-deal-document-type': {
        BROKER_CONTRACT: DOCUMENT_CATEGORY_LABELS.BROKER_CONTRACT,
        POWER_OF_ATTORNEY: DOCUMENT_CATEGORY_LABELS.POWER_OF_ATTORNEY,
      } satisfies Record<DocumentTemplateCategory, string>,
      'entry-property-status-changed': 'Status changed',
      'entry-property-archive-reason-changed': 'Archive reason changed',
      'entry-property-pricing-sale-changed': 'Sale price changed',
      'entry-property-areas-changed': 'Areas changed',
      'entry-property-hand-reservation-marked': 'Marked as reserved',
      'entry-property-hand-reservation-unmarked': 'Reservation lifted',
      'entry-property-hand-reservation-lifted': 'Reservation lifted – property no longer active',
      'entry-deal-resumed-auto': 'Automatically resumed',
      'entry-deal-responsibility-manual': '{actor} set {names} as responsible.',
      'entry-deal-responsibility-created': 'Deal created — responsible: {names}.',
      'entry-deal-responsibility-qualified': 'Deal converted from lead — responsible: {names}.',
      'entry-deal-responsibility-deactivated': '{name} was deactivated and is no longer responsible.',
      'entry-deal-responsibility-none': 'This deal is currently unassigned.',
      'entry-valuation-submitted': '{actor} requested a valuation.',
      'entry-valuation-taken-on': '{actor} started working on the valuation.',
      'entry-valuation-submitted-for-approval': '{actor} submitted the valuation for approval.',
      'entry-valuation-returned': '{actor} returned the valuation for rework: {reason}',
      'entry-valuation-adjusted': '{actor} adjusted the price range: {previous} → {next}',
      'entry-valuation-released': '{actor} released the valuation.',
      'entry-valuation-withdrawn': '{actor} withdrew the valuation.',
      'entry-valuation-price-range': '{from} – {to}',
      'entry-lead-lost': 'Lead lost',
      'entry-lead-archived': 'Lead archived',
      'entry-lead-reopened': 'Lead reopened',
      'entry-lead-contacted': "Status set to 'Contacted'",
      'entry-lead-contacted-meta': 'automatic · {activity}',
      'entry-contacted-after-call-outbound': 'after outgoing call',
      'entry-contacted-after-call-inbound': 'after incoming call',
      'entry-contacted-after-sms-outbound': 'after outgoing SMS',
      'entry-contacted-after-sms-inbound': 'after incoming SMS',
      'entry-contacted-after-whatsapp-outbound': 'after outgoing WhatsApp',
      'entry-contacted-after-whatsapp-inbound': 'after incoming WhatsApp',
      'entry-contacted-after-fax-outbound': 'after outgoing fax',
      'entry-contacted-after-fax-inbound': 'after incoming fax',
      'entry-contacted-after-letter-outbound': 'after outgoing letter',
      'entry-contacted-after-letter-inbound': 'after incoming letter',
      'entry-contacted-after-email-outbound': 'after outgoing email',
      'entry-contacted-after-email-inbound': 'after incoming email',
      'deal-transition': '{from} → {to}',
      'actor-system': 'System',
      'actor-admin': 'immoOS',
      'actor-platform-support': 'immoOS Support',
      'actor-change-system': 'Automatic',
      'property-change-archive-reason': 'Archive reason: {reason}',
      'property-change-archive-note': 'Note: {note}',
      'property-change-line': '{field}: {from} → {to}',
      'property-section-change': {
        'section-title': {
          IDENTITY: 'Identity changed',
          ADDRESS: 'Address changed',
          BUILDING: 'Building changed',
          ENERGY: 'Energy changed',
          INVESTMENT_YIELD: 'Yield changed',
          PLOT_DETAILS: 'Plot changed',
          AMENITIES: 'Amenities changed',
          PRICING_RENTAL: 'Rental price changed',
          RENTAL_TERMS: 'Rental terms changed',
          ADDITIONAL_COSTS: 'Additional costs changed',
          DESCRIPTIONS: 'Texts changed',
          REFERENCE_SETTINGS: 'Reference changed',
        },
        'contact-link-title': 'Contact role changed',
        'item-field-value-line': '{item} – {field}: {from} → {to}',
        'text-changed-line': '{field} changed',
        'note-changed-line': '{item}: note changed',
        'deleted-contact': 'Deleted contact',
      },
      'property-status': PROPERTY_STATUS_LABELS,
      'archive-reason': ARCHIVE_REASON_LABELS,
      'connected-client-marker': 'Via a connected client',
      'connected-client-tooltip': 'This activity was written via a connected MCP client — on behalf of {name}.',
      'recorder-unknown': 'Unknown member',
      'contact-unknown': 'Unknown contact',
      edited: 'edited',
      'outcome-reached': 'Reached',
      'outcome-not-reached': 'Not reached',
      'outcome-voicemail': 'Voicemail',
      'outcome-callback-agreed': 'Callback agreed',
      // The email-only outcome vocabulary (IMM-913.3): "Reply received" /
      // "No reply" are REACHED / NOT_REACHED, relabelled for an email.
      'outcome-email-reached': 'Reply received',
      'outcome-email-not-reached': 'No reply',
      // Appointment outcomes (IMM-430.3) — a completed Termin shows its
      // Ergebnis in the history just as a communication does.
      'outcome-attended': 'Attended',
      'outcome-not-attended': 'Not attended',
      'outcome-rescheduled': 'Rescheduled',
      'edit-cta': 'Edit',
      'delete-cta': 'Delete',
      'empty-heading': 'No activity yet',
      'empty-body': 'Everything that happens here will appear — calls, notes, messages and more.',
      'empty-cta': 'Add activity',
      'load-more': 'Load more',
      'loading-more': 'Loading …',
      loading: 'Loading …',
      'load-error': 'Could not load activity.',
      'load-retry': 'Try again',
      'chip-kind-contact': 'Contact',
      'chip-kind-deal': 'Deal',
      'chip-kind-property': 'Property',
      'filter-label': 'Filter by type',
      'filter-note': 'Note',
      'filter-call': 'Call',
      'filter-sms': 'SMS',
      'filter-whatsapp': 'WhatsApp',
      'filter-fax': 'Fax',
      'filter-letter': 'Letter',
      'filter-email': 'Email',
      'filter-inquiry': 'Inquiry',
      'filter-valuation': 'Valuation',
      'filter-phase': 'Phase',
      'filter-brokerage': 'Brokerage agreement',
      'filter-deal': 'Deal',
      'filter-change': 'Change',
      'filter-type-label': 'Type',
      'filter-reset': 'Reset filter',
      'filter-empty': 'No entries match the selected filter.',
      'email-open-cta': 'Open conversation',
    },
    emailThread: {
      forbidden: 'You are not authorised to perform this action.',
      title: 'Email conversation',
      loading: 'Loading conversation …',
      'load-error': 'The conversation could not be loaded.',
      empty: 'This conversation has no messages.',
      close: 'Close',
      'back-to-activity': 'back to activity',
      'back-to-inbox': 'back to inbox',
      'message-count': '{count} messages',
      'with-counterpart': 'with {counterpart}',
      'direction-inbound': 'Inbound',
      'direction-outbound': 'Outbound',
      'unread-badge': 'Unread',
      from: 'From',
      to: 'To',
      'no-subject': '(No subject)',
      'body-loading': 'Loading message …',
      'body-error': 'The message content could not be loaded right now.',
      'body-error-retry': 'Try again',
      'body-error-disconnected': 'Your mailbox is no longer connected. Reconnect it to read this message.',
      'company-mailbox-expired':
        'The company mailbox {email} is no longer connected. Reconnect it in Settings under Company mailboxes.',
      'company-mailbox-expired-unknown':
        'The company mailbox is no longer connected. Reconnect it in Settings under Company mailboxes.',
      'body-error-gone': 'This message is no longer available at the provider.',
      'show-images': 'Show images',
      'images-blocked-hint': 'External images were blocked to protect against tracking.',
      attachments: 'Attachments',
      'attachments-present': 'This message has attachments.',
      'attachment-fallback-name': 'attachment',
      'show-quoted': 'Show quoted history',
      'hide-quoted': 'Hide quoted history',
      // Short provider brand badged on the expanded message (design 02).
      'provider-brand': { GOOGLE: 'Gmail', MICROSOFT: 'Outlook', IMAP: 'IMAP' },
      collapse: 'Collapse',
      expand: 'Expand',
    },
    mailboxSurface: {
      'connect-heading': 'Connect mailbox',
      'connect-body':
        'Connect your email mailbox to see your conversations with contacts directly in immoOS and send replies from your own address.',
      'connect-cta': 'Connect mailbox',
      'reconnect-heading': 'Reconnect mailbox',
      'reconnect-body': 'Your mailbox is no longer connected. Please reconnect it to receive and send new emails.',
      'reconnect-cta': 'Reconnect',
      'switchover-heading': 'Reconnect your mailbox once',
      'switchover-body':
        'We have improved the mailbox connection: immoOS now connects directly to Google. Until you reconnect, everything keeps working as usual.',
      syncing: 'Synchronisation in progress',
      'no-email-address': 'No email address is on file for this contact.',
      'load-error': 'Your mailbox connection status could not be loaded.',
      'load-retry': 'Try again',
    },
    emailAddress: {
      'compose-tooltip': 'Write an email',
      'connect-body': 'Connect your mailbox to send emails straight from immoOS from your own address.',
      cancel: 'Cancel',
    },
    mailboxComposer: {
      'insert-template': 'Insert template',
      'templates-empty': 'No e-mail templates yet. Owners and admins create them under Settings → Templates.',
      'templates-empty-area':
        'No e-mail templates in the {area} area yet. Owners and admins create them under Settings → Templates.',
      'templates-failed': 'The e-mail templates could not be loaded. Please try again.',
      'template-insert-failed': 'The e-mail template could not be inserted. Please try again.',
      'personal-link-text': 'To your personal page',
      'error-open-fields': 'Replace the marked fields first: {list}.',
      'template-values': {
        'greeting-mr': 'Hello Mr {lastName}',
        'greeting-mrs': 'Hello Ms {lastName}',
        'greeting-name': 'Hello {firstName} {lastName}',
        'salutation-mr': 'Mr',
        'salutation-mrs': 'Ms',
      },
      'new-email': 'New email',
      reply: 'Reply',
      'reply-all': 'Reply all',
      forward: 'Forward',
      'title-new': 'New email',
      'title-reply': 'Reply',
      'title-reply-all': 'Reply all',
      'title-forward': 'Forward',
      from: 'From',
      to: 'To',
      cc: 'Cc',
      bcc: 'Bcc',
      subject: 'Subject',
      'cc-bcc-toggle': 'Cc/Bcc',
      'recipient-placeholder': 'Name or email address',
      'link-title': 'Insert link',
      'link-text-label': 'Text',
      'link-url-label': 'URL',
      'link-insert': 'Insert',
      'link-cancel': 'Cancel',
      preview: 'Preview',
      'preview-subtitle': 'How the recipient sees the e-mail',
      'preview-back': 'Back to editing',
      send: 'Send',
      sending: 'Sending …',
      discard: 'Discard',
      minimise: 'Minimise',
      restore: 'Restore',
      close: 'Close',
      'attachment-add': 'Add attachment',
      'attachment-remove': 'Remove attachment',
      'attachments-total': 'Total size: {size}',
      'attachments-count': '{count, plural, one {# attachment} other {# attachments}}',
      'reply-placeholder': 'Write a reply …',
      'signature-label': 'Signature',
      're-prefix': 'Re: ',
      'fwd-prefix': 'Fwd: ',
      'forward-header': '---------- Forwarded message ----------',
      'forward-from': 'From: {sender}',
      'forward-subject': 'Subject: {subject}',
      'forward-date': 'Date: {date}',
      'forward-to': 'To: {recipients}',
      'sent-success': 'Email sent',
      'send-failure': 'Failed to send the email',
      'error-no-recipient': 'Please add at least one recipient.',
      'error-invalid-recipient': 'Please enter a valid email address: {value}',
      'error-empty-subject': 'Please enter a subject.',
      'error-subject-too-long': 'The subject can be at most {max} characters long.',
      'error-attachments-too-large': 'The attachments exceed the {limit} limit.',
      'grant-expired-block': 'Your mailbox is no longer connected. Please reconnect it to send emails.',
      'no-subject': '(No subject)',
    },
    calendar: {
      title: 'Calendar',
      'viewing-type': { ON_SITE: 'On-site viewing', VIRTUAL: 'Virtual viewing' },
      'view-switcher': 'View',
      'view-week': 'Week',
      'view-day': 'Day',
      'view-month': 'Month',
      'view-agenda': 'Agenda',
      today: 'Today',
      create: 'New appointment',
      'previous-period': 'Previous period',
      'next-period': 'Next period',
      'range-from': 'from {date}',
      month: { label: 'Month view', more: '+{count} more' },
      agenda: {
        label: 'Agenda view',
        today: 'Today · {day}',
        empty: 'No appointments in this period.',
        'all-day': 'All day',
      },
      sidebar: {
        label: 'Calendar navigation',
        shortcuts: 'T — Day · W — Week · M — Month · A — Agenda · H — Today · N — New appointment',
      },
      'quick-create': {
        label: 'New appointment',
        'title-placeholder': 'Add title',
        title: 'Title',
        save: 'Save',
        saving: 'Saving …',
        more: 'More options',
      },
      form: {
        label: 'Appointment form',
        title: 'New appointment',
        'edit-title': 'Edit appointment',
        type: 'Type',
        'title-field': 'Title',
        when: 'Date & time',
        date: 'Date',
        start: 'Start',
        end: 'End',
        duration: 'Duration',
        'duration-option': '{minutes} min',
        'all-day': 'All day',
        location: 'Location',
        'join-url': 'Dial-in link',
        notes: 'Notes',
        links: 'Links',
        link: {
          label: 'Link a record',
          placeholder: 'Search for a property, contact, deal or lead …',
          hint: '↵ link',
          empty: 'Nothing found.',
          loading: 'Searching …',
          failed: 'The search failed.',
          results: 'Results',
          chips: 'Linked',
          remove: 'Remove {name}',
          adopt: {
            title: 'Adopt from the deal',
            property: 'Property',
            contacts: 'Contacts',
            ambiguous: 'This deal has several contacts — choose who is meant.',
            confirm: 'Adopt',
            later: 'Later',
          },
        },
        attendees: 'Attendees',
        attendee: {
          label: 'Add attendees',
          placeholder: 'Name or email address',
          'no-email': 'This contact has no email address.',
          invalid: 'Please enter a valid email address.',
          colleagues: 'Team',
          contacts: 'Contacts',
          loading: 'Searching …',
          failed: 'The search failed.',
          chips: 'Attendees',
          remove: 'Remove {name}',
        },
        conference: {
          label: 'Video conference',
          add: 'Add Google Meet',
          name: 'Google Meet',
          'add-teams': 'Add Teams meeting',
          'name-teams': 'Microsoft Teams',
          'personal-account-hint':
            'Teams meetings are only available with a Microsoft 365 business account. You can paste a link from another service under "Dial-in link".',
          'removed-locally-hint':
            'The link is only removed in immoOS. The Teams meeting stays in Outlook — anyone invited can still join.',
          pending: 'Created when you save.',
          copy: 'Copy link',
          copied: 'Link copied.',
          remove: 'Remove',
        },
        save: 'Save',
        saving: 'Saving …',
        cancel: 'Cancel',
      },
      'mini-month': { label: 'Mini month' },
      'account-connected': 'Connected · {email}',
      loading: 'Loading your calendar …',
      'load-error': 'Your calendar could not be loaded.',
      'load-retry': 'Try again',
      empty: {
        title: 'No calendar connected',
        description: 'Connect your calendar to see appointments and keep them in sync with your team.',
        'description-imap':
          'Your mailbox is connected over IMAP. IMAP carries only e-mail, no appointments — so immoOS shows no calendar here.',
        connect: 'Connect calendar',
        'connect-error': 'The connection could not be started. Please try again.',
      },
      reconnect: {
        title: 'Calendar sync interrupted — please reconnect your calendar.',
        action: 'Reconnect',
      },
      grid: {
        'week-label': 'Week view',
        'day-label': 'Day view',
        'all-day': 'All day',
        'all-day-more': '+{count} more',
        'all-day-less': 'Show less',
        move: 'Move appointment',
        resize: 'Change duration',
      },
      appointment: {
        private: 'Private appointment',
        untitled: '(Untitled)',
        cancelled: 'Cancelled',
        conflict: 'Conflict',
        conference: 'Video conference',
      },
      'conference-warning': 'The Meet link could not be created. The appointment is saved — you can try again.',
      'conference-warning-teams': 'The Teams link could not be created. The appointment is saved — you can try again.',
      types: {
        VIEWING: 'Viewing',
        ON_SITE: 'On-site appointment',
        OWNER_MEETING: 'Owner meeting',
        VALUATION: 'Valuation appointment',
        NOTARY: 'Notary appointment',
        HANDOVER: 'Handover',
        PHONE: 'Phone call',
        ONLINE_MEETING: 'Online meeting',
        OTHER: 'Other appointment',
      },
      legend: { label: 'Appointment types' },
      'link-kinds': { PROPERTY: 'Property', CONTACT: 'Contact', DEAL: 'Deal', LEAD: 'Lead', PROSPECT: 'Prospect' },
      'link-unnamed': '(Unnamed)',
      'create-from-record': { action: 'Create appointment', 'viewing-title': 'Viewing {address}' },
      upcoming: {
        title: 'Upcoming appointments',
        'empty-title': 'No upcoming appointments.',
        'empty-body': {
          PROPERTY: 'Once an appointment is linked to this property, it will appear here.',
          CONTACT: 'Once an appointment is linked to this contact, it will appear here.',
          DEAL: 'Once an appointment is linked to this deal, it will appear here.',
          LEAD: 'Once an appointment is linked to this lead, it will appear here.',
          PROSPECT: 'Once an appointment is linked to this prospect, it will appear here.',
        },
        'unknown-owner': 'Unknown member',
        'load-error': 'The upcoming appointments could not be loaded.',
      },
      'cancel-dialog': {
        title: 'Cancel appointment?',
        body: 'The attendees will be notified through your calendar. This action cannot be undone.',
        confirm: 'Cancel appointment',
        dismiss: 'Back',
        pending: 'Cancelling …',
      },
      'forbidden-error': 'You are not permitted to do this.',
      'change-error': { label: 'Change to the appointment' },
      panel: {
        label: 'Appointment details',
        close: 'Close',
        location: 'Location',
        notes: 'Notes',
        links: 'Links',
        attendees: 'Attendees',
        conference: 'Video conference',
        join: 'Join',
        copy: 'Copy link',
        copied: 'Link copied.',
        'join-url': 'Dial-in link',
        'viewing-assessment-heading': 'Attendance',
        'viewing-assessment-heading-named': 'Attendance · {label}',
        'conflict-notice': 'Changed in both calendars — the last saved version applies.',
        edit: 'Edit',
        'cancel-appointment': 'Cancel appointment',
        'all-day': 'All day',
        'duration-hours': '{hours} h',
        'duration-minutes': '{minutes} min',
        'duration-hours-minutes': '{hours} h {minutes} min',
        response: {
          NEEDS_ACTION: 'Pending',
          ACCEPTED: 'Accepted',
          DECLINED: 'Declined',
          TENTATIVE: 'Tentative',
        },
      },
    },
    mailboxHome: {
      title: 'Email',
      posteingang: 'Inbox',
      postausgang: 'Sent',
      'subtitle-inbox': 'All received emails from your connected mailboxes — newest first.',
      'subtitle-outbox': 'All sent emails from your connected mailboxes — newest first.',
      'search-inbox': 'Search your inbox …',
      'search-outbox': 'Search sent mail …',
      'account-all': 'All accounts',
      'account-filter-label': 'Account',
      'company-badge': 'Company',
      'unread-only': 'Unread only',
      'filter-unplaced': 'Unassigned only',
      'unplaced-count': '{count} unassigned',
      'placement-needs-review': 'Check assignment',
      // Row order for the Posteingang (a sort, not the unread filter).
      'view-default': 'Default',
      'view-unread-first': 'Unread first',
      'load-more': 'Load more',
      loading: 'Loading emails …',
      'load-error': 'Your emails could not be loaded.',
      'load-retry': 'Try again',
      'empty-inbox': 'No emails in your inbox yet',
      'empty-outbox': 'No sent emails yet',
      'no-match-title': 'No matches',
      'no-match-description': 'Try adjusting your search or filters to find emails.',
      filters: {
        reset: 'Reset filters',
      },
      'no-subject': '(No subject)',
      'no-counterpart': '(Unknown)',
      'unread-badge': 'Unread',
      'outgoing-badge': 'Outgoing',
      'has-attachments': 'Attachment',
      'contact-unassigned': 'Not assigned',
      'date-this-year': '{day} {month}',
      'date-older': '{day} {month} {year}',
    },
    mailboxConnectDialog: {
      title: 'Add account',
      intro: 'Choose your mailbox provider.',
      'unsupported-providers':
        'Only a Gmail mailbox can be connected at the moment. Microsoft 365, Exchange and IMAP are not supported yet.',
      'unsupported-providers-with-microsoft':
        'Gmail and Microsoft mailboxes can be connected. Exchange on your own servers and IMAP are not supported yet.',
      'provider-google-label': 'Google',
      'provider-google-desc': 'Gmail or Google Workspace',
      'provider-microsoft-label': 'Microsoft',
      'provider-microsoft-desc': 'Outlook or Microsoft 365',
      'provider-imap-label': 'Other provider (IMAP)',
      'provider-imap-desc': 'GMX, WEB.DE, T-Online, IONOS, etc.',
      'hint-oauth':
        'Secure sign-in via the provider — immoOS never stores passwords. Connecting and disconnecting run through the provider’s secured login.',
      'hint-imap':
        'Other provider (IMAP): sign in with the email address and password (or app password) of your provider.',
      cancel: 'Cancel',
      continue: 'Continue to sign-in',
      'continue-imap': 'Continue',
      'calendar-no-imap': 'IMAP mailboxes have no calendar. You can connect them under Settings → Mailbox.',
      error: 'The connection could not be started — please try again.',
    },
    // Contact assignment for emails (IMM-233.4).
    mailboxAssign: {
      action: 'Assign contact',
      title: 'Assign',
      'title-change': 'Change assignment',
      intro:
        'Choose what this conversation belongs to. Every message of the conversation is recorded there as an activity.',
      'change-hint': 'Every message of this conversation moves — later replies too.',
      'type-contact': 'Contact',
      'type-lead': 'Lead',
      'type-deal': 'Deal',
      'type-property': 'Property',
      'search-placeholder': 'Search records …',
      'suggestion-label': 'Suggestion',
      'suggestion-hint': '{name} has several deals or properties. Choose what this conversation belongs to.',
      'current-tag': 'Current',
      'nothing-selected': 'Please select a record.',
      'success-change': 'Assignment changed.',
      'needs-review': 'Check assignment',
      'action-change': 'Change assignment',
      'search-loading': 'Loading contacts …',
      'search-no-results': 'No contacts found',
      'apply-all-label': 'Assign all emails from this sender',
      confirm: 'Assign',
      'confirm-loading': 'Assigning …',
      cancel: 'Cancel',
      'no-contact-selected': 'Please select a contact.',
      error: 'The assignment failed — please try again.',
      'success-toast': 'Email assigned.',
      'remove-action': 'Remove assignment',
      'remove-loading': 'Removing …',
      'remove-error': 'The assignment could not be removed — please try again.',
      'remove-success-toast': 'Assignment removed.',
      'assigned-label': 'Assigned',
    },
    'assign-membership': {
      'role-label': 'Role',
      'cancel-cta': 'Cancel',
      'save-cta': 'Save',
      'save-loading': 'Saving…',
      'save-error': 'Could not save — please try again.',
    },
    members: {
      title: 'Members',
      subtitle: 'Manage who has access to this workspace and their organisation roles.',
      'invite-cta': 'Invite member',
      'invite-success-toast': 'Member "{email}" invited.',
      'invite-success-dismiss': 'Dismiss',
      'list-load-error': 'Could not load members.',
      'list-load-retry': 'Try again',
      'empty-title': 'No members yet',
      'empty-description': 'Invite your first teammate to get started.',
      'no-match-title': 'No matches',
      'no-match-description': 'Adjust the filter to find members.',
      columns: {
        name: 'Name',
        email: 'Email',
        status: 'Status',
        'last-active': 'Last active',
        'joined-at': 'Joined',
      },
      status: {
        active: 'Active',
        deactivated: 'Deactivated',
      },
      'status-filter': {
        active: 'Active',
        deactivated: 'Deactivated',
        all: 'All',
      },
      'role-change-cta': 'Change role',
      'last-active-never': '—',
      'over-limit-warning':
        'This workspace has more than 50 active members. Larger teams can be harder to manage at a glance.',
      filter: {
        'status-label': 'Status',
        'status-active': 'Active',
        'status-deactivated': 'Deactivated',
        'status-all': 'All members',
        reset: 'Reset',
      },
      sort: {
        label: 'Sort',
        name: 'Name',
        joined: 'Join date',
      },
      'role-edit-label': 'Edit roles',
      'role-modal-heading': 'Organisation roles',
      'role-modal-org-label': 'Location',
      'role-save-cta': 'Save',
      'role-save-loading': 'Saving…',
      'role-save-error': 'Could not save roles — please try again.',
      'role-cancel-cta': 'Cancel',
      'role-self-edit-notice': 'You cannot change your own organisation role.',
      affiliation: {
        label: 'Location affiliation',
        helper: 'Member belongs to one or more locations. Determines record visibility.',
        'primary-label': 'Primary location',
        'primary-helper':
          'Default dashboard view and prefilled when creating new records. Must be part of the location affiliation.',
        'locked-badge': 'Role required',
        'locked-hint': 'Locations without a role cannot be selected here. Grant the role first via “Assign locations”.',
        errors: {
          required: 'At least one location must be selected.',
          'primary-not-in-selection': 'The primary location must be part of the location affiliation.',
          'requires-role':
            'No role has been granted for {locations}. A member can only be affiliated with locations where they hold a role — grant the role first via “Assign locations”.',
          'requires-role-unknown':
            'No role has been granted for at least one selected location. A member can only be affiliated with locations where they hold a role — grant the role first via “Assign locations”.',
        },
      },
      invite: {
        title: 'Invite member',
        'email-label': 'Email',
        'email-placeholder': 'ada@example.com',
        'first-name-label': 'First name',
        'first-name-placeholder': 'Ada',
        'last-name-label': 'Last name',
        'last-name-placeholder': 'Lovelace',
        'organisation-label': 'Organisation',
        'no-organisations': 'No organisations are available to invite into. Reload the page or try again later.',
        'organisation-role-label': 'Organisation role',
        'organisation-role': {
          'organisation-owner': 'Owner',
          agent: 'Agent',
          assistant: 'Assistant',
          viewer: 'Viewer',
        },
        'submit-cta': 'Invite',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'generic-error': 'Invite failed — please try again.',
        'forbidden-error': 'You are not allowed to invite members at this location.',
        'unauthorized-error': 'Your session has expired. Please sign in again and retry.',
        errors: {
          email: {
            required: 'Email is required.',
            invalid: 'Please enter a valid email address.',
            duplicate: 'This person is already a member of the workspace.',
            'duplicate-pending':
              'There is already a pending invitation for this email address. Revoke it below under "Pending invitations" or resend it.',
          },
          'first-name': {
            required: 'First name is required.',
            'too-long': 'First name is too long.',
            invalid: 'First name is invalid.',
          },
          'last-name': {
            required: 'Last name is required.',
            'too-long': 'Last name is too long.',
            invalid: 'Last name is invalid.',
          },
        },
      },
      invitations: {
        title: 'Pending invitations',
        'list-load-error': 'Could not load invitations.',
        'action-error': 'Action failed — please try again.',
        'role-pending': 'Role to follow',
        'resend-cta': 'Resend',
        'revoke-cta': 'Revoke',
        'resend-success-toast': 'Invitation to "{email}" resent.',
        'revoke-success-toast': 'Invitation to "{email}" revoked.',
        columns: {
          email: 'Email',
          role: 'Role',
          'issued-by': 'Invited by',
          'expires-at': 'Expires',
          status: 'Status',
        },
        status: {
          pending: 'Pending',
        },
        confirm: {
          cancel: 'Cancel',
          submitting: 'Saving…',
          resend: {
            title: 'Resend invitation?',
            intro: 'The previous invitation to "{email}" is revoked and a fresh one with a new expiry is sent.',
            submit: 'Resend',
          },
          revoke: {
            title: 'Revoke invitation?',
            intro: 'The link for "{email}" will stop working.',
            submit: 'Revoke',
          },
        },
      },
      'profile-edit': {
        heading: 'Profile of {name}',
        subtitle: "You are editing this member's agent profile for the Exposé and agent profile.",
        intro: "You are editing another member's profile as an Owner/Admin. Display name and email stay unchanged.",
        'save-cta': 'Save profile',
      },
      detail: {
        tabs: {
          overview: 'Overview',
          organisations: 'Organisations',
          activity: 'Activity',
        },
        'profile-heading': 'Profile',
        'status-heading': 'Status',
        'status-label': 'Status',
        'joined-label': 'Joined',
        'last-active-label': 'Last active',
        'organisations-empty': 'This member is not assigned to any organisation.',
        'edit-profile-intro':
          "You are editing another member's profile as an Owner/Admin. Display name and email stay unchanged.",
        'email-label': 'Email',
        'first-name-label': 'First name',
        'last-name-label': 'Last name',
        'workspace-roles-label': 'Workspace roles',
        'organisation-roles-label': 'Organisation roles',
        'edit-cta': 'Edit',
        'edit-profile-cta': 'Edit profile',
        'actions-trigger-label': 'Actions',
        'assign-locations-cta': 'Assign locations',
        'assign-locations-title': 'Assign locations',
        'assign-locations-heading': 'Select the locations this member belongs to.',
        'assign-locations-empty': 'No locations available.',
        'edit-title': 'Edit member',
        'back-cta': 'Cancel',
        'save-cta': 'Save',
        'save-loading': 'Saving…',
        'save-success': 'Profile updated.',
        'save-error': 'Save failed — please try again.',
        'deactivate-cta': 'Deactivate',
        'reactivate-cta': 'Reactivate',
        'deactivate-description': 'Deactivated members lose access to the workspace.',
        'deactivate-confirm-title': 'Deactivate {name}?',
        'deactivate-cancel-cta': 'Cancel',
        'reactivate-description': 'Reactivated members can sign in again.',
        'deactivate-self-error': 'You cannot deactivate yourself.',
        'status-error': 'Action failed — please try again.',
        'status-loading': 'Saving…',
        errors: {
          email: {
            required: 'Email is required.',
            invalid: 'Please enter a valid email address.',
          },
          'first-name': {
            required: 'First name is required.',
            'too-long': 'First name is too long.',
            invalid: 'First name is invalid.',
          },
          'last-name': {
            required: 'Last name is required.',
            'too-long': 'Last name is too long.',
            invalid: 'Last name is invalid.',
          },
        },
      },
    },
    organisations: {
      title: 'Locations',
      'page-description':
        'Locations split your workspace into branches or sub-offices. Create additional locations here.',
      'create-cta': 'New location',
      'success-toast': 'Location "{name}" created.',
      'success-dismiss': 'Dismiss',
      'list-load-error': 'Could not load locations.',
      'empty-title': 'No locations yet',
      'empty-description': 'Create your first location to get started.',
      'no-match-title': 'No matches',
      'no-match-description': 'Adjust the filter to find locations.',
      columns: {
        name: 'Name',
        address: 'Address',
        type: 'Type',
        status: 'Status',
        members: 'Members',
        records: 'Records',
        'created-at': 'Created',
      },
      type: {
        main: 'Main',
        subsidiary: 'Sub-location',
      },
      status: {
        ACTIVE: 'active',
        INACTIVE: 'inactive',
      },
      'single-intro': {
        heading: 'Your workspace currently has only a main location',
        body: 'Locations split a workspace into branches or sub-offices — e.g. a main location in Frankfurt and sub-locations in Hamburg and Munich. As long as only the main location exists, every member works workspace-wide.',
        cta: 'Create sub-location',
      },
      'setup-badge': 'In setup',
      filter: {
        all: 'All',
        'in-setup': 'In setup',
        completed: 'Set up',
        reset: 'Reset',
      },
      stage: {
        invited: 'Invited',
        in_progress: 'In progress',
        submitted: 'Submitted',
        in_review: 'In review',
        changes_requested: 'Changes requested',
        approved: 'Approved',
        rejected: 'Rejected',
      },
      reparent: {
        heading: 'Move location',
        body: 'You are moving location “{name}” under “{newParent}”. This change affects {memberCount} members, whose visibility adjusts accordingly. Sub-locations ({descendantCount}) and all records assigned to them ({recordCount}) move along.',
        confirm: 'Confirm move',
        cancel: 'Cancel',
      },
      review: {
        title: 'Review setup · {name}',
        'operator-model-pill': 'Partner company',
        meta: {
          'location-manager': 'Location manager',
          'created-at': 'Created on',
          'link-valid-until': 'Link valid until',
        },
        legal: {
          heading: 'Company details & legal information',
          'company-name': 'Company name',
          'legal-form': 'Legal form',
          'managing-directors': 'Managing directors',
          'commercial-register': 'Commercial register',
          'vat-id': 'VAT ID',
          'trade-permit-authority': 'Trade-permit authority (§ 34c GewO)',
          'supervisory-authority': 'Supervisory authority',
          'professional-indemnity-insurance': 'Professional indemnity insurance',
          'content-responsible-person': 'Content-responsible person (§ 18 (2) MStV)',
          'external-website': 'External website',
        },
        contact: {
          heading: 'Contact',
          address: 'Address',
          email: 'Email',
          phone: 'Phone',
        },
        consents: {
          heading: 'Consents',
          'granted-by': 'Granted by {by} · {at}',
          declined: 'Not granted',
          empty: 'No consents recorded.',
        },
        'empty-value': '—',
        'no-profile': 'No data has been submitted yet.',
        panel: {
          heading: 'Review',
          'approve-cta': 'Approve',
          'request-changes-cta': 'Request changes',
          'reject-cta': 'Reject',
          'info-heading': 'What happens on approval',
          'info-data': 'Data → location',
          'info-manager': 'MD → location manager',
          'info-badge': '"In setup" badge clears',
        },
        approve: {
          title: 'Approve setup?',
          'info-heading': 'What happens on approval',
          'info-data': 'The reviewed data is applied to the location.',
          'info-manager': 'The location manager is invited as a location admin.',
          'info-badge': 'The "In setup" status clears.',
          'cancel-cta': 'Cancel',
          'confirm-cta': 'Approve & apply',
          'loading-cta': 'Applying…',
        },
        'request-changes': {
          title: 'Request changes',
          'notes-label': 'Review notes',
          'notes-placeholder': 'What does the partner need to adjust?',
          'notes-required': 'Please describe what needs to change.',
          info: "The partner's magic link re-opens onboarding — with the notes visible. No new link needed.",
          'cancel-cta': 'Cancel',
          'confirm-cta': 'Request changes',
          'loading-cta': 'Sending…',
        },
        reject: {
          title: 'Reject',
          description: 'The submission is rejected for good. No data is applied.',
          'cancel-cta': 'Cancel',
          'confirm-cta': 'Reject',
          'loading-cta': 'Rejecting…',
        },
        'load-error': 'Could not load the review data.',
        'decision-error': 'The action could not be completed. Please try again.',
        'approve-success': 'Setup approved.',
        'request-changes-success': 'Changes requested.',
        'reject-success': 'Submission rejected.',
      },
      einzugsgebiet: {
        heading: 'Coverage area (postal codes)',
        count: '{count} postal codes assigned',
        empty: 'No coverage area defined yet. Add postal codes to set this location’s area.',
        'add-cta': 'Add postal codes',
        'remove-label': 'Remove postal code {plz}',
        'removed-toast': 'Postal code {plz} removed.',
        'load-error': 'The coverage area could not be loaded.',
        dialog: {
          title: 'Add postal codes',
          'input-label': 'Postal codes',
          'input-placeholder': '20249, 20251, 20253 …',
          hint: 'Separate multiple postal codes with a comma, semicolon, or line break.',
          'upload-cta': 'Upload CSV/TXT file',
          'parse-summary': '{recognised} postal codes recognised, {skipped} invalid entries skipped.',
          'file-empty': 'The file contains no valid postal codes.',
          'wrong-file-type': 'Only CSV or TXT files are supported.',
          'staged-count': '{count} postal codes selected',
          'remove-staged-label': 'Remove postal code {plz} from the selection',
          'submit-cta': 'Assign postal codes',
          'cancel-cta': 'Cancel',
          'close-cta': 'Close',
          'invalid-token': 'Postal code must be 5 digits.',
          'empty-error': 'Enter at least one postal code.',
          result: {
            assigned: '{count} postal codes assigned.',
            'already-present': '{count} postal codes were already assigned to this location.',
            'conflicts-heading': 'Already assigned to other locations',
            'conflict-line': '“{plz}” already belongs to “{standort}”.',
          },
        },
      },
      detail: {
        tabs: {
          overview: 'Overview',
          members: 'Members',
          einzugsgebiet: 'Coverage area',
        },
        'status-label': 'Status',
        'setup-label': 'Setup',
        'onboarding-label': 'Onboarding',
        'members-empty': 'No members are assigned to this location.',
        'members-loading': 'Loading members…',
        'members-load-error': 'Members could not be loaded.',
        'members-load-retry': 'Try again',
        'assign-members-cta': 'Assign members',
        'assign-members-loading': 'Loading…',
        'assign-members-title': 'Assign members',
        'assign-members-heading': 'Select the members who belong to this location.',
        'assign-members-empty': 'No members available.',
        'role-edit-label': 'Edit roles',
        'role-modal-heading': 'Organisation role',
        'role-save-cta': 'Save',
        'role-save-loading': 'Saving…',
        'role-save-error': 'Could not save role — please try again.',
        'lead-recipient-label': 'Receives new leads',
        'lead-recipient-description':
          'Incoming leads for this location — for example through the partner interface — are assigned to this member automatically.',
        'lead-recipient-owner-hint':
          'Owners of this location are always a backup on every lead. This setting cannot be changed for them.',
        'lead-recipient-save-error': 'The change could not be saved.',
        'lead-recipients-heading': 'Recipients of new leads',
        'lead-recipients-empty': 'Nobody selected yet — incoming leads go to the owners of this location.',
        'role-cancel-cta': 'Cancel',
        'role-self-edit-notice': 'You cannot change your own organisation role.',
        'details-heading': 'Details',
        'address-heading': 'Address',
        'profile-heading': 'Profile',
        'edit-cta': 'Edit',
        'edit-title': 'Edit location',
        'back-cta': 'Cancel',
        'id-label': 'ID',
        'type-label': 'Type',
        'object-number-prefix-label': 'Location prefix',
        'object-number-prefix-hint': 'Properties of this location are numbered from {firstNumber}.',
        'created-at-label': 'Created on',
        'name-label': 'Name',
        'parent-label': 'Parent location',
        'parent-helper': 'Type to search. Defaults to the main location.',
        'parent-placeholder': 'Search a location…',
        'parent-clear': 'Clear selection',
        'parent-no-results': 'No matching locations.',
        'email-label': 'Email',
        'phone-label': 'Phone',
        'description-label': 'Description',
        'street-label': 'Street',
        'house-number-label': 'House number',
        'postal-code-label': 'Postal code',
        'city-label': 'City',
        'country-label': 'Country',
        'coordinates-label': 'Coordinates',
        'legal-heading': 'Company details / legal notice',
        'contact-heading': 'Contact',
        'internal-website-label': 'Internal website',
        'own-branch-note':
          'This location operates under the workspace’s main company; it has no separate legal identity / legal notice.',
        'address-search-label': 'Search address',
        'address-search-placeholder': 'Street, house number, city …',
        'save-cta': 'Save',
        'save-loading': 'Saving…',
        'save-success': 'Location updated.',
        'save-error': 'Save failed — please try again.',
        errors: {
          name: {
            required: 'Location name is required.',
            'too-long': 'Location name must be at most 80 characters.',
            invalid: 'Location name is invalid.',
            duplicate: 'A location with this name already exists in this workspace.',
          },
          email: {
            invalid: 'Please enter a valid email address.',
          },
          'postal-code': {
            invalid: 'Postal code must be 5 digits.',
          },
          parent: {
            required: 'Please choose a parent location.',
            cycle: 'A location cannot be assigned to itself or to one of its sub-locations.',
            main: 'The main location cannot be placed under another location.',
          },
        },
      },
      create: {
        title: 'New location',
        'operating-model-heading': 'Operating model',
        'operating-model-helper': 'Sets who operates the location — not its position in the location hierarchy.',
        'operating-model': {
          OWN_BRANCH: 'Own location',
          PARTNER_COMPANY: 'Partner company',
        },
        'name-label': 'Location name',
        'name-placeholder': 'Hamburg-Eppendorf',
        'parent-label': 'Parent location',
        'parent-helper': 'Every new location is created as a sub-location. Type to search.',
        'parent-context': 'Created under {parent}.',
        'parent-context-default': 'Created under the main location.',
        'parent-placeholder': 'Search a location…',
        'parent-clear': 'Clear selection',
        'parent-no-results': 'No matching locations.',
        'email-label': 'Email',
        'phone-label': 'Phone',
        'description-label': 'Description',
        'location-manager-heading': 'Location lead',
        'location-manager-first-name-label': 'First name',
        'location-manager-first-name-placeholder': 'e.g. Erika',
        'location-manager-last-name-label': 'Last name',
        'location-manager-last-name-placeholder': 'e.g. Mustermann',
        'location-manager-email-label': 'Email',
        'location-manager-email-placeholder': 'name@partner.de',
        'internal-website-label': 'Internal website',
        'internal-website-placeholder': 'https://…',
        'submit-partner-cta': 'Send onboarding to partner',
        'submit-partner-loading': 'Sending…',
        'address-heading': 'Address',
        'address-search-label': 'Search address',
        'address-search-placeholder': 'Street, house number, city …',
        'street-label': 'Street',
        'street-placeholder': 'Eppendorfer Landstraße',
        'house-number-label': 'House number',
        'house-number-placeholder': '47a',
        'postal-code-label': 'Postal code',
        'postal-code-placeholder': '20251',
        'city-label': 'City',
        'city-placeholder': 'Hamburg',
        'country-label': 'Country',
        'country-placeholder': 'Deutschland',
        'submit-cta': 'Create location',
        'submit-loading': 'Creating…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-a-member-error': 'You are not a member of this workspace.',
        'generic-error': 'Could not create location. Please try again.',
        errors: {
          name: {
            required: 'Location name is required.',
            'too-long': 'Location name must be at most 80 characters.',
            duplicate: 'A location with this name already exists in this workspace.',
          },
          email: {
            invalid: 'Please enter a valid email address.',
          },
          parent: {
            required: 'Please choose a parent location.',
            cycle: 'A location cannot be assigned to itself or to one of its sub-locations.',
            main: 'The main location cannot be placed under another location.',
          },
          street: {
            required: 'Please enter a street.',
            'too-long': 'Street must be at most 100 characters.',
          },
          'house-number': {
            required: 'Please enter a house number.',
            'too-long': 'House number must be at most 20 characters.',
          },
          'postal-code': {
            required: 'Please enter a postal code.',
            invalid: 'Postal code must be 5 digits.',
          },
          city: {
            required: 'Please enter a city.',
            'too-long': 'City must be at most 80 characters.',
          },
          country: {
            required: 'Please enter a country.',
            'too-long': 'Country must be at most 80 characters.',
          },
          'location-manager-first-name': {
            required: 'Please enter the location lead first name.',
            'too-long': 'First name must be at most 80 characters.',
          },
          'location-manager-last-name': {
            required: 'Please enter the location lead last name.',
            'too-long': 'Last name must be at most 80 characters.',
          },
          'location-manager-email': {
            required: 'Please enter the location lead email.',
            invalid: 'Please enter a valid email address.',
          },
          'internal-website': {
            invalid: 'Please enter a valid URL (https://…).',
          },
        },
      },
    },
    properties: {
      title: 'Properties',
      subtitle: 'All properties in your workspace.',
      'create-cta': 'New property',
      'import-is24-cta': 'Import ImmoScout24 listings',
      'success-toast': 'Property "{title}" created.',
      'success-dismiss': 'Dismiss',
      'empty-title': 'No properties yet',
      'empty-description': "This is where your workspace's properties will appear.",
      'list-load-error': 'Could not load properties.',
      'list-load-retry': 'Try again',
      'show-archived': 'Show archived',
      'status-view': {
        active: 'Portfolio',
        'in-acquisition': 'In acquisition',
      },
      'no-match-title': 'No matches',
      'no-match-description': 'Try adjusting your search or filters to find properties.',
      // IMM-685 — see the German twin: the rows exist, this view hides them.
      'hidden-title': 'No properties in this view',
      'hidden-description': '{count} properties are under "{label}".',
      'hidden-description-one': 'One property is under "{label}".',
      'hidden-cta': 'Show "{label}"',
      'hidden-culprit': {
        acquisition: 'In acquisition',
        archived: 'Archive',
      },
      unassigned: 'Unassigned',
      filters: {
        'marketing-label': 'Marketing type',
        'status-view-label': 'View',
        'search-placeholder': 'Search title, address or object no.…',
        'search-label': 'Search properties',
        'status-all': 'All statuses',
        'type-all': 'All property types',
        'reason-all': 'All reasons',
        'reason-label': 'Archive reason',
        reset: 'Reset',
      },
      sort: {
        label: 'Sort',
        updated: 'Last updated',
        newest: 'Newest first',
        oldest: 'Oldest first',
        'price-desc': 'Price: high → low',
        'price-asc': 'Price: low → high',
        'area-desc': 'Area: large → small',
        'area-asc': 'Area: small → large',
      },
      pagination: {
        range: '{from}–{to} of {total}',
        'per-page': 'Per page:',
        prev: 'Previous',
        next: 'Next',
      },
      columns: {
        'object-number': 'Object no.',
        title: 'Title',
        address: 'Address',
        type: 'Property type',
        object: 'Property',
        status: 'Status',
        price: 'Price',
        'price-per-sqm': 'Rent/sqm',
        area: 'Primary area',
        rooms: 'Rooms',
        'available-from': 'Available from',
        'min-divisible-area': 'Divisible from',
        assignee: 'Assigned',
        'broker-team': 'Broker team',
        'match-score': 'Match score',
        'last-activity': 'Last activity',
        'last-modified': 'Last modified',
        'created-at': 'Created on',
      },
      'price-on-request': 'Price on request',
      'ancillary-costs-suffix': '+ {amount} charges',
      'price-per-sqm-rent-suffix': '/m²',
      totals: {
        'shown-of-total': '{shown} of {total}',
        'price-sum': 'Σ {amount}',
        'area-avg': 'ø {amount}',
        'broker-count': '{count} brokers',
        'score-avg': 'ø {amount}',
        empty: '—',
      },
      'last-activity-fallback': 'no activity yet',
      'marketing-type': MARKETING_TYPE_LABELS,
      status: PROPERTY_STATUS_LABELS,
      'archive-reason': ARCHIVE_REASON_LABELS,
      'status-archived-with-reason': 'Archived · {reason}',
      reference: {
        badge: 'Reference',
      },
      reservation: {
        badge: {
          RESERVED: 'Reserved',
          SOLD: 'Sold',
        },
        tooltip: {
          'reserved-sales-pipeline': 'Reserved through the sales pipeline',
          'reserved-by-hand': 'Reserved by hand',
          'reserved-both': 'Reserved by hand and through the sales pipeline',
          'sold-sales-pipeline': 'Sold through the sales pipeline',
        },
      },
      // IMM-788.1 — one shared VAT namespace: the treatment labels, the "plus
      // VAT" suffix and the net-price info note appear in three sections and
      // three dialogs (D10). One place to change the copy.
      vat: {
        treatment: VAT_TREATMENT_LABELS,
        'net-plus-vat-suffix': 'plus VAT',
        'net-plus-vat-note': 'All prices are stated net, plus VAT.',
        'treatment-placeholder': 'Please select',
        'per-sqm-unit': '€/m²',
      },
      detail: {
        'id-prefix': 'ID',
        capture: {
          start: 'Start property capture',
          continue: 'Continue capture',
          finish: 'Complete capture',
          'completed-title': 'Site capture of {date} ({viewingKind})',
          'open-line': {
            both: '{fields, plural, one {# field} other {# fields}} and {photos, plural, one {# photo} other {# photos}} open',
            fields: '{fields, plural, one {# field} other {# fields}} open',
            photos: '{photos, plural, one {# photo} other {# photos}} open',
          },
          confirm: {
            text: {
              both: '{fields, plural, one {# required field} other {# required fields}} and {photos, plural, one {# required photo} other {# required photos}} are still missing. Complete anyway?',
              fields:
                '{fields, plural, one {# required field is} other {# required fields are}} still missing. Complete anyway?',
              photos:
                '{photos, plural, one {# required photo is} other {# required photos are}} still missing. Complete anyway?',
            },
            complete: 'Complete',
            continue: 'Keep capturing',
          },
          title: 'Property capture',
          offline: 'No connection. Entries will not be saved from now on.',
          'change-viewing-kind-label': 'Change the kind of viewing',
          fields: {
            heading: 'Still missing for valuation 1b',
            known: 'Already known · {count}',
            need: { REQUIRED: 'Required', RECOMMENDED: 'Recommended' },
            'not-determinable': 'not determinable',
            'not-applicable': 'not applicable',
            'add-unit': 'Add another unit',
            'add-occurrence': 'Add {occurrence}',
            'select-placeholder': 'Please choose',
            boolean: { true: 'Yes', false: 'No' },
            groups: FACT_KEY_GROUP_LABELS,
          },
          photos: {
            heading: 'Photos',
            required: 'Required',
            conditional: 'Recommended',
            'taken-of-minimum': '{taken} of {minimum}',
            'take-label': 'Take photo',
            'load-error': 'The photos could not be loaded.',
            errors: { type: 'Only JPG, PNG and WebP are allowed' },
          },
          'viewing-kind': {
            heading: 'How are you viewing the property?',
            options: {
              INTERIOR_AND_EXTERIOR: 'Interior and exterior viewing',
              EXTERIOR_ONLY: 'Exterior viewing only',
              PARTIAL: 'Partial viewing',
              VIDEO: 'Video viewing',
            },
          },
        },
        'immoos-iq': {
          load: 'Load data from immoOS IQ',
          loading: 'Loading data …',
          'last-loaded': 'Last loaded on {date}',
          'address-incomplete': 'The property needs a complete address to load the data.',
          'no-data': 'immoOS IQ has no data for this address.',
          'status-failed': 'The status of the last load could not be read.',
          'status-retry': 'Try again',
        },
        'header-price-per-sqm-suffix': ' · {amount} / m²',
        'archived-by-deal-loss': {
          text: 'Archived because the deal was lost on {date}.',
          'open-deal': 'Open deal',
        },
        'header-actions': 'Actions',
        'edit-address-action': 'Edit address',
        reference: {
          yes: 'Yes',
          no: 'No',
          title: 'Reference',
          'toggle-label': 'Mark as a reference property',
          'toggle-helper':
            'Marks this successful sale as a reference — for example for presentations and acquisition materials.',
          'homepage-label': 'Publish on homepage',
          'homepage-helper':
            'Allows this property to be shown as a reference on your homepage. Publication happens via a later integration.',
          'homepage-disabled-hint': 'Mark it as a reference property first.',
          'save-error': 'The change could not be saved.',
        },
        tabs: {
          overview: 'Overview',
          prospects: 'Buyer prospects',
          media: 'Media',
          marketing: 'Marketing',
          documents: 'Documents',
          activity: 'Activity',
          property: 'Property',
          contacts: 'Links',
          expose: 'Exposé',
          emails: 'Emails',
          calls: 'Calls',
          team: 'Team',
          notes: 'Notes',
          appointments: 'Appointments',
        },
        identity: {
          'title-label': 'Title',
          'object-type-label': 'Property type',
          'fine-type-label': 'Detailed type',
          'marketing-type-label': 'Marketing type',
          'object-number-label': 'Object no.',
          'reference-number-label': 'Exposé no.',
          'available-from-label': 'Available from',
          'edit-cta': 'Edit',
        },
        'fact-review': {
          title: 'To review ({count})',
          heading: 'To review',
          columns: {
            field: 'Field',
            value: 'Value',
            occurrence: 'Occurrence',
            origin: 'Source',
            evidence: 'Evidence',
            recorded: 'As of',
          },
          confirm: 'Confirm',
          'confirm-selected': 'Confirm selected',
          empty: 'Everything reviewed.',
          filter: {
            all: 'All',
            group: 'Group',
            origin: 'Source',
          },
          'select-row': 'Select row',
          'select-visible': 'Select all visible rows',
          'sort-label': 'Sort by {column}',
          origin: {
            OWNER_STATEMENT: 'Owner statement',
            EXTERNAL: 'External',
            ON_SITE: 'On-site survey',
            DOCUMENT: 'Document',
            PHOTO: 'Photo',
            MEMBER: 'Member',
          },
          status: {
            ASSUMED: 'assumed',
            CONTRADICTED: 'contradictory',
            UNKNOWN: 'unknown',
            NOT_APPLICABLE: 'not applicable',
          },
          group: FACT_KEY_GROUP_LABELS,
          'enum-values': DOSSIER_FACT_ENUM_LABELS,
          boolean: { true: 'Yes', false: 'No' },
          'structured-values': {
            parcel: '{number} (Flur {flur})',
            'land-value-zone': 'Zone {zone}',
            'land-value': '€{amount}/m²',
          },
          evidence: {
            page: 'p. {page}',
            'document-deleted': 'Document deleted',
            external: '{source}, {date}',
          },
          replace: {
            title: 'Replace property fields?',
            line: '{field}: {old} will be replaced by {new}.',
            confirm: 'Replace and confirm',
            cancel: 'Cancel',
          },
          'not-taken-over': 'Confirmed, but not transferred to the property:',
          'dismiss-notice': 'Dismiss notice',
          'load-failed': 'The values to review could not be loaded.',
          retry: 'Try again',
          'confirm-failed': 'Confirming did not work. Please try again.',
          marker: {
            'ai-detected': 'AI detected',
            'member-confirmed': 'Broker confirmed',
            confirm: 'Confirm',
            withdraw: 'Take back confirmation',
            'field-prefix': '{label}: {value}',
            'withdraw-failed': 'Taking the confirmation back did not work. Please try again.',
          },
        },
        'commercial-headline': {
          'subtype-label': 'Type',
          'usable-area-label': 'Usable area',
          'price-per-sqm-label': 'Rent per sqm/month',
          'purchase-price-label': 'Purchase price',
          'vat-label': 'VAT',
          'vat-with-rate': 'plus {rate}% VAT',
          'available-from-label': 'Available from',
          'min-divisible-area-label': 'divisible from',
        },
        address: {
          'street-label': 'Street & no.',
          'postal-city-label': 'Postal code & city',
          'district-label': 'District',
          'city-label': 'City',
          'country-label': 'Country',
          'hide-address-label': 'Hide address',
          yes: 'Yes',
          no: 'No',
          'edit-cta': 'Edit',
        },
        stats: {
          'days-on-market-label': 'Days on market',
          'inquiries-label': 'Inquiries',
          'appointments-label': 'Appointments',
        },
        assigned: {
          'reassign-cta': 'Reassign',
        },
        // The read-only valuation section on the property (IMM-407.6, AC 4) — the
        // property displays the valuation but never produces one (no edit action).
        valuation: {
          'price-range-label': 'Price range',
          'recommended-start-price-label': 'Recommended start price',
          'valuation-date-label': 'Valuation date',
          'report-hint': "The full valuation report is filed in the property's documents.",
          'report-hint-agent': "The full valuation is filed in the property's documents.",
          'report-documents-link': 'Go to documents',
        },
        'reassign-dialog': {
          title: 'Reassign property',
          'member-label': 'Responsible team member',
          cancel: 'Cancel',
          submit: 'Assign',
          submitting: 'Assigning…',
          success: 'Property reassigned',
          'not-found-error': 'Property not found. Please reload the page.',
          'generic-error': 'The assignment could not be saved. Please try again.',
        },
        'delete-dialog': {
          title: 'Delete property?',
          intro:
            '"{title}" will be removed from every list, including the archive. This action can only be undone by support.',
          cancel: 'Cancel',
          submit: 'Delete',
          submitting: 'Deleting…',
          success: 'Property deleted',
          'not-found-error': 'Property not found. Please reload the page.',
          'generic-error': 'The property could not be deleted. Please try again.',
        },
        'archive-dialog': {
          title: 'Archive property',
          'edit-title': 'Change archive reason',
          intro:
            '"{title}" will be archived and will disappear from the active lists. You can un-archive it at any time.',
          'reason-label': 'Why is the property being archived?',
          'reason-placeholder': 'Choose a reason …',
          'note-label-optional': 'Note (optional)',
          'note-label-required': 'Note',
          'note-placeholder': 'e.g. sold to the Müller family',
          counter: '{count} / {max}',
          'history-hint': 'The change is recorded in the status history.',
          submit: 'Archive',
          'edit-submit': 'Save',
          submitting: 'Saving…',
          cancel: 'Cancel',
          'success-toast': 'Property archived.',
          'success-toast-with-deal-loss': 'Property archived and deal marked as lost.',
          'open-deal-note-one': 'This property has an open deal. Archiving it marks the deal as lost.',
          'open-deal-note-many': 'This property has {count} open deals. Archiving it marks them as lost.',
          'edit-success-toast': 'Archive reason updated.',
          'validation-no-reason': 'Please choose an archive reason.',
          'validation-note-required': 'Please add a short note when choosing “Other”.',
          'generic-error': 'The action could not be completed. Please try again.',
        },
        'unarchive-dialog': {
          title: 'Remove archival',
          intro:
            'The property is taken out of the archive and set to “Paused”. The archive reason and note are deleted. They must be chosen again if you archive it later.',
          'intro-with-deal':
            'The property returns to the status “{status}” and the deal is reopened. The archive reason and note are deleted.',
          'success-with-deal': 'Archival removed and deal reopened.',
          'current-reason-label': 'Current archive reason',
          'will-be-deleted': 'will be deleted',
          cancel: 'Cancel',
          submit: 'Remove archival',
          submitting: 'Removing…',
          'generic-error': 'The archival could not be removed. Please try again.',
        },
        prospects: {
          title: 'Buyer prospects',
          'open-in-pipeline': 'Open in pipeline →',
          'reserved-for': 'Reserved for {name} — {price}',
          'reserved-for-no-price': 'Reserved for {name}',
          'without-process': 'Without purchase process · {count}',
          'add-to-process': 'Start purchase process',
          empty: 'No buyer prospects for this property yet.',
          'load-error': 'The buyer prospects could not be loaded.',
          loading: 'Loading buyer prospects…',
          retry: 'Try again',
        },
        buyers: {
          title: 'Buyer prospects ({count})',
          'title-unknown': 'Buyer prospects',
          'without-process': 'Without purchase process',
        },
        deals: {
          title: '{count, plural, one {Deal} other {Deals}}',
          empty: 'No deal for this property.',
          'load-error': 'The deals could not be loaded.',
          retry: 'Try again',
        },
        'deal-link': {
          open: 'Deal: {phase} →',
          other: 'Deal: {phase} · {status} →',
        },
        auftrag: {
          'open-deal': 'Open deal',
          'last-deal': 'Latest deal',
          'load-error': 'The mandate terms could not be loaded.',
          retry: 'Try again',
        },
        'linked-contacts': {
          'link-cta': 'Link contact',
          empty: 'No contacts linked yet.',
        },
        actions: {
          trigger: 'Actions',
          edit: 'Edit',
          'copy-link': 'Copy link',
          'link-copied': 'Link copied',
          activate: 'Start marketing',
          'set-inactive': 'Pause marketing',
          archive: 'Archive',
          reactivate: 'Remove archival',
          'change-archive-reason': 'Change archive reason',
          'mark-reserved': 'Mark as reserved',
          'unmark-reserved': 'Lift reservation',
          'reservation-marked': 'Property marked as reserved.',
          'reservation-unmarked': 'Reservation lifted.',
          'stage-in-acquisition': 'Set to “In acquisition”',
          'revert-to-draft': 'Set to “Draft”',
          'change-status': '{status}, change status',
          assign: 'Assign',
          delete: 'Delete',
          'denial-title': 'Action not possible',
          'cannot-activate-intro':
            'Marketing cannot be started. Please complete the following fields on the property detail page:',
          'invalid-transition': 'This status change is not possible. Please reload the page and try again.',
          'denial-generic': 'The action could not be completed. Please try again.',
          'archive-denied-open-deal':
            'You cannot archive this property because you may not mark its open deal as lost.',
          'denial-close': 'Got it',
          'denial-close-label': 'Close',
          'publish-fields': {
            STREET: 'Street',
            HOUSE_NUMBER: 'House number',
            POSTAL_CODE: 'Postal code',
            CITY: 'City',
            PURCHASE_PRICE: 'Purchase price',
            BASE_RENT: 'Base rent',
            LIVING_AREA: 'Living area',
            USABLE_AREA: 'Usable area',
            TOTAL_AREA: 'Total area',
            PLOT_AREA: 'Plot area',
            PARKING_SPACE_TYPE: 'Parking-space type',
            VAT_TREATMENT: 'VAT treatment',
            AVAILABLE_FROM: 'Available from',
            ANCILLARY_COSTS_TYPE: 'Type of ancillary costs',
          },
          'publish-field-hints': {
            BASE_RENT: 'A letting needs a base rent.',
            AVAILABLE_FROM: 'State from when the property is available.',
            ANCILLARY_COSTS_TYPE: 'State whether the ancillary costs are an advance payment or a flat rate.',
            VAT_TREATMENT: 'State how VAT is treated on the price.',
          },
        },
        contacts: {
          'section-title': 'Contacts',
          loading: 'Loading contacts…',
          empty: 'No contacts linked yet.',
          'load-error': 'Failed to load contacts.',
          'load-retry': 'Try again',
          'open-in-new-tab': 'Open in new tab',
          'add-cta': 'Link contact',
          'row-actions': 'Actions',
          'open-contact': 'Open contact',
          'send-email': 'Send email',
          'send-message': 'Send message',
          'not-implemented': 'This action is not available yet.',
          role: PROPERTY_CONTACT_ROLE_LABELS,
          groups: {
            owner: 'Owner',
            'prospects-sale': 'Purchase prospects',
            'prospects-rental': 'Rental prospects',
            others: 'Other contacts',
          },
          'owner-empty': 'No owner linked yet.',
          'owner-empty-cta': 'Link owner',
          'edit-link': 'Edit link',
          'unlink-link': 'Remove link',
          'link-dialog': {
            title: 'Link contact',
            'contact-label': 'Contact',
            'create-contact-cta': 'Create new contact',
            'role-label': 'Role',
            'role-placeholder': 'Select a role …',
            'note-label': 'Note',
            'note-hint': 'Max. 500 characters',
            'submit-cta': 'Link',
            'submit-loading': 'Linking…',
            'cancel-cta': 'Cancel',
            errors: {
              'contact-required': 'Please select a contact.',
              'role-required': 'Please select a role.',
              'not-found': 'Contact or property not found.',
              'already-linked': 'This link already exists. You can edit the note on the existing link instead.',
              'note-too-long': 'The note must be at most 500 characters.',
              'role-marketing-mismatch': "This role does not match the property's marketing type.",
              forbidden: 'You are not authorized to perform this action.',
              generic: 'Could not link the contact. Please try again.',
            },
          },
          'edit-dialog': {
            title: 'Edit link',
            'role-label': 'Role',
            'note-label': 'Note',
            'note-hint': 'Max. 500 characters',
            'submit-cta': 'Save',
            'submit-loading': 'Saving…',
            'cancel-cta': 'Cancel',
            errors: {
              'already-linked': 'This link already exists. You can edit the note on the existing link instead.',
              'note-too-long': 'The note must be at most 500 characters.',
              'role-marketing-mismatch': "This role does not match the property's marketing type.",
              'not-found': 'This link was not found.',
              forbidden: 'You are not authorized to perform this action.',
              generic: 'Could not save the change. Please try again.',
            },
          },
          'unlink-dialog': {
            title: 'Remove link',
            body: 'Remove the link to “{name}”? The contact and the property are kept.',
            'confirm-cta': 'Remove link',
            'confirm-loading': 'Removing…',
            'cancel-cta': 'Cancel',
            'error-forbidden': 'You are not authorized to perform this action.',
            error: 'Could not remove the link. Please try again.',
          },
        },
        sections: {
          'key-facts': 'Key facts',
          pipeline: 'Pipeline',
          activity: 'Activity',
          tasks: 'Tasks',
          details: 'Details',
          building: 'Building',
          energy: 'Energy',
          'investment-yield': 'Investment yield',
          'plot-details': 'Plot',
          amenities: 'Amenities',
          'pricing-sale': 'Sale price',
          'pricing-rental': 'Rental price',
          'rental-terms': 'Rental terms',
          'additional-costs': 'Additional costs',
          areas: 'Areas',
          identity: 'Identity',
          address: 'Address',
          valuation: 'Valuation',
          assigned: 'Assigned',
          'linked-contacts': 'Linked contacts',
          owner: 'Owner',
          'linked-deals': 'Linked deals',
          categories: 'Categories',
        },
        'owner-empty': 'No owner linked yet.',
        building: {
          'year-built-label': 'Year built',
          'last-renovated-label': 'Last renovated',
          'condition-label': 'Condition',
          'interior-quality-label': 'Interior quality',
          'construction-phase-label': 'Construction phase',
          'edit-cta': 'Edit',
          condition: CONDITION_LABELS,
          'interior-quality': INTERIOR_QUALITY_LABELS,
          'construction-phase': CONSTRUCTION_PHASE_LABELS,
        },
        'pricing-sale': {
          title: 'Sale price',
          'purchase-price-label': 'Purchase price',
          'price-per-sqm-label': 'Price per m²',
          'price-on-inquiry-label': 'Price on inquiry',
          'has-commission-label': 'Commission active',
          'commission-label': 'Commission',
          'commission-note-label': 'Commission note',
          'commission-basis-label': 'Stated as',
          'commission-rate-label': 'Rate (net)',
          'commission-amount-label': 'Amount (net)',
          'commission-statement-label': 'Listing text',
          'commission-legacy-label': 'Previous entry',
          'net-purchase-price-label': 'Net purchase price',
          'vat-treatment-label': 'VAT treatment',
          'vat-rate-label': 'VAT rate',
          'commission-includes-vat-label': 'Commission plus VAT',
          yes: 'Yes',
          no: 'No',
          'edit-cta': 'Edit',
        },
        'pricing-rental': {
          title: 'Rental price',
          'tenant-free-label': 'Commission',
          'has-commission-label': 'Commission',
          'commission-label': 'Commission amount',
          'commission-note-label': 'Commission note',
          'base-rent-label': 'Base rent',
          'price-per-sqm-label': 'Rent per m²/month',
          'total-rent-label': 'Total rent',
          'ancillary-costs-label': 'Service charges',
          'ancillary-costs-type-label': 'Service charge type',
          'heating-costs-label': 'Heating costs',
          'heating-costs-included-value': 'included in the service charges',
          'deposit-label': 'Deposit',
          'deposit-months-value': '{count, plural, one {# monthly rent} other {# monthly rents}}',
          'legacy-deposit-label': 'Deposit (legacy)',
          'parking-rent-label': 'Parking rent',
          'ancillary-costs-type': ANCILLARY_COSTS_TYPE_LABELS,
          'vat-treatment-label': 'VAT treatment',
          'vat-rate-label': 'VAT rate',
          'derivation-hint': '{rate} × {area} m² = {total} base rent',
          'compensation-payment-label': 'Compensation payment',
          'rent-free-months-label': 'Rent-free period',
          'rent-free-months-value': '{count, plural, one {# month} other {# months}}',
          'fit-out-contribution-label': 'Fit-out contribution',
          'renewal-option-label': 'Renewal option',
          'price-on-inquiry-label': 'Price on inquiry',
          yes: 'Yes',
          no: 'No',
          'edit-cta': 'Edit',
        },
        'rental-terms': {
          title: 'Rental terms',
          'furnishing-label': 'Furnishing',
          'lease-term-label': 'Lease term',
          'fixed-term-until-label': 'Fixed term until',
          'minimum-term-label': 'Minimum term',
          'minimum-term-value': '{count, plural, one {# month} other {# months}}',
          'rent-type-label': 'Rent type',
          'subletting-label': 'Subletting',
          furnishing: FURNISHING_LABELS,
          'lease-term': LEASE_TERM_LABELS,
          'rent-type': RENT_TYPE_LABELS,
          subletting: SUBLETTING_LABELS,
          'edit-cta': 'Edit',
        },
        'rental-headline': {
          title: 'Rental summary',
          'available-from-label': 'Available from',
          'lease-term-fixed-until-value': 'fixed term until {date}',
        },
        'additional-costs': {
          title: 'Additional costs',
          'edit-cta': 'Edit',
          'per-sqm-suffix': ' · {amount} / m²/month',
          type: ADDITIONAL_COST_TYPE_LABELS,
        },
        energy: {
          'certificate-basis-label': 'Certificate type',
          'certificate-year-label': 'Certificate vintage',
          'energy-class-label': 'Energy class',
          'energy-value-label': 'Energy value',
          'energy-value-unit': 'kWh/(m²·a)',
          'primary-energy-value-label': 'Primary energy demand',
          'includes-warm-water-label': 'Includes warm water',
          'certificate-issued-at-label': 'Issued at',
          'certificate-valid-until-label': 'Valid until',
          'heating-type-label': 'Heating type',
          'firing-type-label': 'Firing type',
          'edit-cta': 'Edit',
          'boolean-yes': 'Yes',
          'boolean-no': 'No',
          'certificate-basis': ENERGY_CERTIFICATE_BASIS_LABELS,
          'certificate-year': ENERGY_CERTIFICATE_YEAR_LABELS,
          'energy-class': ENERGY_CLASS_LABELS,
          'heating-type': HEATING_TYPE_LABELS,
          'firing-type': FIRING_TYPE_LABELS,
        },
        'investment-yield': {
          'is-rented-label': 'Rented',
          'rental-income-label': 'Rental income',
          'rental-income-target-label': 'Target rental income',
          'rental-income-actual-label': 'Actual rental income',
          'price-multiplier-label': 'Price multiplier',
          'number-of-apartments-label': 'Number of apartments',
          'number-of-commercial-units-label': 'Number of commercial units',
          'occupancy-rate-label': 'Occupancy rate',
          'number-of-vacancies-label': 'Number of vacancies',
          'tenant-structure-label': 'Tenant structure',
          'walt-label': 'WALT',
          'walt-unit': 'years',
          'occupancy-rate-unit': '%',
          'currency-unit': '€',
          'edit-cta': 'Edit',
          'boolean-yes': 'Yes',
          'boolean-no': 'No',
        },
        'plot-details': {
          'ground-area-ratio-label': 'GRZ (site coverage ratio)',
          'floor-area-ratio-label': 'GFZ (floor area ratio)',
          'building-mass-ratio-label': 'BMZ (building mass ratio)',
          'has-building-permission-label': 'Building permit granted',
          'short-term-buildable-label': 'Short-term buildable',
          'requires-demolition-label': 'Demolition required',
          'site-development-type-label': 'Site development',
          'site-constructible-type-label': 'Constructibility',
          'edit-cta': 'Edit',
          'boolean-yes': 'Yes',
          'boolean-no': 'No',
          'site-development-type': SITE_DEVELOPMENT_TYPE_LABELS,
          'site-constructible-type': SITE_CONSTRUCTIBLE_TYPE_LABELS,
        },
        amenities: {
          'feature-column': 'Feature',
          'value-column': 'Value',
          'edit-cta': 'Edit',
          'group-residential': 'Residential',
          'group-commercial': 'Commercial',
          'empty-state': 'No amenities recorded yet.',
          residential: RESIDENTIAL_AMENITY_LABELS,
          commercial: COMMERCIAL_AMENITY_LABELS,
        },
        areas: {
          'code-column-header': 'Area',
          'value-column-header': 'Value',
          'edit-cta': 'Edit',
          'empty-state': 'No areas captured yet.',
          'unit-sqm': 'm²',
          'unit-cbm': 'm³',
          'use-split-remainder-label': 'Remaining area',
          'use-split-exceeded-label': 'Sum of the use parts',
          'use-split-exceeded-value': '{sum} m² of {usableArea} m²',
          code: AREA_CODE_LABELS,
        },
        cards: {
          'status-label': 'Status',
          'status-since-day': 'for 1 day',
          'status-since-days': 'for {days} days',
          'asking-price-label': 'Asking price',
          'asking-price-per-sqm': '€ {amount} / m²',
          'expected-revenue-label': 'Expected revenue',
          'match-score-label': 'Match score',
          'last-activity-label': 'Last activity',
          'last-activity-empty': 'No activity yet',
          'broker-team-label': 'Broker team',
          'member-singular': '1 member',
          'member-plural': '{count} members',
        },
        pipeline: {
          'first-contact': 'First contact',
          viewing: 'Viewing',
          offer: 'Offer',
          negotiation: 'Negotiation',
          notary: 'Notary',
        },
        details: {
          'type-label': 'Property type',
          'marketing-type-label': 'Marketing type',
          'location-label': 'Location',
          'living-area-label': 'Living area',
          'rooms-label': 'Rooms',
          'year-built-label': 'Year built',
          'energy-label': 'Energy',
          'asking-label': 'Asking',
          'expected-label': 'Expected',
          'show-all-fields': 'Show all fields',
        },
        timeline: {
          'add-cta': 'Add activity',
          save: 'Save',
          saving: 'Saving …',
          cancel: 'Cancel',
          'content-label': 'Content',
          'content-counter': '{count, number} / {max, number}',
          'content-placeholder': 'What happened?',
          'occurred-at-label': 'Happened on',
          'now-cta': 'Now',
          'occurred-at-time-label': 'Time',
          'type-label': 'Type',
          'type-note': 'Note',
          'type-appointment': 'Appointment',
          'appointment-type-label': 'Appointment type',
          'appointment-type-placeholder': 'Please choose',
          'appointment-type-valuation': 'Valuation appointment',
          'appointment-type-on-site': 'On-site appointment',
          'appointment-type-property-survey': 'Property survey',
          'appointment-type-contract-signing': 'Contract signing',
          'appointment-type-other': 'Other appointment',
          'appointment-type-viewing-on-site': 'On-site viewing',
          'appointment-type-viewing-virtual': 'Virtual viewing',
          'appointment-type-consultation': 'Consultation',
          'appointment-type-financing-consultation': 'Financing consultation',
          'appointment-type-negotiation': 'Negotiation',
          'appointment-type-notary': 'Notary appointment',
          'appointment-type-handover': 'Handover',
          'error-no-appointment-type': 'Please choose an appointment type.',
          'type-call': 'Call',
          'type-sms': 'SMS',
          'type-whatsapp': 'WhatsApp',
          'type-fax': 'Fax',
          'type-letter': 'Letter',
          'type-email': 'Email',
          'direction-label': 'Direction',
          'direction-inbound': 'Inbound',
          'direction-outbound': 'Outbound',
          'subject-label': 'Subject',
          'entry-note': 'Note',
          'entry-call-inbound': 'Inbound call',
          'entry-call-outbound': 'Outbound call',
          'entry-sms-inbound': 'Inbound SMS',
          'entry-sms-outbound': 'Outbound SMS',
          'entry-whatsapp-inbound': 'Inbound WhatsApp',
          'entry-whatsapp-outbound': 'Outbound WhatsApp',
          'entry-fax-inbound': 'Inbound fax',
          'entry-fax-outbound': 'Outbound fax',
          'entry-letter-inbound': 'Inbound letter',
          'entry-letter-outbound': 'Outbound letter',
          'recorder-unknown': 'Unknown member',
          'group-today': 'Today',
          'group-yesterday': 'Yesterday',
          'relative-just-now': 'Just now',
          'relative-minutes': '{count} minutes ago',
          'relative-hours': '{count} hours ago',
          'relative-yesterday': 'Yesterday',
          'empty-heading': 'No activities yet',
          'empty-body': 'Everything that happens with this property shows up here — calls, notes, messages and more.',
          'empty-cta': 'Add activity',
          'error-empty-content': 'Please enter some content.',
          'error-content-too-long': 'An activity must not exceed 10,000 characters.',
          'error-no-direction': 'Please choose the direction (inbound or outbound).',
          'error-subject-too-long': 'The subject must not exceed 200 characters.',
          'error-future': 'An activity cannot be in the future.',
          'error-occurred-at-incomplete': 'Please provide both a date and a time.',
          'save-error': 'The activity could not be saved.',
          loading: 'Loading activities …',
          'load-error': 'Activities could not be loaded.',
          'load-retry': 'Try again',
          edited: 'edited',
          'edit-cta': 'Edit',
          'delete-cta': 'Delete',
          'delete-dialog-title': 'Delete activity?',
          'delete-dialog-body': 'This action cannot be undone.',
          'delete-confirm': 'Delete',
          'delete-cancel': 'Cancel',
          'delete-error': 'The activity could not be deleted.',
          'update-error': 'The activity could not be saved.',
          'contact-picker-label': 'Contact',
          'no-contacts': 'No contacts are linked to this property yet. Link a contact first to add activities.',
          'outcome-label': 'Outcome',
          'outcome-none': 'No outcome',
          'outcome-reached': 'Reached',
          'outcome-not-reached': 'Not reached',
          'outcome-voicemail': 'Voicemail',
          'outcome-callback-agreed': 'Callback agreed',
          'outcome-email-reached': 'Reply received',
          'outcome-email-not-reached': 'No reply',
        },
        'tab-empty': 'All {tab} entries for this property will land here.',
        'tasks-empty': 'No tasks yet.',
        'tasks-open': '{count} open',
        'edit-permission-reason': 'You are not permitted to edit this property.',
        'coming-soon': 'Coming soon',
        'add-cta': 'Add',
        empty: '—',
        documents: {
          'section-title': 'Documents',
          loading: 'Loading documents…',
          'load-error': 'Documents could not be loaded.',
          'load-retry': 'Try again',
          'analyses-load-error': 'The analysis status of the documents could not be loaded.',
          'add-cta': 'Upload everything',
          'drop-overlay': 'Drop files here',
          'type-fallback': 'File',
          'uploaded-by-valuation-agent': 'Valuation agent',
          'superseded-at': 'Replaced on {date}',
          'uploaded-by-unknown': 'Unknown member',
          selection: {
            'header-label': 'Select all documents',
            hint: 'Tick documents to share them with buyers in one go.',
            count: '{count} selected',
            clear: 'Clear selection',
            marked: '{count, plural, one {# document shared with buyers.} other {# documents shared with buyers.}}',
            unmarked:
              '{count, plural, one {# document no longer for buyers.} other {# documents no longer for buyers.}}',
            'not-changed': '{count} not changed.',
            'disabled-personal': 'Personal documents cannot be shared with buyers.',
            'disabled-valuation':
              'Valuations, valuation reports and acquisition briefings cannot be released for buyers.',
            'disabled-right': 'You do not have permission to edit this document.',
          },
          forBuyers: {
            action: 'Share with buyers',
            actionMarked: 'No longer for buyers',
            badge: 'For buyers',
            counter: '{viewedBy} of {activeReleases} viewed',
            'locked-valuation':
              'Valuations, valuation reports and acquisition briefings cannot be released for buyers.',
          },
          buyerReleases: {
            button: 'Manage releases',
            title: 'Manage releases — {title}',
            released: 'Released on {date}',
            withdrawn: 'Release withdrawn on {date}',
            'not-released': 'Not released yet',
            'will-release': 'Will be released',
            'will-withdraw': 'Will be withdrawn',
            'select-all': 'Select all',
            'deselect-all': 'Deselect all',
            cancel: 'Cancel',
            save: 'Save',
            'confirm-title-both': 'Release {released}, withdraw {withdrawn}?',
            'confirm-title-release': 'Release {released}?',
            'confirm-title-withdraw': 'Withdraw {withdrawn}?',
            'confirm-body': 'Newly released prospects each receive an e-mail with their link.',
            confirm: 'Confirm',
            saved: 'Releases saved.',
            partial: '{count, plural, one {# change not saved.} other {# changes not saved.}}',
            'failed-inactive': 'The prospect is no longer active.',
            empty: 'There are no active prospects for this property.',
          },
          columns: {
            name: 'Name',
            category: 'Category',
            size: 'Size',
            'uploaded-by': 'Uploaded by',
            date: 'Date',
          },
          filters: {
            all: 'All',
            uncategorized: 'Uncategorized',
          },
          'uncategorized-hint':
            '{count, plural, one {# document without category} other {# documents without category}}',
          'uncategorized-hint-cta': 'Assign category',
          'classification-run': {
            started:
              '{count, plural, one {# document is being categorised…} other {# documents are being categorised…}}',
            'already-running': 'Categorisation is already running.',
            none: 'No documents without a category.',
            error: 'Could not start categorisation. Please try again.',
          },
          'analysis-backlog-hint':
            '{count, plural, one {# document without analysis} other {# documents without analysis}}',
          'analysis-backlog-hint-cta': 'Analyse now',
          'analysis-run': {
            confirm: {
              title: '{count, plural, one {Analyse # document?} other {Analyse # documents?}}',
              body: 'Each document is evaluated individually — this can take a few minutes. Documents that have already been analysed stay unchanged.',
              confirm: 'Start analysis',
              cancel: 'Cancel',
            },
            started: '{count, plural, one {# document is being analysed…} other {# documents are being analysed…}}',
            'already-running': 'Analysis is already running.',
            none: 'No documents without analysis.',
            error: 'Could not start the analysis. Please try again.',
          },
          'empty-first-time': {
            line: 'No documents for this property yet',
            cta: 'Upload everything',
          },
          'section-switcher': {
            label: 'Documents shown',
            all: 'All',
          },
          'empty-no-match': {
            line: 'No documents in this category group',
            'section-line': 'No documents under “{section}”',
            cta: 'Reset filter',
          },
          'category-group': DOCUMENT_CATEGORY_GROUP_LABELS,
          'document-section': DOCUMENT_SECTION_LABELS,
          category: DOCUMENT_CATEGORY_LABELS,
          classification: {
            ...DOCUMENT_CLASSIFICATION_MARKERS,
            promote: {
              action: 'Move to photos',
              errors: {
                'gallery-full': 'The photo gallery is full (max. 100). Free up space first.',
                forbidden: 'You are not allowed to move this document.',
                generic: 'The document could not be moved. Please try again.',
              },
            },
          },
          readiness: {
            title: 'Document check',
            signal: {
              SALE_READY: 'Complete',
              ALMOST_COMPLETE: 'Almost complete',
              INCOMPLETE: 'Incomplete',
            },
            counter: '{present} of {required} mandatory documents on file',
            'missing-count':
              '{count, plural, one {# mandatory document missing} other {# mandatory documents missing}}',
            'documents-counter': '{present} of {total} documents on file',
            'checklist-toggle': 'Checklist',
            group: {
              MANDATORY: 'Mandatory',
              OPTIONAL: 'Optional',
            },
            'optional-hint': 'These documents do not block marketing.',
            // IMM-1361 — a letting files a draft lease and a cost overview under two
            // reused catalogue categories; its checklist names them as the letting does.
            'letting-category': {
              RENTAL_CONTRACT: 'Draft tenancy agreement',
              UTILITY_STATEMENT: 'Operating cost overview',
            },
            'conditional-hint': 'only if applicable',
            obligation: {
              MANDATORY: 'Mandatory',
              OPTIONAL: 'Optional',
            },
            status: {
              PRESENT: 'On file',
              MISSING: 'Missing',
              EXPIRING: 'Expires on {date}',
              EXPIRED: 'Expired since {date}',
            },
            request: {
              action: 'Request from owner',
              bulk: 'Request all missing',
              chip: 'Recorded on {date}',
              'disabled-reason': 'No owner with an e-mail address is on file for this property',
              'permission-reason': 'You are not permitted to request documents from the owner.',
              'recipient-fallback': 'the owner',
              'no-owner-fix': 'Link an owner in the “Links” tab, or add their e-mail address on the contact.',
              draft: {
                subject: 'Documents for your property',
                intro: 'to continue marketing your property, I still need the following documents:',
                ask: 'Might I kindly ask you to send them to me as soon as you are able? If you do not have some of the documents, please let me know briefly and we will find a solution together.',
                thanks: 'Thank you very much for your support.',
                closing: 'Kind regards',
              },
              dialog: {
                'mailbox-block': {
                  NOT_CONNECTED:
                    'Your mailbox is not connected, so immoOS cannot open an e-mail to the owner. Connect your mailbox and a ready-made e-mail to them will open when you request documents. Until then you can save the request here as a note.',
                  EXPIRED:
                    'The connection to your mailbox has expired, so immoOS cannot open an e-mail to the owner. Reconnect your mailbox and a ready-made e-mail to them will open when you request documents. Until then you can save the request here as a note.',
                  LOAD_FAILED:
                    'The connection status of your mailbox could not be loaded, so no e-mail opens. Try again in a moment, or save the request here as a note.',
                  NO_PERMISSION:
                    'You are not permitted to send e-mails, so immoOS cannot open an e-mail to the owner. Please contact an admin. Until then you can save the request here as a note.',
                },
                'title-single': 'Request from owner',
                'title-bulk': 'Request all missing',
                'description-single':
                  'The request is recorded as a note on this property. The owner is not notified automatically — please contact them yourself.',
                'description-bulk':
                  'The documents listed below are recorded as a note on this property. The owner is not notified automatically — please contact them yourself.',
                // IMM-749 — the mock's „Empfänger" block. Not a dispatch claim: the
                // description directly above says nothing is sent automatically, so
                // the address is here precisely so the agent can contact the owner.
                'recipient-label': 'Recipient',
                'recipient-unknown': 'No owner with an e-mail address is on file',
                'categories-label': 'Requested documents',
                'note-label': 'Note on this request (optional)',
                'note-placeholder': 'e.g. Would need this before the photo shoot',
                submit: 'Request',
                'submit-loading': 'Saving …',
                cancel: 'Cancel',
              },
              toast: {
                success: 'Request for {name} recorded.',
                'sent-not-recorded': 'The e-mail was sent, but the request could not be recorded.',
                forbidden: 'You are not authorized for this action.',
                'no-owner-email': 'No owner with an e-mail address is on file for this property',
                error: 'The request could not be saved. Please try again.',
              },
            },
          },
          'upload-dialog': {
            title: 'Upload everything',
            subtitle:
              'Just drop in all your files — documents and photos mixed. We sort them into the right categories automatically.',
            'file-picker-cta': 'Select files',
            'drop-hint': 'Drop files here or browse · PDF, JPG, PNG · many at once',
            'size-hint': 'Word too · max. 20 MB per file',
            steps: {
              upload: 'Upload',
              categorise: 'Categorise',
              done: 'Done',
              // Visually hidden. Marks a finished step for assistive tech, which
              // never reaches the check mark on the decorative badge.
              completed: 'completed',
            },
            progress: '{done} of {total}',
            'done-title': 'Done',
            'done-body':
              'Your files are uploaded. They are being sorted in the background — you will see it right in the overview.',
            'done-cta': 'To the overview',
            'display-name-label': 'Display name',
            'category-label': 'Category (optional)',
            'category-placeholder': 'Choose a category or assign later',
            'remove-file': 'Remove file',
            'retry-file': 'Try again',
            status: {
              pending: 'Queued',
              uploading: 'Uploading …',
              done: 'Uploaded',
              failed: 'Failed',
            },
            'submit-cta': 'Upload',
            'submit-loading': 'Uploading …',
            'cancel-cta': 'Cancel',
            errors: {
              'disallowed-type': 'Only PDF, Word, JPG and PNG are allowed',
              'too-large': 'The file must not exceed 20 MB',
              forbidden: 'You are not authorized for this action.',
              generic: 'The document could not be uploaded. Please try again.',
            },
          },
          'category-picker': {
            'search-placeholder': 'Search category…',
            'no-results': 'No category found.',
          },
          'row-actions': 'Actions',
          'action-preview': 'Preview',
          'action-download': 'Download',
          'action-rename': 'Rename',
          'action-categorize': 'Edit',
          'action-delete': 'Delete',
          'delete-locked-signed': 'Signed documents cannot be deleted.',
          'download-error': 'The download could not be started. Please try again.',
          'read-only-info': {
            title: 'You can only view and download documents.',
            body: 'You need a higher permission to upload or edit.',
          },
          rename: {
            label: 'File name',
            save: 'Save',
            cancel: 'Cancel',
            errors: {
              empty: 'Please enter a name.',
              forbidden: 'You are not authorized for this action.',
              'not-found': 'This document no longer exists.',
              generic: 'The document could not be renamed. Please try again.',
            },
          },
          'recategorize-modal': {
            title: 'Edit category',
            save: 'Save',
            'save-loading': 'Saving…',
            cancel: 'Cancel',
            errors: {
              forbidden: 'You are not authorized for this action.',
              'not-found': 'This document no longer exists.',
              generic: 'The category could not be saved. Please try again.',
            },
          },
          'delete-dialog': {
            title: 'Delete document?',
            body: 'The document “{name}” will be permanently removed. This cannot be undone.',
            confirm: 'Delete',
            'confirm-loading': 'Deleting…',
            cancel: 'Cancel',
            errors: {
              forbidden: 'You are not authorized for this action.',
              'not-found': 'This document no longer exists.',
              generic: 'The document could not be deleted. Please try again.',
            },
          },
          'preview-modal': {
            title: 'Preview',
            loading: 'Loading preview…',
            error: 'The preview could not be loaded. Please try again.',
            unsupported: 'No preview available. Download the document to open it.',
          },
          analyse: {
            action: 'Analyse document',
            back: 'Documents',
            again: 'Analyse again',
            apply: 'Apply',
            disclaimer:
              'Extracted automatically from the document — please check against the original. The document remains authoritative.',
            'not-determined': 'not found',
            'please-check': 'please check',
            conflict: {
              label: 'Conflict',
              message: 'Conflict - Extracted value <{extracted}> differs from original value <{original}>',
            },
            running: {
              label: 'Analysing …',
              'toast-title': 'Analysis running …',
              'toast-body': 'You can leave this page and come back later.',
            },
            done: {
              label: 'Analysed',
            },
            status: {
              loading: 'Loading …',
              PENDING: 'Queued',
              ANALYZING: 'Analysing …',
              COMPLETED: 'Completed',
              NO_DATA: 'No data',
              FAILED: 'Error',
            },
            meta: {
              'completed-at': 'Completed on {at}',
              running: 'The analysis is still running …',
            },
            confidence: {
              high: 'high',
              medium: 'medium',
              low: 'low',
            },
            sections: {
              address: 'Address',
              areas: 'Areas',
              building: 'Building',
              energy: 'Energy',
              price: 'Price',
              other: 'Other',
            },
            fields: {
              street: 'Street',
              houseNumber: 'House number',
              postalCode: 'Postal code',
              city: 'City',
              district: 'District',
              state: 'State',
              country: 'Country',
              parcelNumber: 'Parcel number',
              parcelSubdivision: 'Parcel subdivision',
              livingArea: 'Living area',
              plotArea: 'Plot area',
              usableArea: 'Usable area',
              totalArea: 'Total area',
              rooms: 'Rooms',
              bedrooms: 'Bedrooms',
              bathrooms: 'Bathrooms',
              floor: 'Floor',
              floorsTotal: 'Floors total',
              yearBuilt: 'Year built',
              lastRenovated: 'Last renovated',
              condition: 'Condition',
              interiorQuality: 'Interior quality',
              constructionPhase: 'Construction phase',
              certificateBasis: 'Certificate basis',
              energyClass: 'Energy class',
              energyValue: 'Energy value',
              primaryEnergyValue: 'Primary energy demand',
              includesWarmWater: 'Includes warm water',
              certificateIssuedAt: 'Issued on',
              certificateValidUntil: 'Valid until',
              purchasePrice: 'Purchase price',
              baseRent: 'Base rent',
              totalRent: 'Total rent',
              deposit: 'Deposit',
            },
            'no-data': {
              title: 'No data',
              body: 'No readable property data was found in this document.',
              'no-text-body': 'This document contains no readable text to extract.',
            },
            'load-failed': {
              title: 'Analysis could not be loaded',
              body: 'The result could not be read. Please try again.',
              retry: 'Try again',
            },
            failed: {
              title: 'Error',
              body: 'The analysis failed. Please try again.',
              retry: 'Try again',
            },
            errors: {
              forbidden: 'You are not authorised for this action.',
              generic: 'The analysis could not be started. Please try again.',
            },
            'field-edit': {
              pencil: 'Correct value',
              save: 'Apply',
              cancel: 'Cancel',
              saving: 'Saving …',
              'error-forbidden': 'You are not authorised for this action.',
              'error-generic': 'The correction could not be saved. Please try again.',
            },
            corrected: {
              badge: 'manually corrected',
              'read-from': 'Extracted',
              by: 'Corrected by {name} · {at}',
            },
            'rerun-warning': {
              title: 'Re-analyse?',
              body: 'This analysis contains manual corrections. Re-analysing replaces the extracted values — your corrections may be lost. Continue?',
              confirm: 'Re-analyse',
              cancel: 'Cancel',
            },
            'apply-confirm': {
              title: 'Apply {section} values?',
              body: 'Applying fields will update property fields with the extracted values. Conflicts are not selected automatically; selecting one overwrites the current property value.',
              'conflict-label': 'Conflict',
              'conflict-message': 'Conflict - Extracted value <{extracted}> differs from original value <{original}>',
              confirm: 'Apply',
              cancel: 'Cancel',
            },
            'apply-result': {
              success: '{count} fields applied',
              'success-one': 'One field applied',
              // A partial apply is a normal outcome, not a failure: some values
              // cannot reach the property (see the `skipped` reasons below).
              partial: '{applied} of {total} fields applied',
              'none-applied': 'No fields applied',
              // A re-run replaced the reviewed analysis before confirm — nothing
              // was written; the panel reloads to show the new values.
              superseded: 'The analysis was updated in the meantime. Please review the new values.',
              forbidden: 'You may not edit this property.',
              error: 'The values could not be applied. Please try again.',
              skipped: {
                title: 'Not applied:',
                'not-extracted': '{field}: no extracted value',
                'unsupported-field': '{field}: no matching property field',
                'invalid-value': '{field}: value does not match the field format',
                'section-incomplete': '{field}: section not yet complete on the property — create it there first',
                'section-not-applicable': '{field}: does not match the property’s marketing type',
                'analysis-superseded': '{field}: analysis was updated',
                'certificate-not-gmodg': '{field}: only applies to an energy certificate issued from 2027',
                'energy-class-off-scale': '{field}: not on the A to G scale for non-residential buildings',
              },
            },
            'read-only': {
              'banner-title': 'You can view this analysis but not change it.',
              'banner-body': 'Analysing and correcting are reserved for members with write access.',
              denied: 'You are not authorised for this action.',
            },
          },
          redaction: {
            marker: 'Redacted',
            'copies-toggle': '{count, plural, one {# redacted copy} other {# redacted copies}}',
            'original-deleted': 'Original deleted',
            action: 'Redact',
            unsupported: 'Only PDFs and images can be redacted',
            title: 'Redact document',
            hint: 'Mark the areas you want to make unreadable.',
            save: 'Redact & save',
            saving: 'Redacting …',
            cancel: 'Cancel',
            'remove-box': 'Remove marking',
            'no-box': 'Mark at least one area to redact',
            loading: 'Loading document …',
            'load-error': 'The document could not be loaded. Please try again.',
            'page-label': 'Page {number}',
            forbidden: 'You are not allowed to do this.',
            confirm: {
              title: 'Redaction is permanent',
              body: 'The marked content is removed permanently and cannot be restored. The original stays unchanged.',
              'name-label': 'Name of the copy',
              'name-suffix': '{base} (redacted)',
              'name-required': 'Please enter a name for the copy.',
            },
            success: 'Redacted copy created.',
            failed: {
              engine: 'Redaction failed. Please try again.',
              check:
                'The redaction could not be verified safely. No copy was created. Please try again or contact support.',
            },
          },
        },
        media: {
          'section-title': 'Media',
          'drop-overlay': 'Drop files here to upload',
          loading: 'Loading media…',
          'load-error': 'Media could not be loaded.',
          'load-retry': 'Try again',
          'add-cta': 'Upload media',
          'uploaded-by-unknown': 'Unknown member',
          'thumbnail-pending': 'Preview is being generated …',
          'thumbnail-unavailable': 'Preview unavailable',
          areas: {
            photos: 'Photos',
            'floor-plans': 'Floor plans',
            videos: 'Videos',
          },
          type: {
            image: 'Image',
            video: 'Video',
          },
          'row-actions': 'Actions',
          'action-set-cover-image': 'Set as cover',
          'action-als-grundriss': 'Move to floor plans',
          'action-als-foto': 'Move to photos',
          demote: {
            action: 'Move to documents',
            errors: {
              'not-an-image': 'Only images can be moved to the documents.',
            },
          },
          'action-umbenennen': 'Rename',
          'action-loeschen': 'Delete',
          'cover-image-badge': 'Cover',
          'delete-dialog': {
            title: 'Delete medium?',
            body: 'This medium will be permanently removed. This cannot be undone.',
            confirm: 'Delete',
            'confirm-loading': 'Deleting…',
            cancel: 'Cancel',
            errors: {
              forbidden: 'You are not allowed to perform this action.',
              'not-found': 'This medium no longer exists.',
              generic: 'The medium could not be deleted. Please try again.',
            },
          },
          selection: {
            select: 'Select',
            cancel: 'Cancel',
            'select-all': 'Select all',
            'delete-selected': 'Delete selected ({n})',
            'bulk-delete': {
              title: {
                photos: '{n, plural, one {Delete # photo?} other {Delete # photos?}}',
                'floor-plans': '{n, plural, one {Delete # floor plan?} other {Delete # floor plans?}}',
              },
              body: 'These pictures will be permanently removed. This cannot be undone.',
              'partial-failure': {
                photos: '{count, plural, one {# photo} other {# photos}} could not be deleted. Please try again.',
                'floor-plans':
                  '{count, plural, one {# floor plan} other {# floor plans}} could not be deleted. Please try again.',
              },
            },
            'move-failure': {
              photos: '{count, plural, one {# photo} other {# photos}} could not be moved. Please try again.',
              'floor-plans':
                '{count, plural, one {# floor plan} other {# floor plans}} could not be moved. Please try again.',
            },
          },
          rename: {
            title: 'Rename medium',
            label: 'Name',
            hint: '1–255 characters · the original filename is preserved for download',
            save: 'Save',
            cancel: 'Cancel',
            errors: {
              empty: 'Please enter a name.',
              forbidden: 'You are not allowed to perform this action.',
              'not-found': 'This medium no longer exists.',
              generic: 'The medium could not be renamed. Please try again.',
            },
          },
          errors: {
            'cover-image-not-a-photo': 'Only a photo can be set as the cover image',
          },
          'empty-area': {
            photos: 'No photos yet',
            'floor-plans': 'No floor plans yet',
            videos: 'No videos yet',
          },
          'empty-first-time': {
            line: 'No media for this property yet',
            cta: 'Upload media',
          },
          'capture-photos': {
            heading: 'Capture photos',
            hint: 'For the valuation only. These photos never appear in the gallery, the exposé or the data room.',
            'tile-meta': '{takenAt}, {time} · {member}',
          },
          tours: {
            heading: '360° tours',
            'add-cta': 'Add tour',
            empty: 'No tours yet',
            'limit-hint': 'At most {limit} tours per property.',
            loading: 'Loading tours…',
            'load-error': 'The tours could not be loaded.',
            'reorder-error': 'The order could not be saved. Please try again.',
            entry: '{provider} · {title}',
            'default-title': '360° tour',
            actions: {
              open: 'Open',
              edit: 'Edit',
              delete: 'Delete',
            },
            dialog: {
              'add-title': 'Add tour',
              'edit-title': 'Edit tour',
              'link-label': 'Link',
              'link-placeholder': 'https://…',
              'title-label': 'Title',
              save: 'Save',
              cancel: 'Cancel',
              errors: {
                'not-found': 'This tour no longer exists.',
                generic: 'The tour could not be saved. Please try again.',
              },
            },
            providers: {
              MATTERPORT: 'Matterport',
              OGULO: 'Ogulo',
              IMMOVIEWER: 'immoviewer',
              THREE_D_VISTA: '3DVista',
              GIRAFFE360: 'Giraffe360',
              KUULA: 'Kuula',
              PANOEE: 'Panoee',
              NODALVIEW: 'Nodalview',
            },
          },
          'cap-error': 'Maximum of 100 media items per property – {remaining} can still be added',
          'video-link': {
            'add-cta': 'Add video link',
            dialog: {
              title: 'Add video link',
              'link-label': 'Link',
              'link-hint': 'YouTube or Vimeo',
              save: 'Save',
              cancel: 'Cancel',
              errors: {
                generic: 'The video link could not be saved. Please try again.',
              },
            },
            providers: {
              YOUTUBE: 'YouTube',
              VIMEO: 'Vimeo',
            },
            actions: {
              open: 'Open',
            },
            'open-aria': 'Open {title} on {provider} (new tab)',
            'rename-hint': '1–120 characters',
          },
          handles: {
            preview: 'Preview',
            download: 'Download',
          },
          lightbox: {
            close: 'Close',
            previous: 'Previous media item',
            next: 'Next media item',
            download: 'Download',
            'cover-badge': 'Cover',
            position: {
              photos: '{i} of {n} photos',
              'floor-plans': '{i} of {n} floor plans',
              videos: '{i} of {n} videos',
            },
            'download-error': 'The download could not be started. Please try again.',
            'image-unavailable': 'Image unavailable',
          },
          'upload-dialog': {
            title: 'Upload media',
            'file-picker-cta': 'Select photos or videos',
            'drop-hint': 'JPG, PNG, WebP up to 20 MB · MP4 up to 200 MB',
            'remove-file': 'Remove file',
            'submit-cta': 'Upload',
            'submit-loading': 'Uploading …',
            'cancel-cta': 'Cancel',
            errors: {
              type: 'Only JPG, PNG, WebP and MP4 are allowed',
              'image-too-large': 'Images may be at most 20 MB',
              'video-too-large': 'Videos may be at most 200 MB',
              generic: 'The media item could not be uploaded. Please try again.',
            },
          },
        },
        portals: {
          heading: 'Portal listings',
          'load-error': 'The portal listings could not be loaded.',
          'load-retry': 'Try again',
          'enter-commission-cta': "Enter buyer's commission",
          expired: {
            body: 'The ImmoScout24 connection has expired. Existing listings stay online but cannot be changed until it has been renewed.',
            'ask-admin': 'An owner or admin can renew the connection under ‘Portals’.',
            'renew-cta': 'Renew connection',
          },
          'openimmo-expired': {
            manager: 'The access to {portal} is invalid. Listings cannot be published, updated or withdrawn there.',
            member:
              'The access to {portal} is invalid. Existing listings stay online but cannot be changed until an owner or admin has corrected the access under ‘Portals’.',
            'change-cta': 'Change access',
          },
          extension: {
            send: 'Send to ImmoScout24',
            complete: 'Complete listing details',
            update: 'Publish changes',
            retry: 'Try again',
            running: 'Sending to ImmoScout24 …',
            progress: 'Step {done} of {total}',
            draft: 'Draft on ImmoScout24',
            online: 'Online on ImmoScout24',
            offline: 'Offline on ImmoScout24',
            failed: 'The transfer to ImmoScout24 failed.',
            'not-kept': 'ImmoScout24 did not keep {fields}. Enter them by hand in ScoutManager.',
            'no-extension': 'You need the immoOS extension for ImmoScout24.',
            outdated: 'The immoOS extension did not answer. Update it, then try again.',
            'install-cta': 'Install the extension',
            'not-connected': 'Connect the immoOS extension, then you can send.',
            'connect-cta': 'Connect the extension',
            'signed-out':
              'ImmoScout24 does not recognise your session right now. Open ImmoScout24, sign in if asked, then try again.',
            'sign-in-cta': 'Open ImmoScout24',
            client: {
              pending: {
                SEND: 'Your AI client would like to send the property to ImmoScout24.',
                UPDATE: 'Your AI client would like to publish the changes to ImmoScout24.',
                GO_LIVE: 'Your AI client would like to put the listing online on ImmoScout24.',
                TAKE_OFFLINE: 'Your AI client would like to take the listing offline on ImmoScout24.',
                DELETE: 'Your AI client would like to delete the listing on ImmoScout24.',
              },
              'options-label': 'Publish on',
              confirm: 'Confirm',
              decline: 'Decline',
              'decide-error': 'Your decision could not be saved. Please try again.',
              confirmed: 'Confirmed. It will run as soon as your browser with the immoOS extension is active.',
              declined: 'Declined. Nothing was sent to ImmoScout24.',
              lapsed:
                'Not carried out: your browser with the immoOS extension was not active for 24 hours. Please ask again.',
            },
            'go-live': {
              cta: 'Publish',
              title: 'Put this on ImmoScout24?',
              body: 'Choose how ImmoScout24 publishes the listing. This may use up part of your agency’s quota.',
              confirm: 'Publish',
              cancel: 'Cancel',
            },
            end: {
              TAKE_OFFLINE: {
                cta: 'Unpublish',
                title: 'Take this listing offline on ImmoScout24?',
                body: 'Searchers will no longer see it. You can put it online again later.',
                confirm: 'Unpublish',
              },
              DELETE: {
                cta: 'Delete',
                title: 'Delete this listing on ImmoScout24 for good?',
                body: 'Booked products lose their validity, the statistics end and messages are no longer available. This cannot be undone.',
                confirm: 'Delete for good',
              },
              cancel: 'Cancel',
              failed: 'That did not work on ImmoScout24.',
              deleted: 'Deleted on ImmoScout24 on {date}.',
            },
          },
          row: {
            'not-published': 'not published',
            'coming-soon': 'Coming soon',
            publish: 'Publish',
            status: {
              PUBLISHED: 'published',
              TRANSMITTED: 'transmitted',
              STALE: 'out of date',
              FAILED: 'failed',
              WITHDRAWN: 'withdrawn',
            },
            'in-flight': 'Transmitting …',
            since: 'since {date}',
            'portal-id': 'IS24 ID {id}',
            contact: 'Contact: {name}',
            disconnected: 'Connection lost — the listing is still online but can no longer be managed from immoOS.',
            'disconnected-openimmo':
              'Connection lost — the listing is still on {portal} but can no longer be managed from immoOS.',
            'transmitted-note':
              'Transmitted on {date} at {time}. {portal} confirms receipt by e-mail to your agency’s address on file with {portal}.',
            failure: {
              heading: 'The transmission failed.',
              verbatim: "The portal's message, unchanged.",
              'verbatim-server': "The server's message, unchanged.",
              at: 'on {datetime}',
            },
            'retry-cta': 'Transmit again',
            'withdraw-cta': 'Withdraw listing',
            'withdraw-retry-cta': 'Withdraw again',
            'withdrawn-note': 'Withdrawn on {date}. {portal} confirms the deletion by e-mail.',
            'withdrawn-at': 'on {date}',
            'withdrawal-failure': {
              heading: 'The portal refused the withdrawal. The listing is still online.',
              'no-call': 'The listing could not be withdrawn and is still online.',
              openimmo: 'The withdrawal could not be transmitted to {portal}. The listing is still online.',
            },
            'online-despite': {
              ARCHIVED: `online despite ${PROPERTY_STATUS_LABELS.ARCHIVED}`,
              INACTIVE: `online despite ${PROPERTY_STATUS_LABELS.INACTIVE}`,
            },
            stale: {
              notice: 'The property has changed since the last transmission.',
              'update-cta': 'Update listing',
              'update-error': 'The listing could not be updated. Please try again.',
            },
          },
          'transmitted-picture': {
            title: 'Remove picture from marketing?',
            notice: 'The picture disappears from {portals} only with the next transmission.',
            confirm: 'Remove from marketing',
            cancel: 'Cancel',
          },
          'withdraw-dialog': {
            title: 'Withdraw listing?',
            body: 'The property stays unchanged in immoOS and can be published again at any time.',
            'pair-body': 'Both ImmoScout24 listings of this property are withdrawn together.',
            confirm: 'Withdraw',
            cancel: 'Back',
            'generic-error': 'The listing could not be withdrawn. Please try again.',
          },
          dialog: {
            title: 'Publish on {portal}',
            loading: 'Checking readiness …',
            'load-error': 'Readiness could not be checked.',
            'load-retry': 'Try again',
            'show-address': 'Show address',
            'energy-paragraph-label': 'Appended to the property description:',
            'attach-label': 'Take over an existing ImmoScout24 listing (IS24 ID)',
            'attach-warning':
              'The transmission replaces the texts, pictures and contact person of the existing listing with the state in immoOS.',
            'publish-cta': 'Publish',
            'cancel-cta': 'Cancel',
            channel: 'Channel: {portal}',
            contact: 'Contact: {name}',
            'forbidden-error': 'You are not permitted to do this.',
            'generic-error': 'The transmission could not be started. Please try again.',
          },
          readiness: {
            'not-ready-title': 'Not yet ready for {portal}',
            'not-ready-intro': '{portal} still needs these details:',
            'hints-heading': 'Hints',
            ready: 'Ready for {portal}.',
            'unknown-member': 'the responsible member',
            'contact-fields': {
              EMAIL: 'email',
              LAST_NAME: 'last name',
            },
            'company-fields': {
              COMPANY_NAME: 'Company name',
              LEGAL_FORM: 'Legal form',
              ADDRESS: 'Address',
              EMAIL: 'Email',
              PHONE: 'Phone',
              MANAGING_DIRECTORS: 'Managing director(s)',
              COMMERCIAL_REGISTER_COURT: 'Register court',
              COMMERCIAL_REGISTER_NUMBER: 'Register number',
              SUPERVISORY_AUTHORITY: 'Supervisory authority (§34c GewO)',
            },
            items: {
              NOT_ACTIVE:
                'This property is not on the market yet. It can only be published once the brokerage agreement has been received.',
              NOT_CONNECTED:
                '{portalToken, select, IMMOSCOUT24 {The workspace is not connected to ImmoScout24. An owner or admin can connect the account under ‘Portals’.} other {The workspace is not connected to {portal}. An owner or admin can enter the access under ‘Portals’.}}',
              CONNECTION_EXPIRED:
                '{portalToken, select, IMMOSCOUT24 {The ImmoScout24 connection has expired. Publishing is only possible again once it has been renewed.} other {The access to {portal} is invalid. Publishing is only possible again once an owner or admin has corrected the access under ‘Portals’.}}',
              SUBTYPE_MISSING: '{portal} needs the property subtype.',
              CONTACT_INCOMPLETE:
                '{portalToken, select, IMMOSCOUT24 {Details for the ImmoScout24 contact are missing for {name}: {fields}.} other {The responsible member is no longer active. Assign the property to an active member before you publish it on {portal}.}}',
              COMMERCIAL_FINE_TYPE: 'Detailed type',
              STREET: 'Street',
              HOUSE_NUMBER: 'House number',
              POSTAL_CODE: 'Postcode',
              CITY: 'City',
              HOUSE_NUMBER_TOO_LONG: 'House number is longer than ImmoScout24 allows (10 characters).',
              CITY_TOO_LONG: 'City is longer than ImmoScout24 allows (50 characters).',
              TITLE_TOO_LONG:
                '{portalToken, select, IMMOSCOUT24 {Title is too long for ImmoScout24 (100 characters at most).} other {Title is too long for {portal} (at most {n} characters). Please shorten the text.}}',
              DESCRIPTION_TOO_LONG:
                '{portalToken, select, IMMOSCOUT24 {Property description is too long for ImmoScout24. Shorten the text.} other {Property description is too long for {portal} (at most {n} characters). Please shorten the text.}}',
              EQUIPMENT_DESCRIPTION_TOO_LONG:
                '{portalToken, select, IMMOSCOUT24 {Features is too long for ImmoScout24. Shorten the text.} other {Features is too long for {portal} (at most {n} characters). Please shorten the text.}}',
              LOCATION_DESCRIPTION_TOO_LONG:
                '{portalToken, select, IMMOSCOUT24 {Location is too long for ImmoScout24. Shorten the text.} other {Location is too long for {portal} (at most {n} characters). Please shorten the text.}}',
              MISCELLANEOUS_TOO_LONG:
                '{portalToken, select, IMMOSCOUT24 {Other is too long for ImmoScout24. Shorten the text.} other {Other is too long for {portal} (at most {n} characters). Please shorten the text.}}',
              LIVING_AREA: 'Living area',
              ROOMS: 'Rooms',
              PLOT_AREA: 'Plot area',
              NET_FLOOR_SPACE: 'Usable area (office, retail or storage space)',
              TOTAL_FLOOR_SPACE: 'Total area',
              PRICE: 'Price',
              RENTAL_COMMISSION: 'Commission',
              RENTAL_COMMISSION_AMOUNT: 'Commission amount',
              BUYER_COMMISSION_MISSING: 'Buyer’s commission missing',
              COMMISSION_REENTRY_REQUIRED: 'Re-enter the commission',
              BUYER_COMMISSION_ABOVE_SELLER: 'Buyer’s commission higher than seller’s',
              BUYER_COMMISSION_BELOW_SELLER: 'Buyer’s and seller’s commission differ',
              COMMISSION_NOTE_TOO_LONG: 'Commission note is longer than ImmoScout24 allows (500 characters).',
              GEG_CERTIFICATE_BASIS: 'Required under the GEG and missing: certificate type',
              GEG_ENERGY_VALUE: 'Required under the GEG and missing: energy value',
              GEG_ENERGY_SOURCE: 'Required under the GEG and missing: main energy source',
              GEG_YEAR_BUILT: 'Required under the GEG and missing: year built',
              GEG_ENERGY_CLASS: 'Required under the GEG and missing: energy efficiency class',
              GMODG_CERTIFICATE_BASIS: 'Required under the GModG and missing: certificate type',
              GMODG_PRIMARY_ENERGY_VALUE: 'Required under the GModG and missing: primary energy demand',
              GMODG_ENERGY_CLASS: 'Required under the GModG and missing: energy efficiency class',
              GMODG_YEAR_BUILT: 'Required under the GModG and missing: year built',
              GMODG_ENERGY_SOURCE: 'Required under the GModG and missing: main energy source',
              CERTIFICATE_EXPIRED:
                'The energy certificate has been expired since {date}. An advert needs a valid certificate.',
              PRICE_ON_INQUIRY: 'Kleinanzeigen requires a price. ‘Price on request’ is not possible there.',
              COMPANY_DATA_INCOMPLETE: 'Company details are missing for {portal}: {fields}.',
              CATEGORY_UNSUPPORTED: '{portal} has no suitable category for this property type.',
              PICTURES_OVER_LIMIT:
                '{portal} accepts at most {n} pictures. The first {n} in marketing order are transmitted.',
            },
            explanations: {
              BUYER_COMMISSION_MISSING:
                'State whether the buyer’s commission is a percentage or a fixed amount, and enter the value. Without a complete entry the listing cannot be published.',
              COMMISSION_REENTRY_REQUIRED:
                'The commission is still free text on the property. Enter it again so the listing names “incl. VAT” and the paying side correctly.',
              BUYER_COMMISSION_ABOVE_SELLER:
                'For flats and single-family houses, the buyer may not pay more than the seller (§ 656c/d BGB). Otherwise the commission agreement with the buyer is void.',
              BUYER_COMMISSION_BELOW_SELLER:
                'If you also sign a brokerage agreement with the buyer, both commissions must be equal (§ 656c BGB).',
            },
          },
          'web-expose': {
            name: 'Web exposé',
            status: {
              NOT_PUBLISHED: 'not published',
              PUBLISHED: 'published',
              WITHDRAWN: 'withdrawn',
            },
            since: 'since {date}',
            'withdrawn-at': 'on {date}',
            'link-label': 'Link to the web exposé',
            copy: 'Copy link',
            copied: 'Link copied',
            'publish-cta': 'Publish',
            'withdraw-cta': 'Withdraw',
            preview: 'Open preview',
            'share-refused': {
              editor: "The buyer's commission is not fully recorded. Enter it before you share the exposé.",
              viewer:
                "The buyer's commission is not fully recorded. You can share the exposé once it has been entered.",
            },
            openings:
              '{count, plural, =0 {Not opened yet} one {<strong>1 opening</strong> · last on {date} at {time}} other {<strong>{count} openings</strong> · last on {date} at {time}}}',
            dialog: {
              title: 'Publish web exposé',
              link: 'Link: {url}',
              'show-address-help': 'Without the address, the exposé only shows the postcode, town and district.',
              'generic-error': 'The web exposé could not be published. Please try again.',
            },
            items: {
              WEB_ADDRESS_MISSING: {
                manager: 'No web address has been set for the workspace yet.',
                member: 'An owner or admin must first set the web address under ‘Company data’.',
              },
              MARKETING_TEXT_MISSING: 'A marketing text is missing.',
              NO_PUBLISHED_PHOTO: 'No photo has been released for marketing.',
            },
            'withdraw-dialog': {
              title: 'Withdraw web exposé?',
              body: 'The link will then show ‘This exposé is no longer available.’ You can publish the exposé again later under the same link.',
              confirm: 'Withdraw',
              cancel: 'Cancel',
              'generic-error': 'The web exposé could not be withdrawn. Please try again.',
            },
          },
          'listing-type': {
            APARTMENT_BUY: 'Apartment',
            APARTMENT_RENT: 'Apartment',
            HOUSE_BUY: 'House',
            HOUSE_RENT: 'House',
            LIVING_BUY_SITE: 'Plot',
            LIVING_RENT_SITE: 'Plot',
            TRADE_SITE: 'Plot',
            GARAGE_BUY: 'Garage/parking space',
            GARAGE_RENT: 'Garage/parking space',
            OFFICE: 'Office/practice',
            STORE: 'Retail',
            GASTRONOMY: 'Hospitality/hotel',
            INDUSTRY: 'Hall/production',
            SPECIAL_PURPOSE: 'Special-purpose property',
            INVESTMENT: 'Investment property',
            OPENIMMO: 'Listing',
          },
        },
        marketing: {
          'empty-hint': 'This is where you decide what markets this property.',
          loading: 'Loading marketing…',
          'load-error': 'Marketing could not be loaded.',
          'load-retry': 'Try again',
          outputs: {
            portals: {
              heading: 'Online portals',
              empty: 'Not published on any portal yet.',
            },
            print: {
              heading: 'Print / PDF',
              loading: 'Loading exposés…',
              'load-error': 'The exposés could not be loaded.',
              'load-retry': 'Try again',
              versions: {
                FULL: { title: 'Full version', hint: 'With address and all details' },
                SHORT: { title: 'Short exposé', hint: 'Without address and sensitive details' },
              },
              empty: 'No exposé uploaded yet.',
              uploaded: 'Uploaded on {date} by {name}',
              'uploaded-by-unknown': 'Unknown member',
              download: 'Download',
              'download-error': 'The download could not be started. Please try again.',
              upload: 'Upload',
              replace: 'Replace',
              uploading: 'Uploading…',
              'upload-error': 'The exposé could not be uploaded. Please try again.',
              'replace-dialog': {
                title: 'Replace {version}?',
                body: 'The previous exposé will be deleted and will no longer be available.',
                cancel: 'Cancel',
                confirm: 'Replace',
              },
              remove: 'Remove',
              'remove-dialog': {
                title: 'Remove {version}?',
                body: 'The exposé will be deleted and will no longer be available.',
                cancel: 'Cancel',
                confirm: 'Remove',
                error: 'The exposé could not be removed. Please try again.',
              },
            },
          },
          readiness: {
            ready: 'Ready for marketing',
            'incomplete-title': 'Still missing for marketing:',
            gap: {
              'expose-title': 'Exposé title',
              description: 'at least one description',
              'published-image': 'at least one published image',
            },
          },
          texts: {
            heading: 'Texts',
            labels: {
              'expose-title': 'Exposé title',
              highlights: 'Highlights',
              description: 'Property description',
              equipment: 'Fittings',
              location: 'Location',
              miscellaneous: 'Miscellaneous',
            },
            'highlights-subtitle': 'Special features',
            helpers: {
              'expose-title': 'Optional. A punchy one-liner to open the exposé.',
              highlights: 'Optional. 3–5 short selling points that the exposé puts in the spotlight.',
            },
            'expose-title-counter': '{n, number}/120 characters',
            'highlights-add': 'Add highlight',
            'highlights-empty': 'No highlights yet',
            'highlights-nudge': 'Tip: 3–5 highlights work best. You can add more, but less is often more here.',
            'highlights-entry': 'Highlight {n, number}',
            'highlights-move-up': 'Move highlight {n, number} up',
            'highlights-move-down': 'Move highlight {n, number} down',
            'highlights-remove': 'Remove highlight {n, number}',
            'highlights-drag': 'Drag highlight {n, number} to change the order',
            placeholders: {
              'expose-title': 'e.g. Light-filled penthouse with Elbe views',
              highlight: 'e.g. Direct Elbe view',
              description: 'How would you describe this property to someone standing in front of it?',
              equipment: 'What is fitted, in what condition, from which year?',
              location: 'What matters in the area — transport, shopping, schools, quiet?',
              miscellaneous: 'Anything else that matters',
            },
            counter: '{n, number} characters',
            'portal-hint': 'ImmoScout24 truncates from {n, number} characters.',
            save: 'Save',
            saved: 'Marketing texts saved.',
            'saved-state': 'Saved',
            discard: 'Discard',
            'unsaved-prompt': {
              description: 'You have unsaved changes. Discard them?',
            },
            errors: {
              forbidden: 'You are not authorised to perform this action.',
              'not-found': 'This property no longer exists.',
              generic: 'The texts could not be saved. Please try again.',
            },
          },
          images: {
            'section-title': 'Images for marketing',
            explanation: 'Only what you select appears in the exposé. Tours and video links are never sent to portals.',
            areas: {
              photos: 'Photos',
              'floor-plans': 'Floor plans',
              videos: 'Videos',
              tours: '360° tours',
            },
            counter: {
              photos: '{k} of {n} photos are published.',
              'floor-plans': '{k} of {n} floor plans are published.',
              videos: '{k} of {n} videos are published.',
              tours: '{k} of {n} tours are published.',
            },
            'tours-permission-reason': 'You are not permitted to publish tours.',
            'publish-label': 'publish',
            'publish-all': 'Publish all',
            'cover-image-badge': 'Cover',
            empty: 'No image selected for marketing yet.',
            errors: {
              generic: 'The change could not be saved. Please try again.',
            },
          },
        },
      },
      'edit-address': {
        title: 'Edit address',
        'search-label': 'Search address',
        'search-helper': 'Type an address and pick a suggestion to fill the fields.',
        'search-placeholder': 'Street, house number, city …',
        'street-label': 'Street',
        'street-placeholder': 'e.g. Eppendorfer Landstraße',
        'house-number-label': 'House number',
        'house-number-placeholder': 'e.g. 12a',
        'postal-code-label': 'Postal code',
        'postal-code-placeholder': 'e.g. 20249',
        'city-label': 'City',
        'city-placeholder': 'e.g. Hamburg',
        'district-label': 'District',
        'district-placeholder': 'e.g. Eppendorf',
        'state-label': 'State',
        'state-placeholder': 'e.g. Hamburg',
        'country-label': 'Country',
        'country-placeholder': 'e.g. Germany',
        'parcel-number-label': 'Parcel (Flurstück)',
        'parcel-number-placeholder': 'optional',
        'parcel-subdivision-label': 'Cadastral district (Flur)',
        'parcel-subdivision-placeholder': 'optional',
        'latitude-label': 'Latitude',
        'latitude-placeholder': 'e.g. 53.5896',
        'longitude-label': 'Longitude',
        'longitude-placeholder': 'e.g. 9.9876',
        'coordinate-helper': 'Filled automatically from the address search.',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not allowed to perform this action.',
        'not-found-error': 'This property no longer exists.',
        'generic-error': 'The address could not be saved. Please try again.',
        errors: {
          street: { required: 'Please enter a street.', invalid: 'Invalid street.' },
          'house-number': { required: 'Please enter a house number.', invalid: 'Invalid house number.' },
          'postal-code': { required: 'Please enter a postal code.', invalid: 'Postal code must be 5 digits.' },
          city: { required: 'Please enter a city.', invalid: 'Invalid city.' },
          district: { required: 'Please enter a district.', invalid: 'Invalid district.' },
          state: { required: 'Please enter a state.', invalid: 'Invalid state.' },
          country: { required: 'Please enter a country.', invalid: 'Invalid country.' },
          'parcel-number': { required: 'Please enter a parcel.', invalid: 'Invalid parcel.' },
          'parcel-subdivision': {
            required: 'Please enter a cadastral district.',
            invalid: 'Invalid cadastral district.',
          },
          latitude: { required: 'Please enter a latitude.', invalid: 'Latitude must be between -90 and 90.' },
          longitude: { required: 'Please enter a longitude.', invalid: 'Longitude must be between -180 and 180.' },
        },
      },
      'edit-identity': {
        title: 'Edit key details',
        'title-label': 'Title',
        'title-placeholder': 'e.g. Charming period apartment in Eppendorf',
        'object-type-label': 'Property type',
        'marketing-type-label': 'Marketing type',
        'marketing-type-option': MARKETING_TYPE_LABELS,
        'subtype-label': 'Subtype',
        'subtype-unset': 'Not set',
        'fine-type-label': 'Detailed type',
        'fine-type-unset': '—',
        'reference-number-label': 'Exposé no.',
        'reference-number-placeholder': 'e.g. EXP-2026-001',
        'reference-number-helper': 'Optional. Must be unique within the workspace.',
        'available-from-label': 'Available from',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'edit-cta': 'Edit',
        'forbidden-error': 'You are not authorised for this action.',
        'not-found-error': 'This property no longer exists.',
        'generic-error': 'The key details could not be saved. Please try again.',
        errors: {
          title: {
            required: 'Please enter a title.',
            'too-long': 'The title may be at most 160 characters long.',
          },
          'object-type': {
            locked: 'The object type can only be changed while the property is a draft.',
            blocked: 'Clear the type-specific data first ({sections}) to change the object type.',
            section: {
              ertrag: 'Yield',
              grundstueck: 'Plot',
              'ausstattung-wohnen': 'Amenities (residential)',
              'ausstattung-gewerbe': 'Amenities (commercial)',
            },
          },
          'reference-number': {
            'too-long': 'The exposé no. may be at most 64 characters long.',
            invalid: 'Invalid exposé no.',
            conflict: 'This reference number is already in use — please choose a unique number.',
          },
        },
        'marketing-type-switch': {
          title: 'Change marketing type?',
          body: 'The document checklist and the price fields switch with it. Everything already entered is kept.',
          'back-cta': 'Back',
          'confirm-cta': {
            SALE: 'Switch to sale',
            RENTAL: 'Switch to letting',
          },
        },
      },
      'edit-building': {
        title: 'Edit building info',
        'year-built-label': 'Year built',
        'year-built-placeholder': '1912',
        'last-renovated-label': 'Last renovated',
        'last-renovated-placeholder': '2018',
        'condition-label': 'Condition',
        'interior-quality-label': 'Interior quality',
        'construction-phase-label': 'Construction phase',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-found-error': 'This property no longer exists.',
        'generic-error': 'Could not save building info. Please try again.',
        condition: CONDITION_LABELS,
        'interior-quality': INTERIOR_QUALITY_LABELS,
        'construction-phase': CONSTRUCTION_PHASE_LABELS,
        errors: {
          'year-built': {
            required: 'Please enter the year built.',
            invalid: 'Year built must be between 1700 and today + 5 years.',
          },
          'last-renovated': {
            required: 'Please enter the last renovation year.',
            invalid: 'Renovation year must be between 1700 and today.',
          },
          condition: {
            required: 'Please select a condition.',
            invalid: 'Invalid condition.',
          },
          'interior-quality': {
            required: 'Please select an interior quality.',
            invalid: 'Invalid interior quality.',
          },
          'construction-phase': {
            required: 'Please select a construction phase.',
            invalid: 'Invalid construction phase.',
          },
        },
      },
      'edit-pricing-sale': {
        title: 'Edit sale pricing',
        'purchase-price-label': 'Purchase price',
        'price-per-sqm-label': 'Price per m²',
        'price-per-sqm-needs-area': 'Enter a living area to compute',
        'price-on-inquiry-label': 'Price on inquiry',
        'has-commission-label': 'Commission active',
        'commission-group': 'Buyer’s commission',
        'commission-legacy': 'Previous entry: {text}',
        'commission-basis-label': 'Stated as',
        'commission-basis-placeholder': 'Please select',
        'commission-basis': {
          PERCENT_OF_PRICE: 'Percent of purchase price',
          FIXED_AMOUNT: 'Fixed amount',
        },
        'commission-rate-label': 'Rate (net)',
        'commission-amount-label': 'Amount (net)',
        'commission-helper': 'Enter the net value. immoOS adds 19% VAT; the listing shows the gross value.',
        'commission-preview': 'How it appears in the listing: {statement}',
        'commission-note-label': 'Commission note',
        'commission-note-placeholder': 'e.g. Paid by buyer, due at notarisation.',
        'net-purchase-price-label': 'Net purchase price',
        'vat-treatment-label': 'VAT treatment',
        'vat-rate-label': 'VAT rate',
        'decimal-placeholder': '0.00',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-found-error': 'This property no longer exists.',
        'not-applicable-error': 'Sale pricing only applies to sale properties.',
        'generic-error': 'Could not save pricing. Please try again.',
      },
      'edit-pricing-rental': {
        title: 'Edit rental pricing',
        'tenant-free': 'No commission for the tenant (Bestellerprinzip)',
        'commission-legacy':
          'Previous entry: {text}. No longer published: for a residential letting, the tenant pays no commission when the landlord instructed you.',
        'has-commission-label': 'Commission',
        'commission-label': 'Commission amount',
        'commission-placeholder': "e.g. 2 months' net rent plus VAT",
        'commission-note-label': 'Commission note',
        'commission-note-placeholder': 'e.g. Due when the lease is signed.',
        'base-rent-label': 'Base rent',
        'price-per-sqm-label': 'Rent per m²/month',
        'price-per-sqm-needs-area': 'Enter an area to compute',
        'total-rent-label': 'Total rent',
        'ancillary-costs-label': 'Service charges',
        'ancillary-costs-type-label': 'Service charge type',
        'ancillary-costs-type-placeholder': 'Please choose',
        'ancillary-costs-type': ANCILLARY_COSTS_TYPE_LABELS,
        'heating-costs-label': 'Heating costs',
        'heating-costs-included-label': 'Heating costs included in the service charges',
        'heating-costs-included-error': 'Heating costs are included in the service charges — leave the field empty.',
        'total-rent-hint': 'Suggested from base rent + service charges + heating costs',
        'total-rent-divergence': 'Differs from the sum ({difference}).',
        'deposit-mode-label': 'Deposit',
        'deposit-mode': { AMOUNT: 'in euros', MONTHS: 'in monthly rents' },
        'deposit-equivalent-amount': 'equals {amount}',
        'deposit-equivalent-months': '{count, plural, one {equals # monthly rent} other {equals # monthly rents}}',
        'deposit-months-max-error': 'At most 24 monthly rents.',
        'deposit-cap-warning':
          "For residential lettings, a deposit of at most three months' base rent is permitted (§ 551 BGB). The value is saved.",
        'legacy-deposit-label': 'Deposit (legacy)',
        'parking-rent-label': 'Parking rent',
        'parking-rent-hint': 'not part of the base rent',
        'vat-treatment-label': 'VAT treatment',
        'vat-rate-label': 'VAT rate',
        'derivation-hint': '{rate} × {area} m² = {total} base rent',
        'compensation-payment-label': 'Compensation payment',
        'rent-free-months-label': 'Rent-free period',
        'rent-free-months-suffix': 'months',
        'fit-out-contribution-label': 'Fit-out contribution',
        'renewal-option-label': 'Renewal option',
        'renewal-option-placeholder': 'e.g. 2 × 5 years',
        'price-on-inquiry-label': 'Price on inquiry',
        'decimal-placeholder': '0.00',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-found-error': 'This property no longer exists.',
        'generic-error': 'Could not save pricing. Please try again.',
      },
      'edit-rental-terms': {
        title: 'Edit rental terms',
        'furnishing-label': 'Furnishing',
        'lease-term-label': 'Lease term',
        'fixed-term-until-label': 'Fixed term until',
        'minimum-term-label': 'Minimum term (months)',
        'rent-type-label': 'Rent type',
        'subletting-label': 'Subletting',
        'unset-option': 'Not stated',
        furnishing: FURNISHING_LABELS,
        'lease-term': LEASE_TERM_LABELS,
        'rent-type': RENT_TYPE_LABELS,
        subletting: SUBLETTING_LABELS,
        'fixed-term-until-required-error': 'State until when the tenancy is fixed.',
        'fixed-term-until-past-error': 'The date cannot be in the past.',
        'minimum-term-max-error': 'At most 600 months.',
        'not-applicable-error': 'This property is not being let — rental terms cannot be recorded here.',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-found-error': 'This property no longer exists.',
        'generic-error': 'Rental terms could not be saved. Please try again.',
      },
      'edit-additional-costs': {
        title: 'Edit additional costs',
        'type-column': 'Item',
        'amount-column': 'Amount',
        'per-sqm-column': 'per m²/month',
        'vat-treatment-column': 'VAT',
        'note-column': 'Note',
        'type-placeholder': 'Select item…',
        'amount-placeholder': '0.00',
        'per-sqm-placeholder': 'optional',
        'note-placeholder': 'optional',
        'add-row-cta': 'Add entry',
        'remove-row-cta': 'Remove entry',
        'empty-state': 'No entries yet — add one.',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-found-error': 'This property no longer exists.',
        'generic-error': 'Could not save additional costs. Please try again.',
        type: ADDITIONAL_COST_TYPE_LABELS,
      },
      'edit-energy': {
        title: 'Edit energy info',
        'certificate-basis-label': 'Certificate type',
        'certificate-year-label': 'Certificate vintage',
        'energy-class-label': 'Energy class',
        'energy-value-label': 'Energy value',
        'energy-value-placeholder': '87.5',
        'primary-energy-value-label': 'Primary energy demand',
        'primary-energy-value-placeholder': '84.3',
        'energy-class-non-residential-helper': 'Non-residential buildings: scale A to G',
        'energy-value-unit': 'kWh/(m²·a)',
        'includes-warm-water-label': 'Includes warm water',
        'no-certificate-hint': 'The certificate data will be removed when you save.',
        'certificate-issued-at-label': 'Issued at',
        'certificate-valid-until-label': 'Valid until',
        'heating-type-label': 'Heating type',
        'firing-type-label': 'Firing type',
        'heating-type-helper': 'Multiple selection allowed.',
        'firing-type-helper': 'Multiple selection allowed.',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-found-error': 'This property no longer exists.',
        'generic-error': 'Could not save energy info. Please try again.',
        'certificate-basis': ENERGY_CERTIFICATE_BASIS_LABELS,
        'certificate-year': ENERGY_CERTIFICATE_YEAR_LABELS,
        'energy-class': ENERGY_CLASS_LABELS,
        'heating-type': HEATING_TYPE_LABELS,
        'firing-type': FIRING_TYPE_LABELS,
        errors: {
          'certificate-basis': {
            required: 'Please select a certificate type.',
            invalid: 'Invalid certificate type.',
          },
          'certificate-year': {
            required: 'Please select a certificate vintage.',
            invalid: 'Invalid certificate vintage.',
          },
          'energy-class': {
            required: 'Please select an energy class.',
            invalid: 'Invalid energy class.',
          },
          'energy-value': {
            required: 'Please enter the energy value.',
            invalid: 'Energy value is invalid.',
          },
          'primary-energy-value': {
            required: 'Please enter the primary energy demand.',
            invalid: 'Primary energy demand is invalid.',
          },
          'certificate-issued-at': {
            required: 'Please enter the issue date.',
            invalid: 'Issue date is invalid.',
          },
          'certificate-valid-until': {
            required: 'Please enter the expiry date.',
            invalid: 'Expiry date is invalid.',
          },
        },
      },
      'edit-investment-yield': {
        title: 'Edit investment yield',
        'is-rented-label': 'Rented',
        'rental-income-label': 'Rental income',
        'rental-income-target-label': 'Target rental income',
        'rental-income-actual-label': 'Actual rental income',
        'price-multiplier-label': 'Price multiplier',
        'number-of-apartments-label': 'Number of apartments',
        'number-of-commercial-units-label': 'Number of commercial units',
        'occupancy-rate-label': 'Occupancy rate',
        'number-of-vacancies-label': 'Number of vacancies',
        'tenant-structure-label': 'Tenant structure',
        'tenant-structure-placeholder': 'e.g. 8 residential, 2 commercial tenants',
        'walt-label': 'WALT',
        'walt-unit': 'years',
        'occupancy-rate-unit': '%',
        'currency-unit': '€',
        'integer-placeholder': '0',
        'decimal-placeholder': '0.00',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-found-error': 'This property no longer exists.',
        'generic-error': 'Could not save investment yield. Please try again.',
        errors: {
          'rental-income': {
            required: 'Please enter the rental income.',
            invalid: 'Rental income is invalid.',
          },
          'rental-income-target': {
            required: 'Please enter the target rental income.',
            invalid: 'Target rental income is invalid.',
          },
          'rental-income-actual': {
            required: 'Please enter the actual rental income.',
            invalid: 'Actual rental income is invalid.',
          },
          'price-multiplier': {
            required: 'Please enter the price multiplier.',
            invalid: 'Price multiplier is invalid.',
          },
          'number-of-apartments': {
            required: 'Please enter the number of apartments.',
            invalid: 'Number of apartments must be an integer.',
          },
          'number-of-commercial-units': {
            required: 'Please enter the number of commercial units.',
            invalid: 'Number of commercial units must be an integer.',
          },
          'occupancy-rate': {
            required: 'Please enter the occupancy rate.',
            invalid: 'Occupancy rate is invalid.',
          },
          'number-of-vacancies': {
            required: 'Please enter the number of vacancies.',
            invalid: 'Number of vacancies must be an integer.',
          },
          'tenant-structure': {
            required: 'Please describe the tenant structure.',
            invalid: 'Tenant structure is invalid.',
          },
          walt: {
            required: 'Please enter the WALT.',
            invalid: 'WALT is invalid.',
          },
        },
      },
      'edit-plot-details': {
        title: 'Edit plot details',
        'ground-area-ratio-label': 'GRZ (site coverage ratio)',
        'ground-area-ratio-placeholder': '0.4',
        'floor-area-ratio-label': 'GFZ (floor area ratio)',
        'floor-area-ratio-placeholder': '1.2',
        'building-mass-ratio-label': 'BMZ (building mass ratio)',
        'building-mass-ratio-placeholder': '3.5',
        'has-building-permission-label': 'Building permit granted',
        'short-term-buildable-label': 'Short-term buildable',
        'requires-demolition-label': 'Demolition required',
        'site-development-type-label': 'Site development',
        'site-constructible-type-label': 'Constructibility',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-found-error': 'This property no longer exists.',
        'generic-error': 'Could not save plot details. Please try again.',
        'site-development-type': SITE_DEVELOPMENT_TYPE_LABELS,
        'site-constructible-type': SITE_CONSTRUCTIBLE_TYPE_LABELS,
        errors: {
          'ground-area-ratio': {
            required: 'Please enter the GRZ.',
            invalid: 'GRZ is invalid.',
          },
          'floor-area-ratio': {
            required: 'Please enter the GFZ.',
            invalid: 'GFZ is invalid.',
          },
          'building-mass-ratio': {
            required: 'Please enter the BMZ.',
            invalid: 'BMZ is invalid.',
          },
          'site-development-type': {
            required: 'Please select a site development.',
            invalid: 'Invalid site development.',
          },
          'site-constructible-type': {
            required: 'Please select a constructibility.',
            invalid: 'Invalid constructibility.',
          },
        },
      },
      'edit-amenities': {
        title: 'Edit amenities',
        'feature-column': 'Feature',
        'value-column': 'Value',
        'amenity-code-placeholder': 'Choose a feature…',
        'value-placeholder': 'Enter value…',
        'group-residential': 'Residential',
        'group-commercial': 'Commercial',
        'add-row-cta': 'Add entry',
        'remove-row-cta': 'Remove entry',
        'empty-state': 'No entries yet — add one.',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-found-error': 'This property no longer exists.',
        'generic-error': 'Could not save amenities. Please try again.',
        residential: RESIDENTIAL_AMENITY_LABELS,
        commercial: COMMERCIAL_AMENITY_LABELS,
      },
      'edit-areas': {
        title: 'Edit areas',
        'code-column-header': 'Area',
        'value-column-header': 'Value',
        'add-row-cta': 'Add entry',
        'remove-row-cta': 'Remove entry',
        'code-placeholder': 'Select an area…',
        'value-placeholder': '0',
        'empty-state': 'No entries yet — add one.',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-found-error': 'This property no longer exists.',
        'generic-error': 'Could not save areas. Please try again.',
        'unit-sqm': 'm²',
        'unit-cbm': 'm³',
        'use-split-remainder-label': 'Remaining area',
        'use-split-exceeded-label': 'Sum of the use parts',
        'use-split-exceeded-value': '{sum} m² of {usableArea} m²',
        'use-split-exceeded-message':
          'The use parts exceed the usable area. Reduce a part or raise the usable area to save.',
        code: AREA_CODE_LABELS,
      },
      create: {
        title: 'New property',
        'standort-label': 'Location',
        'title-label': 'Property title',
        'title-placeholder': 'e.g. Pre-war flat in Lehel',
        'title-helper': 'Used in tables, exposés and search',
        'type-label': 'Property type',
        'status-label': 'Status',
        'status-helper': 'Choose “In acquisition” for a property from a lead contact.',
        'status-option': {
          DRAFT: 'Draft',
          IN_ACQUISITION: 'In acquisition',
        },
        'marketing-type-label': 'Marketing type',
        'marketing-type-option': MARKETING_TYPE_LABELS,
        'reference-number-label': 'Exposé no.',
        'reference-number-placeholder': 'e.g. EXP-2026-001',
        'living-area-label': 'Living area',
        'living-area-placeholder': '176',
        'living-area-unit': 'm²',
        'rooms-label': 'Rooms',
        'rooms-placeholder': '5',
        'rooms-unit': 'rms',
        'usable-area-label': 'Usable area',
        'usable-area-placeholder': '1,550',
        'usable-area-unit': 'm²',
        'parking-spaces-label': 'Parking spaces',
        'parking-spaces-placeholder': '24',
        'parking-spaces-unit': 'spaces',
        'asking-price-label': 'Asking price',
        'asking-price-placeholder': '2,180,000',
        'asking-price-unit': '€',
        'owner-label': 'Owner',
        'owner-placeholder': 'Select a person…',
        'owner-loading': 'Loading contacts…',
        'owner-no-results': 'No matching contacts.',
        'owner-load-error': 'Failed to load contacts.',
        'owner-clear': 'Clear selection',
        'broker-team-label': 'Broker team',
        'broker-team-placeholder': 'Select people…',
        'tags-label': 'Tags',
        'tags-placeholder': 'Add tags…',
        'coming-soon': 'Coming soon',
        'street-house-label': 'Street & house number',
        'street-house-placeholder': 'Liebigstraße 9',
        'postal-code-label': 'Postal code',
        'postal-code-placeholder': '80538',
        'city-label': 'City',
        'city-placeholder': 'Munich',
        'submit-cta': 'Create property',
        'submit-loading': 'Creating…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised to perform this action.',
        'not-a-member-error': 'You are not a member of this workspace.',
        'generic-error': 'Could not create property. Please try again.',
        errors: {
          title: {
            required: 'Title is required.',
            'too-long': 'Title must be at most 160 characters.',
            invalid: 'Title is invalid.',
          },
          type: {
            required: 'Please choose a property type.',
            invalid: 'Invalid property type.',
          },
          'marketing-type': {
            required: 'Please choose a marketing type.',
            invalid: 'Invalid marketing type.',
          },
          'street-house': {
            required: 'Please enter street and house number.',
            'too-long': 'Street and house number are too long.',
            invalid: 'Street and house number are invalid.',
          },
          'postal-code': {
            required: 'Please enter a postal code.',
            invalid: 'Postal code must be 5 digits.',
          },
          city: {
            required: 'Please enter a city.',
            'too-long': 'City must be at most 80 characters.',
            invalid: 'City is invalid.',
          },
          'living-area-sqm': {
            required: 'Please enter the living area.',
            'too-long': 'Living area is too large.',
            invalid: 'Living area is invalid.',
          },
          rooms: {
            required: 'Please enter the room count.',
            'too-long': 'Room count is too high.',
            invalid: 'Room count is invalid.',
          },
          'asking-price': {
            required: 'Please enter a price.',
            'too-long': 'Price is too high.',
            invalid: 'Price is invalid.',
          },
          'reference-number': {
            'too-long': 'The exposé no. may be at most 64 characters long.',
            invalid: 'Invalid exposé no.',
            conflict: 'This reference number is already in use — please choose a unique number.',
          },
        },
      },
    },
    contacts: {
      type: CONTACT_TYPE_LABELS,
      source: CONTACT_SOURCE_LABELS,
      'contact-type': {
        PERSON: 'Person',
        COMPANY: 'Company',
      },
      salutation: {
        MR: 'Mr',
        MRS: 'Mrs',
        MX: 'Mx',
        NONE: 'No salutation',
      },
      'phone-type': {
        MOBILE: 'Mobile',
        OFFICE: 'Office',
        LANDLINE: 'Landline',
        OTHER: 'Other',
      },
      'phone-messaging': {
        WHATSAPP: 'WhatsApp',
        SMS: 'SMS',
        NONE: 'None',
      },
      list: {
        title: 'Contacts',
        subtitle: 'All contacts and companies your workspace works with.',
        'create-cta': 'New contact',
        'list-load-error': 'Could not load contacts.',
        'list-retry': 'Try again',
        'empty-title': 'No contacts yet.',
        'empty-description': 'Create your first contact to get started.',
        'no-match-title': 'No matches',
        'no-match-description': 'Try adjusting your search or filters to find contacts.',
        'success-toast': 'Contact "{name}" created.',
        'success-dismiss': 'Dismiss',
        filters: {
          'search-placeholder': 'Search name, email, phone…',
          'search-label': 'Search contacts',
          'type-all': 'All types',
          reset: 'Reset',
        },
        sort: {
          label: 'Sort',
          newest: 'Newest first',
          oldest: 'Oldest first',
          'name-asc': 'Name A → Z',
          'name-desc': 'Name Z → A',
        },
        pagination: {
          range: '{from}–{to} of {total}',
          'per-page': 'Per page:',
          prev: 'Previous',
          next: 'Next',
        },
        columns: {
          name: 'Name',
          type: 'Type',
          email: 'Email',
          phone: 'Phone',
          city: 'City',
          'updated-at': 'Updated',
        },
        empty: '—',
      },
      detail: {
        'warning-label': 'Note',
        'delete-cta': 'Delete permanently',
        actions: {
          'trigger-label': 'Actions',
          'send-email': 'Send email',
          'send-message': 'Send message',
          delete: 'Delete permanently',
          edit: 'Edit',
          archive: 'Archive',
          'copy-link': 'Copy link',
          'link-copied': 'Link copied',
          'delete-permission-reason': 'You are not permitted to delete contacts.',
          'reassign-permission-reason': 'You are not permitted to move contacts to another Standort.',
        },
        stats: {
          'linked-properties-label': 'Properties',
        },
        'notes-empty': 'No notes yet.',
        'id-prefix': 'ID',
        'coming-soon': 'Coming soon',
        'tab-empty': '"{tab}" is coming soon.',
        'tasks-empty': 'No tasks yet.',
        'tasks-open': '· {count} open',
        timeline: {
          'company-load-error': "The company mailboxes' e-mails could not be loaded.",
          'mode-label': 'When',
          'mode-log': 'Log now',
          'mode-plan': 'Plan for later',
          'due-at-label': 'Due on',
          'error-due-at-required': 'Please provide a due date in the future.',
          'assignee-label': 'Assignee',
          'assignee-self': 'Me',
          'plan-hint': 'This activity stays an open task until you complete it.',
          'plan-submit': 'Plan as task',
          'add-cta': 'Add activity',
          'contact-picker-label': 'Contact',
          'deal-picker-label': 'Linked deals',
          'deal-picker-none': 'No deal',
          'deal-unnamed': 'Deal without property title',
          save: 'Save',
          saving: 'Saving …',
          cancel: 'Cancel',
          'content-label': 'Content',
          'content-counter': '{count, number} / {max, number}',
          'content-placeholder': 'What happened?',
          'occurred-at-label': 'Happened on',
          'now-cta': 'Now',
          'occurred-at-time-label': 'Time',
          'outcome-label': 'Outcome',
          'outcome-none': 'No outcome',
          'outcome-reached': 'Reached',
          'outcome-not-reached': 'Not reached',
          'outcome-voicemail': 'Voicemail',
          'outcome-callback-agreed': 'Callback agreed',
          'outcome-email-reached': 'Reply received',
          'outcome-email-not-reached': 'No reply',
          'outcome-attended': 'Attended',
          'outcome-not-attended': 'Not attended',
          'outcome-rescheduled': 'Rescheduled',
          'type-label': 'Type',
          'type-note': 'Note',
          'type-appointment': 'Appointment',
          'appointment-type-label': 'Appointment type',
          'appointment-type-placeholder': 'Please choose',
          'appointment-type-valuation': 'Valuation appointment',
          'appointment-type-on-site': 'On-site appointment',
          'appointment-type-property-survey': 'Property survey',
          'appointment-type-contract-signing': 'Contract signing',
          'appointment-type-other': 'Other appointment',
          'appointment-type-viewing-on-site': 'On-site viewing',
          'appointment-type-viewing-virtual': 'Virtual viewing',
          'appointment-type-consultation': 'Consultation',
          'appointment-type-financing-consultation': 'Financing consultation',
          'appointment-type-negotiation': 'Negotiation',
          'appointment-type-notary': 'Notary appointment',
          'appointment-type-handover': 'Handover',
          'error-no-appointment-type': 'Please choose an appointment type.',
          'type-call': 'Call',
          'type-sms': 'SMS',
          'type-whatsapp': 'WhatsApp',
          'type-fax': 'Fax',
          'type-letter': 'Letter',
          'type-email': 'Email',
          'direction-label': 'Direction',
          'direction-inbound': 'Inbound',
          'direction-outbound': 'Outbound',
          'subject-label': 'Subject',
          'entry-note': 'Note',
          'entry-call-inbound': 'Inbound call',
          'entry-call-outbound': 'Outbound call',
          'entry-sms-inbound': 'Inbound SMS',
          'entry-sms-outbound': 'Outbound SMS',
          'entry-whatsapp-inbound': 'Inbound WhatsApp',
          'entry-whatsapp-outbound': 'Outbound WhatsApp',
          'entry-fax-inbound': 'Inbound fax',
          'entry-fax-outbound': 'Outbound fax',
          'entry-letter-inbound': 'Inbound letter',
          'entry-letter-outbound': 'Outbound letter',
          'recorder-unknown': 'Unknown member',
          'group-today': 'Today',
          'group-yesterday': 'Yesterday',
          'relative-just-now': 'Just now',
          'relative-minutes': '{count} minutes ago',
          'relative-hours': '{count} hours ago',
          'relative-yesterday': 'Yesterday',
          'empty-heading': 'No activities yet',
          'empty-body': 'Everything that happens with this contact shows up here — calls, notes, messages and more.',
          'empty-cta': 'Add activity',
          'error-empty-content': 'Please enter some content.',
          'error-content-too-long': 'An activity must not exceed 10,000 characters.',
          'error-no-link': 'An activity must be linked to at least one contact, deal or property.',
          'error-no-direction': 'Please choose the direction (inbound or outbound).',
          'error-subject-too-long': 'The subject must not exceed 200 characters.',
          'error-future': 'An activity cannot be in the future.',
          'error-occurred-at-incomplete': 'Please provide both a date and a time.',
          'save-error': 'The activity could not be saved.',
          loading: 'Loading activities …',
          'load-error': 'Activities could not be loaded.',
          'load-retry': 'Try again',
          edited: 'edited',
          'edit-cta': 'Edit',
          'delete-cta': 'Delete',
          'delete-dialog-title': 'Delete activity?',
          'delete-dialog-body': 'This action cannot be undone.',
          'delete-confirm': 'Delete',
          'delete-cancel': 'Cancel',
          'delete-error': 'The activity could not be deleted.',
          'update-error': 'The activity could not be saved.',
        },
        tabs: {
          overview: 'Overview',
          activity: 'Activity',
          contact: 'Contact',
          emails: 'Emails',
          calls: 'Calls',
          notes: 'Notes',
          appointments: 'Appointments',
          properties: 'Properties',
          firmen: 'Companies',
        },
        sections: {
          identity: 'Identity',
          types: 'Types',
          emails: 'Email addresses',
          phones: 'Phone numbers',
          address: 'Address',
          personal: 'Personal',
          notes: 'Notes',
          crm: 'CRM',
          'key-facts': 'Key facts',
          activity: 'Activity',
          tasks: 'Tasks',
          details: 'Details',
          'linked-properties': 'Linked properties',
          categories: 'Categories',
        },
        properties: {
          'section-title': 'Properties',
          loading: 'Loading properties…',
          empty: 'No properties linked yet.',
          'load-error': 'Failed to load properties.',
          'load-retry': 'Try again',
          'row-actions': 'Actions',
          'open-property': 'Open property',
          'add-cta': 'Link property',
          'edit-link': 'Edit link',
          'unlink-link': 'Remove link',
          'archived-suffix': '(archived)',
          categories: {
            rentals: 'Rentals',
            sales: 'Sales',
          },
          role: PROPERTY_CONTACT_ROLE_LABELS,
          'link-dialog': {
            title: 'Link property',
            'property-label': 'Property',
            'property-placeholder': 'Search by title or address…',
            'property-loading': 'Loading properties…',
            'property-load-error': 'Failed to load properties.',
            'property-no-results': 'No properties found.',
            'property-clear': 'Clear selection',
            'role-label': 'Role',
            'note-label': 'Note',
            'note-hint': 'Max. 500 characters',
            'submit-cta': 'Link',
            'submit-loading': 'Linking…',
            'cancel-cta': 'Cancel',
            errors: {
              'property-required': 'Please select a property.',
              'not-found': 'Contact or property was not found.',
              'already-linked': 'This link already exists. You can edit the note on the existing link instead.',
              'note-too-long': 'The note must be at most 500 characters.',
              forbidden: 'You are not authorized to perform this action.',
              generic: 'Could not create the link. Please try again.',
            },
          },
          'edit-dialog': {
            title: 'Edit link',
            'role-label': 'Role',
            'note-label': 'Note',
            'note-hint': 'Max. 500 characters',
            'submit-cta': 'Save',
            'submit-loading': 'Saving…',
            'cancel-cta': 'Cancel',
            errors: {
              'already-linked': 'This link already exists. You can edit the note on the existing link instead.',
              'note-too-long': 'The note must be at most 500 characters.',
              'role-marketing-mismatch': "This role does not match the property's marketing type.",
              'not-found': 'This link was not found.',
              forbidden: 'You are not authorized to perform this action.',
              generic: 'Could not save the change. Please try again.',
            },
          },
          'unlink-dialog': {
            title: 'Remove link',
            body: 'Remove the link to “{name}”? The contact and the property are kept.',
            'confirm-cta': 'Remove link',
            'confirm-loading': 'Removing…',
            'cancel-cta': 'Cancel',
            'error-forbidden': 'You are not authorized to perform this action.',
            error: 'Could not remove the link. Please try again.',
          },
        },
        'companies-tab': {
          'section-title': 'Companies',
          loading: 'Loading companies…',
          empty: 'No companies linked yet.',
          'load-error': 'Failed to load companies.',
          'row-actions': 'Actions',
          'open-company': 'Open company',
          'add-cta': 'Link company',
          'edit-link': 'Edit link',
          'unlink-link': 'Remove link',
          'archived-suffix': '(archived)',
          'primary-badge': 'primary employer',
        },
        'edit-cta': 'Edit',
        'empty-state': {
          emails: 'No email addresses yet.',
          phones: 'No phone numbers yet.',
        },
        cards: {
          'source-label': 'Source',
          'primary-email-label': 'Primary email',
          'primary-phone-label': 'Primary phone',
          'last-contacted-label': 'Last contacted',
          'last-contacted-empty': 'Never',
          'types-label': 'Types',
          'assigned-to-label': 'Assigned to',
        },
        details: {
          'type-label': 'Contact type',
          'source-label': 'Source',
          'email-label': 'Email',
          'phone-label': 'Phone',
          'city-label': 'City',
          'last-contacted-label': 'Last contacted',
          'created-label': 'Created',
          'updated-label': 'Updated',
        },
        'salutation-label': 'Salutation',
        'first-name-label': 'First name',
        'last-name-label': 'Last name',
        'company-name-label': 'Company name',
        'types-label': 'Types',
        'types-empty': 'No types assigned yet.',
        'source-label': 'Source',
        'emails-label': 'Email addresses',
        'phones-label': 'Phone numbers',
        'primary-badge': 'Primary',
        'street-label': 'Street',
        'postal-code-label': 'Postal code',
        'city-label': 'City',
        'country-label': 'Country',
        'date-of-birth-label': 'Date of birth',
        'birth-name-label': 'Birth name',
        'birth-place-label': 'Place of birth',
        'nationality-label': 'Nationality',
        'language-label': 'Language',
        'last-contacted-at-label': 'Last contacted',
        empty: '—',
      },
      delete: {
        title: 'Delete this contact permanently?',
        body: 'This action cannot be undone. GDPR erasure on request of the data subject is handled separately via the GDPR module.',
        'confirm-cta': 'Delete permanently',
        'confirm-loading': 'Deleting…',
        'cancel-cta': 'Cancel',
        'generic-error': 'Could not delete contact. Please try again.',
      },
      'edit-personal': {
        title: 'Edit personal details',
        'submit-cta': 'Save',
        'submit-loading': 'Saving …',
        'cancel-cta': 'Cancel',
        'saved-toast': 'Personal details saved',
        errors: { 'too-long': 'At most 80 characters.' },
        'forbidden-error': 'You do not have permission to do this.',
        'not-found-error': 'Contact not found.',
        'generic-error': 'Save failed. Please try again.',
      },
      'edit-types': {
        title: 'Edit types',
        'types-label': 'Types',
        helper: 'Pick one or more types for this contact.',
        'submit-cta': 'Save',
        'submit-loading': 'Saving …',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You do not have permission to do this.',
        'not-found-error': 'Contact not found.',
        'generic-error': 'Save failed. Please try again.',
      },
      'edit-emails': {
        title: 'Edit email addresses',
        'empty-state': 'No email addresses yet. Click below to add the first one.',
        'email-placeholder': 'name@example.com',
        'primary-label': 'Primary',
        'add-row-cta': 'Add email',
        'remove-row-cta': 'Remove',
        'submit-cta': 'Save',
        'submit-loading': 'Saving …',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You do not have permission to do this.',
        'not-found-error': 'Contact not found.',
        'generic-error': 'Save failed. Please try again.',
        errors: {
          email: {
            required: 'Email is required.',
            invalid: 'Please enter a valid email address.',
          },
        },
      },
      'edit-address': {
        title: 'Edit address',
        helper: 'Clear street, postal code and city to remove the address.',
        'submit-cta': 'Save',
        'submit-loading': 'Saving …',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You do not have permission to do this.',
        'not-found-error': 'Contact not found.',
        'generic-error': 'Save failed. Please try again.',
        errors: {
          'postal-code': {
            invalid: 'Postal code must be exactly five digits.',
          },
        },
      },
      'edit-phones': {
        title: 'Edit phone numbers',
        'empty-state': 'No phone numbers yet. Click below to add the first one.',
        columns: {
          phone: 'Phone number',
          type: 'Type',
          messaging: 'Messaging',
          primary: 'Primary',
        },
        'phone-placeholder': '+49 151 12345678',
        'phone-helper': 'Preferred format "+49 …" or "0151 …".',
        'primary-label': 'Primary',
        'add-row-cta': 'Add phone',
        'remove-row-cta': 'Remove',
        'submit-cta': 'Save',
        'submit-loading': 'Saving …',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You do not have permission to do this.',
        'not-found-error': 'Contact not found.',
        'generic-error': 'Save failed. Please try again.',
        errors: {
          phone: {
            invalid: 'Phone number is invalid.',
          },
        },
      },
      create: {
        title: 'New contact',
        'return-to': 'Afterwards you return to “{dialog}”.',
        'standort-label': 'Location',
        'contact-type-label': 'Contact type',
        'salutation-label': 'Salutation',
        'salutation-empty': 'No salutation',
        'first-name-label': 'First name',
        'last-name-label': 'Last name',
        'company-name-label': 'Company name',
        'email-label': 'Primary email',
        'email-placeholder': 'name@example.com',
        'email-helper': 'Optional. Add more email addresses after creating the contact.',
        'phone-label': 'Primary phone',
        'phone-placeholder': '+49 151 12345678',
        'phone-helper': 'Optional. Preferred format "+49 …" or "0151 …".',
        'phone-type-label': 'Type',
        'address-helper': 'Optional. You can add the address later.',
        'source-label': 'Source',
        'types-label': 'Types',
        'submit-cta': 'Save',
        'submit-loading': 'Saving …',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You do not have permission to do this.',
        'not-a-member-error': 'You are not a member of this workspace.',
        'generic-error': 'Could not create contact. Please try again.',
        errors: {
          'first-name': {
            required: 'First name is required.',
            'too-long': 'First name is too long.',
            invalid: 'First name is invalid.',
          },
          'last-name': {
            required: 'Last name is required.',
            'too-long': 'Last name is too long.',
            invalid: 'Last name is invalid.',
          },
          'company-name': {
            required: 'Company name is required.',
            'too-long': 'Company name is too long.',
            invalid: 'Company name is invalid.',
          },
          email: {
            required: 'Email is required.',
            invalid: 'Please enter a valid email address.',
            'too-long': 'Email is too long.',
          },
          phone: {
            required: 'Phone number is required.',
            invalid: 'Phone number is invalid.',
            'too-long': 'Phone number is too long.',
          },
          'postal-code': {
            invalid: 'Postal code must be exactly five digits.',
          },
        },
      },
    },
    companies: {
      'company-type': {
        PROJEKTENTWICKLER: 'Property developer',
        IMMOBILIENMAKLER: 'Estate agent',
        HAUSVERWALTUNG: 'Property management',
        IMMOBILIENGUTACHTER: 'Property surveyor',
        NOTARIAT: 'Notary',
        BANK: 'Bank',
        VERSICHERUNG: 'Insurance',
        HANDWERKER: 'Tradesperson',
        ARCHITEKT: 'Architect',
        SONSTIGE: 'Other',
      },
      list: {
        title: 'Companies',
        subtitle: 'All companies your workspace works with.',
        'create-cta': 'Add company',
        'list-load-error': 'Could not load companies.',
        'empty-title': 'No companies yet.',
        'empty-description': 'This is where all your workspace companies will appear',
        'no-match-title': 'No matches',
        'no-match-description': 'Try adjusting your search or filters to find companies.',
        filters: {
          'search-placeholder': 'Search company, city, email',
          'search-label': 'Search companies',
          'type-all': 'All types',
          reset: 'Reset',
        },
        sort: {
          label: 'Sort',
          'name-asc': 'Name A → Z',
          'name-desc': 'Name Z → A',
          'city-asc': 'City A → Z',
          'city-desc': 'City Z → A',
          newest: 'Newest first',
          oldest: 'Oldest first',
        },
        pagination: {
          range: '{from}–{to} of {total}',
          'per-page': 'Per page:',
          prev: 'Previous',
          next: 'Next',
        },
        columns: {
          name: 'Name',
          type: 'Type',
          city: 'City',
          phone: 'Primary phone',
          'contact-count': 'Contacts',
        },
        empty: '—',
      },
      fields: {
        'sections.identity': 'Identity',
        'sections.tax-identity': 'Tax identity',
        'sections.address': 'Address',
        'sections.communication': 'Communication',
        'sections.notes': 'Notes',
        'sections.tags': 'Tags',
        'name-label': 'Company name',
        'type-label': 'Type',
        'type-empty': 'No type',
        'legal-form-label': 'Legal form',
        'register-number-label': 'Commercial register number',
        'vat-id-label': 'VAT ID',
        'tax-number-label': 'Tax number',
        'street-label': 'Street',
        'house-number-label': 'House number',
        'postal-code-label': 'Postal code',
        'city-label': 'City',
        'country-label': 'Country',
        'phone-label': 'Phone',
        'email-label': 'Email',
        'email-placeholder': 'info@example.com',
        'website-label': 'Website',
        'website-placeholder': 'https://…',
        'notes-label': 'Notes',
        'tags-label': 'Tags',
        'tags-placeholder': 'Add a tag and press Enter',
        'tags-remove': 'Remove {label}',
        errors: {
          name: {
            required: 'Please enter a company name.',
            'too-long': 'Company name may be at most 120 characters.',
            invalid: 'Company name is invalid.',
          },
          email: {
            invalid: 'Please enter a valid email address.',
          },
          phone: {
            invalid: 'Phone number is invalid.',
          },
          website: {
            invalid: 'Please enter a valid website address.',
          },
          'postal-code': {
            invalid: 'Please enter a valid postal code.',
          },
        },
      },
      'contact-link': {
        'section-header': 'Link a contact (optional)',
        helper:
          'If you already know the first point of contact, add them now. The company and the link are saved in one step.',
        expand: 'Link a contact (optional)',
        collapse: 'Close contact block',
        'mode-label': 'Contact',
        'mode-existing': 'Existing contact',
        'mode-new': 'New contact',
        'contact-label': 'Contact',
        'contact-placeholder': 'Search contacts …',
        'contact-clear': 'Clear selection',
        'contact-no-results': 'No matching contacts.',
        'contact-searching': 'Searching …',
        'first-name-label': 'First name',
        'last-name-label': 'Last name',
        'new-email-label': 'Email',
        'new-email-placeholder': 'name@example.com',
        'new-phone-label': 'Phone',
        'role-label': 'Role',
        'role-placeholder': 'e.g. Managing Director',
        'primary-employer-label': 'primary employer',
        errors: {
          'contact-required': 'Please select a contact or close the block.',
          'name-required': 'Please enter a first and last name or close the block.',
          'role-required': 'Please enter a role or close the block.',
          'role-too-long': 'Role may be at most 200 characters.',
        },
      },
      create: {
        title: 'Add company',
        'standort-label': 'Location',
        'submit-cta': 'Save',
        'submit-loading': 'Saving …',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You are not authorised for this action.',
        'not-a-member-error': 'You are not a member of this workspace.',
        'generic-error': 'The company could not be created. Please try again.',
      },
      edit: {
        title: 'Edit company',
        'submit-cta': 'Save',
        'submit-loading': 'Saving …',
        'cancel-cta': 'Cancel',
        'contacts-heading': 'Contacts',
        'contacts-readonly': 'Manage contacts on the company detail page via “Link a contact”.',
        'forbidden-error': 'You are not authorised for this action.',
        'not-found-error': 'Company not found.',
        'generic-error': 'Saving failed. Please try again.',
      },
      detail: {
        empty: '—',
        'copy-link': 'Copy link',
        'link-copied': 'Link copied.',
        status: {
          ACTIVE: 'Active',
          ARCHIVED: 'Archived',
        },
        actions: {
          'trigger-label': 'Actions',
          edit: 'Edit',
          archive: 'Archive',
          unarchive: 'Reactivate',
          delete: 'Delete',
          'link-contact': 'Link a contact',
        },
        'viewer-banner':
          'You have read-only access. Your role lets you view companies but not edit, archive, delete, or link contacts.',
        'lifecycle-error': 'Action failed. Please try again.',
        sections: {
          identity: 'Identity',
          'tax-identity': 'Tax identity',
          address: 'Address',
          communication: 'Communication',
          notes: 'Notes',
          tags: 'Tags',
          contacts: 'Contacts',
        },
        fields: {
          type: 'Type',
          'legal-form': 'Legal form',
          'register-number': 'Register number',
          'vat-id': 'VAT ID',
          'tax-number': 'Tax number',
          street: 'Street',
          'house-number': 'House number',
          'postal-code': 'Postal code',
          city: 'City',
          country: 'Country',
          phone: 'Phone',
          email: 'Email',
          website: 'Website',
          status: 'Status',
          created: 'Created',
          updated: 'Updated',
        },
        stats: {
          'contacts-label': 'Contacts',
        },
        contacts: {
          'primary-badge': 'primary employer',
          empty: 'No contacts linked yet.',
          'load-error': 'Contacts could not be loaded.',
        },
      },
      delete: {
        title: 'Delete this company permanently?',
        body: 'This action removes the company from all lists and unlinks all contact links. The contacts themselves remain unchanged. Recovery is only possible via support.',
        'confirm-cta': 'Delete permanently',
        'confirm-loading': 'Deleting …',
        'cancel-cta': 'Cancel',
        'generic-error': 'Deletion failed. Please try again.',
      },
      'add-contact': {
        title: 'Link a contact',
        'submit-cta': 'Link',
        'submit-loading': 'Linking …',
        'cancel-cta': 'Cancel',
        'duplicate-error': 'This company is already linked to the contact. You can edit the role of the existing link.',
        'generic-error': 'Linking failed. Please try again.',
      },
      link: {
        title: 'Link company',
        'company-label': 'Company',
        'company-placeholder': 'Search companies …',
        'company-clear': 'Clear selection',
        'company-no-results': 'No matching companies.',
        'company-searching': 'Searching …',
        'role-label': 'Role',
        'role-placeholder': 'e.g. Managing director',
        'primary-employer-label': 'primary employer',
        'submit-cta': 'Link',
        'submit-loading': 'Linking …',
        'cancel-cta': 'Cancel',
        errors: {
          'company-required': 'Please select a company.',
          'role-required': 'Please enter a role.',
          'role-too-long': 'The role must be at most 200 characters.',
          duplicate: 'This company is already linked to the contact. You can edit the role of the existing link.',
          'not-found': 'Company or contact was not found.',
          forbidden: 'You are not authorized to perform this action.',
          generic: 'Could not create the link. Please try again.',
        },
      },
      'edit-link': {
        title: 'Edit link',
        'role-label': 'Role',
        'role-placeholder': 'e.g. Managing director',
        'primary-employer-label': 'primary employer',
        'submit-cta': 'Save',
        'submit-loading': 'Saving …',
        'cancel-cta': 'Cancel',
        errors: {
          'role-required': 'Please enter a role.',
          'role-too-long': 'The role must be at most 200 characters.',
          'not-found': 'This link was not found.',
          forbidden: 'You are not authorized to perform this action.',
          generic: 'Could not save the change. Please try again.',
        },
      },
      unlink: {
        title: 'Remove link',
        body: 'Remove this link? The contact and the company are kept.',
        'confirm-cta': 'Remove link',
        'confirm-loading': 'Removing …',
        'cancel-cta': 'Cancel',
        'error-forbidden': 'You are not authorized to perform this action.',
        error: 'Could not remove the link. Please try again.',
      },
    },
    salesPipeline: {
      'invite-to-viewing': {
        action: 'Invite to viewing',
        'hint-label': 'Note for the prospect',
        'hint-placeholder': 'e.g. meeting point, doorbell or parking — appears in the invitation',
        'hint-counter': '{count} / {max}',
        locked: 'fixed',
        submit: 'Invite',
        'preview-action': 'Show preview',
        'preview-loading': 'Loading …',
        'preview-failed': 'The preview could not be loaded. Please try again.',
        preview: {
          title: 'Invitation preview',
          from: 'From',
          to: 'To',
          subject: 'Subject',
          'frame-title': 'E-mail preview',
          back: 'Back',
          send: 'Send invitation',
          sending: 'Sending …',
        },
      },
      phase: PROSPECT_PHASE_LABELS,
      dataRoom: {
        title: 'Data room',
        'not-released':
          '{count, plural, one {Not released yet. # document of the property is marked for buyers.} other {Not released yet. # documents of the property are marked for buyers.}}',
        release: 'Release data room',
        'release-confirm-title': 'Release the data room to {name}?',
        'release-confirm-body': 'They will receive an e-mail with the link to the documents.',
        'release-confirm': 'Release',
        'override-inside-withdrawal-period':
          'The withdrawal period runs until {withdrawalPeriodEndsOn, date, ::ddMMyyyy} and no early start was requested. If {name} withdraws from the brokerage agreement, you get no compensation for the services provided until then.',
        'override-not-concluded':
          'There is no brokerage agreement with {name} yet. Without an agreement you have no basis for a commission claim.',
        'override-withdrawal-received':
          '{name} has withdrawn from the brokerage agreement. Without a valid agreement you have no basis for a commission claim.',
        'release-confirm-override': 'Release anyway',
        cancel: 'Cancel',
        active: 'Released on {date} by {name}',
        withdraw: 'Withdraw release',
        'withdraw-confirm-title': 'Withdraw release?',
        'withdraw-confirm-body': '{name} will no longer see the documents.',
        'withdraw-confirm': 'Withdraw release',
        withdrawn: 'Release withdrawn on {date}',
        'ended-lost': 'Release ended on {date} — prospect lost',
        renew: 'Release again',
        'permission-denied': 'You do not have permission to edit this prospect.',
        'unknown-member': 'Former member',
        'load-failed': 'The state of the data room could not be loaded.',
        table: {
          document: 'Document',
          'first-viewed': 'First viewed',
          'last-viewed': 'Last viewed',
          downloaded: 'Downloaded',
          'never-opened': 'Not opened yet',
          'download-count': '({count}×)',
          unshared: 'no longer shared',
          deleted: 'deleted',
        },
      },
      brokerageAgreement: {
        option: 'Send the cancellation notice by e-mail',
        title: 'Brokerage agreement & cancellation',
        'not-sent': 'Notice not sent',
        sent: 'Notice sent on {date} to {email}',
        failed: 'Sending failed — please check the e-mail address and send again.',
        bounced: 'Not delivered — the address rejected the e-mail. Please check it and send again.',
        'not-concluded': 'No brokerage agreement concluded through immoOS',
        'receipt-failed': 'The acknowledgement of receipt could not be sent to the prospect.',
        'badge-waiting': 'Waiting',
        send: 'Send cancellation notice',
        resend: 'Send again',
        'permission-denied': 'You do not have permission to send the cancellation notice.',
        'ask-admin': 'Please contact an admin.',
        'fix-commission': "Enter buyer's commission",
        'fix-email': 'Add e-mail address',
        'company-settings-link': 'Settings → Company',
        'load-failed': 'The status of the cancellation notice could not be loaded.',
        'load-retry': 'Try again',
        'send-confirm-title': 'Send the cancellation notice to {name}?',
        'send-confirm-body': 'The e-mail with the cancellation notice and the personal link goes to {email}.',
        'resend-confirm-title': 'Send the cancellation notice to {name} again?',
        'resend-confirm-body': '{email} receives the e-mail once more, with a new personal link.',
        'send-confirm-submit': 'Send',
        'send-confirm-cancel': 'Cancel',
        'send-error': 'The cancellation notice could not be sent. Please try again.',
        'acceptance-pending': 'Acceptance by e-mail: outstanding',
        'acceptance-received': 'Acceptance by e-mail: received on {date}',
        'mark-acceptance': 'Mark as received',
        'mark-acceptance-permission-denied': 'You do not have permission to mark the acceptance as received.',
        'mark-acceptance-error': 'The acceptance could not be marked as received. Please try again.',
        'mark-acceptance-confirm': "Received {name}'s reply with the acceptance text?",
        'mark-acceptance-confirm-submit': 'Confirm',
        'mark-acceptance-confirm-cancel': 'Cancel',
        reminder: 'Reminder sent on {date}',
        concluded: 'Brokerage agreement concluded online on {date} at {time}',
        'early-start': 'Early start requested: {answer}',
        yes: 'yes',
        no: 'no',
        'period-ends': 'Cancellation period ends on {date}',
        withdrawn: 'Cancelled on {date} at {time}',
        'withdrawal-reason': 'Reason: {reason}',
        'badge-concluded': 'Concluded',
        'badge-withdrawn': 'Cancelled',
        'show-proof': 'View proof',
        proof: {
          title: 'Proof · Brokerage agreement {name}',
          facts: {
            'concluded-at': 'Concluded',
            as: 'As',
            'notice-read': 'Cancellation notice read',
            'early-start': 'Early start requested',
            ip: 'IP address',
            browser: 'Browser',
            muster: 'Wording version',
            'template-line': '{name} · Version {version} · {date} {time} · {ip}',
            'acceptance-reply': 'Acceptance by e-mail',
            'acceptance-reply-pending': 'pending',
            'acceptance-reply-marked': 'received · noted by {name} on {date} at {time}',
            'acceptance-reply-marked-unnamed': 'received · noted on {date} at {time}',
            'withdrawal-received': 'Cancellation received',
            'withdrawal-name': 'Name',
            'withdrawal-contract': 'Agreement',
            'withdrawal-email': 'E-mail address',
            'withdrawal-reason': 'Reason',
            'no-user-agent': 'not transmitted',
          },
          declarations: 'Declarations, word for word',
          emails: 'E-mails sent',
          page: 'The page as it was shown',
          'page-alt': 'The page as it was shown, on {date} at {time}',
          'open-page': 'Open the full page',
          back: 'Back to the proof',
          close: 'Close',
          'email-kind': {
            NOTICE: 'Cancellation notice',
            REMINDER: 'Reminder',
            CONFIRMATION: 'Confirmation',
            WITHDRAWAL_RECEIPT: 'Acknowledgement of receipt',
            ACCEPTANCE_REMINDER: 'Reminder of the acceptance by e-mail',
            DATA_ROOM_RELEASE: 'Data room release',
          },
          'email-sent-at': 'Sent on {date} at {time}',
          'email-status': {
            SENT: 'Sent, no confirmation of delivery yet',
            FAILED: 'Sending failed on {date} at {time}',
            PENDING: 'Not sent yet',
          },
          delivery: {
            DELIVERED: 'Delivered on {date} at {time}',
            BOUNCED: 'Bounced on {date} at {time}',
            COMPLAINED: 'Reported as spam on {date} at {time}',
          },
          'personal-link': '[personal link]',
          tampered: 'This entry was changed after it was saved.',
          loading: 'Loading the proof …',
          'load-failed': 'The proof could not be loaded.',
        },
      },
      status: PROSPECT_STATUS_LABELS,
      source: PROSPECT_SOURCE_LABELS,
      'source-group': PROSPECT_SOURCE_GROUP_LABELS,
      'loss-reason': PROSPECT_LOSS_REASON_LABELS,
      'loss-reason-with-note': '{reason}: {note}',
      title: 'Sales pipeline',
      subtitle: 'All active purchase processes in your workspace.',
      'create-cta': 'Create prospect',
      'create-cta-no-permission': 'You do not have permission to create prospects.',
      'empty-title': 'No prospects yet',
      'empty-description':
        'Create a prospect, or take a prospective buyer straight into the purchase process from the property.',
      'error-title': 'Data could not be loaded',
      'error-description': 'The connection to the backend is disrupted at the moment. Try again in a few seconds.',
      'error-retry': 'Try again',
      'view-label': 'View',
      'view-kanban': 'Kanban',
      'view-list': 'Table',
      'no-match-title': 'No results for your filters',
      'no-match-description':
        'We could not find any prospects that match all active filters. Try loosening or resetting a filter.',
      'reset-filters': 'Reset filters',
      'sort-label': 'Sort by {column}',
      filters: {
        'status-label': 'Status',
        'status-all': 'All',
        'contact-search-label': 'Search contact',
        'contact-search-placeholder': 'Search contact …',
        'assigned-to-me': 'Assigned to me',
        'active-label': 'Active filters:',
        'clear-all': 'Reset all',
        'remove-aria': 'Remove {label}',
        'chip-status': 'Status: {value}',
        'chip-contact': 'Contact: “{query}”',
        'chip-assigned-to-me': 'Assigned to me',
      },
      columns: {
        contact: 'Contact',
        property: 'Property',
        phase: 'Phase',
        status: 'Status',
        days: 'Days',
        'next-activity': 'Next activity',
        responsible: 'Responsible',
        'offered-price': 'Offered purchase price',
        'buyer-commission': 'Buyer commission',
        source: 'Source',
        'created-at': 'Created on',
      },
      'property-selection': {
        label: 'Property',
        all: 'All properties',
        'search-label': 'Search property',
        'search-placeholder': 'Search property …',
        options: 'Properties',
        'reserved-suffix': ' · reserved',
      },
      'property-strip': {
        count: '· {count, plural, one {# prospect} other {# prospects}}',
        'asking-price': '· Asking price {price}',
        'reserved-for': 'Reserved for {name}',
        'to-property': 'To the property',
      },
      board: {
        'aria-label': 'Sales pipeline by phase',
        'empty-column': 'No prospects',
      },
      'viewing-assessment': {
        action: 'Assess viewing',
        'edit-action': 'Edit assessment',
        title: 'Assess viewing',
        'edit-title': 'Edit assessment',
        'card-hint': 'Assess the viewing of {date}',
        subtitle: '{prospect} · {weekday}, {date}, {time}',
        'subtitle-all-day': '{prospect} · {weekday}, {date}',
        'attendance-label': 'Attendance',
        attendance: { ATTENDED: 'Attended', NO_SHOW: 'No-show' },
        'impression-label': 'Impression',
        impression: {
          INTERESTED: 'Interested',
          NOT_INTERESTED: 'Rather not interested',
          UNDECIDED: 'Undecided',
        },
        'comment-label': 'Comment (optional)',
        'comment-placeholder': 'e.g. questions, objections, next steps',
        save: 'Save',
        cancel: 'Cancel',
        'attendance-required': 'Please choose whether the prospect attended.',
        'impression-required': 'Please choose the impression.',
        forbidden: 'You are not permitted to do this.',
      },
      'viewing-type': {
        ON_SITE: 'On-site viewing',
        VIRTUAL: 'Virtual viewing',
      },
      'viewing-type-short': {
        ON_SITE: 'on site',
        VIRTUAL: 'virtual',
      },
      'next-viewing': {
        card: 'Viewing {date} · {time}',
        'card-typed': '{type} {date} · {time}',
        label: 'Next viewing',
        value: '{date} · {time}',
        'value-typed': '{type} · {date} · {time}',
        'all-day': 'all day',
      },
      notary: {
        card: 'Notary appointment {date}',
        'card-missing': 'Notary appointment: date missing',
        'card-passed': 'Notary appointment on {date} has passed',
      },
      'next-activity': {
        overdue: 'Overdue since {date}',
        viewing: 'Viewing',
        label: 'Next activity',
        due: 'Due {date}',
      },
      card: {
        'no-offer': 'No offer yet',
        'no-open-offer': 'No open offer',
        'days-in-phase': '{days, plural, one {# day} other {# days}}',
        empty: '—',
        advance: 'Move to the next phase: {phase}',
      },
      move: {
        offer: {
          title: 'Record offer',
          hint: 'Record further offers and counter-offers in the ‘Purchase process’ card.',
          'asking-price': 'Asking price of the property: {price}',
          confirm: 'Save and continue',
        },
        purchase: {
          title: 'Complete purchase',
          body: 'Completing the purchase wins the purchase process and it can no longer be changed.',
          field: 'Purchase price (contract)',
          hint: 'Prefilled with the latest offer: {price}',
          'hint-accepted': 'Prefilled with the accepted offer: {price}',
          confirm: 'Complete purchase',
        },
        notary: {
          title: 'Record notary appointment',
          field: 'Date of the notary appointment',
          hint: 'You can change the date later in the ‘Purchase process’ card.',
          confirm: 'Save and continue',
          'edit-title': 'Change notary appointment',
          save: 'Save',
        },
        cancel: 'Cancel',
        'reserved-link': 'Open {name}',
        forbidden: 'You do not have permission to change the phase.',
        'generic-error': 'The phase could not be changed. Please try again.',
      },
      revert: {
        title: 'Reset phase',
        'phase-label': 'Back to phase',
        'phase-placeholder': 'Please choose',
        'phase-required': 'Please choose a phase.',
        'reason-label': 'Reason',
        confirm: 'Reset',
        cancel: 'Cancel',
        'success-toast': 'Phase reset.',
        forbidden: 'You do not have permission to reset the phase.',
        'generic-error': 'The phase could not be reset. Please try again.',
      },
      lost: {
        title: 'Mark as lost',
        'reason-label': 'Loss reason',
        'reason-placeholder': 'Please choose',
        'reason-required': 'Please choose a loss reason',
        'note-label': 'Note (optional)',
        confirm: 'Mark as lost',
        cancel: 'Cancel',
        'success-toast': 'Marked as lost.',
        forbidden: 'You do not have permission to edit prospects.',
        'generic-error': 'The prospect could not be marked as lost. Please try again.',
      },
      reopen: {
        'success-toast': 'Reopened.',
        forbidden: 'You do not have permission to edit prospects.',
        'generic-error': 'The prospect could not be reopened. Please try again.',
      },
      'notary-edit': {
        'success-toast': 'Notary appointment saved.',
        'generic-error': 'The notary appointment could not be saved. Please try again.',
      },
      'offer-record': {
        'title-offer': 'Record offer',
        'title-counter-offer': 'Record the owner’s counter-offer',
        amount: 'Amount',
        date: 'Date',
        'financing-reservation': 'Offer subject to financing',
        note: 'Note',
        save: 'Save',
        forbidden: 'You do not have permission to edit prospects.',
        'generic-error': 'The entry could not be saved. Please try again.',
      },
      actions: {
        revert: 'Reset phase',
        'mark-lost': 'Mark as lost',
        reopen: 'Reopen',
        'move-back-forbidden': 'You do not have permission to reset the phase.',
        'update-forbidden': 'You do not have permission to edit prospects.',
      },
      create: {
        title: 'Create prospect',
        confirm: 'Create prospect',
        loading: 'Creating …',
        cancel: 'Cancel',
        'standort-label': 'Standort',
        'contact-label': 'Contact',
        'contact-placeholder': 'Search contacts…',
        'contact-clear': 'Clear contact selection',
        'contact-no-results': 'No contacts found.',
        'create-contact-cta': 'Create new contact',
        'contact-searching': 'Searching contacts …',
        'property-label': 'Property',
        'property-hint':
          'Only properties marketed for sale that are not archived. It is pre-filled from the tab on the property.',
        'source-label': 'Source (optional)',
        'existing-link': 'Go to the ongoing purchase process',
        'contact-required': 'Please select a contact.',
        'property-required': 'Please select a property.',
        'property-not-found-error':
          'The selected property does not belong to the chosen Standort. Please select it again.',
        'contact-not-found-error': 'A selected contact was not found.',
        'forbidden-error': 'You do not have permission for this action.',
        'generic-error': 'The prospect could not be created. Please try again.',
      },
      detail: {
        empty: '—',
        header: {
          'aria-label': 'Buyer prospect header',
          'strip-aria-label': 'Purchase process phases',
          'offer-label': 'Offer',
          'purchase-label': 'Purchase price',
          'change-phase': 'Change phase',
          'change-phase-forbidden': 'You do not have permission to change the phase.',
          'won-marker': 'Purchase won',
          'lost-marker': 'Purchase lost',
          more: 'More actions',
          'days-in-phase': '{days, plural, one {# day} other {# days}} in phase',
          closed: 'Closed',
        },
        banner: {
          won: 'Purchase won — phase and prices are locked.',
          lost: 'Purchase lost — choose „Reopen" to work on it again.',
          'lost-reason': 'Loss reason: {reason}',
          'lost-note': 'Loss note: {note}',
          'read-only': 'You can view this purchase process but not edit it.',
        },
        activity: {
          title: 'Activity',
          'no-access': 'No access — you lack the read permission for this information.',
          'add-cta': 'Add a note, follow-up or viewing …',
          'cancel-confirm': 'The activity will be cancelled and leave „Anstehend". It stays visible in the history.',
        },
        rail: {
          contact: { title: 'Contact', name: 'Name', email: 'Email', phone: 'Phone' },
          property: {
            title: 'Property',
            name: 'Property',
            address: 'Address',
            'asking-price': 'Asking price',
            prospects: 'Buyer prospects',
            'prospects-value': '{count} active',
            reservation: 'Reservation',
            reserved: 'reserved',
            'reserved-won': 'reserved · purchased',
            free: 'free',
          },
          process: {
            title: 'Purchase process',
            offer: 'Offer',
            'offer-accepted': '{amount} · accepted',
            notary: 'Notary appointment',
            'notary-missing': 'Date missing',
            'purchase-price': 'Purchase price (contract)',
            source: 'Source',
            responsible: 'Responsible',
            created: 'Created',
            edit: 'Edit',
            'notary-edit-aria': 'Edit notary appointment',
            'edit-forbidden': 'You do not have permission to edit prospects.',
          },
          offers: {
            title: 'Offers',
            'record-offer': 'Record offer',
            'record-counter-offer': 'Record counter-offer',
            kind: { OFFER: 'Offer', COUNTER_OFFER: 'Counter-offer' },
            status: { OPEN: 'Open', ACCEPTED: 'Accepted', REJECTED: 'Rejected', WITHDRAWN: 'Withdrawn' },
            'financing-reservation': 'Subject to financing',
            'actions-label': 'Actions for this entry',
            actions: { ACCEPTED: 'Accept', REJECTED: 'Reject', WITHDRAWN: 'Withdraw', OPEN: 'Reopen' },
          },
        },
      },
    },
    deals: {
      phase: DEAL_PHASE_LABELS,
      status: DEAL_STATUS_LABELS,
      type: DEAL_TYPE_LABELS,
      source: DEAL_SOURCE_LABELS,
      probability: DEAL_PROBABILITY_LABELS,
      'completion-horizon': DEAL_COMPLETION_HORIZON_LABELS,
      'loss-reason': DEAL_LOSS_REASON_LABELS,
      'mandate-type': DEAL_MANDATE_TYPE_LABELS,
      auftrag: {
        card: {
          title: 'Mandate',
          edit: 'Edit',
          empty: 'No mandate terms recorded yet.',
          'mandate-type-label': 'Mandate type',
          'start-label': 'Start of activity',
          'duration-label': 'Term',
          'duration-value': '{months, plural, one {# month} other {# months}}',
          'ends-on': 'Runs until {date}',
          'seller-commission-label': 'Seller commission',
          'price-label': 'Expected sale price',
          'percent-value': '{value}%',
        },
        dialog: {
          title: 'Edit mandate',
          'mandate-type-label': 'Mandate type',
          'mandate-type-placeholder': 'Please choose',
          'start-label': 'Start of activity',
          'duration-label': 'Term (months)',
          'duration-suffix': 'months',
          'seller-commission-label': 'Seller commission (%)',
          'commission-hint': 'Net, plus VAT.',
          'price-label': 'Expected sale price',
          save: 'Save',
          cancel: 'Cancel',
          saved: 'Mandate saved',
          'generic-error': "The mandate couldn't be saved. Please try again.",
          errors: {
            commission: 'Enter a value between 0 and 100 with at most two decimal places.',
            duration: 'Enter a term between 1 and 24 months.',
          },
        },
      },
      'buyer-commission-difference': {
        banner:
          "{count, plural, one {On # deal the buyer's commission in the mandate differs from the property's.} other {On # deals the buyer's commission in the mandate differs from the property's.}}",
        'banner-action': 'View',
        'dialog-title': "Differing buyer's commission",
        'dialog-body':
          "The buyer's commission now belongs to the property and applies to the deal, the mandate and the brokerage agreement. These deals had a different value in the mandate. Open the deal and decide which value applies.",
        row: 'Mandate: {mandate} · Property: {objekt}',
        'mandate-value': '{percent, number, ::.00} % net',
        'open-deal': 'Open deal',
        'card-notice': "The mandate said {mandate}. The property's value now applies: {objekt}.",
        'keep-objekt': 'Keep property value',
        'use-mandate': 'Apply mandate value to property',
        'dismiss-error': "That didn't work. Please try again.",
      },
      list: {
        title: 'Acquisitions',
        // IMM-1333 — follows the status filter. `ALL` is the "All" option (`''`).
        subtitle: {
          ALL: 'All acquisition pipelines in your workspace.',
          DRAFT: 'All draft acquisition pipelines in your workspace.',
          ACTIVE: 'All active acquisition pipelines in your workspace.',
          DEFERRED: 'All deferred acquisition pipelines in your workspace.',
          WON: 'All won acquisition pipelines in your workspace.',
          LOST: 'All lost acquisition pipelines in your workspace.',
        } satisfies EnumTranslation<DealStatus | 'ALL'>,
        'create-cta': 'Create deal',
        // IMM-836 — its own reason key for the withheld CTA, in the same
        // register as `phase-lock.no-permission` on the same page.
        'create-cta-no-permission': 'You do not have permission to create deals.',
        'row-actions': 'Actions',
        'list-load-error': 'Deals could not be loaded.',
        'empty-title': 'No deals yet',
        'empty-description': 'Your active deals will appear here. Create a deal directly or qualify an owner lead.',
        'empty-leads-cta': 'Go to owner leads',
        'no-match-title': 'No matches for your filters',
        'no-match-description':
          'We could not find any deals matching all active filters. Try loosening or resetting a filter.',
        'reset-filters': 'Reset filters',
        'error-title': 'Data could not be loaded',
        'error-description': 'The connection to the backend is currently disrupted. Try again in a few seconds.',
        'error-retry': 'Try again',
        'success-toast': 'Deal created.',
        filters: {
          'status-label': 'Status',
          'status-all': 'All',
          'assigned-to-me': 'Assigned to me',
          orphaned: 'Unassigned / orphaned deals',
          'contact-search-label': 'Search contact',
          'contact-search-placeholder': 'Search contact …',
          'property-search-label': 'Search property',
          'property-search-placeholder': 'Search property …',
          'price-min-label': 'Sale price from',
          'price-max-label': 'Sale price up to',
          'price-min-placeholder': 'Min €',
          'price-max-placeholder': 'Max €',
          'active-label': 'Active filters:',
          'clear-all': 'Clear all',
          'remove-aria': 'Remove {label}',
          'chip-status': 'Status: {value}',
          'chip-lost-in-phase': 'Lost in: {phase}',
          'lost-in-phase-label': 'Lost in phase',
          'lost-in-phase-all': 'All phases',
          'chip-assigned-to-me': 'Assigned to me',
          'chip-orphaned': 'Unassigned / orphaned deals',
          'chip-contact': 'Contact: “{query}”',
          'chip-property': 'Property: “{query}”',
          'chip-price-range': 'Sale price: {min} – {max}',
          'chip-price-min': 'Sale price from {min}',
          'chip-price-max': 'Sale price up to {max}',
        },
        'orphan-banner':
          'These deals currently have no responsible member. Assign them explicitly — nothing is transferred automatically.',
        columns: {
          contact: 'Contact(s)',
          property: 'Property',
          type: 'Transaction',
          phase: 'Phase',
          status: 'Status',
          commission: 'Commission',
          'days-in-phase': 'Days',
          probability: 'Probability',
          'completion-horizon': 'Horizon',
          responsible: 'Responsible',
          'former-responsible': 'Formerly responsible',
          'created-by': 'Created by',
        },
        'sort-label': 'Sort by {column}',
        'created-by-coming-soon': 'Coming soon',
        'not-assigned': 'Unassigned',
        'unknown-member': 'Unknown member',
        'former-responsible-deactivated': '{name} (deactivated)',
        'assign-cta': 'Assign',
        'days-in-phase-value': '{days, plural, one {# day} other {# days}}',
        'days-of-target': '{days, plural, one {# day} other {# days}} in phase, target {target}',
        'owners-suffix': '+{count}',
        'owners-hover-label': 'Linked contacts',
        'view-label': 'View',
        'view-kanban': 'Kanban',
        'view-list': 'Table',
        board: {
          'aria-label': 'Deal pipeline by phase',
          'empty-column': 'No deals',
          'not-valued': 'Not yet valued',
          commission: 'Commission {amount}',
          'commission-estimated': 'Commission ~{amount} estimated',
          'commission-label': 'Commission',
          'commission-missing': 'No expected commission',
          estimate: '~{amount} estimated',
          'column-commission': 'Expected commission',
          'next-activity': {
            none: 'No next step',
            overdue: 'Overdue since {date}',
          },
          'hold-lane': 'Deferred',
          'lost-lane': 'Lost',
          'hold-empty': 'No deferred deals',
          // TODO(IMM-22): wire to a specific backend 409/conflict error code
          'concurrent-change': 'This deal was changed by someone else — the board has been refreshed.',
          'drag-column-drop-hint': 'Drop here',
        },
        'deferred-until-card': 'Follow-up {date}',
        empty: '—',
      },
      // Deal overview (IMM-430.7) — the branch manager's cross-deal surface.
      overview: {
        title: 'Deal overview',
        // IMM-726.1 — „active", as the design run sets it and as the read
        // already behaves: drafts never appear here.
        subtitle: 'Every active deal across your locations — most urgent first.',
        empty: 'No deals match this filter.',
        'load-failed': 'The overview could not be loaded.',
        'load-failed-hint': 'Please reload the page.',
        retry: 'Try again',
        // IMM-665 — the server refused the filter, not the page. The list stays
        // on screen, so the notice says what is being shown instead.
        'filter-rejected': 'This filter could not be applied — all deals are shown.',
        truncated: 'Only the first 2,000 deals are shown.',
        filter: {
          label: 'Filter:',
          phase: 'Phase',
          lost: 'Lost',
          attention: 'Attention',
          // IMM-629 — its own key rather than `column.assignee`: the column and
          // the filter share a word today but are independently rewordable.
          assignee: 'Assignee',
          'assignee-all': 'All assignees',
          // IMM-726.2 — the fourth filter the design run draws, plus the „all …"
          // entries a filter is cleared with. As a dropdown pill every filter
          // needs one; the bare filter name means nothing as a menu entry.
          standort: 'Location',
          'standort-all': 'All locations',
          'phase-all': 'All phases',
          'attention-all': 'All states',
        },
        // IMM-726.2 — the page-level scope. It overrides the global location
        // switcher's narrow for this page.
        scope: {
          // The control's accessible name. Separate from `all`/`one` because the
          // visible label is the current VALUE — a control whose name is its value
          // cannot be found again by the name it was found by — and because the
          // Standort filter beside it already carries „Location".
          label: 'Choose location',
          all: 'Location: All',
          one: 'Location: {name}',
        },
        // IMM-726.2 — its own key rather than `pages.workspace.deals.list.create-cta`,
        // following the precedent IMM-629 set on this page with `filter.assignee`:
        // two surfaces sharing a word today must stay independently rewordable.
        'create-cta': 'Create deal',
        // IMM-836 — its own reason key, for the same reason `create-cta` here is
        // one: two surfaces sharing a sentence today must stay independently
        // rewordable.
        'create-cta-no-permission': 'You do not have permission to create deals.',
        // IMM-629 — the switch between the flat list and the phase grouping. It
        // lives in the URL so a grouped view can be shared.
        view: {
          label: 'View',
          list: 'List',
          phase: 'By phase',
        },
        // IMM-629 — the number in a group header comes from `phaseCounts`, which
        // is counted without the phase filter and may therefore differ from the
        // number of rows rendered under it.
        'group-count': '{count, plural, one {# deal} other {# deals}}',
        // IMM-726.1 — several assignees collapse to „first name +N" so the row
        // stays one line tall. The design run only ever drew the single case.
        'assignee-more': '+{count}',
        column: {
          object: 'Property',
          owner: 'Owner',
          phase: 'Phase',
          days: 'Days in phase',
          assignee: 'Assignee',
          'next-activity': 'Next activity',
          status: 'Status',
        },
      },
      detail: {
        'id-prefix': 'Deal',
        'acquisition-briefing': {
          start: 'Create acquisition briefing',
          preparing: 'Acquisition briefing is being created … (started {time})',
          delivered: 'Acquisition briefing from {date}',
          view: 'View',
          renew: 'Create new',
          retry: 'Try again',
          'object-type-unsupported': 'There is no acquisition briefing for this property type yet.',
          'address-incomplete': 'A briefing needs the property to have a complete address.',
          'renew-dialog': {
            title: 'Create a new acquisition briefing?',
            description: 'The previous one will be replaced.',
            confirm: 'Create new',
            cancel: 'Cancel',
          },
        },
        'load-error': 'Deal could not be loaded.',
        empty: '—',
        'owners-suffix': '+ {count} owners',
        'owner-unknown': 'Unknown contact',
        'no-access': 'No access — you do not have read rights for this information.',
        // The persistent deal header (IMM-456.2). Its secondary actions carry
        // their own existing labels; only what the header itself labels lives
        // here.
        header: {
          'aria-label': 'Deal header',
          'commission-label': 'Est. commission',
          'days-in-phase': 'Day {days} in phase',
          closed: 'Acquisition closed',
          'lost-in': 'Lost in: {phase}',
          'won-marker': 'Deal won',
          'lost-marker': 'Deal lost',
          more: 'More',
          valuation: 'Valuation',
          'back-to-deal': 'Back to the deal',
          'open-object': 'Open property: {address}',
          'publish-to-immoscout24': 'Publish on ImmoScout24 now',
          'publish-to-portal': 'Publish on {portal} now',
        },
        // The activity composer's assignee select. On a deal, "no choice" does
        // NOT mean "me": the server fills in the deal's responsible member
        // (IMM-430.2). On a lead it stays "Me".
        'activity-assignee-default': "The deal's responsible member",
        // A deal's attention signal (IMM-430.6). Exactly one applies, in this
        // order, and only to an active deal.
        attention: {
          OVERDUE: 'Overdue',
          NO_NEXT_STEP: 'No next step',
          TOO_LONG_IN_PHASE: 'Too long in phase',
          ON_TRACK: 'On track',
        },
        playbook: {
          heading: 'Recommended for this phase',
          dismiss: 'Hide',
          'in-exchange-first-contact': 'First contact',
          'in-exchange-on-site': 'Arrange an on-site appointment',
          'in-exchange-call-outcome': 'Record the call outcome',
          'appointment-scheduled-on-site': 'Plan the on-site appointment',
          'appointment-scheduled-confirm': 'Confirm the appointment',
          'appointment-scheduled-preparation': 'Note the preparation',
          'appointment-completed-survey': 'Plan the property survey',
          'appointment-completed-request-valuation': 'Request the valuation',
          'appointment-completed-price-expectation': 'Note the price expectation',
          'valuation-completed-present': 'Present the valuation',
          'valuation-completed-call': 'Call the owner',
          'valuation-completed-prepare-mandate': 'Prepare the mandate',
          'mandate-received-send-mandate': 'Send the mandate',
          'mandate-received-signing': 'Signing',
          'mandate-received-identification': 'AML identification',
          'contract-sent-follow-up': 'Follow up',
          'contract-received-survey': 'Plan the property survey',
          'contract-received-marketing-strategy': 'Exposé · marketing strategy',
          'expose-review-seek-approval': 'Seek approval',
          'expose-review-letter': 'Request approval in writing',
          'expose-review-approval-recorded': 'Approval recorded',
          'online-handover': 'Hand over to marketing',
        },
        // The "Absagen" confirmation body. A lead drops the activity entirely; a
        // deal keeps it in the history (IMM-430.3) so what was planned and then
        // called off stays auditable.
        'activity-cancel-confirm':
          'The activity will be cancelled and leave „Anstehend". It stays visible in the history.',
        // Valuation (Bewertung) tab — IMM-407.1. Quiet states only in this slice.
        // The app is German-facing; these mirror the DE keys for the catalogue.
        valuation: {
          'empty-title': 'No valuation requested yet',
          'empty-hint': 'We already know a lot from the property. You only need to review it.',
          'request-action': 'Request valuation',
          'read-only-hint': 'No valuation has been requested for this deal yet.',
          // A deal always has a property (property_id is NOT NULL); a missing
          // property here means it could not be loaded, not that the deal has none.
          'unavailable-title': 'Property could not be loaded',
          'unavailable-hint':
            'The property linked to this deal is currently unavailable. Reload the page or try again later.',
          loading: 'Loading valuation …',
          'load-error-title': 'Valuation could not be loaded',
          'load-error-hint': 'The valuation request for this deal could not be read right now. Nothing was changed.',
          'load-retry': 'Try again',
          'status-label': 'Request status',
          // The Anfrage draft form (IMM-407.2.1) — mirrors the DE catalogue.
          form: {
            'status-label': 'Request status',
            'status-value': VALUATION_STATUS_LABELS,
            save: 'Save',
            saving: 'Saving…',
            'save-success': 'Draft saved.',
            delete: 'Delete draft',
            deleting: 'Deleting…',
            'delete-success': 'Draft deleted.',
            'delete-confirm': {
              title: 'Delete draft?',
              body: 'The draft for {address} and its uploaded documents will be deleted. This cannot be undone.',
              'body-unnamed': 'The draft and its uploaded documents will be deleted. This cannot be undone.',
              cancel: 'Cancel',
            },
            creating: 'Creating…',
            'prefill-marker': 'Taken from the property',
            // Preview + submit + withdraw (IMM-407.3).
            'to-preview': 'Continue to preview',
            withdraw: 'Withdraw',
            withdrawing: 'Withdrawing…',
            'withdraw-success': 'Request withdrawn.',
            'requested-by': 'Requested by {name}',
            'requested-at': 'Requested on {date}',
            // The mandatory-field banner shown on return from the preview when
            // submit is refused server-side (IMM-407.3 AC 29). The offending fields
            // are listed by name below the intro.
            missing: {
              title: 'Mandatory fields missing',
              intro: 'Please complete these mandatory fields before sending the request:',
            },
            'not-saved': {
              title: 'Your changes were not saved',
              body: 'The request had already been sent. The form still shows what you entered, not the request as it was sent.',
            },
            errors: {
              forbidden: 'You do not have permission to do that.',
              create: 'The request could not be created. Please try again.',
              save: 'The draft could not be saved. Please try again.',
              // A business-rule refusal (IMM-1198) — the request was already submitted;
              // not retryable, so no "Please try again".
              'save-not-draft':
                'This request has already been submitted and can no longer be edited. Withdraw it first to make changes.',
              // A specific value refusal — not retryable. The occupancy rate is a
              // percentage (0–100); an out-of-range value is caught inline before save.
              'occupancy-rate-range': 'The occupancy rate must be between 0 and 100%.',
              delete: 'The draft could not be deleted. Please try again.',
              submit: 'The request could not be sent. Please try again.',
              withdraw: 'The request could not be withdrawn. Please try again.',
              // A business-rule refusal (IMM-511) — permanent, not retryable. No
              // "Please try again": the request is being worked on / closed.
              'withdraw-taken-on': 'This request is already being worked on and can no longer be withdrawn.',
              'withdraw-not-possible': 'This request can no longer be withdrawn.',
              // A business-rule refusal (IMM-1198) — permanent, not retryable.
              'not-draft-withdraw': 'This request has already been sent. Withdraw it first to make changes.',
              'not-draft': 'This request is no longer a draft and can no longer be changed.',
            },
            objectType: VALUATION_OBJEKTART_LABELS,
            usage: VALUATION_NUTZUNG_LABELS,
            'reason-enum': VALUATION_ANLASS_LABELS,
            timeHorizon: VALUATION_ZEITHORIZONT_LABELS,
            priceType: VALUATION_PREISVORSTELLUNG_ART_LABELS,
            priceExpectationKind: VALUATION_PRICE_EXPECTATION_KIND_LABELS,
            // Type-specific enum option tables + field-name labels (IMM-407.2.2).
            heatingType: VALUATION_HEIZUNGSART_LABELS,
            energyEfficiencyClass: VALUATION_ENERGIEEFFIZIENZKLASSE_LABELS,
            houseType: VALUATION_HAUSTYP_LABELS,
            basement: VALUATION_UNTERKELLERT_LABELS,
            developmentPlan: VALUATION_BEBAUUNGSPLAN_LABELS,
            servicingStatus: VALUATION_ERSCHLIESSUNGSZUSTAND_LABELS,
            contaminationSuspicion: VALUATION_ALTLASTENVERDACHT_LABELS,
            'graduated-index-rent': VALUATION_STAFFEL_INDEX_MIETE_LABELS,
            'unit-breakdown': VALUATION_ANGABE_DER_EINHEITEN_LABELS,
            // Gesamtzustand + collection enum option tables (IMM-407.2.3).
            quality: VALUATION_QUALITAET_LABELS,
            condition: VALUATION_ZUSTAND_LABELS,
            buildingElement: VALUATION_GEBAEUDEELEMENT_LABELS,
            parkingSpaceType: VALUATION_STELLPLATZART_LABELS,
            lettingStatus: VALUATION_EINHEIT_VERMIETUNGSSTATUS_LABELS,
            field: VALUATION_FIELD_NAME_LABELS,
            // The names the switch-warning uses for the two discardable collections.
            collection: {
              parkingSpaces: 'Parking spaces',
              units: 'Units',
            },
            'select-placeholder': 'Please select',
            'boolean-ja': 'Yes',
            'boolean-nein': 'No',
            'income-annual-rent-label': 'Annual net cold rent',
            'income-annual-rent-hint': 'Actual rent × 12 · calculated',
            block: {
              condominium: 'Condominium details',
              house: 'House details',
              multiFamily: 'Multi-family house details',
              plot: 'Plot details',
              income: 'Income (actual)',
              hoa: 'Homeowners association',
              overallCondition: 'Quality and condition',
              parkingSpaces: 'Parking spaces',
              units: 'Units',
            },
            // Gesamtzustand block (IMM-407.2.3) — the two star axes + a hint.
            overallCondition: {
              'axis-quality': 'Quality',
              'axis-condition': 'Condition',
              hint: 'No stars means “not specified” — not the lowest step.',
            },
            // Parking spaces block (IMM-407.2.3) — repeatable type + count rows.
            parkingSpaces: {
              'type-label': 'Type',
              'count-label': 'Count',
              add: 'Add parking space',
              remove: 'Remove',
              empty: 'No parking spaces added yet.',
            },
            // Multi-family house per-unit rent roll (IMM-407.2.3).
            units: {
              'unit-heading': 'Unit {index}',
              label: 'Label',
              lettingStatus: 'Letting status',
              'let-since': 'Let since',
              livingArea: 'Living area',
              'current-rent': 'Current rent',
              'last-rent-increase': 'Last rent increase',
              add: 'Add unit',
              remove: 'Remove',
              empty: 'No units added yet.',
            },
            unavailable: {
              title: 'Intake not yet available',
              body: 'Intake for commercial properties and garages / parking spaces is not yet available. These object types will follow in a later increment.',
            },
            'switch-warning': {
              title: 'Entries will be lost',
              intro: 'This change will discard your entries for these fields. Continue?',
              confirm: 'Continue',
              cancel: 'Cancel',
            },
            basics: {
              title: 'Basics',
              'objectType-label': 'Valuation object type',
              'objectType-placeholder': 'Please select',
              'usage-label': 'Usage',
              'usage-placeholder': 'Please select',
              'address-label': 'Address',
              'address-placeholder': 'Street, postal code, city',
            },
            reason: {
              title: 'Reason & expectation',
              'reason-label': 'Valuation reason',
              'reason-placeholder': 'Please select',
              'timeHorizon-label': 'Time horizon',
              'timeHorizon-placeholder': 'Please select',
              'completion-label': 'Desired completion',
              'price-expectation-from-label': 'Amount / from',
              'price-expectation-to-label': 'to',
              'price-expectation-hint':
                'For the appraiser’s information only — the valuation is formed independently of it.',
              'note-label': 'Note',
              'note-placeholder': "Free text for the appraiser — anything the form doesn't ask for.",
            },
            documents: {
              title: 'Documents',
              // The Objekt hand-off (IMM-447.3) — uploaded documents also land on
              // the property; referenced property documents are already there.
              'hand-off-hint':
                'Uploaded documents are the property’s documents, and are sorted into a category there automatically.',
              empty: 'No documents yet. Upload files or select them from the property.',
              'upload-action': 'Upload',
              uploading: 'Uploading…',
              'select-action': 'Select from property',
              'upload-success': 'Document added.',
              'reference-success': 'Property document linked.',
              remove: 'Remove {name}',
              source: {
                UPLOADED: 'Uploaded',
                REFERENCED: 'From the property',
              },
              errors: {
                disallowed: 'File type or size not allowed (PDF, DOCX, JPEG, PNG; max. 20 MB).',
                upload: 'The document could not be uploaded. Please try again.',
                reference: 'The property document could not be linked. Please try again.',
                remove: 'The document could not be removed. Please try again.',
              },
              picker: {
                title: 'Select from property',
                loading: 'Loading property documents…',
                'load-error': 'The property documents could not be loaded.',
                empty: 'The property has no documents yet.',
                select: 'Add',
              },
            },
            // The images block (IMM-447.2) — directly below Documents, image files
            // only, with its own upload-and-remove list.
            images: {
              title: 'Images',
              'policy-hint': 'JPG, PNG · max. 25 MB',
              // The Objekt hand-off (IMM-447.4) — uploaded pictures also land in
              // the property's gallery, but never as the cover image.
              'hand-off-hint': 'Uploaded images also land in the property’s images (never as the cover image).',
              empty: 'No images yet. Upload photos of the property.',
              'upload-action': 'Upload',
              'upload-input-label': 'Upload images',
              uploading: 'Uploading…',
              'upload-success': 'Image added.',
              remove: 'Remove {name}',
              errors: {
                disallowed: 'Only image files are allowed (JPG, PNG; max. 25 MB).',
                upload: 'The image could not be uploaded. Please try again.',
                remove: 'The image could not be removed. Please try again.',
              },
            },
          },
          // The deal already has an open request (IMM-407.3 AC 34) — a second
          // create is refused.
          'one-open': 'This deal already has an ongoing valuation request.',
          // The agent's waiting line while the valuation sits at the approval gate
          // (IMM-475.4, parent AC 2). The agent sees the status and this line only
          // — neither the held figures nor the report.
          'awaiting-release-hint': 'The valuation is under review. You will be notified as soon as it is released.',
          'activity-title': 'Activity',
          // The reviewer's surface (IMM-475.4) — only for members holding
          // `organization:valuation:valuation-request-release`. A member without it
          // gets the status and nothing else; there are deliberately no disabled
          // controls here.
          review: {
            title: 'Review the price range and start price',
            'price-range-label': 'Price range',
            'recommended-start-price-label': 'Recommended start price',
            'valuation-date-label': 'Valuation date',
            'produced-by-label': 'Produced by',
            'submitted-at-label': 'Submitted on',
            'not-provided': '—',
            // Adjusting is deliberately its own step BEFORE the release (save
            // first, then release) — never a field inside the release dialog.
            'adjust-title': 'Adjust the figures',
            'price-from-label': 'Price range from',
            'price-to-label': 'Price range to',
            save: 'Save figures',
            saving: 'Saving…',
            'save-success': 'Figures saved.',
            release: 'Release',
            releasing: 'Releasing…',
            'release-success': 'Valuation released. The agent has been notified.',
            return: 'Return for rework',
            returning: 'Returning…',
            'return-success': 'Valuation returned for rework.',
            'return-cancel': 'Cancel',
            'return-submit': 'Return',
            'return-reason-label': 'Reason for the return',
            // A release always delivers the SAVED figures — it sends none of its
            // own. While the fields differ from them, „Freigeben" is blocked and
            // this line says why.
            'unsaved-figures':
              'The changed figures have not been saved yet. Save them first — a release always delivers the saved figures.',
            errors: {
              forbidden: 'You do not have permission to release valuations.',
              // A business-rule refusal (BL-020) — permanent, never "try again":
              // the valuation has left the awaiting-release state.
              'not-awaiting-release': 'This valuation is not awaiting release.',
              // 404 — the request is deleted or out of scope. Permanent too: never
              // "try again", re-read instead.
              'not-found': 'This valuation request is no longer available.',
              'result-invalid': 'The price range is invalid: the start price must sit within the range.',
              'return-reason-required': 'Please give a reason for the return.',
              'prices-required': 'Please give both ends of the price range.',
              save: 'The figures could not be saved. Please try again.',
              release: 'The valuation could not be released. Please try again.',
              return: 'The valuation could not be returned. Please try again.',
            },
          },
          // The read-only preview (IMM-407.3 AC 28) — every captured value grouped
          // by block, with "Back to the form" and "Submit".
          preview: {
            title: 'Request preview',
            intro: 'Review every entry before the request reaches the appraiser.',
            back: 'Back to the form',
            submit: 'Submit',
            submitting: 'Sending…',
            'not-provided': '—',
            // An uncaptured price reads as "no figure given", not as the em dash
            // every other field falls back to.
            'price-expectation-none': 'No figure given',
            'documents-empty': 'No documents',
            'images-empty': 'No images',
            'parking-empty': 'No parking spaces added',
            'units-empty': 'No units added',
            'unit-heading': 'Unit {index}',
            // Block titles for every preview block (the layout comes from the shared
            // `valuationBlockLayout` function).
            block: {
              basics: 'Basics',
              reason: 'Reason & expectation',
              condominium: 'Condominium details',
              house: 'House details',
              multiFamily: 'Multi-family house details',
              plot: 'Plot details',
              overallCondition: 'Quality and condition',
              parkingSpaces: 'Parking spaces',
              income: 'Income (actual)',
              units: 'Units',
              hoa: 'Homeowners association',
              documents: 'Documents',
              images: 'Images',
            },
            // Field names for the Basics and Reason blocks (the type-specific fields
            // use `form.field`).
            field: {
              objectType: 'Valuation object type',
              usage: 'Usage',
              address: 'Address',
              valuationReason: 'Valuation reason',
              timeHorizon: 'Time horizon',
              desiredCompletion: 'Desired completion',
              note: 'Note',
            },
          },
          // The delivered result on the tab (IMM-407.6, AC 3/52) — read-only once
          // the property carries a valuation. Leads with the price range, then the
          // recommended start price + valuation date, and offers the report for
          // preview / download.
          result: {
            title: 'Valuation result',
            'completed-at': 'Completed on {date}',
            'price-range-label': 'Price range',
            'recommended-start-price-label': 'Recommended start price',
            'valuation-date-label': 'Valuation date',
            'report-label': 'Valuation report',
            'report-fallback-name': 'Valuation report',
            'report-preview': 'Preview',
            'report-download': 'Download',
            'report-in-documents': "In the property's documents",
            'report-error': 'The valuation report could not be opened. Please try again.',
            // The report viewer (IMM-475.3): the toggle between the sandboxed HTML
            // preview and the existing PDF viewer, plus the note beneath the preview
            // saying nothing in the report is executed.
            viewer: {
              'format-label': 'Report format',
              html: 'HTML preview',
              pdf: 'PDF',
              'frame-title': 'Report preview',
              // Opens the report in the full-screen dialog (IMM-609): the preview
              // stays the glance, the dialog is where it is actually read.
              expand: 'Full screen',
              close: 'Close',
              note: 'Preview of the report — scripts and external content are disabled.',
              loading: 'Loading the preview …',
              error: 'The preview could not be loaded. Please try again.',
            },
            // Shown when this valuation replaced an earlier one (AC 47).
            superseded: "This valuation replaces the property's previous valuation of {date}.",
          },
          // Action on the completed screen (IMM-407.6) — the only action; the deal
          // phase is set through its normal phase controls, not from here.
          'request-again': 'Request new valuation',
          'revalue-hint': 'You can request a new valuation for this property at any time.',
          // The valuation agent's results above the tab content (IMM-1391.4).
          'agent-results': {
            title: VALUATION_PRODUCT_LABELS,
            author: 'Created by the valuation agent on {date}',
            superseded: 'Replaced on {date}',
            'price-range': 'Price range',
            'recommended-start-price': 'Recommended start price',
            view: 'View',
            download: 'Download',
          },
          // The completed-request history list on the tab (IMM-407.3 AC 35).
          history: {
            title: 'Completed valuations',
            'requested-by': 'Requested by {name}',
            'requested-at': 'Requested on {date}',
          },
          // Translated status labels for the valuation request (IMM-407).
          status: {
            DRAFT: 'Draft',
            REQUESTED: 'Requested',
            IN_PROGRESS: 'In progress',
            COMPLETED: 'Completed',
            WITHDRAWN: 'Withdrawn',
          },
        },
        sections: {
          object: 'Property',
          contacts: 'Contacts',
          commercial: 'Commercial data',
          activity: 'Activity',
          notes: 'Notes',
          progress: 'Phase progress',
          metadata: 'Metadata',
          responsible: 'Responsible members',
        },
        lifecycle: {
          responsible: {
            label: 'Responsible members',
            empty: 'Unassigned',
            'rbac-hint': 'Only the Owner, Workspace Admin and Standortleitung can assign deals.',
          },
          assign: {
            cta: 'Assign',
            title: 'Assign deal',
            'members-label': 'Responsible members',
            'empty-error': 'Please select at least one responsible member.',
            'no-results': 'No members found.',
            remove: 'Remove {name}',
            submit: 'Assign',
            submitting: 'Assigning…',
            cancel: 'Cancel',
            success: 'Deal assigned.',
            'forbidden-error': 'You are not allowed to assign this deal.',
            'not-found-error': 'This deal no longer exists.',
            'generic-error': 'The deal could not be assigned. Please try again.',
          },
        },
        pipeline: {
          'aria-label': 'Pipeline progress',
        },
        // The reference rail (IMM-456.3). Read-first: every „change" / „add"
        // affordance opens an already-shipped dialog, so only the card titles
        // and field labels live here — the actions keep the labels they have.
        rail: {
          'aria-label': 'Reference data',
          'standort-title': 'Location & responsibility',
          'standort-label': 'Location',
          'standort-no-access': 'No access',
          'responsible-label': 'Responsible',
          'owners-title': 'Owners',
          'phone-label': 'Phone',
          'email-label': 'E-mail',
          'object-title': 'Property',
          'object-type-label': 'Property type',
          'year-built-label': 'Year built',
          maps: 'On map',
          // Valuation (IMM-456.4). The card's status labels are DELIBERATELY its
          // own strings rather than the shared VALUATION_STATUS_LABELS: there
          // AWAITING_RELEASE reads „Awaiting approval", naming an internal immoOS
          // review the customer must never see. On the card the only true
          // statement is that it is being worked on.
          'valuation-title': 'Valuation',
          'valuation-empty': 'No valuation requested yet.',
          'valuation-request': 'Request valuation',
          'valuation-status-requested': 'Requested',
          'valuation-status-in-progress': 'In progress',
          'valuation-status-done': 'Completed',
          'valuation-requested-at-label': 'Requested on',
          'valuation-price-range-label': 'Price range',
          'valuation-start-price-label': 'Rec. starting price',
          'valuation-completed-at-label': 'Created on',
          'valuation-report': 'Open valuation document',
          'valuation-previous-result': 'Previous valuation',
          // Unterlagen-Check (IMM-456.5). The row notes are DELIBERATELY shorter
          // than the documents tab's („Läuft ab am {date}"): in a 360 px column
          // the date does not earn its place, the state does. The counter, the
          // „requested on" chip and the missing-e-mail reason are read unchanged
          // from the documents tab — a second copy of those is exactly how the
          // two surfaces would drift apart.
          'unterlagen-title': 'Document check',
          'unterlagen-count': '{present} / {required}',
          'unterlagen-request': 'Request missing',
          'unterlagen-status-missing': 'missing',
          'unterlagen-status-expiring': 'expiring',
          'unterlagen-status-expired': 'expired',
          'unterlagen-complete': 'Data room complete — every mandatory document is on file.',
          'unterlagen-more': 'and {count} more documents in the property data room',
          // Origin & notes (IMM-456.6). „Quelle" means two different things on
          // purpose: with a lead origin it is the lead's acquisition channel, and
          // without one it is the deal's own source. Both tables are total over
          // their enum, so a new token breaks the build rather than rendering a
          // raw key.
          'origin-title': 'Origin & notes',
          'origin-source-label': 'Source',
          'origin-received-label': 'Received',
          'origin-qualified-label': 'From lead',
          'origin-qualified-by': 'Qualified · {name}',
          'origin-qualified-anonymous': 'Qualified',
          'origin-concern-label': 'Request',
          'origin-created-label': 'Created on',
          'origin-lead-source': LEAD_SOURCE_LABELS,
          'origin-deal-source': DEAL_SOURCE_LABELS,
          // Deal key figures (IMM-456.6). The commission and days-in-phase are
          // deliberately absent — both sit permanently in the sticky header
          // (IMM-456.2), and repeating them one column over spends the rail's
          // scarce space saying something already on screen.
          'facts-title': 'Deal key figures',
          'facts-probability-label': 'Probability',
          'facts-horizon-label': 'Completion horizon',
          'facts-created-label': 'Created on',
          'facts-created-by-label': 'Created by',
          // Brokerage agreement (IMM-1171.8): the card at the top of the deal.
          'brokerage-contract': {
            title: 'Brokerage agreement',
            empty: 'No brokerage agreement created yet.',
            create: 'Create brokerage agreement',
            'load-error': 'The brokerage agreement could not be loaded.',
            'sub-line': '{templateName} · Created on {date}',
            'status-not-sent': 'Not sent yet',
            'status-sent': 'Sent for signature',
            'status-partially-signed': 'Partially signed',
            'status-signed': 'Signed',
            'status-revoked': 'Revoked',
            'status-withdrawn': 'Withdrawn',
            progress: '{signed} of {total} signed',
            'owner-signed': 'Signed on {date}',
            'owner-not-signed': 'Not signed yet',
            'owner-not-delivered': 'Not delivered',
            'acceptance-pending': 'Acceptance by e-mail: outstanding',
            'acceptance-received': 'Acceptance by e-mail: received on {date}',
            'acceptance-mark': 'Mark as received',
            'acceptance-dialog-title': 'Record acceptance by e-mail',
            'acceptance-dialog-body': 'Have you received the reply from {name} with the acceptance text?',
            'acceptance-dialog-confirm': 'Record',
            'acceptance-dialog-cancel': 'Cancel',
            'revoked-by': 'Revoked by {name} on {date}',
            'signed-on': 'Signed on {date}',
            send: 'Send for signature',
            remind: 'Send reminder',
            'download-signed': 'Download signed PDF',
            'view-pdf': 'View PDF',
            'view-proof': 'View proof',
            withdraw: 'Withdraw',
            'pending-file': 'Signed — PDF is being created …',
          },
          // Documents (IMM-268.4): the documents generated on the deal.
          documents: {
            title: 'Documents',
            empty: 'No document created yet.',
            'row-title': '{documentType} · {templateName}',
            'row-meta': '{createdBy}, {createdAt}',
            generating: 'Generating …',
            failed: 'Generation failed.',
            retry: 'Try again',
            download: 'Download',
            delete: 'Delete',
            'manually-adjusted': 'Edited by hand',
            'manually-adjusted-tooltip': 'The text of this document was edited and differs from the template.',
            'delete-title': 'Delete document',
            'delete-body': "Delete document ‘{fileName}’? Its copy in the property's documents is deleted too.",
            'delete-confirm': 'Delete',
            'delete-cancel': 'Cancel',
            'original-received': 'Original received',
            'original-received-on': 'Original received on {date}',
            'document-type': {
              BROKER_CONTRACT: DOCUMENT_CATEGORY_LABELS.BROKER_CONTRACT,
              POWER_OF_ATTORNEY: DOCUMENT_CATEGORY_LABELS.POWER_OF_ATTORNEY,
            } satisfies Record<DocumentTemplateCategory, string>,
            'load-error': 'The documents could not be loaded.',
            'generic-error': 'That did not work. Please try again.',
            signing: {
              send: 'Send for signature',
              'dialog-title':
                'Send {documentType, select, BROKER_CONTRACT {brokerage agreement} other {power of attorney}} for signature',
              'dialog-subtitle': '{templateName} · created on {date}',
              'dialog-hint':
                'Each owner gets an e-mail of their own with the document as a PDF and a personal link to sign it.',
              'email-label': 'E-mail address',
              'email-hint': 'Saved on the contact when you send.',
              'email-missing-no-permission':
                'No e-mail address on file. Please ask someone with access to the contact to add one.',
              'agency-signature-label': 'Your signature for {companyName}',
              'agency-signature-required': 'Your signature and confirmation are required.',
              'agency-signature-checkbox': 'I sign for {companyName}',
              'company-data-missing': 'Please ask an owner or admin to complete the company data.',
              'power-of-attorney-hint':
                'Land registries and some authorities require the power of attorney as an original. Have it signed on paper as well.',
              submit: 'Send',
              cancel: 'Cancel',
              'sent-notice':
                '{documentType, select, BROKER_CONTRACT {Brokerage agreement} other {Power of attorney}} sent to {count, plural, one {# owner} other {# owners}} for signature',
              'state-sent': 'Sent for signature · {signed} of {total} signed',
              'state-partially-signed': 'Partially signed · {signed} of {total}',
              'state-signed-pending-file': 'Signed — PDF is being created …',
              'state-withdrawn': 'Withdrawn',
              'state-revoked': 'Revoked',
              'delete-locked-open': 'Withdraw the signature request first.',
              'delete-locked-signed': 'Signed documents cannot be deleted.',
              'state-signed': 'Signed',
              'download-signed': 'Download signed PDF',
              'retry-load': 'Try again',
              'email-invalid': 'Enter a valid e-mail address.',
              'draft-load-error': 'The dialog could not be loaded. Please try again.',
              'generic-error': 'That did not work. Please try again.',
              remind: 'Send reminder',
              withdraw: 'Withdraw',
              'reminder-automatic': 'Automatic reminder on {date}',
              'reminder-last': 'Last reminder: {date}, {time}',
              'reminded-notice': 'Reminder sent to {count, plural, one {# owner} other {# owners}}',
              'withdraw-title': 'Withdraw signature request',
              'withdraw-body':
                'Withdraw the signature request for “{templateName}”? The owners’ links will stop working.',
              'withdraw-confirm': 'Withdraw',
              'withdraw-cancel': 'Cancel',
              'withdrawn-notice': 'Signature request withdrawn',
              'signer-sent': 'Sent on {date} to {email}',
              'signer-delivered': 'Delivered',
              'signer-not-delivered': 'Not delivered — the address rejected the e-mail.',
              'signer-signed': 'Signed on {date} at {time}',
              'signer-not-signed': 'Not signed yet',
              'signer-early-start': 'Early start requested',
              'signer-revoked': 'Revoked by {name} on {date}',
              'proof-open': 'View proof',
              'proof-title': 'Proof · {documentType}',
              'proof-subtitle': '{templateName} · {address}',
              'proof-sent-by': 'Sent by {name} on {date}, {time}',
              'proof-sent-at': 'Sent on {date}, {time}',
              'proof-agency-signature': 'Signature for the company:',
              'proof-signed': 'Signed',
              'proof-early-start': 'Early start requested',
              'proof-acceptance': 'Acceptance by e-mail',
              'proof-acceptance-received': 'received on {date}, {time} · recorded by {name}',
              'proof-acceptance-received-at': 'received on {date}, {time}',
              'proof-acceptance-pending': 'outstanding',
              'proof-yes': 'yes',
              'proof-no': 'no',
              'proof-ip': 'IP address',
              'proof-browser': 'Browser',
              'proof-no-browser': 'Not transmitted',
              'proof-link-sent-to': 'Link sent to',
              'proof-typed-name': 'Typed name, as signed',
              'proof-emails': 'E-mails sent',
              'proof-email-meta': '{kind} to {recipient} · {date}, {time}',
              'proof-email-delivered': 'Delivered',
              'proof-email-not-delivered': 'Not delivered',
              'proof-personal-link': '[personal link]',
              'proof-declarations': 'Declarations, word for word',
              'proof-checked': 'ticked',
              'proof-unchecked': 'not ticked',
              'proof-page': 'Page as displayed',
              'proof-not-signed': 'Not signed yet',
              'proof-close': 'Close',
              'proof-loading': 'Loading proof …',
              'proof-load-failed': 'The proof could not be loaded.',
              'email-kind': {
                INVITATION: 'Invitation',
                REMINDER: 'Reminder',
                CONFIRMATION: 'Confirmation',
                COMPLETION: 'Fully signed',
                ACCEPTANCE_REMINDER: 'Acceptance reminder',
                WITHDRAWAL_RECEIPT: 'Withdrawal receipt',
              } satisfies Record<SigningProofEmail['kind'], string>,
            },
          },
        },
        // „Dokument erstellen" (IMM-268.5): Vorlage wählen und fehlende Angaben ergänzen.
        'documents-dialog': {
          open: 'Create document',
          title: 'Create document',
          'document-type-label': 'Document type',
          'template-label': 'Template',
          'template-placeholder': 'Please select',
          'no-template': 'There is no template for this document type yet.',
          'no-template-link': 'Go to templates',
          'no-template-ask': 'Please ask an owner or admin to create a template.',
          'owner-coverage': 'This template names only {named} of {total} owners.',
          'templates-load-error': 'The templates could not be loaded.',
          'generic-error': 'That did not work. Please try again.',
          next: 'Next',
          cancel: 'Cancel',
          'missing-title': 'Missing details',
          'missing-hint':
            'Some details are still missing for this document. What you enter here is saved on the contact, property, deal or company details when the document is created.',
          'group-owner': 'Owner {name}',
          'group-property': 'Property',
          'group-deal': 'Deal',
          'group-location': 'Location {name}',
          'group-broker': 'Broker {name}',
          'group-document-only-hint': 'Applies to this document only — the location and profile stay unchanged.',
          'group-company': 'Company details',
          'optional-hint': 'Optional — if left empty, the document prints a line to fill in.',
          'required-empty': 'Fill in all required details.',
          'company-locked': 'Only owners and admins can change the company details.',
          'company-blocked': 'Please ask an owner or admin to complete the company details.',
          'source-locked': 'You are not allowed to change this detail.',
          'source-blocked': 'Please ask someone with edit rights to complete the missing details.',
          'not-applicable': 'This detail does not exist for a company as owner.',
          'not-applicable-blocked': 'Mark the field in the template as “May stay empty” to create the document.',
          'to-preview': 'Continue to preview',
          back: 'Back',
          'preview-title': 'Preview',
          'preview-hint':
            'Check the document. You can change values, tick boxes and adjust the text for this document. The template stays unchanged.',
          'save-hint': {
            contact: 'Saved on the contact when the document is created.',
            property: 'Saved on the property when the document is created.',
            deal: 'Saved on the deal when the document is created.',
            company: 'Saved in the company details when the document is created.',
            'document-only': 'Applies to this document only — the location and profile stay unchanged.',
          },
          'apply-value': 'Apply',
          'text-edited': 'Text edited manually — the template stays unchanged.',
          'reset-text': 'Reset text changes',
          'reset-confirm': 'Reset all text changes to this document?',
          'reset-confirm-ok': 'Reset',
          'reset-confirm-cancel': 'Cancel',
          create: 'Create',
          creating: 'Creating {documentType} …',
          'creating-hint': 'This only takes a moment.',
          'create-failed': 'Creation failed. Please try again.',
          created: '{documentType} created',
          'open-pdf': 'Open PDF',
          'box-toggle': 'Tick box',
          'change-value': 'Change {field}',
          'owner-row': 'Owner {name}: {input}',
          'broker-row': 'Broker {name}: {input}',
          'location-no-manager': 'This location has no location manager.',
          'location-no-manager-blocked': 'Please ask an owner or admin to assign a location manager to this location.',
          'location-option-label': 'Broker block',
          'location-option': {
            LOCATION: 'Location incl. location manager',
            HEADQUARTERS: 'Main location',
          },
          'location-option-replace-title': 'Replace location details?',
          'location-option-replace-body': 'Your changes to the location details will be lost.',
          'location-option-replace-confirm': 'Replace',
          'location-option-replace-cancel': 'Cancel',
          part: {
            'first-name': 'First name',
            'last-name': 'Last name',
            'street-and-number': 'Street and house number',
            street: 'Street',
            'house-number': 'House number',
            'postal-code': 'Postcode',
            city: 'City',
          },
          input: {
            OWNER: {
              NAME: 'Name',
              ADDRESS: 'Home address',
              DATE_OF_BIRTH: 'Date of birth',
              NATIONALITY: 'Nationality',
              BIRTH_NAME: 'Birth name',
              BIRTH_PLACE: 'Place of birth',
              EMAIL: 'Email',
              PHONE: 'Phone',
            },
            LOCATION: { NAME: 'Name', ADDRESS: 'Address', MANAGERS: 'Location manager' },
            BROKER: { NAME: 'Name', EMAIL: 'Email', PHONE: 'Phone' },
            PROPERTY: { ADDRESS: 'Address' },
            DEAL: {
              EXPECTED_SALE_PRICE: 'Expected sale price',
              MANDATE_TYPE: 'Mandate type',
              MANDATE_START: 'Start of services',
              MANDATE_DURATION: 'Term',
              SELLER_COMMISSION: 'Seller commission',
              BUYER_COMMISSION: 'Buyer commission',
            },
            COMPANY: {
              COMPANY_NAME: 'Company name',
              LEGAL_FORM: 'Legal form',
              ADDRESS: 'Address',
              MANAGING_DIRECTORS: 'Managing directors',
              REGISTER_COURT: 'Commercial register',
              REGISTER_NUMBER: 'Register number',
              VAT_ID: 'VAT ID',
              TRADE_PERMIT_AUTHORITY: 'Supervisory authority §34c',
              PROFESSIONAL_INDEMNITY: 'Professional indemnity insurance',
            },
          } satisfies {
            [S in keyof typeof DOCUMENT_SOURCE_INPUTS]: Record<(typeof DOCUMENT_SOURCE_INPUTS)[S][number], string>;
          },
        },
        object: {
          open: 'Open property',
        },
        contacts: {
          count: '({count})',
          'primary-badge': 'Primary',
          add: 'Add owner',
          remove: 'Remove {name}',
        },
        'add-dialog': {
          title: 'Add owner',
          intro: 'Pick one or more additional owner contacts for this deal.',
          submit: 'Add',
          submitting: 'Adding …',
          cancel: 'Cancel',
        },
        errors: {
          forbidden: 'You are not allowed to perform this action.',
          duplicate: 'This contact is already linked to the deal.',
          'non-owner-link-conflict':
            'This contact already holds a different role on the property. A deal requires an owner relationship.',
          'not-owner': 'This contact is not an owner of the property. Add them as an owner on the property first.',
          'active-deal-overlap': 'An active deal already exists for this owner + property.',
          'won-immutable': 'Won deals are immutable.',
          'last-contact': 'At least one owner contact is required. Add another owner before removing this one.',
          'defer-not-active': 'Won or lost deals cannot be deferred.',
          'resume-not-deferred': 'This deal is not deferred.',
          'mark-lost-won-immutable': 'Won deals are immutable.',
          'mark-lost-already-lost': 'This deal is already lost.',
          'reopen-not-lost': 'This deal is not lost.',
          'delete-won-immutable': 'Won deals are immutable.',
          'no-loss-reason': 'Please pick a loss reason',
          generic: 'The action failed. Please try again.',
        },
        commercial: {
          'fee-label': 'Commission fee',
          'fee-helper': '{percent} % of the sale price',
          'provision-helper': '{percent} % of the sale price · net, plus VAT · standard commission split',
          'sale-price-label': 'Expected sale price',
          'commission-label': 'Commission',
          'commission-percent-label': 'Commission',
          'expected-fee-label': 'Expected commission fee',
          'probability-label': 'Probability',
          'completion-horizon-label': 'Completion horizon',
          'vot-label': 'Listing appointment agreed',
          'vot-yes': 'Yes',
          'vot-no': 'No',
        },
        actions: {
          defer: 'Defer',
          'mark-lost': 'Mark as lost',
          resume: 'Resume',
          reopen: 'Reopen',
          'no-update-permission': 'You do not have permission to edit this deal.',
          delete: 'Delete',
          more: 'More actions',
          'coming-soon-hint': 'Coming soon',
        },
        'defer-dialog': {
          title: 'Defer deal',
          intro: 'Choose when this deal should automatically resume.',
          'presets-label': 'Quick presets',
          'date-label': 'Deferred until',
          'preset-week': '1 week',
          'preset-month': '1 month',
          'preset-quarter': '3 months',
          'preset-custom': 'Custom date',
          submit: 'Defer',
          submitting: 'Deferring …',
          cancel: 'Cancel',
          'error-no-date': 'Please pick a date for the deal to resume.',
          'error-past-date': 'The follow-up date must be today or in the future.',
        },
        hold: {
          title: 'Deferral',
          'deferred-until-label': 'Deferred until',
          edit: 'Edit',
          'edit-aria': 'Edit follow-up date',
          save: 'Save',
          saving: 'Saving …',
          cancel: 'Cancel',
        },
        'defer-success': 'Deal deferred until {date}.',
        'resume-success': 'Deal resumed.',
        'resume-error': 'The deal could not be resumed.',
        'auto-resume-banner': 'This deal was automatically resumed on {date} — you had deferred it until then.',
        'auto-resume-dismiss': 'Got it',
        'lost-dialog': {
          title: 'Mark as lost',
          intro: 'Mark “{title}” as lost. Pick a loss reason. A note is optional.',
          'reason-label': 'Loss reason',
          'reason-placeholder': 'Pick a loss reason …',
          'note-label': 'Loss note',
          'note-placeholder': 'Optional note about the loss …',
          'note-recommended': 'Recommended for “Other” — please add a short explanation.',
          submit: 'Mark as lost',
          submitting: 'Marking …',
          cancel: 'Cancel',
          'error-no-reason': 'Please pick a loss reason',
        },
        lost: {
          'reason-label': 'Loss reason',
          'note-label': 'Loss note',
          banner: 'Deal lost — choose “Reopen” to work on it again.',
        },
        'mark-lost-success': 'Deal marked as lost.',
        'mark-lost-error': 'The deal could not be marked as lost. Please try again.',
        'reopen-success': 'Deal reopened.',
        'reopen-error': 'The deal could not be reopened.',
        'delete-dialog': {
          title: 'Delete deal',
          intro: 'Are you sure you want to delete the deal “{name}”? It will disappear from every view and list.',
          submit: 'Delete',
          submitting: 'Deleting …',
          cancel: 'Cancel',
        },
        'delete-success': 'Deal deleted.',
        'delete-error': 'The deal could not be deleted.',
        activity: {
          empty: 'No activity yet.',
          'action-created': 'Deal created',
          'action-forward': 'Phase advanced',
          'action-backward': 'Phase moved back',
          'action-contact-added': 'Owner added',
          'action-contact-removed': 'Owner removed',
          transition: '{from} → {to}',
          'actor-system': 'System',
          'actor-unknown': 'Unknown member',
          'contact-unknown': 'Unknown contact',
          'reason-label': 'Reason',
          'load-more': 'Load more',
          'loading-more': 'Loading …',
        },
        notes: {
          'obstacles-label': 'Obstacles',
          'next-steps-label': 'Next steps',
          'coming-soon': 'Coming soon',
          empty: 'Nothing recorded yet.',
          edit: 'Edit',
          'edit-aria': 'Edit {label}',
          save: 'Save',
          saving: 'Saving …',
          cancel: 'Cancel',
          'save-error': 'The note could not be saved.',
          'obstacles-placeholder': 'E.g. spouse still needs to agree, heirs undecided …',
          'next-steps-placeholder': 'E.g. valuation appointment Thursday 2 pm …',
        },
        kpis: {
          'days-since-creation-label': 'Days since creation',
          'probability-label': 'Probability',
          'days-value': '{days, plural, one {# day} other {# days}}',
        },
        progress: {
          count: '{done} of {total} phases completed',
          'horizon-hint': 'Completion horizon · {value}',
        },
        metadata: {
          'created-at-label': 'Created on',
          'days-since-creation-label': 'Days since creation',
          'created-by-label': 'Created by',
          'created-by-coming-soon': 'Coming soon',
          'source-label': 'Source',
        },
        won: {
          banner: 'Deal won — commercial data is locked. The sale price and commission can no longer be changed.',
          'lock-hint': 'Locked — deal won',
        },
        rbac: {
          banner: 'You have read access to this deal. Only the owner, an admin, or the creator can make changes.',
          'read-only': 'Read-only',
        },
      },
      create: {
        title: 'Create deal',
        'standort-label': 'Location',
        intro: 'Link an owner and a property. You can add the optional commercial data later.',
        sections: {
          contacts: 'Contacts',
          commercial: 'Commercial data',
          notes: 'Notes',
        },
        'property-label': 'Property',
        'property-placeholder': 'Search property…',
        'property-helper': 'Exactly one property. The transaction type is taken from the property.',
        'property-clear': 'Clear property selection',
        'property-loading': 'Loading properties…',
        'property-load-error': 'Could not load properties.',
        'property-no-results': 'No properties found.',
        'owners-label': 'Owners',
        'owners-placeholder': 'Search contact…',
        'owners-helper':
          'Select every owner of this property. For co-ownership (e.g. a married couple or a community of heirs) add all of them here — they share this single deal. The first one is the primary contact.',
        'owners-primary-badge': 'Primary',
        'owners-remove': 'Remove {name}',
        'owners-loading': 'Loading contacts…',
        'owners-load-error': 'Could not load contacts.',
        'owners-no-results': 'No contacts found.',
        'source-label': 'Source',
        'source-value': 'Manual',
        'source-hint': 'Set automatically · not editable',
        'expected-sale-price-label': 'Expected sale price',
        'expected-sale-price-placeholder': 'e.g. 450000',
        'expected-sale-price-helper': 'Estimated sale price',
        'commission-label': 'Seller commission (%)',
        'commission-hint': 'Net, plus VAT.',
        'fee-label': 'Expected commission fee',
        'fee-helper': 'Calculated from sale price × commission',
        'fee-empty': '—',
        'notes-obstacles-label': 'Obstacles',
        'notes-obstacles-placeholder': 'e.g. spouse still needs to agree, community of heirs not aligned …',
        'notes-next-steps-label': 'Next steps',
        'notes-next-steps-placeholder': 'e.g. valuation appointment Thursday 2 pm …',
        'probability-label': 'Probability',
        'probability-empty': 'Not set',
        'completion-horizon-label': 'Completion horizon',
        'completion-horizon-empty': 'Not set',
        'submit-cta': 'Create deal',
        'submit-loading': 'Creating…',
        'cancel-cta': 'Cancel',
        'forbidden-error': 'You do not have permission for this action.',
        'property-not-found-error':
          'The selected property does not belong to the chosen Standort. Please select it again.',
        'contact-not-found-error': 'A selected contact was not found.',
        'type-mismatch-error': 'The transaction type does not match the selected property.',
        'link-conflict-error': 'A contact is already linked to the property with a different role.',
        'active-deal-exists-error': 'An active deal of this transaction type already exists for this property.',
        'property-archived-error': 'This property is archived. Remove the archival first to create a deal.',
        'generic-error': 'Could not create deal. Please try again.',
        errors: {
          property: {
            required: 'Please select a property.',
          },
          owners: {
            required: 'Please select at least one owner contact.',
          },
          'expected-sale-price': {
            invalid: 'Please enter an amount of 0 or more.',
          },
          commission: {
            invalid: 'Commission must be between 0 and 100.',
          },
        },
      },
      // Why a deal can't change phase — shared by the board's lock popover and
      // the detail page's banner so both explain the block the same way.
      'lost-phase': {
        label: 'Lost',
        in: 'in: {phase}',
      },
      'phase-lock': {
        won: 'This deal is won and its phase is locked. Won deals can’t be moved.',
        deferred: 'This deal is on hold. Resume it to move it through the pipeline.',
        lost: 'This deal is marked lost. Reopen it to change its phase.',
        'final-phase': 'This deal is already in the final phase and can’t advance any further.',
        'no-permission': 'You don’t have permission to move this deal to another phase.',
        // Short tags shown next to the lock icon on board cards.
        short: {
          won: 'Won',
          deferred: 'On hold',
          lost: 'Lost',
          'final-phase': 'Final phase',
          'no-permission': 'No access',
        },
      },
      advance: {
        cta: 'Move to next phase',
        'dialog-title': 'Change phase',
        'dialog-transition': '{from} → {to}',
        'note-label': 'Note',
        'note-placeholder': 'What happened in this phase? (optional)',
        'note-helper': 'The note appears in the phase history.',
        'note-too-long': 'The note may be at most 1000 characters.',
        submit: 'Change phase',
        submitting: 'Changing …',
        cancel: 'Cancel',
        'success-toast': 'Deal moved to the next phase.',
        'forecast-nudge': 'Please add probability and completion horizon so your funnel forecast stays meaningful.',
        'contract-received':
          'Contract received. Marketing of the property starts automatically once all required fields are present — otherwise complete them on the property page.',
        promotion: {
          'activated-toast': 'Marketing started automatically.',
          pending: 'Marketing is being started — this can take a moment.',
          'gate-failed':
            'Marketing could not be started — the following fields are missing: {fields}. Please complete them on the property detail page.',
          'subscriber-failed':
            'Marketing could not be started automatically — please start it manually with “Start marketing” or retry.',
          cta: 'Go to property detail',
        },
        'won-banner': 'Congratulations! This deal is won and marked as “Won”.',
        'banner-dismiss': 'Dismiss',
        'skip-error': 'Phases cannot be skipped.',
        'not-active-error': 'Only active deals can be moved to the next phase.',
        'forbidden-error': 'You do not have permission for this action.',
        'not-found-error': 'This deal could not be found. Please reload the page.',
        'generic-error': 'Could not change the phase. Please try again.',
      },
      revert: {
        cta: 'Move phase back',
        'success-toast': 'Deal moved back to an earlier phase.',
        'dialog-title': 'Move phase back',
        'dialog-intro': 'Pick an earlier phase and give a reason for moving the deal back.',
        'phase-label': 'Earlier phase',
        'phase-placeholder': 'Select phase',
        'reason-label': 'Reason',
        'reason-placeholder': 'Why is this deal being moved back?',
        submit: 'Move back',
        submitting: 'Moving back …',
        cancel: 'Cancel',
        'reason-required': 'Please give a reason for moving the deal back.',
        'not-earlier-error': 'A deal can only be moved back to an earlier phase.',
        'not-active-error': 'Only active deals can be moved back.',
        'forbidden-error': 'You do not have permission for this action.',
        'not-found-error': 'This deal could not be found. Please reload the page.',
        'generic-error': 'Could not move the phase back. Please try again.',
      },
    },
    leads: {
      title: 'Owners',
      'create-cta': 'New lead',
      status: {
        NEW: 'New',
        CONTACTED: 'Contacted',
        CONVERTED: 'Converted',
        LOST: 'Lost',
        ARCHIVED: 'Archived',
      },
      source: LEAD_SOURCE_LABELS,
      salutation: {
        MRS: 'Mrs',
        MR: 'Mr',
        DIVERSE: 'Diverse',
        FAMILY: 'Family',
        OTHER: 'Other',
      },
      // The lead shares the deal's Verlustgrund set (IMM-429), so the labels
      // are the deal's too.
      'loss-reason': DEAL_LOSS_REASON_LABELS,
      'legal-basis': {
        CONSENT: 'Consent',
        CONTRACT_INITIATION: 'Contract initiation',
        PRE_CONTRACTUAL_MEASURES: 'Pre-contractual measures',
        LEGAL_OBLIGATION: 'Legal obligation',
        LEGITIMATE_INTEREST: 'Legitimate interest',
      },
      // The owner's Absicht (IMM-792.1) — two values, never a third. `null` is
      // "not yet asked" and is rendered separately (list `intent-unanswered`),
      // never as a choice here.
      intent: {
        SALE: 'sell',
        RENTAL: 'let',
      },
      list: {
        subtitle:
          'Every incoming Owner-Lead lands here — capture owners from partners, portals, your website, referrals, and cold calls in one inbox.',
        'load-error': 'Could not load owner leads.',
        'load-error-hint': 'Try again to reload the list.',
        'load-retry': 'Try again',
        'filter-save-error': 'Could not save your filters — please try again.',
        'empty-title': 'No Owner-Leads yet.',
        'empty-description':
          'Every incoming Owner-Lead lands here. Capture your first one — from a partner, a portal enquiry, your website, a referral, or a cold call.',
        'success-toast': 'Lead “{name}” captured.',
        'no-match': 'No leads match your search.',
        filters: {
          'search-label': 'Search leads',
          'search-placeholder': 'Search name, phone, address …',
          'status-label': 'Status',
          'status-all': 'All statuses',
          'source-label': 'Source',
          'source-all': 'All sources',
          'partner-label': 'Lead Partner',
          'partner-all': 'All partners',
          'assigned-to-me': 'Assigned to me',
          orphaned: 'Orphaned leads',
          'show-archived': 'Show archived',
          'clear-all': 'Clear all',
        },
        'sort-label': 'Sort by {column}',
        columns: {
          name: 'Owner',
          address: 'Address',
          intent: 'Intent',
          value: 'Property value',
          source: 'Source',
          partner: 'Lead Partner',
          standort: 'Location',
          assigned: 'Assigned',
          status: 'Status',
          received: 'Received',
          'last-contact': 'Last contact',
        },
        'no-address': '—',
        'intent-unanswered': 'not recorded',
        'no-value': '—',
        'no-partner': '—',
        'no-standort': '—',
        'no-assignee': '–',
        'no-last-contact': '—',
      },
      'deactivated-suffix': '(deactivated)',
      create: {
        title: 'Capture Owner-Lead',
        subtitle:
          'Only Last name and Source are required — you can complete the rest from the lead’s detail page later.',
        'step-indicator': 'Step {current} of {total}',
        'address-search-label': 'Search address',
        'address-search-placeholder': 'Start typing a street or place…',
        'address-search-helper': 'Pick a suggestion to fill street, postcode, city, and country.',
        sections: {
          owner: 'Owner',
          property: 'Property',
          'source-gdpr': 'Source & GDPR',
        },
        fields: {
          salutation: 'Salutation',
          'first-name': 'First name',
          'last-name': 'Last name',
          standort: 'Location',
          email: 'Email',
          phone: 'Phone',
          mobile: 'Mobile',
          street: 'Street',
          'house-number': 'House number',
          'postal-code': 'Postcode',
          city: 'City',
          country: 'Country',
          'cadastral-district': 'Cadastral district',
          'cadastral-parcel': 'Cadastral parcel',
          'object-type': 'Property type',
          subtype: 'Subtype',
          intent: 'Intent',
          'construction-year': 'Construction year',
          'living-area': 'Living area (m²)',
          'plot-area': 'Plot area (m²)',
          'estimated-value': 'Estimated value',
          source: 'Source',
          'lead-partner': 'Lead Partner',
          'received-at': 'Received at',
          'legal-basis': 'Legal basis',
          notes: 'Notes',
        },
        'lead-partner-placeholder': 'Pick an active Lead Partner',
        'optional-hint': 'optional',
        'unset-option': 'Not specified',
        'intent-placeholder': 'Please select',
        'intent-hint': 'Stays empty until someone has asked.',
        back: 'Back',
        next: 'Next',
        submit: 'Capture lead',
        submitting: 'Capturing…',
        cancel: 'Cancel',
        errors: {
          'last-name-required': 'Last name is required.',
          'last-name-too-long': 'Last name must be at most 80 characters.',
          'first-name-too-long': 'First name must be at most 80 characters.',
          'email-invalid': 'Please enter a valid email address.',
          'phone-invalid': 'Phone number is invalid.',
          'mobile-invalid': 'Mobile number is invalid.',
          'received-at-bounds': 'Received at must be between 90 days ago and now.',
          'source-required': 'Source is required.',
          'partner-required': 'A Lead Partner is required for source »Partner«.',
          generic: 'Could not capture the lead — please try again.',
          forbidden: 'You are not allowed to capture leads in this workspace.',
          'not-a-member': 'You are not a member of this workspace.',
        },
      },
      dedup: {
        title: 'Possible duplicate found',
        body: '{count, plural, one {# existing lead matches this entry.} other {# existing leads match this entry.}} Save anyway, or open the existing lead?',
        'matched-on': {
          email: 'email',
          address: 'address',
          cadastral: 'cadastral ID',
        },
        'matched-label': 'matched on {dimensions}',
        'save-anyway': 'Save anyway',
        'open-existing': 'Open existing lead',
        saving: 'Saving…',
      },
      detail: {
        'load-error': 'Could not load this lead.',
        'load-retry': 'Try again',
        'back-to-inbox': 'Back to inbox',
        'no-value': '—',
        'notes-show-more': 'Show more',
        'notes-show-less': 'Show less',
        'address-open-maps': 'Open in Google Maps',
        standort: {
          heading: 'Location',
          unassigned: 'No location',
          change: 'Change',
        },
        timeline: {
          'add-cta': 'Add activity',
          save: 'Save',
          saving: 'Saving …',
          cancel: 'Cancel',
          'type-label': 'Type',
          'type-note': 'Note',
          'type-appointment': 'Appointment',
          'appointment-type-label': 'Appointment type',
          'appointment-type-placeholder': 'Please choose',
          'appointment-type-valuation': 'Valuation appointment',
          'appointment-type-on-site': 'On-site appointment',
          'appointment-type-property-survey': 'Property survey',
          'appointment-type-contract-signing': 'Contract signing',
          'appointment-type-other': 'Other appointment',
          'appointment-type-viewing-on-site': 'On-site viewing',
          'appointment-type-viewing-virtual': 'Virtual viewing',
          'appointment-type-consultation': 'Consultation',
          'appointment-type-financing-consultation': 'Financing consultation',
          'appointment-type-negotiation': 'Negotiation',
          'appointment-type-notary': 'Notary appointment',
          'appointment-type-handover': 'Handover',
          'error-no-appointment-type': 'Please choose an appointment type.',
          'type-call': 'Call',
          'type-sms': 'SMS',
          'type-whatsapp': 'WhatsApp',
          'type-fax': 'Fax',
          'type-letter': 'Letter',
          'type-email': 'Email',
          'direction-label': 'Direction',
          'direction-inbound': 'Inbound',
          'direction-outbound': 'Outbound',
          'subject-label': 'Subject',
          'content-label': 'Content',
          'content-counter': '{count, number} / {max, number}',
          'content-placeholder': 'What happened?',
          'occurred-at-label': 'Occurred at',
          'now-cta': 'Now',
          'occurred-at-time-label': 'Time',
          'error-empty-content': 'Please enter some content.',
          'error-content-too-long': 'An activity must not exceed 10,000 characters.',
          'error-no-direction': 'Please choose a direction (inbound or outbound).',
          'error-subject-too-long': 'The subject must not exceed 200 characters.',
          'error-future': 'An activity cannot be in the future.',
          'error-occurred-at-incomplete': 'Please provide both date and time.',
          'save-error': 'The activity could not be saved.',
          'update-error': 'The activity could not be saved.',
          'mode-label': 'When',
          'mode-log': 'Log now',
          'mode-plan': 'Plan for later',
          'due-at-label': 'Due on',
          'assignee-label': 'Assignee',
          'assignee-self': 'Me',
          'plan-hint': 'This activity stays an open task until you complete it.',
          'plan-submit': 'Plan as task',
          'error-due-at-required': 'Please provide a due date in the future.',
          'upcoming-heading': 'Upcoming',
          'history-heading': 'History',
          'badge-planned': 'Planned',
          'badge-overdue': 'Overdue',
          'due-prefix': 'Due on {date}',
          'assignee-prefix': 'Assignee: {name}',
          'created-by-prefix': 'Created by {name}',
          'upcoming-empty': 'No upcoming activities.',
          'history-empty': 'No completed activities yet.',
          'activity-empty': 'No activity yet — log the first contact or plan a follow-up.',
          'action-complete': 'Mark as done',
          'action-reschedule': 'Reschedule',
          'action-cancel': 'Cancel',
          'outcome-label': 'Outcome',
          'outcome-none': 'No outcome',
          'outcome-reached': 'Reached',
          'outcome-not-reached': 'Not reached',
          'outcome-voicemail': 'Voicemail',
          'outcome-callback-agreed': 'Callback agreed',
          'outcome-email-reached': 'Reply received',
          'outcome-email-not-reached': 'No reply',
          'outcome-attended': 'Attended',
          'outcome-not-attended': 'Not attended',
          'outcome-rescheduled': 'Rescheduled',
          'complete-heading': 'Complete activity',
          'complete-note-label': 'How did it go?',
          'complete-note-placeholder': 'Optional note on the outcome',
          'complete-submit': 'Done',
          'complete-submit-follow-up': 'Done & follow-up',
          'complete-error': 'The activity could not be completed.',
          'follow-up-heading': 'A follow-up will be created',
          'follow-up-auto': 'automatic',
          'follow-up-due-label': 'Due on',
          'follow-up-footer': 'Default: next day, same time — adjustable.',
          'follow-up-reset': 'Default',
          'reschedule-heading': 'Reschedule activity',
          'reschedule-due-label': 'New due date',
          'reschedule-submit': 'Reschedule',
          'reschedule-error': 'The activity could not be rescheduled.',
          'cancel-heading': 'Cancel activity?',
          'cancel-confirm-text':
            'The activity will be cancelled and disappear from “Upcoming”. It will not appear in the history.',
          'cancel-confirm': 'Cancel activity',
          'cancel-keep': 'Keep',
          'cancel-error': 'The activity could not be cancelled.',
          'reassign-label': 'Assignee',
          'reassign-error': 'The assignee could not be changed.',
          // Mirror of the German reasons (IMM-804.1) — see the de dictionary.
          'action-no-permission': 'You do not have permission to edit activities.',
          'reassign-no-permission': 'You do not have permission to change who is responsible.',
          'complete-instead-prefix': 'Or instead:',
        },
        sections: {
          owner: 'Owner',
          property: 'Property',
          address: 'Address',
          notes: 'Notes',
          gdpr: 'GDPR & provenance',
        },
        fields: {
          name: 'Name',
          'street-no': 'Street & no.',
          'postcode-city': 'Postcode & city',
          'created-at': 'Created at',
        },
        kpi: {
          'days-in-inbox': 'Days in inbox',
          'est-value': 'Est. value',
        },
        'status-heading': 'Status',
        'status-since': 'since {date}',
        'tab-activity': 'Activity',
        edit: 'Edit',
        'edit-coming-soon': 'Editing owner leads is coming soon.',
        'edit-owner': {
          title: 'Edit owner',
          save: 'Save',
          saving: 'Saving…',
          cancel: 'Cancel',
          success: 'Owner updated.',
          'forbidden-error': 'You do not have permission to edit this lead.',
          'not-found-error': 'This lead no longer exists.',
          'generic-error': 'Could not save the owner. Please try again.',
        },
        'edit-notes': {
          title: 'Edit notes',
          field: 'Notes',
          save: 'Save',
          saving: 'Saving…',
          cancel: 'Cancel',
          success: 'Notes updated.',
          'forbidden-error': 'You do not have permission to edit this lead.',
          'not-found-error': 'This lead no longer exists.',
          'generic-error': 'Could not save the notes. Please try again.',
        },
        'edit-legal-basis': {
          title: 'Edit legal basis',
          save: 'Save',
          saving: 'Saving…',
          cancel: 'Cancel',
          success: 'Legal basis updated.',
          'forbidden-error': 'You do not have permission to edit this lead.',
          'not-found-error': 'This lead no longer exists.',
          'generic-error': 'Could not save the legal basis. Please try again.',
        },
        'edit-address': {
          title: 'Edit address',
          save: 'Save',
          saving: 'Saving…',
          cancel: 'Cancel',
          success: 'Address updated.',
          'forbidden-error': 'You do not have permission to edit this lead.',
          'not-found-error': 'This lead no longer exists.',
          'generic-error': 'Could not save the address. Please try again.',
        },
        'edit-property': {
          title: 'Edit property',
          save: 'Save',
          saving: 'Saving…',
          cancel: 'Cancel',
          success: 'Property updated.',
          'forbidden-error': 'You do not have permission to edit this lead.',
          'not-found-error': 'This lead no longer exists.',
          'generic-error': 'Could not save the property. Please try again.',
        },
        lifecycle: {
          heading: 'Actions',
          more: 'More actions',
          'mark-lost': {
            cta: 'Mark as lost',
            title: 'Mark lead as lost',
            'reason-label': 'Loss reason',
            'reason-placeholder': 'Choose a loss reason',
            'note-label': 'Loss note',
            'note-placeholder': 'Optional note (max. 500 characters)',
            submit: 'Mark as lost',
            submitting: 'Marking…',
            cancel: 'Cancel',
            success: 'Lead marked as lost.',
            errors: {
              'reason-required': 'Please choose a loss reason',
              'note-too-long': 'The loss note must be at most 500 characters long',
              generic: 'The lead could not be marked as lost — please try again.',
            },
          },
          archive: {
            cta: 'Archive',
            success: 'Lead archived.',
            error: 'The lead could not be archived — please try again.',
          },
          reopen: {
            cta: 'Reopen',
            success: 'Lead reopened.',
            error: 'The lead could not be reopened — please try again.',
          },
          responsible: {
            label: 'Responsible members',
            empty: 'Unassigned',
          },
          assign: {
            cta: 'Assign',
            title: 'Assign lead',
            'members-label': 'Responsible members',
            'group-standort': 'Location {name}',
            'group-other': 'Other members',
            'empty-error': 'Please select at least one responsible member.',
            'no-results': 'No members found.',
            remove: 'Remove {name}',
            submit: 'Assign',
            submitting: 'Assigning…',
            cancel: 'Cancel',
            success: 'Lead assigned.',
            'forbidden-error': 'You do not have permission to assign this lead.',
            'not-found-error': 'This lead no longer exists.',
            'generic-error': 'The lead could not be assigned — please try again.',
          },
        },
        qualify: {
          heading: 'Convert',
          cta: 'Qualify',
          'confirm-title': 'Qualify lead',
          'confirm-body':
            'This owner lead becomes a contact, a property and a deal. Check the location, choose the start phase and optionally record the reason.',
          confirm: 'Qualify lead',
          'location-label': 'Location',
          'location-placeholder': 'Select a location',
          'startphase-label': 'Start phase',
          'startphase-hint': 'Which phase does the deal start in?',
          'marketing-type-label': 'Is the property being sold or let?',
          'marketing-type-hint': 'This decides which documents and price fields the property gets.',
          'marketing-type-option': MARKETING_TYPE_LABELS,
          'owner-first-name-label': "Owner's first name",
          'note-label': 'Note',
          'note-placeholder': 'What prompted the qualification? (optional)',
          'note-hint': "The note appears in the deal's phase history.",
          'note-too-long': 'The note may be at most 1000 characters long.',
          'preview-title': 'What will be created',
          'preview-contact': 'Contact',
          'preview-object': 'Property',
          'preview-deal': 'Deal',
          submitting: 'Qualifying…',
          cancel: 'Cancel',
          success: 'Lead qualified.',
          errors: {
            forbidden: 'You do not have permission to qualify this lead.',
            'not-found': 'This lead no longer exists.',
            conflict: 'This lead can no longer be qualified.',
            'missing-fields': 'The property is missing fields needed to publish it: {fields}. Add them, then qualify.',
            generic: 'The lead could not be qualified — please try again.',
          },
          'publish-fields': {
            STREET: 'Street',
            HOUSE_NUMBER: 'House number',
            POSTAL_CODE: 'Postal code',
            CITY: 'City',
            PURCHASE_PRICE: 'Purchase price',
            BASE_RENT: 'Base rent',
            LIVING_AREA: 'Living area',
            USABLE_AREA: 'Usable area',
            TOTAL_AREA: 'Total area',
            PLOT_AREA: 'Plot area',
            PARKING_SPACE_TYPE: 'Parking-space type',
            VAT_TREATMENT: 'VAT treatment',
            AVAILABLE_FROM: 'Available from',
            ANCILLARY_COSTS_TYPE: 'Type of ancillary costs',
          },
        },
        converted: {
          'panel-heading': 'This lead was qualified.',
          'qualified-by': 'Qualified by {name} on {date}.',
          'qualified-by-anon': 'Qualified by a team member on {date}.',
          'records-intro': 'These records were created from the lead:',
          contact: 'Contact',
          property: 'Property',
          deal: 'Deal',
        },
      },
    },
    // The Activities tab — Upcoming (IMM-776.3), the successor to "My tasks"
    // above. Merges the two bounded reads IMM-776.1/.2 client-side; each source
    // carries its own failure (BL-058).
    activities: {
      title: 'Activities',
      'subtitle-own': 'Everything assigned to you — overdue first.',
      'subtitle-oversight': 'Every activity in your locations — overdue first.',
      'subtitle-workspace': 'Every activity in the workspace — overdue first.',
      'past-when': { cancelled: 'Cancelled on {date}', planned: 'Planned for {date}' },
      // Counted day separators. A group with no rows is not rendered at all, not
      // shown with "(0)".
      groups: {
        overdue: 'Overdue ({count})',
        today: 'Today ({count})',
        tomorrow: 'Tomorrow ({count})',
        'this-week': 'This week ({count})',
        later: 'Later ({count})',
      },
      'group-count': {
        whole: '{total}',
        partial: '{shown} of {total}',
        'whole-bounded': '{total}+',
        'partial-bounded': '{shown} of {total}+',
      },
      segments: {
        upcoming: 'Upcoming',
        past: 'Past',
      },
      columns: {
        due: 'Due',
        occurred: 'Occurred on',
        kind: 'Type',
        content: 'Activity',
        subject: 'Relates to',
        assignee: 'Assignee',
        standort: 'Location',
        status: 'Status',
        actions: 'Actions',
      },
      // The Due cell is group-relative (Overdue/Later vs. Today/Tomorrow vs.
      // This week) — two ICU templates rather than string concatenation (ADR 0009).
      'due-date-time': '{date}, {time}',
      'due-weekday-time': '{weekday}, {time}',
      status: {
        open: 'Open',
        overdue: 'Overdue',
        done: 'Done',
        cancelled: 'Cancelled',
      },
      actions: {
        complete: 'Done',
        reschedule: 'Reschedule',
        cancel: 'Cancel',
        'reschedule-title': 'Reschedule activity',
        'reschedule-date': 'New date',
        'reschedule-time': 'New time',
        'reschedule-submit': 'Save new date',
        back: 'Back',
        'cancel-title': 'Really cancel this activity?',
        'cancel-confirm': 'Cancel activity',
        'context-line': '{kind} · {content}',
        completed: 'Activity marked as done.',
        rescheduled: 'Activity rescheduled.',
        cancelled: 'Activity cancelled.',
        refused: 'You may not change this activity. Reload the page.',
        failed: 'The change could not be saved. Please try again.',
      },
      unassigned: 'Unassigned',
      'no-standort': 'No location',
      // The "relates to" cell, "On {type}: {label}" — per subject type, with the
      // neutral wording when there is no label. Exhaustive over
      // ActivitySubjectType so a sixth type cannot fall through unnoticed
      // (BL-024 / BL-083).
      subject: {
        DEAL: { prefix: 'On deal: {label}', neutral: 'On a deal' },
        LEAD: { prefix: 'On lead: {label}', neutral: 'On a lead' },
        PROPERTY: { prefix: 'On property: {label}', neutral: 'On a property' },
        CONTACT: { prefix: 'On contact: {label}', neutral: 'On a contact' },
        VALUATION_REQUEST: { prefix: 'On valuation request: {label}', neutral: 'On a valuation request' },
        PROSPECT: { prefix: 'On prospect: {label}', neutral: 'On a prospect' },
      },
      filters: {
        search: 'Search activity or subject',
        standort: 'Location',
        assignee: 'Assignee',
        kind: 'Type',
        'kind-communication': 'Communication',
        subject: 'Relates to',
        'subject-types': {
          LEAD: 'Lead',
          DEAL: 'Deal',
          CONTACT: 'Contact',
          PROPERTY: 'Property',
          VALUATION_REQUEST: 'Valuation request',
          PROSPECT: 'Prospect',
        } as const satisfies Record<ActivitySubjectType, string>,
        period: 'Period',
        periods: {
          TODAY: 'Today',
          NEXT_7_DAYS: 'Next 7 days',
          NEXT_30_DAYS: 'Next 30 days',
          LAST_7_DAYS: 'Last 7 days',
          LAST_30_DAYS: 'Last 30 days',
          THIS_MONTH: 'This month',
          LAST_MONTH: 'Last month',
        },
        reset: 'Reset filters',
        'no-match': 'No activities match these filters.',
        'sort-label': 'Sort by {column}',
      },
      'load-more': 'Load more',
      'empty-title': 'No activities',
      'empty-description': 'Planned and completed activities from leads, deals, contacts and properties appear here.',
      'load-error': 'Activities could not be loaded.',
      'load-retry': 'Try again',
      // The one source this tab merges client-side can fail independently of the
      // other (BL-058) — each notice names which half is missing.
      'partial-store': 'Activities could not be fully loaded, so some are missing.',
      'partial-lead': 'Lead activities could not be loaded, so some are missing.',
      truncated: 'There are more activities than shown here. Narrow the time range or the filters.',
      'subject-unavailable': 'This activity no longer has a reachable subject.',
    },
    // Valuations (IMM-467.1) — the member's cross-deal list. Status, object type
    // and usage reuse the shared dictionaries rather than a second label table.
    // Mandates (IMM-1152) — every Objekt from „Auftrag erhalten“ on. The property-state words
    // reference the Objekt's own labels so the list and the Objekt never drift apart.
    mandates: {
      title: 'Mandates',
      subtitle: 'Every property from „Mandate received“ — from the mandate to the close.',
      column: {
        object: 'Property',
        'deal-status': 'Deal status',
        'property-state': 'Property status',
        responsible: 'Responsible',
        'mandate-since': 'Mandate since',
      },
      'property-state': {
        NOT_ONLINE: 'Not online',
        ONLINE: 'Online',
        RESERVED: 'Reserved',
        SOLD: ARCHIVE_REASON_LABELS.SOLD,
        RENTED: ARCHIVE_REASON_LABELS.RENTED,
        INACTIVE: PROPERTY_STATUS_LABELS.INACTIVE,
        ARCHIVED: PROPERTY_STATUS_LABELS.ARCHIVED,
      } as const satisfies EnumTranslation<MandatePropertyState>,
      'property-unavailable': 'Property unavailable',
      empty: {
        title: 'No mandates yet',
        description: 'Every property appears here once its deal reaches „Mandate received“.',
      },
      truncated: 'Only the first 2,000 mandates are shown.',
      'load-failed': 'The mandates could not be loaded.',
      'load-failed-hint': 'Check your connection and try again.',
      'load-retry': 'Try again',
    },
    valuations: {
      title: 'Valuations',
      subtitle: "All valuations on the deals you're assigned to.",
      // For oversight holders (IMM-755). Deliberately not "every valuation": the
      // capability is resolved per Standort, so the list is a union — someone who
      // oversees one location sees all of it plus their own deals elsewhere. Both
      // halves are in the sentence.
      'subtitle-oversight': 'All valuations in the locations you oversee — plus the valuations on your own deals.',
      columns: {
        object: 'Property',
        'object-type': 'Property type',
        usage: 'Usage',
        standort: 'Location',
        status: 'Status',
        'price-range': 'Price range',
        requested: 'Requested',
        completed: 'Completed',
      },
      filters: {
        all: 'All',
        'status-label': 'Filter by status',
        'search-label': 'Search valuations',
        'search-placeholder': 'Search by property',
        'standort-label': 'Filter by location',
        'standort-all': 'All locations',
        // The accessible name of the sort button in each column head.
        'sort-label': 'Sort by {column}',
      },
      status: VALUATION_STATUS_LABELS,
      objectType: VALUATION_OBJEKTART_LABELS,
      usage: VALUATION_NUTZUNG_LABELS,
      'empty-title': 'No valuations yet',
      'empty-description':
        "Valuations are requested from within a deal. As soon as you're assigned to a deal that has one, it appears here.",
      // The "assigned" sentence is simply false for an oversight holder: they see
      // their locations' valuations without any assignment at all (IMM-755).
      'empty-description-oversight':
        'Valuations are requested from within a deal. As soon as one is requested in a location you oversee, it appears here.',
      // A truncated list that says nothing is the same defect as the narrowed list
      // that said nothing — which is what IMM-755 was.
      truncated: 'Only the first 2,000 valuations are shown.',
      'no-match': 'No matches for your selection.',
      // A filter narrows only the page that was loaded. Over a truncated read
      // "no matches" can therefore be false rather than merely incomplete: the
      // cap may have excluded this location's valuations before the client saw
      // them.
      'no-match-truncated':
        'No matches for your selection. Only the first 2,000 valuations are loaded — this location may have more that are not among them.',
      'load-error': 'Valuations could not be loaded. Please try again.',
      // When a valuation's location falls outside the tree the viewer can see.
      'no-standort': 'No location',
      // Any cell without a value.
      'empty-cell': '—',
      // One valuation in full (IMM-467.2). The request, the result, the report
      // and the activity come from the deal's shared renderers — only the frame
      // and the four states that show no figures live here.
      detail: {
        'to-deal': 'Go to deal',
        'activity-title': 'Activity',
        'awaiting-release': {
          title: 'Valuation ready, not released yet',
          body: 'The valuation has been produced and is being reviewed. Once it is released, you will find the price range and the report here.',
        },
        'in-progress': {
          title: 'immoOS is producing your valuation',
          body: 'In production since {date} · {duration}. The result appears here as soon as it is released.',
          days: '{count, plural, one {# day} other {# days}}',
          today: 'today',
        },
        'load-error': {
          title: 'Valuation could not be loaded',
          body: 'Please try again. If it still does not work, open the valuation from the deal.',
          retry: 'Try again',
        },
      },
    },
  },
  support: {
    'not-registered': {
      title: 'Account not registered',
      body: "Your account isn't linked to an organisation yet. Please contact our sales team to get set up.",
      'whatsapp-cta': 'WhatsApp Sales: {number}',
      'back-to-sign-in': 'Back to sign-in',
    },
  },
  onboarding: {
    'workspace-switcher': {
      label: 'Switch workspace',
      current: 'Current workspace',
      'not-onboarded': 'This organisation is not fully set up yet',
    },
    welcome: {
      heading: 'Welcome to {workspaceName}.',
      body: 'In a few steps you complete your profile — photo, position, contact details and a few notes about what you do. That way you show up correctly in exposés and on your agent profile. You can pause at any time and continue later via the same link.',
      cta: 'Start onboarding',
      reprompt: 'Finish your onboarding.',
      'platform-mark': 'powered by immoOS',
    },
    wizard: {
      'stepper-label': 'Onboarding progress',
      'pause-hint': 'You can pause at any time — continue later via your link.',
      'next-cta': 'Save & continue',
      'back-cta': 'Back',
      'finish-cta': 'Finish onboarding',
      saving: 'Saving…',
      'load-error': 'Your onboarding could not be loaded. Please reload the page.',
      'generic-error': 'Something went wrong. Please try again.',
      steps: {
        photo_intro: 'Photo & introduction',
        contact: 'Contact',
        expertise: 'Professional profile',
        consent: 'Consent',
      },
      identity: {
        heading: 'Your details',
        'display-name': 'Display name',
        email: 'Email',
        location: 'Location',
        'location-empty': 'Not yet assigned',
      },
      consent: {
        text: 'I agree that my profile, including my photo, may be used in exposés and on my company’s public agent profile.',
      },
      validation: {
        'required-core': 'Please fill in this field to complete your onboarding.',
        consent: 'Please confirm the consent to continue.',
      },
      connect: {
        'step-label': 'Sign-in',
        heading: 'Set up your sign-in for {workspaceName}',
        body: 'Connect your sign-in once so you can always sign back in later. Pick a provider to continue.',
        'email-note': 'Please use the same e-mail address as in your invitation:',
        'google-cta': 'Continue with Google',
        'microsoft-cta': 'Continue with Microsoft',
        'password-cta': 'Continue with e-mail & password',
        loading: 'Opening…',
        error: 'We could not start the sign-in. Please try again.',
      },
    },
    done: {
      heading: 'Done — your profile is ready.',
      body: 'Your profile is saved and now appears in exposés and on your agent profile. You can adjust it any time under My profile.',
      cta: 'Go to {workspaceName}',
    },
    member: {
      'platform-mark': 'powered by immoOS',
      loading: 'One moment …',
      code: {
        heading: 'Confirm your e-mail address',
        body: 'We sent you a code by e-mail. Enter it below to continue to {workspaceName}.',
        'code-label': 'Verification code',
        'code-placeholder': '123456',
        'submit-cta': 'Confirm',
        submitting: 'Checking…',
        'resend-cta': 'Resend code',
        errors: {
          'invalid-code': 'This code is invalid or has expired. Please try again.',
        },
      },
      link: {
        'expired-heading': 'This link is no longer valid.',
        'expired-body': 'Your invitation has expired or has already been used. Ask the sender to invite you again.',
        'error-heading': 'Something went wrong.',
        'error-body': 'We could not load your invitation right now. Please try again.',
        'retry-cta': 'Try again',
      },
    },
    partner: {
      'platform-mark': 'powered by immoOS',
      loading: 'One moment …',
      welcome: {
        heading: 'Welcome to the location onboarding.',
        body: 'In a few steps you provide everything we need for your location — your legal details, contact information and the required consents. You can pause at any time and continue later via the same link.',
        cta: 'Start onboarding',
      },
      code: {
        heading: 'Confirm your e-mail address',
        body: 'We sent a six-digit code to your e-mail. Enter it below to continue.',
        'code-label': 'Verification code',
        'code-placeholder': '123456',
        'submit-cta': 'Confirm',
        submitting: 'Verifying…',
        'resend-cta': 'Resend code',
        errors: {
          'invalid-code': 'This code is invalid or has expired. Please try again.',
        },
      },
      shell: {
        'stepper-label': 'Onboarding progress',
        'changes-requested': 'The team left some notes: {reviewNotes}',
        'save-error': 'Your details could not be saved. Please try again.',
        'field-required': 'This field is required.',
        steps: {
          legal: 'Legal details',
          contact: 'Contact',
          consents: 'Rights & consents',
        },
      },
      legal: {
        heading: 'Legal details',
        intro: 'Enter your company’s legal details here. You can leave fields blank and complete them later.',
        'next-cta': 'Save & continue',
        'back-cta': 'Back',
        saving: 'Saving…',
        fields: {
          'company-name': 'Company name',
          'legal-form': 'Legal form',
          'managing-directors': 'Managing directors / authorised representatives',
          'commercial-register-court': 'Commercial register (court)',
          'commercial-register-number': 'Commercial register (number)',
          'vat-id': 'VAT ID',
          'trade-permit-authority': 'Trade-permit authority (§ 34c GewO)',
          'supervisory-authority': 'Competent supervisory authority',
          'professional-indemnity-insurance': 'Professional indemnity insurance',
          'content-responsible-person': 'Person responsible for content (§ 18 (2) MStV)',
          website: 'External website',
        },
        'legal-form-placeholder': 'Please select',
        'register-hint': 'Only required for legal forms entered in the Handelsregister.',
        errors: {
          'vat-id': 'Please enter a valid VAT ID (format: DE followed by 9 digits).',
          website: 'Please enter a valid website URL.',
        },
        'legal-forms': {
          GMBH: 'GmbH',
          UG: 'UG (haftungsbeschränkt)',
          GMBH_CO_KG: 'GmbH & Co. KG',
          EINZELUNTERNEHMEN: 'Einzelunternehmen',
          EK: 'e.K.',
          GBR: 'GbR',
          PARTG_MBB: 'PartG mbB',
          OHG: 'OHG',
          KG: 'KG',
          UG_CO_KG: 'UG & Co. KG',
          AG: 'AG',
        },
        preview: {
          heading: 'Imprint preview',
          empty: 'As soon as you enter details, a preview of your imprint appears here.',
        },
      },
      contact: {
        heading: 'Contact',
        intro: "Enter your location's public contact details here. You can leave fields blank and complete them later.",
        'next-cta': 'Save & continue',
        'back-cta': 'Back',
        saving: 'Saving…',
        fields: {
          email: 'E-mail',
          phone: 'Phone',
        },
        'address-search-label': 'Address',
        'address-search-placeholder': 'Search for an address…',
        'remove-address': 'Remove address',
        'street-label': 'Street',
        'street-placeholder': 'e.g. Musterstraße',
        'house-number-label': 'House number',
        'house-number-placeholder': 'e.g. 1',
        'postal-code-label': 'Postal code',
        'postal-code-placeholder': 'e.g. 10115',
        'city-label': 'City',
        'city-placeholder': 'e.g. Berlin',
        'country-label': 'Country',
        'country-placeholder': 'e.g. Deutschland',
        errors: {
          email: 'Please enter a valid e-mail address.',
          phone: 'Please enter a valid phone number.',
        },
      },
      consents: {
        heading: 'Rights & consents',
        intro:
          'So that we may process the data you have submitted, we need your explicit consent. Confirm it and state who is granting it.',
        'save-cta': 'Save consents',
        'submit-cta': 'Submit onboarding',
        submitting: 'Submitting…',
        saving: 'Saving…',
        consent: {
          'data-protection': {
            label: 'Data protection & processing',
            description:
              'I consent to the company and personal data submitted here being stored and processed for the purpose of location onboarding.',
          },
        },
        'granted-by-name-label': 'Name of the consenting person',
        errors: {
          'granted-by-name': 'Please provide the name of the consenting person.',
        },
      },
      confirmation: {
        heading: 'All done — your onboarding has been submitted.',
        body: 'Your details go to the team for review. We will get in touch as soon as your location is approved.',
      },
      state: {
        expired: {
          heading: 'Link expired or invalid',
          body: 'This link has expired or is invalid. Please request a new one.',
        },
        'already-submitted': {
          heading: 'Already submitted',
          body: 'Your onboarding has already been submitted and is currently being reviewed.',
        },
        error: {
          heading: 'Something went wrong',
          body: 'We couldn’t load your onboarding just now. Please try again in a moment.',
          retry: 'Try again',
        },
      },
    },
    company: {
      heading: 'Company onboarding for {workspaceName}',
      intro:
        'Before you get started, complete your company’s legal details and contact information. These appear in the imprint of your exposés, among other places.',
      'pause-hint': 'You can pause at any time — pick up where you left off next time you sign in.',
      blocked: {
        heading: 'Setup for {workspaceName} isn’t finished yet',
        body: 'Before you can get started, an owner of your organisation needs to complete the company details. Please reach out to an organisation owner.',
        'platform-mark': 'runs on immoOS',
      },
    },
  },
  public: {
    webExpose: {
      title: 'Exposé',
      loading: 'The page is loading',
      photos: '{count, plural, one {# photo} other {# photos}}',
      previous: 'Previous photo',
      next: 'Next photo',
      counter: '{n} / {total}',
      'photo-alt': 'Photo {n} of {total}',
      tour: {
        mark: '360°',
        start: 'Start tour',
        pill: '360°',
        'pill-label': 'Start 360° tour (opens in a new tab)',
      },
      video: {
        watch: {
          YOUTUBE: 'Watch on YouTube',
          VIMEO: 'Watch on Vimeo',
        },
      },
      'new-tab': '(opens in a new tab)',
      facts: {
        'living-area': 'Living area',
        rooms: 'Rooms',
        'expose-number': 'Exposé no.',
      },
      'price-on-request': 'Price on request',
      share: {
        label: 'Share',
        copy: 'Copy link',
        copied: 'Link copied',
        email: 'By email',
        whatsapp: 'WhatsApp',
      },
      gallery: {
        close: 'Close',
      },
      footer: {
        'offered-by': 'An offer from {companyName}',
        imprint: 'Imprint',
        privacy: 'Privacy policy',
      },
      unavailable: {
        title: 'This exposé is no longer available.',
        body: 'The offer may already have been sold or withdrawn.',
      },
      failed: {
        message: 'That did not work. Please try again later.',
        retry: 'Try again',
      },
      sections: {
        'object-data': 'Property data',
        equipment: 'Features',
        video: 'Video',
        tour: '360° tour',
        'floor-plans': 'Floor plan',
        location: 'Location',
        energy: 'Energy',
        commission: 'Commission',
      },
      'section-nav': 'Sections on this page',
      'show-more': 'Show more',
      'show-less': 'Show less',
      'object-data': {
        type: 'Property type',
        rooms: 'Rooms',
        bedrooms: 'Bedrooms',
        bathrooms: 'Bathrooms',
        'living-area': 'Living area',
        'usable-area': 'Usable area',
        'plot-area': 'Plot area',
        floor: 'Floor',
        'floors-total': 'Floors',
        'year-built': 'Year built',
        condition: 'Condition',
        'last-renovated': 'Last modernised',
        'available-from': 'Available from',
        'parking-spaces': 'Parking spaces',
        'service-charge': 'Service charge',
        'expose-number': 'Exposé no.',
        miscellaneous: 'Miscellaneous',
      },
      equipment: {
        entry: '{label}: {value}',
      },
      'floor-plans': {
        name: 'Floor plan {n}',
        download: 'Download floor plan',
      },
      location: {
        'address-on-request': 'The exact address is available on request.',
        'map-alt': 'Map: area of postcode {postalCode}',
        'map-caption': 'The highlighted area shows postcode {postalCode}.',
      },
      energy: {
        badge: '{energyClass} · {energyValue} kWh/(m²·a)',
        'scale-label': 'Energy efficiency class {energyClass} on the scale from A+ to H',
        'scale-label-non-residential': 'Energy efficiency class {energyClass} on the scale from A to G',
        certificate: 'Energy certificate',
        demand: 'Final energy demand',
        consumption: 'Final energy consumption',
        'primary-energy': 'Primary energy demand',
        value: '{value} kWh/(m²·a)',
        'warm-water': 'incl. hot water',
        'energy-class': 'Efficiency class',
        'firing-type': 'Energy source',
        'heating-type': 'Heating type',
        'year-built': 'Year built per energy certificate',
        'issued-at': 'Issued on',
        'not-available': 'There is no energy certificate.',
        'at-viewing': 'The energy certificate is presented at the viewing.',
      },
      commission: {
        label: 'Buyer commission',
        'plus-vat': '{rate} plus VAT',
        'of-price': 'of the purchase price incl. VAT',
        disclaimer:
          'All information is based on details provided by the owner and is given without guarantee. Errors and prior sale excepted.',
      },
      contact: {
        label: 'Contact',
        'purchase-price': 'Purchase price',
        'plus-costs': 'plus purchase costs',
        'living-area': 'Living area',
        rooms: 'Rooms',
        energy: 'Energy',
        'energy-class': 'Class {energyClass}',
        kicker: 'Your contact',
        call: 'Call',
        email: 'Email',
      },
      'phone-bar': {
        rooms: '{rooms} rooms',
      },
      inquiry: {
        actions: { 'full-expose': 'Request the exposé', inquiry: 'Ask without obligation' },
        heading: { 'full-expose': 'Request the full exposé', inquiry: 'Ask without obligation' },
        fields: {
          'first-name': 'First name',
          'last-name': 'Last name',
          email: 'Email',
          phone: 'Phone',
          message: 'Message (optional)',
        },
        privacy: 'I agree that {company} processes my details to handle my enquiry. More in the privacy policy.',
        'privacy-link-word': 'privacy policy',
        'privacy-fallback-company': 'the agent',
        submit: { 'full-expose': 'Request the exposé', inquiry: 'Send enquiry' },
        hint: 'An enquiry does not commit you to anything. You receive the full exposé once the brokerage contract is concluded.',
        close: 'Close',
        errors: {
          required: 'Please fill in this field.',
          'email-invalid': 'Please enter a valid email address.',
          privacy: 'Please confirm the privacy notice.',
          failed: 'That did not work. Please try again later.',
        },
        sent: {
          'heading-inquiry': 'Enquiry sent',
          'heading-full-expose': 'Thank you.',
          inquiry: 'Thank you for your enquiry. {name} will get back to you shortly.',
          'inquiry-no-name': 'Thank you for your enquiry. We will get back to you shortly.',
          'full-expose':
            'Thank you. We have sent an email to {email}. It contains the notice of withdrawal and the link to conclude the brokerage contract and receive the full exposé.',
        },
      },
    },
    brokerageAgreement: {
      title: 'Your brokerage agreement',
      loading: 'The page is loading',
      'load-failed': 'The page could not be loaded right now.',
      'load-retry': 'Try again',
      greeting: {
        mr: 'Good day Mr {lastName},',
        mrs: 'Good day Ms {lastName},',
        person: 'Good day {firstName} {lastName},',
        company: 'Good day,',
      },
      intro:
        '{companyName} is presenting this property to you. Before we give you the address and the full property brochure, you conclude the brokerage agreement here.',
      steps: {
        label: 'Your next steps',
        read: 'Read the documents',
        conclude: 'Conclude the agreement',
        expose: 'Receive the brochure',
        done: 'done',
      },
      chips: {
        'purchase-price': 'Purchase price',
        'living-area': 'Living area',
        rooms: 'Rooms',
      },
      contact: {
        heading: 'Your contact',
        call: 'Call',
        email: 'Send an e-mail',
      },
      commission: {
        heading: 'Commission',
      },
      documents: {
        heading: 'Your contract documents',
        subtitle: 'You also receive the same texts by e-mail.',
        count: '3 documents',
        contract: 'Brokerage agreement',
        'withdrawal-notice': 'Cancellation notice',
        'withdrawal-form': 'Model cancellation form',
      },
      form: {
        heading: 'Conclude the agreement',
        identity: 'You are concluding the agreement as {name}.',
        'privacy-link-word': 'Datenschutzerklärung',
      },
      inactive: 'This offer is no longer active. If you have any questions, please contact {companyName}.',
      expose: {
        tag: 'Exposé · PDF',
        ready: 'Your exposé is ready',
        details: 'With the address and every detail of the property.',
        download: 'Download exposé',
        heading: 'Your exposé',
        soon: 'You will receive the full exposé from {companyName} shortly.',
        'not-requested': 'You did not request an early start. {companyName} will be in touch with you.',
      },
      dataRoom: {
        heading: 'Documents',
        intro:
          '{companyName} has shared these documents on the property with you. You can view and download them, for example for your bank.',
        view: 'View',
        download: 'Download',
        'view-label': 'View {name}',
        'download-label': 'Download {name}',
        meta: '{category} · {type} · {size}',
        ended: 'These documents are no longer shared. If you have any questions, please contact {companyName}.',
        privacy:
          'When you view or download a document, we store the document and the time so that {companyName} can advise you and prove the service provided. More in the privacy policy of {companyName}.',
      },
      concluded: {
        heading: 'Your brokerage agreement is concluded',
        place: '{title} · {city}',
        summary: {
          heading: 'Your details',
          'concluded-at-label': 'Concluded on',
          'concluded-at': '{date} at {time}',
          'early-start-label': 'Early start requested',
          yes: 'yes',
          no: 'no',
          'contract-number-label': 'Contract number',
          'confirmation-to-label': 'Confirmation sent to',
        },
        help: {
          heading: 'Questions or a viewing?',
        },
      },
      withdrawal: {
        heading: 'Withdrawal',
        'contract-number': 'No. {contractNumber}',
        'period-over': 'Your withdrawal period ended on {date}.',
        open: 'Withdraw from contract',
        'form-heading': 'Withdraw from contract',
        name: 'Name',
        contract: 'Contract',
        email: 'E-mail address',
        reason: 'Reason (optional)',
        'contract-prefill': 'Brokerage agreement “{title}” of {date}, No. {contractNumber}',
        confirm: 'Confirm withdrawal',
        cancel: 'Cancel',
        'receipt-heading': 'Your withdrawal has been received',
        'receipt-body': 'Received on {date} at {time}. We have sent an acknowledgement of receipt to {email}.',
        'reason-none': '—',
        revoked: 'You withdrew from this contract on {date} at {time}.',
        'period-ended':
          'The period for withdrawing through this page has ended. If you have any questions, please contact {companyName}.',
      },
      footer: {
        withdraw: 'Withdraw from contract',
        privacy: 'Privacy policy',
      },
      imprint: {
        phone: 'Phone: {value}',
        email: 'E-mail: {value}',
        'managing-directors': 'Managing directors: {value}',
        register: 'Commercial register court: {court} · Register number: {number}',
        'register-court-only': 'Commercial register court: {court}',
        'register-number-only': 'Register number: {number}',
        'vat-id': 'VAT ID: {value}',
        'supervisory-authority': 'Supervisory authority: {value}',
        'trade-permit-authority': 'Licensing authority (§ 34c GewO): {value}',
        'professional-indemnity-insurance': 'Professional indemnity insurance: {value}',
      },
    },
    signing: {
      title: 'Signature',
      loading: 'The page is loading',
      heading: '{documentType, select, BROKER_CONTRACT {Brokerage contract} other {Power of attorney}} for {address}',
      'document-label':
        '{documentType, select, BROKER_CONTRACT {Brokerage contract} other {Power of attorney}} · {count, plural, one {# page} other {# pages}}',
      download: 'Download PDF',
      'page-label': 'Page {page} of {count}',
      'document-failed': 'The document could not be loaded.',
      confirmation:
        'Thank you, you have signed the {documentType, select, BROKER_CONTRACT {brokerage agreement} other {power of attorney}}. A confirmation with the document is on its way by e-mail. Once all owners have signed, you will receive the fully signed document.',
      'signed-state': 'You signed on {date} at {time}.',
      'signed-at': 'Electronically signed on {date} at {time}',
      'form-heading': 'Sign',
      'name-label': 'Your full name',
      'name-hint': 'Your name is adopted as the signature in the document.',
      'contact-heading': 'Your contact',
      'privacy-link': 'Datenschutzerklärung',
      inactive: 'This signature request is no longer active. If you have any questions, please contact {companyName}.',
      close: 'Close',
      failed: 'Something went wrong. Please try again later.',
      retry: 'Try again',
      'withdrawal-title': 'Withdrawal',
      'withdrawal-text':
        'Your withdrawal period ends on {date}. Until then you may withdraw from this contract without giving reasons.',
      'withdrawal-action': 'Withdraw from contract',
      'withdrawal-contract-prefill': 'Brokerage agreement for {address}, signed on {date}',
    },
  },
  admin: {
    'sign-in': {
      title: 'immoOS Admin',
      subtitle: 'Platform sign-in',
      google: 'Sign in with Google',
      loading: 'Signing in…',
      error: 'Sign-in failed. Please check your credentials.',
    },
    callback: {
      loading: 'Finishing sign-in…',
      'missing-code': 'The sign-in flow is incomplete. Please start again.',
      failed: 'Sign-in failed. Please check your credentials.',
      'back-to-sign-in': 'Back to sign-in',
    },
    'sign-out': {
      loading: 'Signing out…',
      failed: 'Sign-out failed — please try again.',
      'back-to-sign-in': 'Go to sign-in',
    },
    'access-denied': {
      title: 'No access',
      body: 'This surface is reserved for platform staff. Please use app.immoos.ai for your workspace.',
      'body-missing-permission': 'You do not have permission for this area.',
      'back-to-sign-in': 'Back to sign-in',
    },
    notifications: {
      title: 'Notifications',
      intro: 'Choose what the app should notify you about.',
      events: {
        FLEET_QUESTION_PARKED: 'Agent is waiting for an answer',
        FLEET_MACHINE_ENROLMENT_WAITING: 'Machine is waiting for approval',
        LANDIQ_DATA_RUN_FAILED: 'LandIQ run failed',
        LANDIQ_STAGE_FINISHED: 'LandIQ stage finished',
        LANDIQ_DATA_RUN_STALE: 'LandIQ run without new progress',
        FLEET_RELEASE_DEPLOYED: 'Deployments',
      },
      bodies: {
        FLEET_MACHINE_ENROLMENT_WAITING: '{machine} wants to join the fleet.',
        LANDIQ_DATA_RUN_FAILED: '{ticket}: The run failed. Open it to resume or restart it.',
        LANDIQ_STAGE_FINISHED: '{ticket}: {stage} is done. The next stage can start.',
        LANDIQ_DATA_RUN_STALE: '{ticket}: No new progress for 20 minutes. Open the run to check.',
        FLEET_QUESTION_PARKED: '{ticket} needs a decision.',
        FLEET_RELEASE_DEPLOYED: {
          immoos: '{immoos, plural, one {# immoOS ticket was deployed} other {# immoOS tickets were deployed}}',
          landiq: '{landiq, plural, one {# LandIQ ticket was deployed} other {# LandIQ tickets were deployed}}',
          both: '{immoos} immoOS and {landiq, plural, one {# LandIQ ticket} other {# LandIQ tickets}} were deployed',
        },
      },
      blocked: 'Notifications are turned off in iOS Settings.',
      'open-settings': 'Open Settings',
    },
    'update-prompt': {
      title: 'New version available',
      body: 'This version of the app is outdated. Install the latest version from TestFlight.',
      action: 'Open TestFlight',
    },
    shell: {
      // Nur unter `sm` sichtbar: dort liegt die Navigation in einer Schublade.
      'open-nav': 'Open navigation',
      nav: {
        workspaces: 'Workspaces',
        members: 'Members',
        organizations: 'Organizations',
        properties: 'Properties',
        'deleted-deals': 'Deleted deals',
        'deleted-companies': 'Deleted companies',
        'api-keys': 'API keys',
        'api-calls': 'API calls',
        logs: 'Logs',
        'api-key-requests': 'API key requests',
        emails: 'E-mails',
        valuations: 'Valuations',
        'valuation-agent': 'Valuation agent',
        'messenger-chats': 'Messenger chats',
        admin: 'Admin',
        admins: 'Admins',
        skills: 'Skills',
        'document-analyses': 'Document analyses',
        'mcp-access': 'MCP access',
        'fact-keys': 'Fact keys',
        'agent-skills': 'Agent skills',
        fleet: 'Fleet',
        machines: 'Machines',
        board: 'Board',
        metrics: 'Metrics',
        'fleet-accounts': 'Accounts',
        landiq: 'LandIQ',
        'data-runs': 'Data runs',
        sandbox: 'Sandbox',
      },
      'user-menu': {
        'sign-out': 'Sign out',
      },
      table: {
        'sort-label': 'Sort by {column}',
      },
      filters: {
        search: 'Search…',
        'more-workspaces': 'More workspaces',
        'more-workspaces-placeholder': 'More workspaces …',
        all: 'All',
        status: 'Status',
        organization: 'Location',
        'organization-all': 'All locations',
        // The operator's test-workspace filter (IMM-593.2): one pill shared by
        // every workspace-scoped list, its row badge, and the empty state shown
        // while the filter is hiding rows.
        'test-workspaces': 'Test environments',
        'test-workspaces-aria': 'Show test environments',
        'test-badge': 'Test',
        'test-badge-full': 'Test environment',
        'empty-test-hidden': 'No entries — test environments are hidden. Switch on "Test environments" to show them.',
        'empty-test-hidden-cta': 'Show test environments',
      },
    },
    // Deleted deals (IMM-851.1) — cross-tenant operator restore surface.
    'deleted-by': { unknown: 'Unknown', system: 'System', operator: '{name} (Operator)' },
    deals: {
      deleted: {
        title: 'Deleted deals',
        empty: 'There are no deleted deals.',
        'unresolved-workspace': '—',
        'unresolved-property': '—',
        'property-deleted': 'Property deleted',
        phase: DEAL_PHASE_LABELS,
        status: DEAL_STATUS_LABELS,
        type: DEAL_TYPE_LABELS,
        columns: {
          workspace: 'Workspace',
          object: 'Property',
          type: 'Type',
          status: 'Status',
          phase: 'Phase',
          'deleted-at': 'Deleted',
          'deleted-by': 'Deleted by',
          actions: 'Action',
        },
        restore: 'Restore',
        'restore-aria': 'Restore the deal for property {reference}',
        'restore-aria-unresolved': 'Restore deal',
        confirm: {
          title: 'Restore this deal?',
          body: 'The deal reappears in the {workspace} workspace with its phase, status, contacts and notes as they were before deletion. Its activity timeline shows customers that immoOS restored it.',
          'body-property-deleted': 'The linked property is also deleted and is not restored with it.',
          cancel: 'Cancel',
          cta: 'Restore',
          loading: 'Restoring …',
        },
        errors: {
          'cannot-restore-not-deleted': 'This deal is not deleted — there is nothing to restore.',
          'cannot-restore-active-deal-exists':
            'An active deal already exists for this property. The deleted deal can only be restored once the other one is completed or deleted.',
          'cannot-restore-property-archived':
            "This deal's property is archived. Remove the archival first to restore the deal.",
          'blocking-deal': 'Blocking deal: property {reference}',
          forbidden: 'You do not have permission to restore deals.',
          generic: 'The deal could not be restored. Please try again.',
        },
      },
    },
    // Deleted companies (IMM-1096.4) — cross-tenant operator read.
    companies: {
      deleted: {
        title: 'Deleted companies',
        empty: 'No deleted companies.',
        'unresolved-workspace': '—',
        columns: { name: 'Company', workspace: 'Workspace', 'deleted-by': 'Deleted by', 'deleted-at': 'Deleted' },
      },
    },
    // Valuation queue (IMM-407.4) — the cross-tenant operator surface. Enum
    // option tables and type-field names are the SAME shared constants the
    // web-app request form/preview use; only the queue/detail/take-on chrome
    // is admin-specific.
    valuations: {
      title: 'Valuations',
      empty: 'No open valuation requests at the moment.',
      unassigned: '—',
      // IMM-572.2 — a machine holder (`takenByKind === 'ADMIN_API_KEY'`) is
      // labelled as such wherever an assignee renders. At the parent namespace,
      // NOT under `detail`: the queue page cannot reach `detail.*` keys.
      'admin-key-holder': '{name} (admin API key)',
      'admin-key-holder-unknown': 'Unknown admin API key',
      // `ALL` is a filter-only pill (every admin-visible status), not a request status.
      status: { ...VALUATION_STATUS_LABELS, ALL: 'All' },
      columns: {
        workspace: 'Workspace',
        object: 'Property',
        objectType: 'Object type',
        usage: 'Usage',
        'requested-by': 'Requested by',
        'requested-at': 'Requested on',
        'desired-completion': 'Desired completion',
        age: 'Age',
        'recommended-start-price': 'Recommended start price',
        status: 'Status',
        assignee: 'Assignee',
      },
      age: {
        days: '{count, plural, one {# day} other {# days}}',
        hours: '{count, plural, one {# hour} other {# hours}}',
        minutes: '{count, plural, one {# minute} other {# minutes}}',
        now: 'just now',
      },
      filters: {
        'status-all': 'All statuses',
        'workspace-all': 'All workspaces',
        'more-workspaces': 'More customers',
        'more-workspaces-placeholder': 'More customers …',
      },
      detail: {
        back: 'Valuations',
        'not-provided': '—',
        'boolean-ja': 'Yes',
        'boolean-nein': 'No',
        'requester-label': 'Requested by',
        'requested-at-label': 'Requested on',
        'assignee-label': 'Assignee',
        'taken-at-label': 'Taken on',
        'take-on': 'Take on',
        'taking-on': 'Taking on…',
        'already-taken': '{name} is already handling this request.',
        'take-on-error': 'The request could not be taken on. Please try again.',
        'take-on-forbidden': 'You are not authorised to do this.',
        // IMM-572.2 — release a claim: hands an IN_PROGRESS claim back to the queue.
        'release-claim': {
          cta: 'Release claim',
          'confirm-title': 'Release the claim held by “{name}”?',
          'confirm-body': 'The request goes back to the queue and can be taken on again — by a consumer or by you.',
          'confirm-cta': 'Release claim',
          'cancel-cta': 'Cancel',
          loading: 'Releasing…',
          'not-claimed-error': 'This request is no longer claimed. The view has been refreshed.',
          'forbidden-error': 'You are not authorised to do this.',
          error: 'The claim could not be released. Please try again.',
        },
        'price-expectation-note':
          'Price expectations captured on the request — context only, not a target and not the valuation result.',
        'price-expectation-none': 'No figure given',
        'documents-empty': 'No documents',
        'images-empty': 'No images',
        'category-group': DOCUMENT_CATEGORY_GROUP_LABELS,
        category: DOCUMENT_CATEGORY_LABELS,
        classification: DOCUMENT_CLASSIFICATION_MARKERS,
        // Per-attachment actions on the Unterlagen / Bilder rows. Each mints a
        // short-lived presigned URL on click; the error covers every failed mint.
        'attachment-preview': 'Preview',
        'attachment-download': 'Download',
        'attachment-error': 'The file could not be opened. Please try again.',
        'parking-empty': 'No parking spaces captured',
        'units-empty': 'No units captured',
        'unit-heading': 'Unit {index}',
        // Operator production panel (IMM-407.5, renamed IMM-475.2). Shown for
        // IN_PROGRESS behind the -complete permission; producing no longer delivers
        // anything — it parks the result at the approval gate.
        submit: {
          title: 'Submit for release',
          'price-from-label': 'Price range from',
          'price-to-label': 'Price range to',
          'recommended-label': 'Recommended start price (optional)',
          'date-label': 'Valuation date',
          // Two independent slots, one per report kind — the contract carries two
          // nullable ids and allows one file per kind.
          'report-html-label': 'Report (HTML)',
          'report-document-label': 'Report (PDF/DOCX)',
          'report-hint': 'At least one report required · HTML or PDF · max 20 MB',
          'field-errors': {
            priceFrom: 'Please enter the lower price bound.',
            priceTo: 'Please enter the upper price bound.',
            priceRange: 'The upper price bound must be at least as high as the lower bound.',
            report: 'Please upload at least one report.',
          },
          'report-selected': '“{name}” uploaded',
          'report-none': 'No file selected yet',
          uploading: 'Uploading…',
          'upload-in-progress-hint': 'A report is still uploading…',
          submit: 'Submit for release',
          submitting: 'Submitting…',
          // AC 43: names each missing/invalid item back to the operator.
          'missing-prefix': 'To submit, the following are missing: {list}.',
          // A disallowed / oversized file is refused before presigning.
          'upload-policy-error': 'This file is not permitted. HTML or PDF · max 20 MB.',
          'upload-error': 'The file could not be uploaded. Please try again.',
          'submit-error': 'The valuation could not be submitted. Please try again.',
          // Non-blocking advisory: the entered recommended start price sits
          // outside the entered price range. Submitting still succeeds.
          'recommended-out-of-range': 'The start price is outside the price range. You can still submit the valuation.',
          // A returned request keeps its held result and reports; the producer sees
          // what the reviewer asked to be corrected.
          'returned-title': 'Reason for return',
          'returned-by': 'Returned by {name} on {date}',
          // Maps the backend's `missing` keys → operator-facing labels.
          'missing-labels': {
            priceFrom: 'Price range from',
            priceTo: 'Price range to',
            priceRange: 'a valid price range (from ≤ to)',
            report: 'Report',
            reportHtmlMediaFileId: 'Report (HTML)',
            reportDocumentMediaFileId: 'Report (PDF/DOCX)',
          },
        },
        // The approval gate (IMM-475.2). Shown for AWAITING_RELEASE: the held
        // figures and report filenames read-only, plus — behind the -release
        // permission — the two reviewer actions.
        review: {
          title: 'Awaiting release',
          'price-from-label': 'Price range from',
          'price-to-label': 'Price range to',
          'recommended-label': 'Recommended start price',
          'valuation-date-label': 'Valuation date',
          'submitted-by-label': 'Submitted by',
          'submitted-at-label': 'Submitted on',
          'reports-label': 'Reports',
          'reports-empty': 'No reports',
          'report-kind': {
            HTML: 'HTML',
            DOCUMENT: 'Document',
          },
          release: 'Release',
          releasing: 'Releasing…',
          'release-success': 'Valuation released. The agent has been notified.',
          'release-error': 'The valuation could not be released. Please try again.',
          // The permanent refusal (BL-020): the request already left "Awaiting
          // release" — deliberately NO retry invitation, because a retry cannot work.
          'release-conflict': 'This valuation is no longer awaiting release. The status has been updated.',
          return: 'Return for rework',
          'return-reason-label': 'Reason for return',
          'return-cancel': 'Cancel',
          'return-submit': 'Return',
          returning: 'Returning…',
          'return-success': 'Valuation returned for rework.',
          'return-reason-required': 'Please give a reason for the return.',
          'return-error': 'The valuation could not be returned. Please try again.',
          'return-conflict': 'This valuation is no longer awaiting release. The status has been updated.',
        },
        // The report controls (IMM-598), used by BOTH the approval gate and the
        // released panel — one block, never two copies (BL-022).
        report: {
          preview: 'Preview',
          download: 'Download',
          // Accessible name carrying the report's own filename (IMM-828.1) — the
          // visible "Download" is identical for both reports, so a reader of
          // assistive technology needs the name to distinguish them.
          'download-aria': 'Download: {name}',
          // A permanent refusal: the report is not readable at this status, or not
          // there at all. Deliberately NO retry invitation (BL-020).
          error: 'The report could not be opened.',
          loading: 'Loading report…',
          'frame-title': 'Valuation report (preview)',
          // Opens the report in the full-screen dialog (IMM-609). At the approval
          // gate the report has to be read, not just glanced at.
          expand: 'Full screen',
          close: 'Close',
          note: 'The report is rendered in isolation and issues no external requests.',
        },
        // Read-only result shown once the request is released: the delivered
        // valuation record (price range / recommended start price / valuation date
        // / report file), read from the property's property_valuation, alongside
        // the who/when release stamp. An HTML-only release files no property
        // document, so the report row is simply absent.
        result: {
          title: 'Valuation result',
          'price-from-label': 'Price range from',
          'price-to-label': 'Price range to',
          'recommended-label': 'Recommended start price',
          'valuation-date-label': 'Valuation date',
          'report-label': 'Valuation report',
          'released-by-label': 'Released by',
          'released-at-label': 'Released on',
          note: "The result has been saved as the property's valuation and the report filed against the property.",
        },
        block: {
          basics: 'Basics',
          reason: 'Reason & expectation',
          condominium: 'Condominium details',
          house: 'House details',
          multiFamily: 'Multi-family house details',
          plot: 'Plot details',
          overallCondition: 'Quality and condition',
          parkingSpaces: 'Parking spaces',
          income: 'Income (actual)',
          units: 'Units',
          hoa: 'Homeowners association',
          documents: 'Documents',
          images: 'Images',
        },
        field: {
          objectType: 'Object type',
          usage: 'Usage',
          address: 'Address',
          valuationReason: 'Valuation reason',
          timeHorizon: 'Time horizon',
          desiredCompletion: 'Desired completion',
          note: 'Note',
        },
        'field-name': VALUATION_FIELD_NAME_LABELS,
        objectType: VALUATION_OBJEKTART_LABELS,
        usage: VALUATION_NUTZUNG_LABELS,
        'reason-enum': VALUATION_ANLASS_LABELS,
        timeHorizon: VALUATION_ZEITHORIZONT_LABELS,
        priceType: VALUATION_PREISVORSTELLUNG_ART_LABELS,
        priceExpectationKind: VALUATION_PRICE_EXPECTATION_KIND_LABELS,
        heatingType: VALUATION_HEIZUNGSART_LABELS,
        energyEfficiencyClass: VALUATION_ENERGIEEFFIZIENZKLASSE_LABELS,
        houseType: VALUATION_HAUSTYP_LABELS,
        basement: VALUATION_UNTERKELLERT_LABELS,
        developmentPlan: VALUATION_BEBAUUNGSPLAN_LABELS,
        servicingStatus: VALUATION_ERSCHLIESSUNGSZUSTAND_LABELS,
        contaminationSuspicion: VALUATION_ALTLASTENVERDACHT_LABELS,
        'graduated-index-rent': VALUATION_STAFFEL_INDEX_MIETE_LABELS,
        'unit-breakdown': VALUATION_ANGABE_DER_EINHEITEN_LABELS,
        quality: VALUATION_QUALITAET_LABELS,
        condition: VALUATION_ZUSTAND_LABELS,
        parkingSpaceType: VALUATION_STELLPLATZART_LABELS,
        lettingStatus: VALUATION_EINHEIT_VERMIETUNGSSTATUS_LABELS,
        parkingSpaces: {
          'type-label': 'Type',
          'count-label': 'Count',
        },
        units: {
          label: 'Label',
          lettingStatus: 'Letting status',
          'let-since': 'Let since',
          livingArea: 'Living area',
          'current-rent': 'Current rent',
          'last-rent-increase': 'Last rent increase',
        },
      },
    },
    'messenger-chats': {
      title: 'Messenger chats',
      description: "Every member's chat with the immoOS assistant, across all workspaces. Read-only.",
      experimental: 'Experimental',
      unassigned: '—',
      columns: {
        channel: 'Channel',
        account: 'Account',
        member: 'Member',
        workspace: 'Workspace',
        'last-message': 'Last message',
      },
      channels: {
        whatsapp: 'WhatsApp',
        telegram: 'Telegram',
      },
      truncated: 'Showing the {limit} most recently active chats.',
      empty: 'No messenger chats yet.',
      detail: {
        back: 'All chats',
        'member-panel': 'Member',
        email: 'E-mail',
        workspace: 'Workspace',
        channel: 'Channel',
        account: 'Account',
        immoos: 'immoOS',
        'read-only': "Read-only — operators can't write into this chat.",
        truncated: 'Showing the newest {limit, number} messages.',
      },
    },
    workspaces: {
      title: 'Workspaces',
      placeholder: 'Workspace administration will appear here once provisioning ships.',
      empty: 'No workspaces yet.',
      'create-cta': 'New workspace',
      columns: {
        name: 'Name',
        owner: 'Owner',
        'created-at': 'Created on',
      },
      create: {
        title: 'Create new workspace',
        'name-label': 'Workspace name',
        'name-placeholder': 'Mustermakler GmbH',
        'is-test-label': 'Testing environment (non-production)',
        'owner-email-label': 'Owner email',
        'owner-email-placeholder': 'owner@example.com',
        'owner-first-name-label': 'First name',
        'owner-first-name-placeholder': 'Ada',
        'owner-last-name-label': 'Last name',
        'owner-last-name-placeholder': 'Lovelace',
        'address-heading': 'Main office address',
        'address-search-label': 'Search address',
        'address-search-placeholder': 'Type an address…',
        'street-label': 'Street',
        'street-placeholder': 'Eppendorfer Landstraße',
        'house-number-label': 'House number',
        'house-number-placeholder': '47a',
        'postal-code-label': 'Postal code',
        'postal-code-placeholder': '20251',
        'city-label': 'City',
        'city-placeholder': 'Hamburg',
        'country-label': 'Country',
        'country-placeholder': 'Deutschland',
        'submit-cta': 'Create workspace',
        'submit-loading': 'Creating…',
        'back-cta': 'Back',
        success: 'Workspace "{name}" created. Owner: {ownerEmail}.',
        'forbidden-error': 'You do not have permission to create workspaces.',
        'generic-error': 'Could not create workspace. Please try again.',
        errors: {
          name: {
            required: 'Please enter a name.',
            'too-short': 'Workspace name must be at least 2 characters.',
            'too-long': 'Workspace name must be at most 80 characters.',
          },
          'owner-email': {
            required: 'Please enter an email address.',
            invalid: 'This email address is not valid.',
          },
          'owner-first-name': {
            required: 'Please enter a first name.',
            'too-long': 'First name must be at most 80 characters.',
          },
          'owner-last-name': {
            required: 'Please enter a last name.',
            'too-long': 'Last name must be at most 80 characters.',
          },
          street: {
            required: 'Please enter a street.',
            'too-long': 'Street must be at most 100 characters.',
          },
          'house-number': {
            required: 'Please enter a house number.',
            'too-long': 'House number must be at most 20 characters.',
          },
          'postal-code': {
            required: 'Please enter a postal code.',
            invalid: 'Postal code must be 5 digits.',
          },
          city: {
            required: 'Please enter a city.',
            'too-long': 'City must be at most 80 characters.',
          },
          country: {
            required: 'Please enter a country.',
            'too-long': 'Country must be at most 80 characters.',
          },
        },
      },
      detail: {
        title: 'Workspace',
        // Shown right after creating a test workspace while the filter hides it —
        // the one moment the default reliably surprises the operator (IMM-593.2).
        'created-test-hidden':
          'This workspace is a test environment and is hidden from the lists unless "Test environments" is switched on.',
        'id-label': 'ID',
        'created-at-label': 'Created on',
        'owner-heading': 'Owner',
        'owner-name-label': 'Name',
        'owner-email-label': 'Email',
        'address-heading': 'Main office address',
        'street-label': 'Street',
        'house-number-label': 'House number',
        'postal-code-label': 'Postal code',
        'city-label': 'City',
        'country-label': 'Country',
        'coordinates-label': 'Coordinates',
        'organization-heading': 'Main office',
        'organization-name-label': 'Name',
        'status-aktiv': 'Active',
        'edit-cta': 'Edit',
        'back-cta': 'Back to list',
        'pending-heading': 'Pending invitations',
        'pending-empty': 'No pending invitations.',
        'pending-expires-label': 'Expires',
        'pending-resend-cta': 'Resend',
        'pending-resending': 'Sending…',
        'pending-resent': 'Invitation resent.',
        'pending-resend-error': 'Could not resend the invitation. Please try again.',
        'pending-revoke-cta': 'Revoke',
        'pending-revoking': 'Revoking…',
        'pending-revoke-error': 'Could not revoke the invitation. Please try again.',
        whatsapp: {
          section: 'WhatsApp',
          'load-error': 'The WhatsApp list could not be loaded.',
          'sort-label': 'Sort by {column}',
          truncated: 'Only the newest {limit} entries are shown.',
          failed: {
            heading: 'Failed dispatches',
            recipient: 'Recipient',
            reason: 'Reason',
            'provider-code': 'Meta error code',
            'sent-by': 'Sent by',
            'occurred-at': 'Time',
            empty: 'No failed WhatsApp dispatches.',
          },
          unmatched: {
            heading: 'Unmatched messages',
            'scope-note': 'Platform-wide — these messages belong to no workspace.',
            phone: 'Number',
            name: 'Name',
            body: 'Message',
            'received-at': 'Received at',
            reason: 'Reason',
            empty: 'No unmatched messages.',
          },
          reason: {
            'no-match': 'No match',
            ambiguous: 'Ambiguous',
            'window-closed': '24-hour window closed',
            'not-on-whatsapp': 'Number not on WhatsApp',
            'unsupported-content': 'Content not supported',
            unknown: 'Unknown reason',
          },
        },
        portals: {
          section: 'Portals',
          'load-error': 'The portal connections could not be loaded.',
          portal: { IMMOSCOUT24: 'ImmoScout24', KLEINANZEIGEN: 'Kleinanzeigen', IMMOWELT: 'Immowelt' },
          'status-label': 'Status',
          status: {
            'not-connected': 'Not connected',
            connected: 'Connected',
            expired: 'Connection expired',
            invalid: 'Access invalid',
          },
          'expired-since': 'since {at}',
          'account-label': 'IS24 account',
          'connected-by-label': 'Connected by',
          'connected-at-label': 'Connected on',
          'entered-by-label': 'Entered by',
          'entered-at-label': 'Entered on',
          'invalid-since-label': 'Invalid since',
          'last-error-label': 'Last connection error',
          'last-error-note': "The portal's message, unchanged.",
          'last-error-none': 'None',
        },
        'invite-heading': 'Invite member',
        'invite-email-label': 'Email',
        'invite-email-placeholder': 'name@example.com',
        'invite-first-name-label': 'First name',
        'invite-last-name-label': 'Last name',
        'invite-organisation-role-label': 'Organisation role',
        'invite-workspace-role-label': 'Workspace role',
        'invite-workspace-role-none': 'None',
        'invite-standorte-label': 'Standorte',
        'invite-standorte-empty': 'No Standorte are available for this workspace.',
        'invite-submit-cta': 'Send invitation',
        'invite-cancel-cta': 'Cancel',
        'invite-submitting': 'Sending…',
        'invite-success': 'Invitation sent.',
        'invite-error': 'Could not send the invitation. Please try again.',
        'invite-validation-error': 'Please fill in all required fields and select at least one Standort.',
        'org-role': {
          'organisation-owner': 'Organisation owner',
          agent: 'Agent',
          assistant: 'Assistant',
          viewer: 'Viewer',
        },
        'workspace-role': {
          'workspace-owner': 'Workspace owner',
          'workspace-admin': 'Workspace admin',
          billing: 'Billing',
        },
      },
      edit: {
        title: 'Edit workspace',
        'name-label': 'Workspace name',
        'is-test-label': 'Test environment (non-production)',
        'is-test-confirm-title': 'Mark as a test environment?',
        'is-test-confirm-body':
          'All members of this workspace will immediately see the „Testumgebung" notice in the app, and the workspace will be hidden from the lists as long as „Testumgebungen" is not enabled.',
        'is-test-remove-confirm-title': 'Remove the test-environment mark?',
        'is-test-remove-confirm-body':
          'The „Testumgebung" notice disappears for all members of this workspace, and the workspace reappears in every list.',
        'is-test-confirm-cta': 'Confirm',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'back-cta': 'Cancel',
        forbidden: 'You do not have permission to update this workspace.',
        'generic-error': 'Could not update workspace. Please try again.',
        errors: {
          name: {
            required: 'Please enter a name.',
            'too-short': 'Workspace name must be at least 2 characters.',
            'too-long': 'Workspace name must be at most 80 characters.',
          },
        },
      },
      'buyer-contract': {
        heading: 'Buyer brokerage agreement',
        active: 'Active: {name} · Version {version}',
        'active-own': 'Active: {name} · Version {version} · saved on {date} {time} by {member}',
        'inactive-own': 'Own template (inactive): {name} · Version {version}',
        'load-error': 'The buyer template could not be loaded.',
      },
      'email-templates': {
        title: 'E-mail template',
        heading: 'E-mail templates',
        empty: 'This workspace has no e-mail templates.',
        failed: 'The e-mail templates could not be loaded.',
        columns: {
          name: 'Name',
          area: 'Area',
          subject: 'Subject',
          'updated-at': 'Updated on',
          'updated-by': 'Updated by',
        },
        'updated-at-value': '{date} {time}',
        immoos: 'immoOS',
        'subject-label': 'Subject',
      },
    },
    members: {
      title: 'Members',
      empty: 'No members yet.',
      'empty-filtered': 'No member matches the current filters.',
      columns: {
        name: 'Name',
        email: 'Email',
        workspace: 'Workspace',
        status: 'Status',
        onboarding: 'Onboarding',
        role: 'Role',
        'primary-organization': 'Location',
        'last-active': 'Last active',
        'created-at': 'Created on',
      },
      'last-active-never': 'Never',
      'sort-label': 'Sort by {column}',
      'status-filter': {
        label: 'Status',
        all: 'All',
        active: 'Active',
        deactivated: 'Deactivated',
      },
      onboarding: {
        complete: 'Complete',
        open: '{count} open',
      },
      'onboarding-filter': {
        label: 'Onboarding',
        all: 'All',
        complete: 'Complete',
        incomplete: 'Incomplete',
      },
      roles: {
        'workspace-owner': 'Owner',
        'workspace-admin': 'Admin',
        billing: 'Billing',
        'organisation-owner': 'Location owner',
        agent: 'Agent',
        assistant: 'Assistant',
        viewer: 'Viewer',
      },
      detail: {
        title: 'Member',
        'id-label': 'Member ID',
        'user-id-label': 'IdP user ID',
        'last-active': 'Last active on {date}',
        'last-active-never': 'Last active: never',
        empty: '—',
        'roles-section': {
          title: 'Roles & locations',
          'workspace-roles-label': 'Workspace roles',
          primary: 'Primary',
          empty: 'No roles assigned.',
          'edit-cta': 'Edit roles & locations',
        },
        'profile-section': {
          title: 'Profile',
          'position-label': 'Position',
          'phone-label': 'Phone',
          'mobile-label': 'Mobile',
        },
        'technical-section': {
          title: 'Technical details',
        },
        activity: {
          title: 'Activity',
          'active-days': 'Active on {n, plural, one {# day} other {# days}} in the last 30 days',
          filter: {
            label: 'Filter events',
            ALL: 'All',
            SIGN_INS: 'Sign-ins',
            LEADS_AND_DEALS: 'Leads & deals',
            APPOINTMENTS: 'Appointments',
            NOTES_AND_TASKS: 'Notes & tasks',
            ADMIN_ACTIONS: 'Admin actions',
          },
          kind: {
            SIGN_IN: 'Sign-in',
            SIGN_IN_FAILED: 'Failed sign-in',
            LEAD_STATUS_CHANGED: 'Lead status changed',
            DEAL_PHASE_CHANGED: 'Deal phase changed',
            APPOINTMENT_CREATED: 'Appointment created',
            NOTE_CREATED: 'Note created',
            TASK_CREATED: 'Task created',
            CALL_LOGGED: 'Call logged',
            ACTED_ON_BEHALF: 'Acted on behalf',
            EDITED_BY_OPERATOR: 'Member edited',
          },
          description: {
            transition: '{record} · {from} → {to}',
            'transition-unlabelled': '{from} → {to}',
            status: '{record} · {to}',
            'status-unlabelled': '{to}',
            appointment: '{title} · {when}',
            note: 'About {record}',
            'call-with': 'With {record}',
            'call-about': 'About {record}',
            'acted-on-behalf': 'By {operator} · Reason: {reason}',
            'acted-on-behalf-no-reason': 'By {operator}',
            'edited-by-operator': 'By {operator} · {area}',
          },
          'on-behalf': 'on behalf, by {operator}',
          'unknown-operator': 'Unknown operator',
          'edit-area': {
            DETAILS: 'Name and e-mail',
            PROFILE: 'Profile',
            ACCESS: 'Roles & locations',
          },
          'lead-status': {
            NEW: 'New',
            CONTACTED: 'Contacted',
            CONVERTED: 'Converted',
            LOST: 'Lost',
            ARCHIVED: 'Archived',
          },
          'deal-phase': { ...DEAL_PHASE_LABELS },
          empty: 'No activity in the last 90 days.',
          'empty-filtered': 'No events of this kind in the last 90 days.',
          loading: 'Loading activity…',
          error: 'The activity could not be loaded.',
          retry: 'Try again',
        },
        onboarding: {
          title: 'Onboarding',
          collapsed: {
            open: 'Open: {items}',
            'expired-suffix': '{item} (expired)',
            invitation: {
              pending: 'First sign-in (invitation pending)',
              expired: 'First sign-in (invitation expired)',
              revoked: 'First sign-in (invitation revoked)',
            },
            lapsing: 'Expiring soon: {items}',
            'lapsing-item': '{item} ({date})',
          },
          progress: '{done} of {total} mandatory items done',
          complete: 'Onboarding complete',
          loading: 'Loading onboarding status …',
          error: 'The onboarding status could not be loaded.',
          retry: 'Try again',
          items: {
            FIRST_SIGN_IN: 'First sign-in',
            PROFILE_PHOTO: 'Profile photo',
            MOBILE: 'Mobile number',
            POSITION: 'Position',
            IMAGE_RIGHTS_CONSENT: 'Image rights consent',
            MAILBOX: 'Mailbox connected',
            CALENDAR: 'Calendar connected',
            WHATSAPP: 'WhatsApp linked',
            ORGANIZATION_ROLE: 'Location and role',
            RECEIVES_LEADS: 'Receives leads',
          },
          states: {
            MISSING: 'Missing',
            OPTIONAL: 'Optional',
            EXPIRED: 'Expired since {date}',
            EXPIRING: 'Expires on {date}',
            COMPANY_MAILBOX_ONLY: 'Company mailbox only',
            INVITATION_PENDING: 'Invitation pending since {date}',
            INVITATION_EXPIRED: 'Invitation expired on {date}',
            INVITATION_REVOKED: 'Invitation revoked on {date}',
            NOT_APPLICABLE: 'Not applicable (IMAP)',
          },
          details: {
            'signed-in-at': 'On {date}',
            'photo-present': 'Present',
            'consent-granted-at': 'Granted on {date}',
            'mailbox-synced': '{provider} · last synced on {dateTime}',
            'whatsapp-since': 'Since {date}',
          },
          providers: {
            GOOGLE: 'Google',
            MICROSOFT: 'Microsoft',
            IMAP: 'IMAP',
          },
          'imap-providers': {
            GMX: 'GMX',
            WEB_DE: 'WEB.DE',
            T_ONLINE: 'T-Online',
            IONOS: 'IONOS',
            STRATO: 'STRATO',
            MAILBOX_ORG: 'mailbox.org',
            POSTEO: 'Posteo',
            FREENET: 'freenet',
          },
        },
        'created-at-label': 'Created on',
        'edit-cta': 'Edit',
        'back-cta': 'Back to list',
        'impersonate-cta': 'Act on behalf',
        'technical-cta': 'Technical details',
        'technical-dialog': {
          close: 'Close',
        },
        impersonate: {
          title: 'Start impersonation?',
          body: 'You will act as {name} in their workspace, with that member’s permissions. You’ll be redirected to the customer app. Every action is recorded in the audit log against both your account and the impersonated member.',
          confirm: 'Start impersonation',
          cancel: 'Cancel',
          submitting: 'Starting…',
          'forbidden-error': 'You do not have permission to impersonate members.',
          'generic-error': 'Could not start impersonation. Please try again.',
        },
        status: {
          active: 'Active',
          deactivated: 'Deactivated',
        },
        'deactivate-cta': 'Deactivate',
        'reactivate-cta': 'Reactivate',
        deactivate: {
          title: 'Deactivate {name}?',
          body: 'Deactivated members lose access to the workspace.',
          cancel: 'Cancel',
          confirm: 'Deactivate',
        },
        'status-errors': {
          'last-owner':
            'This member is the last active member with workspace-wide permissions. Give another member the Owner or Admin role before you deactivate them.',
          generic: 'Action failed — please try again.',
        },
        emails: {
          title: 'E-mails',
          empty: 'No e-mails in the last 90 days.',
          loading: 'Loading e-mails…',
          error: 'The e-mails could not be loaded.',
          retry: 'Try again',
          columns: { kind: 'Type', subject: 'Subject', 'sent-at': 'Time', status: 'Status' },
        },
      },
      access: {
        title: 'Edit roles & locations',
        'workspace-role-label': 'Workspace role',
        'workspace-role-none': 'None',
        'organizations-label': 'Locations & roles',
        'organizations-hint': 'The member belongs to every location where they hold at least one role.',
        'main-suffix': 'Main location',
        'primary-label': 'Primary location',
        'primary-none': '—',
        'organizations-load-error': "The workspace's locations could not be loaded.",
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'back-cta': 'Cancel',
        errors: {
          'workspace-must-retain-owner':
            'The workspace needs at least one active member with the Owner or Admin role. Give the role to another member first.',
          'affiliation-required': 'The member needs at least one location. Choose a role at at least one location.',
          'primary-not-in-affiliation': "Choose a primary location among the member's locations.",
          'affiliation-requires-role':
            'In a workspace with no further locations, every member belongs to the main location. Choose at least one role there.',
          'organization-not-found': 'A chosen location is no longer available. Reload the page and try again.',
          generic: 'Roles and locations could not be saved. Please try again.',
          forbidden: 'You are not authorised to do this.',
        },
      },
      edit: {
        title: 'Edit member',
        'email-label': 'Email',
        'first-name-label': 'First name',
        'last-name-label': 'Last name',
        'position-label': 'Position',
        'phone-label': 'Phone',
        'mobile-label': 'Mobile',
        'phone-placeholder': '+49 40 123456',
        'mobile-placeholder': '+49 151 23456789',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'back-cta': 'Cancel',
        'forbidden-error': 'You do not have permission to update members.',
        'generic-error': 'Could not update member. Please try again.',
        photo: {
          label: 'Profile photo',
          'upload-cta': 'Upload photo',
          'replace-cta': 'Replace photo',
          'remove-cta': 'Remove',
          uploading: 'Uploading…',
          'upload-error': 'The photo could not be uploaded. Please try again.',
        },
        errors: {
          email: {
            required: 'Please enter an email address.',
            invalid: 'This email address is not valid.',
          },
          'first-name': {
            required: 'First name is required.',
            'too-long': 'First name is too long.',
            invalid: 'First name is invalid.',
          },
          'last-name': {
            required: 'Last name is required.',
            'too-long': 'Last name is too long.',
            invalid: 'Last name is invalid.',
          },
          position: { 'too-long': 'Position is too long.' },
          phone: { invalid: 'Phone number is invalid. Enter it with the country code, e.g. +49 40 123456.' },
          mobile: { invalid: 'Mobile number is invalid. Enter it with the country code, e.g. +49 151 23456789.' },
        },
      },
    },
    organizations: {
      title: 'Organizations',
      empty: 'No organizations yet.',
      'empty-filtered': 'No organization matches the current filters.',
      columns: {
        name: 'Name',
        type: 'Type',
        workspace: 'Workspace',
        'created-at': 'Created on',
      },
      types: {
        main: 'Main',
        subsidiary: 'Subsidiary',
      },
      'setup-status': {
        label: 'Setup status',
        IN_SETUP: 'In setup',
        COMPLETED: 'Set up',
      },
      detail: {
        title: 'Organization',
        'id-label': 'ID',
        'name-label': 'Name',
        'type-label': 'Type',
        'workspace-label': 'Workspace',
        'created-at-label': 'Created on',
        'address-heading': 'Address',
        'street-label': 'Street',
        'house-number-label': 'House number',
        'postal-code-label': 'Postal code',
        'city-label': 'City',
        'country-label': 'Country',
        'coordinates-label': 'Coordinates',
        'edit-cta': 'Edit',
        'edit-address-cta': 'Edit address',
        'edit-name-cta': 'Edit name',
        'back-cta': 'Back to list',
        'members-heading': 'Members',
        'members-empty': 'No members are assigned to this location.',
        'members-columns': {
          name: 'Name',
          email: 'Email',
        },
      },
      records: {
        heading: 'Records',
        'load-failed': "This location's records couldn't be loaded.",
        tabs: { deals: 'Deals', properties: 'Properties', contacts: 'Contacts', leads: 'Leads' },
        empty: {
          deals: 'This location has no deals.',
          properties: 'This location has no properties.',
          contacts: 'This location has no contacts.',
          leads: 'This location has no leads.',
        },
        'select-all': 'Select all',
        'select-row': 'Select {label}',
        selected: '{count, plural, =0 {Nothing selected} one {# record selected} other {# records selected}}',
        'move-cta': 'Move to…',
        'no-targets': 'This workspace has no other active location.',
        unassigned: 'Unassigned',
        columns: {
          object: 'Property/address',
          phase: 'Phase',
          status: 'Status',
          responsible: 'Responsible',
          'object-number': 'Property no.',
          address: 'Address',
          name: 'Name',
          type: 'Type',
          owner: 'Owner',
          received: 'Received',
        },
        'contact-type': { PERSON: 'Person', COMPANY: 'Company' },
        target: {
          title: 'Choose the target location',
          label: 'Target location',
          placeholder: 'Choose a location…',
          cancel: 'Cancel',
          cta: 'Continue to review',
        },
      },
      move: {
        title: 'Move records',
        heading: 'From “{source}” to “{target}”',
        'back-cta': 'Back to the location',
        totals:
          '{deals, plural, one {# deal} other {# deals}} · {properties, plural, one {# property} other {# properties}} · {contacts, plural, one {# contact} other {# contacts}} · {leads, plural, one {# lead} other {# leads}}',
        'brings-heading': 'Moves along',
        'brings-none': 'Brings no further records.',
        kind: { DEAL: 'Deal', PROPERTY: 'Property', CONTACT: 'Contact', LEAD: 'Lead' },
        relation: {
          DEAL_PROPERTY: 'Property of deal {label}',
          DEAL_CONTACT: 'Contact of deal {label}',
          PROPERTY_CONTACT: 'Contact of property {label}',
          LEAD_CONTACT: 'Contact of lead {label}',
          LEAD_PROPERTY: 'Property of lead {label}',
        },
        shared: 'Shared – {source} keeps access through the link',
        'linked-to': 'Linked to: {records}',
        'shared-note':
          'Shared records move to the new location too. The previous location keeps access through their links.',
        responsibility: {
          heading: 'Responsibility',
          note: 'Whoever sees the target location stays responsible. Everyone else loses the responsibility, and the access it gave them. When nobody stays, the target location manager takes over, or the workspace owner when it has none.',
          summary:
            '{kept, plural, one {# stays responsible} other {# stay responsible}} · {removed, plural, one {# removed} other {# removed}} · {assigned, plural, one {# newly responsible} other {# newly responsible}}',
          kept: 'Stays responsible: {members}',
          removed: 'Removed: {members}',
          assigned: {
            LOCATION_MANAGER: 'Newly responsible (location manager): {members}',
            OWNER_FALLBACK: 'Newly responsible (workspace owner): {members}',
          },
          'unknown-member': 'Former member',
        },
        confirm: 'Confirm move',
        confirming: 'Moving…',
        outdated: 'The preview has changed. Review the new preview and confirm again.',
        failed: "The records couldn't be moved.",
        'plan-failed': "The preview couldn't be created.",
        'nothing-selected': 'No records are selected.',
        success: {
          title: 'Records moved',
          body: 'The records now belong to “{target}”.',
          'to-source': 'To {source}',
          'to-target': 'To {target}',
        },
      },
      edit: {
        title: 'Edit organization',
        'name-label': 'Name',
        'address-search-label': 'Search address',
        'address-search-placeholder': 'Type an address…',
        'street-label': 'Street',
        'house-number-label': 'House number',
        'postal-code-label': 'Postal code',
        'city-label': 'City',
        'country-label': 'Country',
        'submit-cta': 'Save',
        'submit-loading': 'Saving…',
        'back-cta': 'Cancel',
        'forbidden-error': 'You do not have permission to update this organisation.',
        'generic-error': 'Could not update organisation. Please try again.',
        errors: {
          name: { required: 'Please enter a name.', 'too-long': 'Name must be at most 80 characters.' },
          street: { required: 'Please enter a street.', 'too-long': 'Street must be at most 100 characters.' },
          'house-number': {
            required: 'Please enter a house number.',
            'too-long': 'House number must be at most 20 characters.',
          },
          'postal-code': { required: 'Please enter a postal code.', invalid: 'Postal code must be 5 digits.' },
          city: { required: 'Please enter a city.', 'too-long': 'City must be at most 80 characters.' },
          country: { required: 'Please enter a country.', 'too-long': 'Country must be at most 80 characters.' },
        },
      },
    },
    'email-log': {
      title: 'E-mails',
      'search-placeholder': 'Search by recipient…',
      columns: {
        'sent-at': 'Time',
        kind: 'Type',
        recipient: 'Recipient',
        workspace: 'Workspace',
        subject: 'Subject',
        status: 'Status',
      },
      'empty-filtered': 'No e-mail matches the current filters.',
      empty: 'No e-mails sent yet.',
      filters: {
        workspace: 'Workspace',
        kind: 'Type',
        all: 'All',
        'workspaces-unavailable': 'Workspaces could not be loaded.',
      },
      placeholder: '—',
      'load-more': 'Load more',
      'load-more-loading': 'Loading…',
      'sort-label': 'Sort by {column}',
      kinds: {
        MEMBER_INVITE: 'Team invitation',
        NEW_LEAD: 'New lead',
        LEAD_QUALIFIED: 'Lead qualified',
        VALUATION_RELEASED: 'Valuation ready',
        VIEWING_DECLINED: 'Viewing declined',
        ONBOARDING_INVITE: 'Onboarding invitation',
        ONBOARDING_SUBMITTED: 'Onboarding submitted',
        ONBOARDING_CHANGES_REQUESTED: 'Onboarding: changes required',
        ONBOARDING_APPROVED: 'Location activated',
        OPERATOR_INVITE: 'Platform admin invitation',
        BROKERAGE_CONCLUDED: 'Brokerage agreement concluded',
        BROKERAGE_WITHDRAWN: 'Brokerage agreement withdrawn',
        VIEWING_INVITATION: 'Viewing invitation',
        NEW_INQUIRY: 'New inquiry',
        SIGNING_SIGNED: 'Signature received',
        SIGNING_COMPLETED: 'Fully signed',
        SIGNING_REVOKED: 'Withdrawal received',
      } satisfies EnumTranslation<EmailLogKind>,
      statuses: {
        SENT: 'Sent',
        DELIVERED: 'Delivered',
        BOUNCED: 'Bounced',
        COMPLAINED: 'Reported as spam',
        FAILED: 'Failed',
      } satisfies EnumTranslation<EmailLogStatus>,
    },
    // Global inbound-call log (IMM-457.2) — mirror of the German block. User
    // strings ship in German; English is the fallback locale copy.
    'api-calls': {
      title: 'API calls',
      subtitle: 'Every inbound request to the public API — regardless of authentication.',
      'temporary-notice':
        'This log is the only record of inbound requests and carries the replay history. Retention: 14 days.',
      empty: 'No matching API calls found.',
      placeholder: '—',
      'load-more': 'Load more',
      'load-more-loading': 'Loading…',
      columns: {
        'occurred-at': 'Time',
        workspace: 'Workspace',
        'credential-class': 'Credential',
        origin: 'Origin',
        endpoint: 'Endpoint',
        'mcp-method': 'MCP method',
        status: 'Status',
        outcome: 'Outcome',
      },
      // The three tables below render contract enums verbatim
      // (`t(\`rejection-reasons.${call.rejectionReason}\`)`), so a missing
      // label puts the raw token on the operator's screen. `satisfies
      // EnumTranslation<E>` makes that a `tsc` failure instead (ADR 0009).
      'credential-classes': {
        'api-key': 'API key',
        'member-session': 'Member session',
        impersonation: 'Impersonation',
        'backoffice-credential': 'MCP (backoffice)',
        'operator-session': 'Operator session',
        'workspace-mcp': 'MCP (customer)',
        'workspace-mcp-oauth': 'MCP (connected client)',
        'valuation-agent': 'Valuation agent',
        anonymous: 'No credential',
        malformed: 'Malformed header',
      } satisfies EnumTranslation<ApiCallCredentialClass>,
      outcomes: {
        success: 'Success',
        denied: 'Denied',
        'rate-limited': 'Rate limited',
        'client-error': 'Client error',
        'server-error': 'Server error',
        'auth-failed': 'Authentication failed',
      } satisfies EnumTranslation<ApiKeyRequestOutcomeDto>,
      'rejection-reasons': {
        'credential-absent': 'No credentials sent',
        'credential-malformed': 'Credentials malformed',
        'api-key-unknown': 'API key unknown or revoked',
        'api-key-member-inactive': "The API key's member is deactivated",
        'service-key-admin-inactive': "The service key's operator is deactivated",
        'session-expired': 'Session expired',
        'session-invalid': 'Session invalid',
        'impersonation-invalid': 'Impersonation session invalid',
        'workspace-mismatch': 'API key belongs to a different workspace',
        'backoffice-credential-invalid': 'Backoffice credential invalid or revoked',
        'backoffice-credential-wrong-route': 'Backoffice credential used on the wrong route',
        'backoffice-surface-disabled': 'Backoffice MCP access is disabled platform-wide',
        'workspace-mcp-surface-disabled': 'Customer MCP access is disabled platform-wide',
        'tool-not-exposed': 'Tool not exposed platform-wide',
        'oauth-token-invalid': 'OAuth token invalid or grant revoked',
        'workspace-key-required': 'Endpoint requires a workspace API key',
        'app-handoff-key-unbound': 'API key is not assigned to an approved application',
        'valuation-agent-token-invalid': 'Valuation agent: token invalid',
      } satisfies EnumTranslation<ApiCallRejectionReason>,
      filters: {
        'workspace-id': 'Workspace ID',
        'workspace-placeholder': 'Filter by workspace…',
        'api-key-id': 'API key ID',
        'api-key-placeholder': 'Filter by API key…',
        'credential-class': 'Credential',
        method: 'Method',
        'endpoint-placeholder': 'Filter by endpoint…',
        from: 'From',
        to: 'To',
      },
      detail: {
        title: 'API call',
        'id-label': 'ID',
        'request-id-label': 'Request ID',
        'occurred-at-label': 'Time',
        'credential-class-label': 'Credential',
        'rejection-reason-label': 'Rejection reason',
        'method-label': 'Method',
        'endpoint-label': 'Endpoint',
        'path-params-label': 'Path parameters',
        'mcp-method-label': 'MCP method',
        'tool-name-label': 'MCP tool',
        'status-label': 'Status code',
        'outcome-label': 'Outcome',
        'duration-label': 'Duration',
        'duration-value': '{ms} ms',
        'permission-label': 'Permission',
        'origin-heading': 'Origin',
        'workspace-label': 'Workspace',
        'api-key-label': 'API key',
        'member-label': 'Member',
        'operating-admin-label': 'Acting operator',
        'client-ip-label': 'Client IP',
        'user-agent-label': 'User agent',
        'request-body-heading': 'Request body',
        'response-body-heading': 'Response body',
        'no-body': 'No body present.',
        'suppressed-auth-route': 'Bodies are not stored for authentication endpoints, as they carry credentials.',
        'suppressed-unmatched-anonymous': 'Bodies are not stored for unmatched requests without a credential.',
        'stored-request-only': 'The response body is not stored for these calls; the request is retained.',
      },
    },
    'api-key-requests': {
      title: 'API key requests',
      subtitle: 'Every request authenticated by an API key — across all workspaces, with replay.',
      empty: 'No matching requests found.',
      placeholder: '—',
      'load-more': 'Load more',
      'load-more-loading': 'Loading…',
      columns: {
        'occurred-at': 'Time',
        workspace: 'Workspace',
        'api-key': 'API key',
        endpoint: 'Endpoint',
        status: 'Status',
        outcome: 'Outcome',
        replay: 'Replay',
      },
      outcomes: {
        success: 'Success',
        denied: 'Denied',
        'rate-limited': 'Rate limit',
        'client-error': 'Client error',
        'server-error': 'Server error',
        'auth-failed': 'Authentication failed',
      },
      filters: {
        workspace: 'Workspace',
        'workspace-placeholder': 'Filter by workspace…',
        'workspace-unresolved': 'No workspace',
        'api-key': 'API key',
        'api-key-placeholder': 'Filter by API key…',
        'api-key-unresolved': 'No API key',
        method: 'Method',
        'endpoint-placeholder': 'Filter by endpoint…',
        from: 'From',
        to: 'To',
      },
      detail: {
        title: 'API request',
      },
    },
    'valuation-agent': {
      title: 'Valuation agent',
      empty: 'No token issued',
      issue: 'Issue token',
      replace: 'Replace token',
      revoke: 'Revoke token',
      status: { active: 'Active', replaced: 'Replaced (still valid)' },
      'valid-until': 'Old token valid until {validUntil}',
      'issued-at-by': 'Issued on {date} by {name}',
      'issued-at': 'Issued on {date}',
      'permissions-label': 'Permissions',
      'last-used': 'Last used: {dateTime}',
      'once-hint': 'The token is shown only now. Keep it safe.',
      copy: 'Copy',
      'revoke-dialog': {
        title: 'Revoke token?',
        body: 'The valuation agent can no longer read or deliver anything afterwards.',
        confirm: 'Revoke',
      },
      'no-permission-chosen': 'Choose at least one permission.',
      'load-error': 'The token could not be loaded.',
      'issue-error': 'The token could not be issued — please try again.',
      'replace-error': 'The token could not be replaced — please try again.',
      permission: {
        'catalog-read': 'Read catalogue',
        'property-read': 'Read property data',
        'request-receive': 'Receive requests',
        'result-deliver': 'Deliver results',
        'finding-write': 'Write findings',
        'capture-photo-read': 'Read capture photos',
      },
    },
    'mcp-access': {
      title: 'MCP access',
      description:
        'Create a read-only access that lets an MCP client (e.g. Claude Code) analyse platform data across every workspace \u2014 for support and fault analysis. The access can only read.',
      'load-error': 'MCP accesses could not be loaded.',
      never: 'Never',
      empty: {
        heading: 'No MCP access yet',
        description:
          'Create your first access to ask questions about leads, permissions and events through an MCP client.',
      },
      columns: {
        name: 'Name',
        status: 'Status',
        'expires-at': 'Expires',
        'created-at': 'Created',
        'last-used': 'Last used',
        actions: 'Actions',
      },
      status: {
        active: 'Active',
        revoked: 'Revoked',
        expired: 'Expired',
      },
      issue: {
        cta: 'Create access',
        title: 'Create MCP access',
        'name-label': 'Name',
        'name-placeholder': 'Claude Code \u2014 support laptop',
        'validity-label': 'Validity',
        'validity-hint': 'Every access expires automatically. Pick the shortest period that works for you.',
        'validity-option': '{days} days',
        'grants-label': 'Additional permissions',
        grant: {
          'skill-read': {
            label: 'Read document-analysis skills',
            hint: 'The client can read the analysis prompts of every document type, the fallback skill and the field catalog.',
          },
          'skill-manage': {
            label: 'Edit document-analysis skills',
            hint: 'The client can change, activate and deactivate analysis prompts. Every change applies immediately to all future document analyses in every workspace and is attributed to you.',
          },
          'memory-manage': {
            label: 'Edit Objekt memory',
            hint: "The client can correct, edit, add and delete statements in an Objekt's memory \u2014 in every workspace. Every change flows into the dossier immediately and is attributed to you.",
          },
          'document-update': {
            label: 'Rename and re-file documents',
            hint: 'The client can rename any document of an Objekt and move it to another category \u2014 in every workspace. Every change is attributed to you and recorded in the audit log.',
          },
        },
        'submit-cta': 'Create access',
        'submit-loading': 'Creating\u2026',
        'cancel-cta': 'Cancel',
        'done-cta': 'Done',
        'secret-heading': 'Your new MCP access',
        'secret-warning': 'Copy the command now \u2014 the access is shown only once and cannot be retrieved later.',
        'command-label': 'Command for Claude Code',
        'copy-cta': 'Copy',
        'copied-confirmation': 'Command copied to the clipboard.',
        footnote: 'The access can only read. Every request is logged and attributed to you.',
        'footnote-write':
          'The access can read and make the changes you ticked. Every request is logged and attributed to you.',
        errors: {
          'name-required': 'Name is required.',
          'name-too-long': 'Name is too long.',
          'name-already-used': 'You already have an access with this name.',
          'validity-required': 'Choose a validity period.',
          'limit-reached':
            'You have reached the maximum of {max} active accesses. Revoke an existing access to create a new one.',
          generic: 'The access could not be created \u2014 please try again.',
        },
      },
      incident: {
        heading: 'All accesses',
        description: 'Accesses of every operator — for the case that an access is compromised.',
        'issued-by': 'Created by',
        'revoke-confirm-body':
          'The access "{name}" by {operator} will be permanently deactivated immediately. This cannot be undone.',
        'disable-cta': 'Disable MCP access',
        'disable-confirm-title': 'Disable MCP access for the whole platform?',
        'disable-confirm-body':
          'All accesses will be refused immediately but remain in place. You can re-enable access at any time.',
        'disable-confirm-cta': 'Disable',
        'disable-success': 'MCP access has been disabled.',
        'enable-cta': 'Enable MCP access',
        'enable-success': 'MCP access has been enabled.',
        'disabled-banner':
          'MCP access is currently disabled platform-wide. Existing accesses are refused until it is re-enabled.',
        'switch-error': 'The switch could not be changed — please try again.',
      },
      // Operator containment for the CUSTOMER MCP surface (IMM-601.6) — a
      // separate switch from the operator-surface one above.
      'customer-surface': {
        heading: 'Customer MCP access',
        'state-enabled': 'Active — customer clients can connect.',
        'state-disabled': 'Disabled — every customer key is refused.',
        'disable-cta': 'Disable customer MCP access',
        'enable-cta': 'Enable customer MCP access',
        'disable-confirm-title': 'Disable customer MCP access?',
        'disable-confirm-body':
          'Every connected customer client is refused immediately. The keys themselves are unchanged and work again as soon as you re-enable access.',
        'disabled-banner': 'Disabled — every customer key is refused.',
        'switch-error': 'The switch could not be changed — please try again.',
      },
      // Per-tool exposure on the customer MCP surface (IMM-625.2) — one tool at
      // a time, as opposed to the platform-wide kill switch above.
      tools: {
        heading: 'Exposed tools',
        description:
          "Every tool in the catalogue with its exposure. Changes take effect on a connected client's next request.",
        none: '—',
        // Field labels of the expanded row. Name, identifier and exposure sit
        // unlabelled in the collapsed row — that selection is what keeps the
        // list readable.
        details: {
          description: 'Description',
          kind: 'Kind',
          'side-effect': 'Side effect',
          permission: 'Permission',
          'changed-by': 'Changed by',
          'changed-at': 'Changed at',
        },
        kind: {
          query: 'Reads',
          command: 'Writes',
        },
        'side-effect': {
          auto_with_audit: 'Automatic with audit',
          approval_required: 'Approval required',
        },
        exposure: {
          exposed: 'Exposed',
          withdrawn: 'Not exposed',
          'switch-label': 'Exposure for {name}',
        },
        'expose-cta': 'Expose',
        'already-exposed-cta': 'Already exposed',
        'keep-closed-cta': 'Reviewed — keep closed',
        'withdraw-cta': 'Withdraw exposure',
        'withdraw-confirm-title': 'Withdraw exposure?',
        'withdraw-confirm-body':
          'Every workspace loses access to “{name}”. Connected clients stop seeing it from their next request.',
        'cancel-cta': 'Cancel',
        'save-error': 'The exposure could not be changed — please try again.',
        filters: {
          label: 'Filter',
          all: 'All',
          exposed: 'Exposed only',
          unreviewed: 'Awaiting review only',
          writes: 'Write tools only',
          stale: 'Orphaned entries',
          search: 'Search tools',
          'search-placeholder': 'Name or identifier…',
        },
        // What the list currently holds. The catalogue is expected to grow well
        // past a screenful, so the tally answers "how much am I looking at, and
        // how much of it is live?" without counting rows.
        count: '{count, plural, one {# tool} other {# tools}} · {exposed} of them exposed',
        'count-filtered': '{visible} of {total} tools · {exposed} of them exposed',
        module: {
          label: 'Module',
          all: 'All modules',
        },
        page: {
          range: '{from}–{to} of {total}',
          previous: 'Previous',
          next: 'Next',
        },
        selection: {
          label: 'Selection',
          count: '{count, plural, one {# selected} other {# selected}}',
          clear: 'Clear selection',
          'select-page': 'Select page',
        },
        bulk: {
          'confirm-expose-title': '{count, plural, one {Expose # tool?} other {Expose # tools?}}',
          'confirm-withdraw-title':
            '{count, plural, one {Withdraw exposure for # tool?} other {Withdraw exposure for # tools?}}',
          'confirm-keep-closed-title':
            '{count, plural, one {Mark # tool as reviewed?} other {Mark # tools as reviewed?}}',
          'confirm-body':
            'The change takes effect on a connected client’s next request. It is saved as one action — all of it or none.',
          'confirm-keep-closed-body':
            'Nothing changes for connected clients — none of these tools is exposed. Only the review is recorded, as one action — all of it or none.',
          error: 'The selection could not be changed. Nothing was saved.',
        },
        unreviewed: 'New — not yet reviewed',
        stale: {
          notice: 'This tool no longer exists.',
          'remove-cta': 'Remove entry',
          'confirm-title': 'Remove entry?',
          'confirm-body':
            'The entry points at a tool the catalogue no longer knows. Removing it changes nothing for any client — the tool is already unreachable.',
          'removed-confirmation': 'Entry removed.',
          'remove-error': 'The entry could not be removed — please try again.',
        },
        empty: {
          heading: 'No tools in the catalogue',
          description: 'As soon as the catalogue contains a tool, it appears here.',
        },
        'empty-filtered': {
          heading: 'No tools found',
          description: 'No tool matches the selected filters. Reset the filters to see them all.',
        },
        'empty-unreviewed': {
          heading: 'Every tool has been reviewed.',
          description: 'No tool is awaiting a decision. New tools appear here as soon as they arrive.',
        },
        'empty-stale': {
          heading: 'No orphaned entries',
          description: 'Every entry points at a tool the catalogue still knows.',
        },
        'kill-switch-notice':
          'The MCP interface is currently fully disabled. Exposure changes are saved and take effect as soon as the interface is active again.',
        'load-error': 'The tool list could not be loaded. Reload the page — this is not caused by missing permissions.',
        // Reachability diagnosis (IMM-625.4) — why one named access cannot reach
        // one named tool. Runs on `mcp-surface-read`, so it sits outside the
        // manage gate on the row actions. The diagnosis opens together with
        // the row's details — it has no trigger of its own.
        reachability: {
          // The panel's own affordance inside an expanded row. It is not
          // mounted with the details, because it asks for the API-key list of
          // every workspace the moment it appears.
          cta: 'Check reachability',
          heading: 'Check reachability',
          'kind-label': 'Kind of access',
          kind: {
            'api-key': 'API key',
            'oauth-grant': 'OAuth access',
          },
          'credential-label': 'Check access',
          'credential-placeholder': 'Choose an access…',
          never: 'Never',
          'all-tools': 'All tools',
          'seat-label': 'Which location?',
          'seat-hint':
            'This access covers several locations. Permissions differ per location — choose the one the question is about.',
          'seat-placeholder': 'Choose a location…',
          loading: 'Checking…',
          reachable: 'This tool is reachable for this access.',
          excluded: {
            SURFACE_DISABLED: 'The MCP interface is currently fully disabled — no client reaches any tool right now.',
            NOT_EXPOSED: 'This tool is not exposed.',
            CREDENTIAL_NOT_MCP:
              'This access is not enabled for MCP clients, so it reaches no tool at all. The customer has to issue a new key with the MCP capability.',
            CREDENTIAL_GRANTS: 'The access does not hold the required permission: {permission}.',
            MEMBER_PERMISSIONS:
              'The member the access is bound to does not hold the required permission: {permission}.',
          },
          'credential-not-found': 'This access no longer exists.',
          empty: {
            'api-key': 'There are no API keys.',
            'oauth-grant': 'There are no OAuth accesses.',
          },
          'load-error': 'The accesses could not be loaded — please try again.',
          error: 'The check could not be run — please try again.',
        },
      },
      revoke: {
        cta: 'Revoke',
        'confirm-title': 'Revoke access?',
        'confirm-body':
          'The access \u201C{name}\u201D will be permanently deactivated immediately. Requests using it will be refused from then on. This cannot be undone.',
        'cancel-cta': 'Cancel',
        'confirm-cta': 'Revoke',
        loading: 'Revoking\u2026',
        'success-confirmation': 'Access \u201C{name}\u201D was revoked.',
        'generic-error': 'The access could not be revoked \u2014 please try again.',
      },
    },
    'api-keys': {
      title: 'API keys',
      kind: {
        workspace: 'Workspace key',
        admin: 'Admin API key',
      },
      scope: {
        all: 'All workspaces',
        selected: '{count} workspaces',
        // A workspace key has no scope: its reach is the one workspace it is
        // bound to, which the Workspace column already shows.
        none: '\u2014',
      },
      'issue-service-cta': 'Issue admin API key',
      'issue-service': {
        title: 'Issue an admin API key',
        'name-label': 'Name',
        'name-helper':
          'Who uses this key. Shown operator-side wherever this key\u2019s actions appear \u2014 never to the customer.',
        'description-label': 'Description (optional)',
        'rate-limit-label': 'Rate limit (requests per second)',
        'rate-limit-helper':
          '1\u201360. Defaults to 30 \u2014 a consumer polling the queue calls more often than a customer integration.',
        'scope-label': 'Workspace scope',
        'scope-helper': 'Required. \u201CAll workspaces\u201D means access to every valuation request on the platform.',
        'scope-all': 'All workspaces',
        'scope-selected': 'Selected workspaces',
        'workspace-search-placeholder': 'Search workspaces',
        'submit-cta': 'Issue key',
        'submit-loading': 'Issuing\u2026',
        'cancel-cta': 'Cancel',
        'secret-heading': 'This key is shown exactly once.',
        'secret-warning':
          'Copy it now and store it with the consumer. We cannot show it again \u2014 if it is lost, issue a new one and revoke this one.',
        'copy-cta': 'Copy key',
        'copied-cta': 'Copied',
        'secret-acknowledge-cta': 'I have saved the key',
        errors: {
          'name-required': 'Name is required.',
          'name-too-long': 'Name is too long.',
          'name-taken': 'An active admin API key named \u201C{name}\u201D already exists.',
          'description-too-long': 'Description is too long.',
          'rate-limit-range': 'Rate limit must be between 1 and 60.',
          'scope-required': 'Please choose a scope.',
          'scope-empty': 'Please choose at least one workspace.',
          'workspace-unknown': 'One of the selected workspaces no longer exists.',
          forbidden: 'You do not have permission to issue keys.',
          generic: 'The key could not be issued \u2014 please try again.',
        },
      },
      'revoke-service': {
        title: 'Revoke admin API key \u201C{name}\u201D?',
        body: 'The next call with this key will be refused. Valuations already released remain in place and are not withdrawn.',
        'confirm-cta': 'Revoke',
        'cancel-cta': 'Cancel',
      },
      empty: 'No matching API keys found.',
      unknown: 'Unknown',
      'customer-created': 'Created by the customer',
      'customer-revoked': 'Revoked by the customer',
      // Generic stand-in when an operator id can't be resolved to a name
      // (deactivated + pruned admin, or an edge case) — never the raw uuid.
      'operator-fallback': 'immoOS operator',
      columns: {
        name: 'Name',
        workspace: 'Workspace',
        member: 'Member',
        status: 'Status',
        'rate-limit': 'Rate limit',
        'last-used': 'Last used',
        'issued-by': 'Issued by',
        scope: 'Scope',
      },
      status: {
        active: 'Active',
        revoked: 'Revoked',
        // Derived at read time from `expiresAt` (IMM-601.1) — never stored.
        expired: 'Expired',
      },
      revoke: {
        cta: 'Revoke',
        'confirm-title': 'Revoke API key?',
        'confirm-body':
          '“{name}” becomes invalid immediately and permanently. Running integrations that use this key will break. This action cannot be undone.',
        'confirm-cta': 'Revoke permanently',
        'cancel-cta': 'Cancel',
        loading: 'Revoking …',
        'generic-error': 'The API key could not be revoked. Please try again.',
        'forbidden-error': 'You do not have permission to revoke API keys.',
      },
      filters: {
        'workspace-id': 'Workspace ID',
        'uuid-placeholder': 'Paste a UUID',
        status: 'Status',
      },
      never: 'Never',
      detail: {
        title: 'API key',
        'id-label': 'ID',
        'name-label': 'Name',
        'description-label': 'Description',
        'kind-label': 'Kind',
        'workspace-label': 'Workspace',
        'member-label': 'Member',
        'scope-label': 'Scope',
        'status-label': 'Status',
        'rate-limit-label': 'Rate limit',
        'rate-limit-value': '{count} / second',
        'reach-all': 'This key acts on behalf of {operatorName} and reaches every workspace.',
        'reach-selected':
          'This key acts on behalf of {operatorName} and reaches {count, plural, one {one selected workspace} other {# selected workspaces}}.',
        'permissions-label': 'Permissions',
        'issued-by-label': 'Issued by',
        'last-used-label': 'Last used',
        'revoked-at-label': 'Revoked at',
        'revoked-by-label': 'Revoked by',
        'created-at-label': 'Created at',
        'requests-heading': 'Requests',
        'requests-empty': 'No matching requests found.',
        'load-more': 'Load more',
        'load-more-loading': 'Loading…',
      },
      requests: {
        columns: {
          'occurred-at': 'Time',
          endpoint: 'Endpoint',
          status: 'Status',
          outcome: 'Outcome',
        },
        outcomes: {
          success: 'Success',
          denied: 'Denied',
          'rate-limited': 'Rate limited',
          'client-error': 'Client error',
          'server-error': 'Server error',
          'auth-failed': 'Auth failed',
        },
        filters: {
          'endpoint-placeholder': 'Filter by endpoint…',
          from: 'From',
          to: 'To',
        },
      },
      replay: {
        heading: 'Replay',
        mark: 'replayed',
        'in-flight': 'in progress…',
        'original-outcome': 'Originally: {status} · {outcome}',
        description:
          'Re-submit this request — the stored payload is processed against the current contract. Partner attribution is preserved.',
        action: 'Replay request',
        submitting: 'Replaying…',
        'already-replayed': 'Replayed on {date}.',
        'forbidden-error': 'You do not have permission to replay requests.',
        'generic-error': 'The request could not be replayed — please try again.',
        blocked: {
          NOT_A_FAILURE: 'This request succeeded — there is nothing to recover.',
          ALREADY_REPLAYED: 'This request has already been replayed.',
          ENDPOINT_NOT_REPLAYABLE: 'This endpoint cannot be replayed.',
          NO_REQUEST_BODY: 'No payload was stored for this request.',
          REQUEST_BODY_TRUNCATED: 'The stored payload is truncated — replaying it would be incomplete.',
          API_KEY_REVOKED: 'The issuing API key has been revoked.',
          PERMISSION_NO_LONGER_HELD: 'The key no longer holds the required permission.',
        },
      },
      'request-detail': {
        title: 'API request',
        'id-label': 'ID',
        'request-id-label': 'Request ID',
        'occurred-at-label': 'Time',
        'method-label': 'Method',
        'endpoint-label': 'Endpoint',
        'status-label': 'Status code',
        'outcome-label': 'Outcome',
        'duration-label': 'Duration',
        'duration-value': '{ms} ms',
        'permission-label': 'Permission',
        'origin-heading': 'Origin',
        'workspace-label': 'Workspace',
        'api-key-label': 'API key',
        'member-label': 'Member',
        'client-ip-label': 'Client IP',
        'user-agent-label': 'User agent',
        'request-body-heading': 'Request body',
        'response-body-heading': 'Response body',
        'no-body': 'No body present.',
      },
      // In-place edit of an issued key (IMM-493.1). Api-key failures render from
      // page-scoped copy keyed on the error code, matching the issue flow —
      // none of the identity.api-key.* codes is in the ERROR_CODES seed registry.
      edit: {
        cta: 'Edit',
        title: 'Edit API key',
        'rate-limit-label': 'Rate limit',
        'permissions-label': 'Permissions',
        'submit-cta': 'Save changes',
        'submit-loading': 'Saving …',
        'cancel-cta': 'Cancel',
        errors: {
          revoked: 'This API key has been revoked and can no longer be edited.',
          forbidden: "You don't have permission to edit API keys.",
          generic: 'The changes could not be saved. Please try again.',
        },
      },
      issue: {
        cta: 'New API key',
        title: 'Issue a new API key',
        'workspace-label': 'Workspace',
        'workspace-search-placeholder': 'Search workspaces …',
        'workspace-empty': 'No matching workspace found.',
        'owner-line': 'Acts as: {email}',
        'owner-helper': 'The key inherits this member’s permissions. If the member loses a role, the key loses it too.',
        'name-label': 'Name',
        'description-label': 'Description (optional)',
        'rate-limit-label': 'Rate limit',
        'rate-limit-suffix': 'requests / second',
        'rate-limit-helper': 'Between 1 and 60.',
        'permissions-label': 'Permissions',
        'permissions-helper':
          'API-key management permissions cannot be granted — a key can never create or revoke further keys.',
        'permission-search-placeholder': 'Search permissions …',
        'permission-empty': 'No matching permission found.',
        scope: {
          WORKSPACE: 'Workspace permissions',
          ORG: 'Standort permissions',
        },
        module: {
          identity: 'Administration & access',
          relationship: 'Contacts & companies',
          property: 'Properties',
          deal: 'Deals',
          lead: 'Leads',
          valuation: 'Valuations',
          activity: 'Activities',
          communications: 'Communications',
          media: 'Media',
          assistant: 'Assistant',
          search: 'Search',
        },
        'select-all': 'Select all',
        'deselect-all': 'Deselect all',
        'submit-cta': 'Issue API key',
        'submit-loading': 'Issuing …',
        'cancel-cta': 'Cancel',
        'secret-heading': 'API key created',
        'secret-warning':
          'This key is shown only once. Copy it now and store it securely — it cannot be retrieved again later.',
        'copy-cta': 'Copy',
        'copied-cta': 'Copied',
        'done-cta': 'Done',
        errors: {
          'workspace-required': 'Please select a workspace.',
          'name-required': 'Please enter a name.',
          'name-too-long': 'The name must be at most 80 characters long.',
          'description-too-long': 'The description must be at most 500 characters long.',
          'rate-limit-range': 'The rate limit must be between 1 and 60.',
          'permissions-required': 'Please select at least one permission.',
          'no-eligible-owner':
            'There is no active member with workspace-wide permissions for “{workspace}”. Without such a member, no key can be issued.',
          generic: 'The API key could not be issued. Please try again.',
          forbidden: 'You do not have permission to issue API keys.',
          // Names the bound member but NOT the permission: the refusal's wire
          // message stopped disclosing which grant was refused (IMM-569.3),
          // because the message is part of the API response.
          'permission-not-held':
            '{member} no longer holds one of the selected permissions. Please review the selection and submit again.',
        },
      },
    },
    properties: {
      title: 'Properties',
      empty: 'No properties yet.',
      filters: { search: 'Search by title or address…' },
      'empty-filtered': 'No property matches the current filters.',
      columns: {
        title: 'Title',
        address: 'Address',
        type: 'Property type',
        object: 'Property',
        status: 'Status',
        price: 'Price',
        area: 'Area',
        'broker-team': 'Broker team',
        'match-score': 'Match score',
        'last-activity': 'Last activity',
        workspace: 'Workspace',
        organization: 'Location',
        tenancy: 'Location',
        'created-at': 'Created on',
      },
      totals: {
        'shown-of-total': '{shown} of {total}',
        'price-sum': 'Σ {amount}',
        'area-avg': 'ø {amount}',
        'broker-count': '{count} brokers',
        empty: '—',
      },
      'marketing-type': MARKETING_TYPE_LABELS,
      status: PROPERTY_STATUS_LABELS,
      detail: {
        title: 'Property',
        deleted: { title: 'Property deleted', body: 'Deleted on {deletedAt} by {deletedBy}' },
        'id-prefix': 'ID',
        'header-price-per-sqm-suffix': ' · {amount} / m²',
        tabs: {
          overview: 'Overview',
          activity: 'Activity',
          property: 'Property',
          contacts: 'Contacts',
          expose: 'Exposé',
          emails: 'Emails',
          calls: 'Calls',
          team: 'Team',
          notes: 'Notes',
          appointments: 'Appointments',
          dossier: 'Dossier',
          memory: 'Memory',
          inquiries: 'Inquiries',
        },
        dossier: {
          loading: 'Loading dossier…',
          'no-facts-detected':
            "No facts have been detected from documents yet. The values below come from the property's own data; facts appear once documents have been analysed.",
          forbidden: 'You do not have permission to view this property’s dossier.',
          error: 'The dossier could not be loaded.',
          retry: 'Try again',
          brief: {
            heading: 'Summary',
            'created-at': 'Created on {date}',
            stale: 'The summary is being regenerated and may still differ from the facts below.',
            empty: 'No summary is available for this property yet.',
          },
          groups: {
            PROPERTY_INTRINSIC: 'Property facts',
            DEAL_CONTEXT: 'Deal context',
          },
          'confirmed-value': 'Confirmed value',
          'extracted-values': 'Detected values',
          conflict: 'Conflicting information',
          'public-safe': 'Publicly visible',
          columns: {
            feature: 'Value',
            source: 'Source',
            confidence: 'Confidence',
            'extracted-at': 'Extracted on',
          },
          modality: {
            DOCUMENT: 'Document',
            IMAGE: 'Image',
            NONE: 'No source medium',
          },
          'source-unavailable': 'Source no longer available',
          'search-placeholder': 'Search facts…',
          'only-conflicts': 'Conflicts only ({count})',
          'no-match': 'No facts found.',
          category: DOCUMENT_CATEGORY_LABELS,
          'enum-values': DOSSIER_FACT_ENUM_LABELS,
          'boolean-values': { true: 'Yes', false: 'No' },
        },
        memory: {
          operator: {
            by: 'by {name} · {date}',
            edited: 'edited by {name} · {date}',
            added: 'added by {name} · {date}',
            orphaned: 'Orphaned · no longer in the source',
            deleted: 'deleted by {name} · {date}',
            unknown: 'an operator',
          },
          loading: 'Loading memory…',
          forbidden: 'You do not have permission to view this property’s memory.',
          error: 'The memory could not be loaded.',
          retry: 'Try again',
          empty: 'Nothing has been read for this property yet.',
          columns: {
            label: 'Statement',
            value: 'Value',
            correction: 'Corrected',
            source: 'Source',
            passage: 'Passage',
            prompt: 'Read with',
            model: 'Model',
            confidence: 'Confidence',
            'read-at': 'Date',
          },
          'sort-label': 'Sort by {column}',
          'source-type': { DOCUMENT: 'Document', EMAIL: 'Email', CALL: 'Call', NOTE: 'Note', OPERATOR: 'Manual' },
          filter: {
            label: 'Source type',
            chip: 'Source type: {type}',
            remove: 'Remove the source type filter',
          },
          confidence: { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' },
          page: 'p. {page}',
          viewer: {
            title: '{name}',
            download: 'Download',
            'no-preview': 'No preview available.',
            'not-found': 'Document not found.',
            loading: 'Loading preview…',
            error: 'The preview could not be loaded.',
          },
        },
        inquiries: {
          loading: 'Loading inquiries…',
          forbidden: 'You do not have permission to view this property’s inquiries.',
          error: 'The inquiries could not be loaded.',
          retry: 'Try again',
          empty: 'No inquiries have been received for this property yet.',
          columns: {
            'received-at': 'Received',
            source: 'Source',
            name: 'Name',
            email: 'Email',
            phone: 'Phone',
            message: 'Message',
            outcome: 'Outcome',
          },
          'sort-label': 'Sort by {column}',
          source: PROSPECT_SOURCE_LABELS,
          outcome: INQUIRY_OUTCOME_LABELS,
        },
        contacts: {
          'section-title': 'Contacts',
          loading: 'Loading contacts…',
          empty: 'No contacts linked yet.',
          'load-error': 'Failed to load contacts.',
          'open-in-new-tab': 'Open in new tab',
          'add-cta': 'Link contact',
          'row-actions': 'Actions',
          'open-contact': 'Open contact',
          'send-email': 'Send email',
          'send-message': 'Send message',
          'not-implemented': 'This action is not available yet.',
          role: PROPERTY_CONTACT_ROLE_LABELS,
          groups: {
            owner: 'Owner',
            'prospects-sale': 'Purchase prospects',
            'prospects-rental': 'Rental prospects',
            others: 'Other contacts',
          },
          'owner-empty': 'No owner linked yet.',
          'owner-empty-cta': 'Link owner',
          'edit-link': 'Edit link',
          'unlink-link': 'Remove link',
          'link-dialog': {
            title: 'Link contact',
            'contact-label': 'Contact',
            'create-contact-cta': 'Create new contact',
            'role-label': 'Role',
            'role-placeholder': 'Select a role …',
            'note-label': 'Note',
            'note-hint': 'Max. 500 characters',
            'submit-cta': 'Link',
            'submit-loading': 'Linking…',
            'cancel-cta': 'Cancel',
            errors: {
              'contact-required': 'Please select a contact.',
              'role-required': 'Please select a role.',
              'not-found': 'Contact or property not found.',
              'already-linked': 'This link already exists. You can edit the note on the existing link instead.',
              'note-too-long': 'The note must be at most 500 characters.',
              'role-marketing-mismatch': "This role does not match the property's marketing type.",
              forbidden: 'You are not authorized to perform this action.',
              generic: 'Could not link the contact. Please try again.',
            },
          },
          'edit-dialog': {
            title: 'Edit link',
            'role-label': 'Role',
            'note-label': 'Note',
            'note-hint': 'Max. 500 characters',
            'submit-cta': 'Save',
            'submit-loading': 'Saving…',
            'cancel-cta': 'Cancel',
            errors: {
              'already-linked': 'This link already exists. You can edit the note on the existing link instead.',
              'note-too-long': 'The note must be at most 500 characters.',
              'role-marketing-mismatch': "This role does not match the property's marketing type.",
              'not-found': 'This link was not found.',
              forbidden: 'You are not authorized to perform this action.',
              generic: 'Could not save the change. Please try again.',
            },
          },
          'unlink-dialog': {
            title: 'Remove link',
            body: 'Remove this link? The contact and the property are kept.',
            'confirm-cta': 'Remove link',
            'confirm-loading': 'Removing…',
            'cancel-cta': 'Cancel',
            'error-forbidden': 'You are not authorized to perform this action.',
            error: 'Could not remove the link. Please try again.',
          },
        },
        sections: {
          'key-facts': 'Key facts',
          pipeline: 'Pipeline',
          activity: 'Activity',
          tasks: 'Tasks',
          details: 'Details',
          tenancy: 'Workspace & location',
          owner: 'Owner',
          'linked-deals': 'Linked deals',
          categories: 'Categories',
        },
        'owner-empty': 'No owner linked yet.',
        cards: {
          'status-label': 'Status',
          'status-since-day': 'for 1 day',
          'status-since-days': 'for {days} days',
          'asking-price-label': 'Asking price',
          'asking-price-per-sqm': '€ {amount} / m²',
          'expected-revenue-label': 'Expected revenue',
          'match-score-label': 'Match score',
          'last-activity-label': 'Last activity',
          'last-activity-empty': 'No activity yet',
          'broker-team-label': 'Broker team',
          'member-singular': '1 member',
          'member-plural': '{count} members',
        },
        pipeline: {
          'first-contact': 'First contact',
          viewing: 'Viewing',
          offer: 'Offer',
          negotiation: 'Negotiation',
          notary: 'Notary',
        },
        details: {
          'type-label': 'Property type',
          'location-label': 'Location',
          'living-area-label': 'Living area',
          'rooms-label': 'Rooms',
          'year-built-label': 'Year built',
          'energy-label': 'Energy',
          'asking-label': 'Asking',
          'expected-label': 'Expected',
          'kind-label': 'Kind',
          'workspace-label': 'Workspace',
          'scope-label': 'Scope',
          'organization-label': 'Location',
          'show-all-fields': 'Show all fields',
        },
        'activity-empty': 'All activity for this property will land here.',
        'tab-empty': 'All {tab} entries for this property will land here.',
        'tasks-empty': 'No tasks yet.',
        'tasks-open': '{count} open',
        'coming-soon': 'Coming soon',
        'add-cta': 'Add',
        empty: '—',
      },
    },
    admins: {
      title: 'Admins',
      'api-keys': {
        title: "This operator's admin API keys",
        empty: 'This operator has no admin API keys yet.',
        'view-cta': 'View admin API keys',
      },
      detail: {
        title: 'Admin',
        'id-label': 'ID',
        'back-cta': 'Back to list',
        'back-cta-heading': 'Navigation',
      },
      empty: 'No admins yet.',
      'no-match-title': 'No matches',
      'no-match-description': 'Adjust the search to find admins.',
      'search-reset-cta': 'Reset search',
      'search-placeholder': 'Search name, email or ID…',
      'create-cta': 'Add admin',
      status: {
        active: 'Active',
        inactive: 'Inactive',
      },
      'deactivate-cta': 'Deactivate',
      'deactivate-confirm-title': 'Deactivate admin?',
      'deactivate-confirm-body':
        '{fullName} ({email}) will lose access to the admin console immediately. This can only be reversed by adding them again.',
      'deactivate-confirm-cta': 'Deactivate',
      'deactivate-keys-confirm-title': 'Deactivate operator?',
      'deactivate-keys-confirm-body':
        '{count, plural, one {This operator holds one active admin API key. It stops working the moment you deactivate them. Affected: {names}. Re-issue the key first if the integration should keep running.} other {This operator holds {count} active admin API keys. They stop working the moment you deactivate them. Affected: {names}. Re-issue the keys first if the integrations should keep running.}}',
      'deactivate-keys-confirm-cta': 'Deactivate anyway',
      'deactivate-cancel-cta': 'Cancel',
      'deactivate-loading': 'Deactivating…',
      'deactivate-self-error': 'You cannot deactivate your own admin account.',
      'deactivate-last-error': 'At least one active admin must remain at all times.',
      'deactivate-generic-error': 'Could not deactivate admin. Please try again.',
      'created-by-system': 'System',
      columns: {
        name: 'Name',
        email: 'Email',
        status: 'Status',
        'created-at': 'Added on',
        'created-by': 'Added by',
        id: 'ID',
        actions: 'Actions',
      },
      create: {
        title: 'Add admin',
        'full-name-label': 'Full name',
        'full-name-placeholder': 'Erika Mustermann',
        'email-label': 'Email',
        'email-placeholder': 'name@immoos.ai',
        'submit-cta': 'Invite admin',
        'submit-loading': 'Inviting…',
        'back-cta': 'Cancel',
        'forbidden-error': 'You do not have permission to add admins.',
        'duplicate-error': 'An admin with this email already exists.',
        'generic-error': 'Could not add admin. Please try again.',
        errors: {
          'full-name': {
            required: 'Please enter a name.',
            'too-long': 'Name must be at most 120 characters.',
          },
          email: {
            required: 'Please enter an email address.',
            invalid: 'This email address is invalid.',
          },
        },
      },
    },
    sandbox: {
      title: 'Sandbox',
      intro:
        'Here you reset the sandbox data (app.sandbox.immoos.ai) to a fresh copy of production. This also happens automatically every Monday at 7:00.',
      'latest-heading': 'Latest reset',
      started: 'Started: {startedAt}',
      'status-label': 'Status:',
      statuses: {
        QUEUED: 'Waiting',
        IN_PROGRESS: 'Running',
        SUCCEEDED: 'Succeeded',
        FAILED: 'Failed',
      },
      'view-run': 'View run',
      empty: 'No reset has run yet.',
      'failure-note': 'The latest reset failed. The sandbox stays paused until a reset runs through.',
      'status-unavailable': 'The status from GitHub cannot be loaded right now. Reload the page in a moment.',
      'reset-cta': 'Reset data',
      'busy-note': 'A sandbox run is in progress. Reload the page to see whether it has finished.',
      'started-toast': 'Reset started. Reload the page to see its progress.',
      dialog: {
        title: 'Reset the sandbox data?',
        body: 'The sandbox database is replaced with the newest production backup. Everything done in the sandbox since the last reset is lost, and everyone has to sign in again. This takes a few minutes.',
        'choice-label': 'Data',
        options: {
          ANONYMIZED: { label: 'Anonymised', hint: 'Personal data is pseudonymised.' },
          DEBUG: {
            label: 'Debug data',
            hint: 'Real customer data stays readable (names marked "SBX - "). For debugging only.',
          },
        },
        confirm: 'Reset',
        cancel: 'Cancel',
        'busy-refusal': 'A sandbox run is already in progress. Wait until it has finished, then try again.',
        'start-failed': 'The reset could not be started. Try again in a moment.',
      },
    },
    'landiq-data-runs': {
      title: 'LandIQ · Data runs',
      board: {
        columns: { PENDING: 'Pending', UPLOAD: 'Upload', BUILD: 'Build', BACKFILL: 'Backfill', IMPORTED: 'Imported' },
        'column-states': { RUNNING: 'Running', DONE: 'Done', FAILED: 'Failed', INCOMPLETE: 'Incomplete' },
        linear: 'Open in Linear',
        'linear-label': '{ticket} – Open in Linear',
        'move-to': 'Move to {stage}',
        'stages-label': 'Stages: {stages}',
        empty: 'No data runs – LandIQ has no tickets in its manifest yet.',
        refusals: {
          IMPORTED_NOT_A_TARGET: 'A ticket reaches Imported only when its last stage is done.',
          NOT_NEXT_STAGE: 'This ticket can only move to {stage} – nothing was started.',
          NO_NEXT_STAGE: 'This ticket has no further stage – nothing was started.',
          STAGE_NOT_DONE: '{stage} is not done yet – nothing was started.',
          PARTLY_STARTED:
            "Render accepted only part of the stage – {started} of {total} runs were started. Start the rest again shortly on the entry's page.",
          UNKNOWN_TICKET: 'This ticket no longer exists. The board was reloaded.',
        },
      },
      states: {
        PENDING: 'Pending',
        BLOCKED: 'Blocked',
        RUNNING: 'Running',
        DONE: 'Done',
        FAILED: 'Failed',
      },
      'failure-reasons': { SOURCE_OUTAGE: 'Failed – source outage' },
      entries: {
        toggle: '{count} entries{failed, plural, =0 {} other { · # failed}}',
        label: 'Entries of {ticket}',
      },
      'waits-for': 'Waiting for: {entries}',
      confirm: {
        title: 'Start data run?',
        body: 'This command runs on {environment}:',
        environment: 'Environment: {environment}',
        cancel: 'Cancel',
        submit: 'Start',
      },
      refusals: {
        BLOCKED: 'Cannot start yet – waiting for: {entries}.',
        ALREADY_RUNNING: 'This entry is already running.',
        'to-running-run': 'Go to the running run →',
        ALREADY_DONE: 'This entry is already done. Nothing was started.',
        ENVIRONMENT_NOT_ALLOWED: 'LandIQ is not running on staging here – starting is not allowed.',
        RUNNER_UNAVAILABLE: 'Render did not accept the job. Nothing was started – try again in a moment.',
        UNKNOWN_ENTRY: 'This entry no longer exists. The list has been reloaded.',
      },
    },
    'landiq-data-run': {
      breadcrumb: 'LandIQ · Data runs',
      sections: { command: 'Command', log: 'Log', entries: 'All entries of {ticket}' },
      facts: {
        state: 'Status',
        'job-status': 'Job status',
        started: 'Started',
        finished: 'Finished',
        duration: 'Duration',
        error: 'Error',
        origin: 'Started via',
        'render-job': 'Render job',
      },
      origins: { API: 'Via immoOS', MANUAL: 'By hand' },
      chunks: 'Chunks – incoming {incoming} · processed {processed} · failed {failed}',
      queue: 'Queue (all sources): {queueDepth}',
      'never-ran': 'Never ran.',
      // A run's metrics (LandIQ ADR data-runs/0027): board card and entry page.
      metrics: {
        progress: 'Progress',
        'progress-label': 'Progress: {phase}',
        'phase-since': 'since {time}',
        'done-of': '{done} of {total} ({percent} %)',
        percent: '{percent} %',
        'done-ratio': '{done} / {total}',
        'done-only': '{done} done',
        rate: '{rate}/min',
        'rate-long': '{rate} per minute',
        eta: 'done ca. {time}',
        stale: 'No new snapshot for {age}',
        'stale-banner':
          'The latest snapshot is {age} old. A running command reports at least every 10 min – it may be stuck.',
        labels: {
          rate: 'Rate',
          eta: 'Expected to finish',
          requests: 'Requests',
          retries: 'Retries',
          updated: 'Snapshot',
        },
        requests: '{live} live · {replayed} replayed',
        times: 'Times',
        stages: { queued: 'Queue', command: 'Command', import: 'Import', total: 'Total' },
        'stage-time': '{stage} {duration}',
        'card-chunks': '{imported} imported · {failed} failed',
        'chunk-stats': 'Chunks of this run',
        'chunk-labels': {
          seen: 'Seen',
          imported: 'Imported',
          failed: 'Failed',
          rows: 'Rows written',
          p50: 'Median',
          max: 'Longest',
        },
        slowest: 'Slowest chunks',
        'slowest-columns': {
          chunk: 'Chunk',
          outcome: 'Outcome',
          duration: 'Duration',
          attempts: 'Attempts',
          rows: 'Rows',
          finished: 'Finished',
        },
        'chunk-outcomes': { IMPORTED: 'Imported', FAILED: 'Failed' },
        // The manifest's estimate against the command's run time, without queue time (LandIQ pull request 720).
        estimate: {
          label: 'Estimate',
          planned: 'est. {duration}',
          'actual-of': '{actual} of {estimate}',
          over: '+{overrun} over estimate ({percent} %)',
          left: 'ca. {left} left',
        },
      },
      'no-log': 'There is no log for this run – it was started by hand outside Render.',
      actions: {
        retry: 'Start again',
        start: 'Start',
        'retry-failed-chunks': 'Retry failed chunks ({count})',
        resume: 'Resume',
      },
      'retry-hint': 'Reloads the upload in full.',
      'resume-dialog': {
        title: 'Resume upload?',
        body: 'The upload restarts on {environment} and skips every chunk already uploaded.',
        cancel: 'Cancel',
        submit: 'Resume',
        refusals: {
          RESUME_NOT_POSSIBLE: 'Resuming is no longer possible for this run. The page has been reloaded.',
        },
      },
      'retry-failed-chunks-dialog': {
        title: 'Retry failed chunks?',
        body: '{count, plural, one {# failed chunk goes back into the queue and is imported again.} other {# failed chunks go back into the queue and are imported again.}} Nothing is uploaded again.',
        cancel: 'Cancel',
        submit: 'Retry',
        retried: '{count, plural, one {# chunk} other {# chunks}} put back into the queue.',
        refusals: {
          'no-longer-possible': 'Retrying is no longer possible for this run. The page has been reloaded.',
          STORAGE_UNAVAILABLE:
            'LandIQ could not read S3. Chunks already moved will be imported – try again in a moment.',
        },
      },
    },
    // Fleet — status and enrolment approvals (IMM-910.7, ADR 0047). `profile`,
    // `title`, field `label`/`value` and `badges` are agent-chosen and never
    // translated; only the five `state` and four `approval` tokens are.
    jobs: {
      worker: 'Worker',
      machine: 'Machine',
      claimed: 'Claimed',
      silent: 'No response',
      // A running round's progress and suspect flag (fleet-agent ADR 0012):
      // a round a review judges stuck is flagged, never killed.
      'beat-label': 'Heartbeat',
      'beat-never': 'No heartbeat yet',
      'beat-stale': 'STALE',
      'log-label': 'Log',
      'log-silent': 'SILENT',
      'progress-line': '{line} · {time}',
      'suspect-badge': 'Suspect',
      'suspect-title': 'This round looks stuck',
      'suspect-note': 'Flagged {time}: {reason}',
      'suspect-clear-cta': "It's fine",
      'suspect-release-cta': 'Release',
      'release-cta': 'Release job',
      'release-title': 'Release this job?',
      'release-body':
        'The work item is handed back and can be claimed again. The current holder is refused from its next call onward — release a still-running holder only when you mean to stop it.',
      'release-confirm': 'Release',
      'cancel-cta': 'Cancel',
      'logs-heading': 'Logs',
      'timeline-heading': 'Timeline',
      'timeline-empty': 'This round has not reported anything yet.',
      'timeline-kind-claimed': 'Claimed',
      'timeline-kind-progress': 'Progress',
      'timeline-kind-phase': 'Phase',
      'timeline-kind-done': 'Done',
      'timeline-kind-dead-letter': 'Needs a person',
      'timeline-kind-note': 'Note',
      'messages-heading': 'Messages to the round',
      'messages-empty': 'No messages yet. The round reads a message after its next tool call.',
      'message-input': 'Message to the round',
      'message-send-cta': 'Send',
      'message-stop-cta': 'Stop round',
      'message-stop-hint': 'A message that is just “stop” ends the round at its next safe point.',
      'stop-requested-note': '{name} asked it to stop at {time}.',
      'message-meta': '{name} · {time}',
      'message-stop-badge': 'Stop',
      'message-delivery': {
        waiting: 'Waiting for the host',
        sent: 'Sent to the host',
        received: 'Received by the host',
        delivered: 'Delivered',
        'not-delivered': 'Not delivered',
      },
      'generic-error': 'The action could not be completed. Please try again.',
      'question-options-heading': 'Options',
      'own-answer': 'Own answer',
      'own-answer-input': 'Answer in your own words',
      'recommended-badge': 'Recommended',
      'answer-cta': 'Send answer',
      'ask-again-cta': 'Ask again',
      'ask-again-success': 'The question on {ticket} goes out next on the messenger.',
      'question-read-only-note': 'You can read the question. Answering needs backoffice:fleet:command.',
    },
    'fleet-history': {
      title: 'History',
      machine: 'Machine',
      range: 'Period',
      'range-24h': '24 hours',
      'range-7d': '7 days',
      'range-30d': '30 days',
      'range-custom': 'Custom',
      from: 'From',
      to: 'To',
      'no-readings': 'There are no readings for this period.',
    },
    'fleet-board': {
      title: 'Board',
      subtitle:
        'What each lane is doing now and takes next — like `fleet-agent agent preview`, for every machine. Drag a queued ticket to change the order.',
      columns: 'Lanes',
      'no-lanes': 'No board yet – no machine has reported its lanes.',
      stale: 'Linear could not be read. The board shows the state of {time}.',
      linear: 'Open in Linear',
      'linear-label': '{ticket} – Open in Linear',
      priority: { '0': 'No priority', '1': 'Urgent', '2': 'High', '3': 'Medium', '4': 'Low' },
      assistant: {
        open: 'Fleet assistant',
        title: 'Fleet assistant',
        close: 'Close',
        placeholder: 'Ask the fleet assistant …',
        send: 'Send',
        empty: 'Ask about a blocker, a ticket or the state of the fleet.',
        'channel-telegram': 'Telegram',
        working: 'The fleet assistant is writing …',
      },
      views: { all: 'All projects' },
      free: '{n} free',
      running: '{n} running',
      next: 'next up',
      workers: {
        button: 'Workers',
        'button-label': 'Show the workers of {lane}',
        title: 'Workers of {lane}',
        count: '{count, plural, one {# worker reports this lane} other {# workers report this lane}}',
        none: 'No worker reports {lane}.',
        failed: 'Could not read the machines.',
        reported: 'reported {duration} ago',
        'out-of-service': 'Out of service',
        offline: 'Offline',
        close: 'Close',
        'stop-after-round': 'Stops after the round in hand',
        'verb-sent': 'Sent to {machine} — it is collected on the next tick.',
        'verb-failed': 'Could not be sent.',
      },
      nothing: 'Nothing in the queue',
      'not-taken': 'The lane does not take',
      'pr-first': 'Fix PRs first (needs-fixes), oldest first — then tickets',
      flow: '{states} → {running}',
      'flow-with': 'with {labels}',
      'flow-without': 'without {labels}',
      tags: {
        running: 'Running',
        silent: 'No response',
        stranded: 'No job',
        free: 'Free',
        bound: 'Bound',
        parked: 'Parked',
        answered: 'Answer in',
        assisted: 'Assisted',
        capped: 'Out of runs',
        waiting: 'Waiting on blocker',
      },
      since: 'for {duration}',
      beat: 'heartbeat {duration} ago',
      waiting: 'waiting for {duration}',
      'history-runs': '{count, plural, one {# run} other {# runs}}',
      'history-parked': '{count} parked',
      'history-resumed': '{count} resumed',
      stranded: 'is in {state}, but no job holds it',
      bound: 'only for {worker}',
      capped: '{runs}/{limit} runs — top up or finish by hand',
      assisted: 'a person builds it by hand',
      answered: 'goes before all others',
      'waiting-on': 'waiting on {tickets}',
      'waiting-on-unknown': 'waiting on a blocker',
      'running-on': 'On {machine}',
      'open-ticket': '{ticket} – Open details',
      'drag-handle': 'Move {ticket}',
      'drag-multi-project': 'Pick one project to sort',
      reorder: {
        'move-up': 'Move up',
        'move-down': 'Move down',
      },
      manual: 'manual',
      'manual-title': 'Placed by hand',
      dnd: {
        instructions: 'Drag, or move up and down with the arrow keys.',
        moved: '{ticket} at place {position} of {total}.',
      },
      panel: {
        close: 'Close details',
        loading: 'Loading details…',
        'load-failed': 'The details could not be loaded.',
        'off-board': 'Not on the board right now.',
        lane: 'Lane',
        state: 'State',
        priority: 'Priority',
        order: 'Order',
        'order-manual': 'Placed by hand, place {position}',
        'order-manual-unqueued': 'Placed by hand',
        'order-linear': 'Linear order',
        tag: 'Status',
        'current-heading': 'Current job',
        'no-current': 'No job holds this ticket.',
        'blockers-heading': 'Blockers',
        'blocker-slice': 'Slice',
        'blocker-open': 'Show {ticket} here',
        worker: 'Worker',
        machine: 'Machine',
        claimed: 'Claimed',
        'last-beat': 'Last heartbeat',
        silent: 'No response',
        runs: 'Rounds',
        'runs-value': '{runs} of {limit}',
        'history-heading': 'Runs',
        'history-empty': 'This ticket has never been claimed.',
        'run-live': 'running',
        'run-label': 'Run by {worker} on {time}',
        outcome: {
          finished: 'finished',
          parked: 'parked',
          abandoned: 'abandoned',
          'no-run': 'did not start',
          released: 'released',
        },
        duration: 'Took {duration}',
        tokens: '{count} tokens',
        'usage-unknown': 'Usage unknown',
        questions: 'Questions and answers',
        unanswered: 'Not answered yet',
        'answered-by': 'Answered by {name} on {time}',
        'asked-at': 'Asked on {time}',
        'question-brief': 'What it is: {brief}',
      },
    },
    'fleet-accounts': {
      title: 'Accounts',
      subtitle:
        'The accounts the workers are logged in with. Connecting, re-authenticating and disconnecting happen on the worker only, with `fleet-agent accounts`.',
      'read-only-hint': 'You can view the accounts but not change them.',
      empty: 'No worker has reported an account yet.',
      'save-error': 'That did not work. Please try again.',
      columns: {
        account: 'Account',
        machine: 'Machine',
        session: 'Session',
        week: 'Week',
        state: 'Status',
        enabled: 'Active',
        actions: 'Actions',
      },
      'no-usage': 'Not measured yet',
      'usage-read-at': 'read {time}',
      'max-weekly': 'max {percent}',
      state: {
        ready: 'Ready',
        paused: 'Paused',
        reauth: 'Needs re-auth',
        limited: 'At its limit',
        'limited-session': 'Session limit',
        'limited-week': 'Weekly limit',
      },
      'resets-in': 'resets in {duration}',
      'resets-at': 'resets {time}',
      'back-at': 'back {time}',
      'back-at-in': 'back {time} · in {duration}',
      duration: {
        minutes: '{minutes}m',
        'hours-minutes': '{hours}h {minutes}m',
        'days-hours': '{days}d {hours}h',
      },
      'reauth-hint': 'Re-auth on the worker: `fleet-agent accounts`.',
      'enabled-label': '{label} active',
      'limit-cta': {
        capped: 'Max {percent}%',
        uncapped: 'No limit',
      },
      'cancel-cta': 'Cancel',
      'max-weekly-dialog': {
        title: 'Weekly limit for {label}',
        field: 'Weekly limit in percent',
        hint: 'From this share of the weekly quota on, the worker stops using the account.',
        'range-error': 'Enter a whole number from {min} to {max}.',
        'save-cta': 'Save',
      },
    },
    'fleet-metrics': {
      title: 'Metrics',
      subtitle: 'Token usage of the fleet — per machine, agent and model.',
      loading: 'Loading metrics…',
      'view-machine': 'By machine',
      'view-agent': 'By agent',
      'view-model': 'By model',
      'filter-period': 'Period',
      'filter-agents': 'Agents',
      'filter-class': 'Class',
      'scale-day': 'Day',
      'scale-week': 'Week',
      'scale-month': 'Month',
      'scale-thirteen-weeks': '13 weeks',
      previous: 'Earlier period',
      next: 'Later period',
      'label-day': '{weekday} {date}',
      'label-week': 'Week {week}',
      'label-thirteen-weeks': 'Weeks {first}–{last}',
      range: '{from}–{to}',
      'agents-all': 'All agents',
      'agents-some': '{count} of {total}',
      'agents-runs': '{count, plural, one {# run} other {# runs}}',
      'class-all': 'All four',
      'kpi-tokens-all': 'Tokens · all four classes',
      'kpi-tokens-class': 'Tokens · {class}',
      'kpi-runs': 'Runs',
      'kpi-per-run': 'Tokens per run',
      'kpi-runtime': 'Runtime',
      'kpi-cost': 'Cost',
      'kpi-cost-sub': 'calculated, not measured',
      'kpi-unparsed': '{count} of them without readable usage',
      'kpi-all-readable': 'Usage of every run readable',
      'kpi-output-per-run': 'output per run {value}',
      'kpi-runtime-per-run': 'avg. {value} per run',
      'no-previous': 'no previous period in the ledger',
      'versus-day': 'vs. previous day',
      'versus-week': 'vs. previous week',
      'versus-month': 'vs. previous month',
      'versus-thirteen-weeks': 'vs. the 13 weeks before',
      'delta-new': 'new',
      'series-machine': 'Over time per machine',
      'series-agent': 'Over time per agent',
      'series-model': 'Over time per model',
      'rows-agents': 'Rows: agents',
      'rows-machines': 'Rows: machines',
      'one-scale': 'every row on one scale',
      sum: 'Total',
      'hint-all': 'Total of all four classes — {share} of it cache read. Pick a class above to see it alone.',
      'hint-class': 'Only {class}. The other three classes are in the breakdown below.',
      'share-of-fleet': '{share} of the fleet',
      'no-run-for': 'No run in this period: {list}.',
      'no-run-selected': 'No run of the selected agents in this period.',
      'heat-title': 'When the machines work',
      'heat-hint': 'Tokens per hour and weekday',
      'heat-none': 'no run',
      'heat-low': 'little',
      'heat-high': 'a lot',
      'heat-cell': '{weekday} {hour}:00 · {runs, plural, one {# run} other {# runs}}',
      'split-machine': 'Breakdown per machine',
      'split-agent': 'Breakdown per agent',
      'split-model': 'Breakdown per model',
      'split-hint': 'the four classes side by side, each on its own order of magnitude',
      'split-shape': 'Shape',
      'column-machine': 'Machine',
      'column-agent': 'Agent',
      'column-model': 'Model',
      'no-cache-write': '{model} writes no cache — the 0 under cache write is measured, not missing.',
      'leader-title': 'Agent ranking',
      'leader-hint': 'sorted by tokens of the selected class',
      'leader-tokens': 'Tokens',
      'leader-share': 'Share',
      'leader-delta': 'vs. previous period',
      'leader-runs': 'Runs',
      'leader-per-run': 'per run',
      'leader-output-per-run': 'output per run',
      'leader-runtime': 'avg. runtime',
      'leader-cost': 'Cost',
      'mix-title': 'Model mix per agent',
      'mix-hint': "Each model's share of the agent's tokens",
      'mix-runs': '{count}×',
      'tip-runs': '{count, plural, one {# run} other {# runs}}',
      'tip-unparsed': '{count} without readable usage',
      'tip-unknown': 'Usage not readable — tokens unknown, not 0.',
      'unparsed-note':
        '{count, plural, one {# run} other {# runs}} without readable usage — they count as runs and in the runtime, not in the tokens. Every total they fall into carries ≈.',
      'unknown-legend': 'dashed: runs happened, usage not readable',
      'approx-title': 'This total is a lower bound.',
      'pricing-footnote':
        'Tokens are measured. Euro is calculated from them — price table as of {asOf}, 1 USD = {rate} €, older runs at today’s prices.',
      'pricing-fallback': 'Models without their own price ({models}) are calculated at {fallback} prices.',
      'empty-title': 'No run happened in this period.',
      'empty-body': 'The first run in the ledger is from {date}. Pick a later period.',
      'empty-body-ledger': 'The ledger holds no run yet.',
      'empty-cta': 'Go to the latest period',
      'unreachable-title': 'Backend unreachable',
      'unreachable-body': 'The metrics could not be loaded. Better nothing here than stale figures that look current.',
      retry: 'Try again',
      'tokens-thousand': '{value}K',
      'tokens-million': '{value}M',
      'tokens-billion': '{value}B',
      'duration-hours': '{hours} h {minutes} min',
      'duration-minutes': '{minutes} min',
      'duration-seconds': '{seconds} s',
      'weekday-1': 'Mon',
      'weekday-2': 'Tue',
      'weekday-3': 'Wed',
      'weekday-4': 'Thu',
      'weekday-5': 'Fri',
      'weekday-6': 'Sat',
      'weekday-7': 'Sun',
    },
    fleet: {
      title: 'Fleet',
      'history-cta': 'History',
      'back-to-index': 'Back to the machine list',
      'panel-close': 'Close',
      'approved-by': 'Approved by',
      'public-key': 'Key',
      worker: 'Worker',
      'capacity-label': 'Capacity',
      capacity: '{running} of {max} machines',
      'group-pending': 'Awaiting approval',
      'group-machines': 'Machines',
      host: 'Host',
      platform: 'Platform',
      'agent-version': 'fleet-agent',
      'first-seen': 'First seen',
      'last-seen': 'Last seen',
      'reported-at': 'Reported',
      'clean-logs': 'Clean logs',
      'no-profiles': 'This machine reports no profiles.',
      stale: 'No response',
      'stale-note': 'Last seen {time}. The states shown may be out of date.',
      health: {
        disk: 'Disk',
        memory: 'Memory',
        swap: 'Swap',
        pressure: 'Memory pressure',
        temperature: 'Temperature',
        load: 'Load',
        'percent-value': '{percent} %',
        'temperature-value': '{degrees} °C',
        'load-value': '{factor} × cores',
        gigabytes: '{value} GB',
        'used-of-total': '{used} of {total} · {percent} %',
        measured: 'Measured {time}',
        'no-data': 'No data',
        'disk-full': 'Disk nearly full',
        'memory-full': 'Memory nearly full',
        throttled: 'Throttled — {reason}',
        'reason-temperature': 'Temperature {degrees} °C',
        'reason-load': 'Load {factor} × cores',
        'reason-memory-pressure': 'Memory pressure {percent} %',
        'reason-thermal-pressure': 'Thermal pressure',
        'state-heading': 'Health',
        'last-24h': 'Last 24 hours',
        'no-readings-24h': 'No readings in the last 24 hours.',
        'series-load-error': 'The readings could not be loaded.',
        'history-link': 'History',
        'threshold-percent': '{percent} %',
        'threshold-load': '{factor} ×',
      },
      'read-only-note': 'You can read the fleet. Commands and approvals need backoffice:fleet:command.',
      'join-request-title': 'Enrolment request',
      'join-request-body':
        'This machine signed in with the fleet key. It reports its state, but runs no command until it is approved.',
      'approve-cta': 'Approve',
      'reject-cta': 'Reject',
      'revoke-cta': 'Revoke access',
      'cancel-cta': 'Cancel',
      'approve-title': 'Approve this machine?',
      'approve-body':
        'This machine will then be able to run commands. No key is shown — the machine collects its own token.',
      approved: 'Approved. The machine will check in within a minute.',
      'reject-title': 'Reject this enrolment?',
      'reject-body':
        'The machine stays in the list, so the rejection is visible. Its enrolments are no longer accepted.',
      'revoke-title': 'Revoke access?',
      'revoke-body': 'The machine stops being controlled immediately. Its token is no longer accepted.',
      'empty-heading': 'No machine has enrolled yet.',
      'empty-body': 'Once a host starts the fleet-agent, it appears here as an enrolment awaiting your approval.',
      'generic-error': 'The action could not be completed. Please try again.',
      state: {
        pending: 'Pending',
        idle: 'Idle',
        working: 'Working',
        'waiting-on-human': 'Waiting on human',
        stopped: 'Stopped',
      },
      approval: {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        revoked: 'Revoked',
      },
      'scope-line': '{machine} · {profile}',
      'force-update-toggle': 'Discard local changes in the clone',
      'finish-toggle': 'Let the round in hand finish first',
      engine: {
        title: 'Engine',
        orchestrator: 'orchestrator {engine}',
        models: 'plan {plan} / implement {implement}',
        source: {
          flag: 'Run flag',
          'host-project-lane': 'Host: lane in this project',
          'host-lane': 'Host: lane',
          'host-default': 'Host: default',
          project: 'Project config',
          'built-in': 'Built-in',
        },
        'lane-field': 'Lane',
        'lane-placeholder': 'Blank — the host default',
        'project-field': 'Project',
        'project-placeholder': 'Blank — every project',
        'scope-default-hint': 'With no lane, the choice applies to every lane on the host that has none of its own.',
        'scope-lane-hint': 'Applies to this lane only — with a project, to this lane in that project only.',
        unset: 'Unset the host default',
        'unset-lane': "Unset this lane's choice",
        'engine-field': 'Engine',
        any: 'Any',
        pinned: 'Pinned: {engine}',
        running: 'Running on {engine}',
        'plan-model-field': 'Plan model',
        'plan-model-placeholder': "Blank — the engine's default",
        'implement-model-field': 'Implement model',
        'implement-model-placeholder': "Blank — the engine's default",
        'models-hint': 'Model ids as the engine takes them (e.g. opus, sonnet).',
      },
      accounts: {
        title: 'Claude accounts',
        'unknown-email': 'Unknown account',
        'default-dir': '~/.claude (default)',
        week: 'Week {percent}',
        session: 'Session {percent}',
        'rate-limited': 'rate-limited',
        'quota-read-at': 'As of {time}',
        lanes: 'Lanes: {lanes}',
        auth: {
          ok: 'Logged in',
          'logged-out': 'Logged out',
          'usage-limit': 'Usage limit',
          unknown: 'Unknown',
        },
      },
      'weekly-limit': {
        fact: 'Weekly limit',
        percent: '{percent}%',
        none: 'No limit',
        cta: 'Weekly limit',
        title: 'Weekly limit for {machine}',
        field: 'Weekly limit in percent',
        hint: 'From this weekly usage on, the machine takes no new jobs. Running jobs finish.',
        'save-cta': 'Save',
        'clear-cta': 'Remove limit',
        'range-error': 'Enter a whole number from {min} to {max}.',
        held: 'Weekly limit reached',
        'held-note':
          '{usagePercent}% of {limitPercent}% used. The machine takes no new jobs until {resetsAt}; running jobs finish.',
        'held-note-no-reset':
          '{usagePercent}% of {limitPercent}% used. The machine takes no new jobs until the weekly window resets; running jobs finish.',
      },
      command: {
        'sync-branch-label': 'Update branch',
        'sync-branch-title': 'Update the branch?',
        'sync-branch-body': 'Only on a clean working tree. Nothing is overwritten.',
        'sync-branch-cta': 'Update branch',
        'clean-checkout-label': 'Reset checkout',
        'clean-checkout-title': 'Reset the checkout?',
        'clean-checkout-body':
          "Puts the host's primary checkout back on an up-to-date main. Anything left there — uncommitted, or on a branch that was never pushed — is committed, pushed and opened as a draft PR first, so nothing is lost. Ignored files such as node_modules are kept. Rounds run in their own worktrees and are unaffected.",
        'clean-checkout-cta': 'Rescue and reset to main',
        'restart-agent-label': 'Restart agent',
        'restart-agent-title': 'Restart the fleet-agent?',
        'restart-agent-body':
          "This machine's fleet-agent is restarted through its service unit. Rounds in flight are unaffected — the agent only watches them. The machine reports no status for a short while.",
        'restart-agent-cta': 'Restart',
        'update-agent-label': 'Update agent',
        'update-agent-title': 'Update the fleet-agent?',
        'update-agent-body':
          "This machine's fleet-agent pulls the latest code into its clone, re-installs and restarts through its service unit. Then every agent restarts onto the new code — each round in flight ends at a step boundary first and resumes after. The update stops if the clone has local changes.",
        'update-agent-cta': 'Update',
        'force-update-agent-label': 'Force update',
        'force-update-agent-title': 'Force a fleet-agent update?',
        'force-update-agent-body':
          "Resets the fleet-agent's clone to origin — local changes and local commits in the clone are lost — re-installs even if nothing changed, and restarts the service. Then every agent restarts onto the new code; each round in flight ends at a step boundary first.",
        'force-update-agent-cta': 'Force update',
        'start-all-label': 'Start all',
        'start-all-title': 'Start every agent?',
        'start-all-body':
          "Every stopped agent on this machine starts with its profile's defaults. Agents already running are left alone.",
        'start-all-cta': 'Start all',
        'stop-all-label': 'Stop all',
        'stop-all-title': 'Stop every agent?',
        'stop-all-body':
          'Every agent on this machine stops — a round in flight ends at its next step boundary and resumes on the next start. The stop holds across reboots and updates until someone starts it.',
        'stop-all-cta': 'Stop all',
        'restart-all-label': 'Restart all',
        'restart-all-title': 'Restart every agent?',
        'restart-all-body':
          'Every agent on this machine restarts, a stopped one included. A round in flight ends at its next step boundary and resumes afterwards.',
        'restart-all-cta': 'Restart all',
        'start-label': 'Start',
        'start-title': 'Start this lane?',
        'start-body': 'Lifts the stop on this lane on this machine. The lane takes work again.',
        'start-cta': 'Start',
        'stop-label': 'Stop',
        'stop-title': 'Stop this lane?',
        'stop-body':
          'The lane stops on this machine — a round in flight ends at its next step boundary and resumes on the next start. The stop holds across reboots and updates until someone starts it.',
        'stop-cta': 'Stop',
        'restart-label': 'Restart',
        'restart-title': 'Restart this lane?',
        'restart-body':
          'The lane restarts, even if it was stopped. A round in flight ends at its next step boundary and resumes afterwards.',
        'restart-cta': 'Restart',
        'logs-reset-label': 'Clear build log',
        'logs-reset-title': 'Clear the build log?',
        'logs-reset-body': "This profile's build log is reset to empty. A running build is not interrupted.",
        'logs-reset-cta': 'Clear build log',
        'logs-label': 'Fetch log',
        'logs-download-label': 'Request the full log',
        'handover-label': 'Hand over a ticket',
        'e2e-unlock-label': 'Unlock e2e stack',
        'run-command-label': 'Run command',
        'system-preview-label': 'Disk report',
        'system-prune-label': 'Prune disk',
        'system-prune-title': 'Prune disk?',
        'system-prune-body':
          "The machine deletes every project's stale worktrees, leaked testcontainers, old Docker images and build cache, and prunes the pnpm store. Rounds in flight and their worktrees are left alone.",
        'system-prune-cta': 'Prune',
        'agent-label': 'Agent command',
        'steer-label': 'Message to job',
        'engine-label': 'Choose engine',
        'engine-title': 'Choose the engine?',
        'engine-body':
          'The chosen lanes run on this engine from their next round. Rounds in flight keep theirs. The machine refuses a lane it does not declare or an engine it does not have installed.',
        'engine-cta': 'Set engine',
        'engine-pin-label': 'Pin engine',
        'engine-pin-title': 'Pin a lane to an engine?',
        'engine-pin-body':
          'From its next round the lane runs only on accounts of this engine. Rounds in flight keep theirs. With “Any” the fleet again picks the account with the most limit left, whatever its engine.',
        'engine-pin-cta': 'Pin',
      },
      'run-command': {
        title: 'Run command',
        body: "Runs one pnpm command in the chosen project's checkout on this machine — without a shell, for at most ten minutes.",
        'project-field': 'Project',
        'project-placeholder': 'Choose a project',
        'no-projects': 'This machine has not reported a project yet.',
        'command-field': 'Command',
        'command-hint': 'Spaces split the words; quotes keep one word whole.',
        'unclosed-quote': 'A quote ({quote}) is not closed.',
        cta: 'Run',
        'scope-line': '{machine} · {project}',
        'running-hint': 'Closing does not cancel the command — its result then shows in the command history.',
        'exit-code': 'Exit code {code}',
        'no-exit-code': 'No exit code — stopped after ten minutes',
        output: 'Output',
        'no-output': 'No output.',
        truncated: 'Earlier output was cut — only the last 16,000 characters are shown.',
        'again-cta': 'Another command',
        'close-cta': 'Close',
      },
      'e2e-unlock': {
        title: 'Unlock e2e stack',
        body: "Breaks the lock on one e2e stack on this machine, through each project's stack unlock — for a stack a dead round left locked.",
        'project-field': 'Project',
        'every-project': 'Every project',
        'worker-field': 'Worker',
        'worker-hint': "Whose stack, e.g. mini-pc-engineer-1. Empty: the host's manual stack.",
        'worker-invalid': 'A worker is letters, digits, dots, dashes and underscores, starting with a letter or digit.',
        cta: 'Unlock',
        'scope-line': '{machine} · {project} · {worker}',
        holder: 'Was locked by {owner} on {host} since {since}',
        'holder-worktree': 'Worktree {worktree}',
        'no-holder': 'No lock was held.',
        'again-cta': 'Unlock another',
      },
      'system-preview': {
        title: 'Disk report',
        body: 'Reports what a prune would free on this machine — stale worktrees, leaked testcontainers, old Docker images and build cache, the pnpm store. Deletes nothing.',
        'project-field': 'Project',
        'every-project': 'Every project',
        cta: 'Get report',
        'scope-line': '{machine} · {project}',
        'again-cta': 'Report again',
      },
      outcome: {
        accepted: 'Issued — waiting for the machine',
        taken: 'Sent — not confirmed yet',
        received: 'Received by the machine',
        running: 'Running',
        started: 'Started',
        'already-running': 'Was already running',
        stopped: 'Stopped',
        'already-stopped': 'Was already stopped',
        synced: 'Updated',
        dirty: 'Working tree not clean',
        'not-fast-forwardable': 'Cannot fast-forward',
        cleaned: 'Reset and up to date',
        restarting: 'Restart issued',
        'unknown-profile': 'Unknown profile',
        refused: 'Refused',
        failed: 'Failed',
        'timed-out': 'Not picked up — no machine took the command',
        'timed-out-no-result': 'Picked up but no result — the machine went silent',
        ok: 'Done',
        'no-pane': 'No tmux window',
        'log-absent': 'No log present',
        reset: 'Cleared',
        uploaded: 'Uploaded',
        'handed-over': 'Handed over',
      },
      log: {
        'toggle-open': 'Logs',
        'toggle-close': 'Close log',
        'stream-build': 'Build log',
        'stream-agent': 'Agent log',
        'stream-group': 'Log stream',
        lines: 'Lines',
        'newest-first': 'Newest first',
        'newest-at': 'Newest line {time}',
        copy: 'Copy log',
        copied: 'Copied',
        'fetch-again': 'Fetch again',
        fetching: 'Loading…',
        'scroll-hint': 'Scroll sideways for long lines',
        'empty-title': 'No lines stored yet.',
        'empty-body': 'The machine has reported nothing on this stream.',
        'read-only-empty': 'Reading logs needs backoffice:fleet:read.',
      },
      activity: {
        title: 'Command history',
        'requested-by': 'Requested by',
        'requested-by-machine': 'Requested from',
        'e2e-unlock-manual': "The host's manual stack",
        'lane-project': 'project {project} only',
        finish: 'after the round in hand',
        'engine-default': 'host default',
        'engine-lane': 'lane {lane}',
        'engine-lane-project': 'lane {lane} in {project}',
        'engine-unset': 'unset',
        'engine-plan': 'plan {model}',
        'engine-implement': 'implement {model}',
        'dirty-paths': 'Changed paths ({count})',
        steps: {
          sent: 'sent',
          received: 'received',
          running: 'running',
          settled: 'done',
        },
      },
    },
    skills: {
      'document-analyses': {
        title: 'Document analyses',
        empty: 'No skills found.',
        search: 'Search skill…',
        status: {
          active: 'Active',
          inactive: 'Inactive',
          unconfigured: 'Not configured',
        },
        'last-edited-by': 'Last edited by {name} · {date}',
        'never-edited': '—',
        columns: {
          name: 'Skill',
          status: 'Status',
          'last-edited': 'Last edited',
        },
        'category-group': DOCUMENT_CATEGORY_GROUP_LABELS,
        category: DOCUMENT_CATEGORY_LABELS,
        fallback: {
          title: 'Fallback skill for documents',
          subtitle:
            'Used for every document with no matching active category skill — including documents that are not yet categorised.',
          status: 'Active',
          'system-attribution': 'Set up by the system',
          'last-edited-by': 'Last edited by {name} · {date}',
        },
        editor: {
          'prompt-label': 'Analysis prompt',
          'baseline-marker': 'Fallback skill for documents (starting point)',
          'baseline-hint':
            'Pre-filled with the currently effective prompt (fallback skill). Adapt it and activate the skill to use it for this document type.',
          save: 'Save',
          activate: 'Activate skill',
          deactivate: 'Deactivate skill',
          cancel: 'Cancel',
          'back-cta': 'Back to list',
          saved: 'Changes saved.',
          'last-edited-by': 'Last edited by {name} · {date}',
          'never-edited': 'Not edited yet',
          status: {
            active: 'Active',
            inactive: 'Inactive',
            unconfigured: 'Not configured',
          },
          errors: {
            'prompt-empty': 'The prompt must not be empty.',
            'prompt-too-long': 'The prompt is too long (at most 10,000 characters).',
            generic: 'The skill could not be saved. Please try again.',
          },
          stale: {
            title: 'Skill was changed',
            body: 'This skill was changed in the meantime. Please reload the page before saving.',
            reload: 'Reload',
          },
          'deactivate-confirm': {
            title: 'Deactivate skill?',
            body: 'Subsequent analyses of this document type will use the fallback prompt again.',
            confirm: 'Deactivate',
            cancel: 'Cancel',
          },
        },
        'fallback-editor': {
          title: 'Fallback skill for documents',
          'prompt-label': 'Prompt',
          save: 'Save',
          cancel: 'Cancel',
          'back-cta': 'Back to list',
          saved: 'Changes saved.',
          status: 'Active',
          'last-edited-by': 'Last edited by {name} · {date}',
          'system-attribution': 'Set up by the system',
          errors: {
            'prompt-empty': 'The prompt must not be empty.',
            'prompt-too-long': 'The prompt is too long (at most 10,000 characters).',
            generic: 'The skill could not be saved. Please try again.',
          },
          stale: {
            title: 'Skill was changed',
            body: 'This skill was changed in the meantime. Please reload the page before saving.',
            reload: 'Reload',
          },
        },
      },
      'fact-keys': {
        title: 'Fact keys',
        empty: 'No fact keys found.',
        search: 'Search fact key…',
        'read-only-hint': 'You can view the taxonomy but not edit it.',
        'save-error': 'The change could not be saved. Please try again.',
        create: {
          button: 'Add field',
          title: 'Add field',
          'id-hint': 'Group prefix and name, for example FLA.WOHNFLAECHE. The ID cannot be changed later.',
          'immutable-hint': 'Cannot be changed after creation.',
          placeholder: 'Please choose',
          submit: 'Add',
          cancel: 'Cancel',
        },
        retire: {
          button: 'Retire',
          title: 'Retire field?',
          body: 'For {label} ({key}), no new values can be recorded afterwards. Stored values stay readable. The field no longer counts as a need for any product. This cannot be undone, and the ID cannot be reused.',
          'warning-document-analysis':
            'The document analysis still fills this field. New values from it will be lost after retiring.',
          'warning-property-field': 'This field is mapped to the Objekt field {propertyField}.',
          confirm: 'Retire',
          cancel: 'Cancel',
        },
        columns: {
          key: 'ID',
          label: 'Label',
          group: 'Group',
          'value-type': 'Value type',
          frequency: 'Frequency',
          categories: 'Object types',
          needs: 'Need',
          origin: 'Origin',
          status: 'Status',
          scope: 'Scope',
          'public-safe': 'Public-safe',
          'property-field': 'Objekt field',
          parent: 'Belongs to',
        },
        group: {
          AUF: 'Assignment, reference dates and inspection',
          KAT: 'Cadastre and plot',
          LAG: 'Location and surroundings',
          PLR: 'Planning law and public-law status',
          ERS: 'Servicing and contributions',
          GB: 'Land register, rights and encumbrances',
          GEB: 'Buildings in general',
          FLA: 'Areas',
          BAU: 'Structural elements and fittings',
          ENE: 'Energy and building services',
          MOD: 'Modernisation',
          ZUS: 'Condition and damage',
          NEB: 'Outbuildings, parking and outdoor areas',
          NUT: 'Use and letting',
          WEG: 'Condominium ownership',
          ZVG: 'Compulsory auction',
          UNT: 'Documents and information',
          SON: 'Other',
          VMK: 'Marketing',
          GEW: 'Commercial',
        },
        frequency: {
          ONCE: 'once',
          LIST: 'list',
          PER_BUILDING: 'per building',
          PER_UNIT: 'per unit',
          PER_RIGHT: 'per right',
          PER_DOCUMENT: 'per document',
          PER_FINDING: 'per finding',
          PER_PARCEL: 'per parcel',
          PER_MEASURE: 'per measure',
          PER_PARTIAL_AREA: 'per partial area',
          PER_LAND_REGISTER_SHEET: 'per land register sheet',
          PER_INVENTORY_ENTRY: 'per inventory entry no.',
          PER_OWNER: 'per owner',
          PER_TRANSITION: 'per transition',
          PER_BENEFICIARY: 'per beneficiary',
          PER_ENCROACHMENT: 'per encroachment',
          PER_BUILDING_ENCUMBRANCE: 'per building encumbrance',
        },
        origin: {
          OAB_0_1: 'oab-0.1',
          IMMOOS_ADDITION: 'immoOS addition',
        },
        status: {
          ACTIVE: 'Active',
          RETIRED: 'Retired',
        },
        product: {
          ACQUISITION_BRIEFING: 'Acquisition briefing',
          VALUATION_1A: 'Valuation 1a',
          VALUATION_1B: 'Valuation 1b',
          EXPOSE: 'Exposé',
        },
        need: {
          REQUIRED: 'Required',
          RECOMMENDED: 'Recommended',
          OPTIONAL: 'Optional',
          NOT_USED: 'Not used',
        },
        needs: {
          columns: {
            category: 'Property type',
            'marketing-type': 'Marketing type',
            subtype: 'Subtype',
            situation: 'Situation',
            need: 'Need',
          },
          'marketing-type': { ALL: 'All', SALE: 'Sale', RENTAL: 'Rental' },
          subtype: { ALL: 'All' },
          situation: { ALL: 'All', LET: 'Let', VACANT: 'Vacant', NEW_BUILD: 'New build' },
          'add-row': 'Add entry',
          'remove-row': 'Remove entry',
          'product-control': '{control} – {product}',
          'row-control': '{control} – {product}, entry {position}',
          conflict:
            'This need contradicts the entry {conditions} ({need}): a property can meet both, and neither is more specific. Add an entry with both conditions or align the levels.',
        },
        'need-filter-option': '{need} for {product}',
        filters: {
          group: 'Group',
          need: 'Need',
          origin: 'Origin',
          status: 'Status',
          all: 'All',
        },
        'yes-no': {
          true: 'Yes',
          false: 'No',
        },
        'number-style': {
          PLAIN_INTEGER: 'Integer without thousands separator',
          GROUPED_DECIMAL: 'Decimal with thousands separator',
        },
        source: {
          landiq: 'immoOS IQ',
          altlasten_kampfmittel_auskunft: 'Contaminated land and unexploded ordnance enquiry',
          auftrag_beweisbeschluss: 'Instruction, evidentiary order',
          bauakte_genehmigung: 'Building file, permit',
          baubeschreibung: 'Building specification',
          baulastenverzeichnis: 'Register of building encumbrances',
          bebauungsplan_planungsauskunft: 'Development plan, planning enquiry',
          besichtigungsprotokoll: 'Inspection report',
          betriebskostenabrechnung: 'Service charge statement',
          bgf_berechnung: 'Gross floor area calculation',
          boris: 'BORIS',
          denkmal_auskunft: 'Heritage listing enquiry',
          eigentuemer_auskunft: 'Information from the owner',
          energieausweis: 'Energy certificate',
          erschliessungsbeitrag_auskunft: 'Servicing contribution enquiry',
          flurkarte_kataster: 'Cadastral map, cadastre',
          fotos_aussen: 'Exterior photos',
          fotos_innen: 'Interior photos',
          geoportal_luftbild: 'Geoportal, aerial photo',
          grundbuch: 'Land register',
          grundrisse_schnitte: 'Floor plans, sections',
          heizung_schornsteinfeger: 'Heating, chimney sweep',
          mietspiegel_wohnlagenkarte: 'Rent index, residential location map',
          mietvertrag_mieterliste: 'Lease, tenant list',
          modernisierungsbelege: 'Modernisation receipts',
          satzungen_sanierung_erhaltung: 'Bylaws (redevelopment, preservation)',
          sonstiges: 'Other',
          teilungserklaerung_aufteilungsplan: 'Declaration of division, division plan',
          weg_unterlagen: 'Condominium documents',
          wohnflaechenberechnung: 'Living area calculation',
        },
        entry: {
          'needs-heading': 'Need per product and property type',
          'details-heading': 'From the property survey form',
          'details-hint':
            'These details come from the property survey form (Objektaufnahmebogen) and cannot be edited here.',
          description: 'Description',
          'value-list': 'Value list',
          subfields: 'Subfields',
          sources: 'Sources',
          'photo-derivable': 'Derivable from photos',
          'consequence-when-missing': 'Consequence when missing',
          'collection-hint': 'Collection hint',
          'rule-references': 'Rule references',
          'number-style': 'Number format',
          unit: 'Unit',
          parent: 'Belongs to',
          photo: {
            YES: 'yes',
            PARTLY: 'partly',
            NO: 'no',
          },
          'personal-data-hint': 'Personal data cannot be made public.',
          save: 'Save',
          cancel: 'Cancel',
          close: 'Close',
          'load-error': 'The entry could not be loaded. Please try again.',
        },
        'value-type': {
          text: 'Text',
          number: 'Number',
          enum: 'Choice',
          boolean: 'Yes/No',
          date: 'Date',
          list: 'List',
          structure: 'Structure',
          document_reference: 'Document reference',
        },
        scope: {
          PROPERTY_INTRINSIC: 'Property attribute',
          DEAL_CONTEXT: 'Deal context',
        },
        'property-field-none': 'No mapping',
        'public-safe-warning':
          'Public-safe fact keys may appear in an Exposé. Only enable this for information buyers are allowed to see.',
        'public-safe-confirm': {
          title: 'Make fact key public-safe?',
          confirm: 'Make public-safe',
          cancel: 'Cancel',
        },
      },
      'agent-skills': {
        title: 'Agent skills',
        empty: 'No agent skills yet.',
        search: 'Search agent skills…',
        new: 'New agent skill',
        'last-edited-by': 'Last edited by {name} · {date}',
        'never-edited': '—',
        columns: {
          name: 'Name',
          key: 'Key',
          status: 'Status',
          triggers: 'Triggers',
          'last-edited': 'Last edited',
        },
        status: {
          active: 'Active',
          inactive: 'Inactive',
        },
        trigger: {
          EVENT: 'Event',
          SCHEDULE: 'Schedule',
          MANUAL: 'Manual',
        },
        editor: {
          'create-title': 'New agent skill',
          'key-label': 'Key',
          'key-hint': 'Immutable once created. Lower-case letters, digits, hyphens, underscores.',
          'name-label': 'Name',
          'description-label': 'Description',
          'description-hint': 'What this skill does, in plain language — members see this text.',
          'instructions-label': 'Instructions',
          'instructions-hint': 'The prompt every run of this skill starts from.',
          'triggers-label': 'Supported triggers',
          'triggers-hint': 'Pick at least one. An instance uses exactly one of them.',
          'allowlist-label': 'Default tools',
          'allowlist-hint': 'The tools a new instance starts from. Members may narrow this list, never widen it.',
          'allowlist-empty': 'No tools selected.',
          'approval-window-label': 'Approval window (days)',
          'approval-window-hint':
            'How long a proposed action waits for approval before it expires: {min} to {max} days.',
          create: 'Create',
          save: 'Save',
          activate: 'Activate',
          deactivate: 'Deactivate',
          cancel: 'Cancel',
          created: 'Agent skill created. It is still inactive — activate it when it is ready.',
          saved: 'Saved.',
          'inactive-hint': 'This agent skill is inactive. Members cannot activate it.',
          errors: {
            generic: 'The change could not be saved. Please try again.',
          },
          stale: {
            title: 'Changed in the meantime',
            body: 'Someone else edited this agent skill since you opened the page. Reload to see the current version — your unsaved input will be lost.',
            reload: 'Reload',
          },
          'deactivate-confirm': {
            title: 'Deactivate agent skill?',
            body: 'Every instance of this skill pauses. Members keep their grants and the run history stays readable — activating it resumes them unchanged.',
            confirm: 'Deactivate',
            cancel: 'Cancel',
          },
        },
      },
    },
  },
};
